import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { Button } from '../components/ui/button'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import { api, getApiErrorMessage } from '../lib/api'
import { normalizeComplianceCheckResult } from '../lib/complianceCheckResult'
import { formatBirthYmdInput, formatKoreanPhoneInput, normalizeBirthDateForApi } from '../lib/crmInputFormat'
import { compareExtractedToCurrent } from '../lib/customerBusinessCardExtract'
import { openCustomerCardNewsWindow } from '../lib/customerCardNews'
import { formatApiDateTimeDisplay, formatDate, formatDateTimeMinute } from '../lib/date'
import { segmentOptionsForStored, sourceOptionsForStored } from '../lib/customerCrmOptions'
import sgezLogo from '../assets/logo.png'

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toDatetimeLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

function defaultScheduleRangeLocal() {
  const start = new Date()
  start.setMinutes(0, 0, 0)
  start.setHours(start.getHours() + 1)
  const end = new Date(start.getTime() + 60 * 60 * 1000)
  return { startLocal: toDatetimeLocal(start), endLocal: toDatetimeLocal(end) }
}

function defaultContactAtLocal() {
  const now = new Date()
  now.setSeconds(0, 0)
  return toDatetimeLocal(now)
}

function crmQuery(id) {
  return `customerRefId=${encodeURIComponent(String(id))}`
}

function customerPath(path, customerId) {
  if (!path || !customerId) return path
  const glue = path.includes('?') ? '&' : '?'
  return `${path}${glue}${crmQuery(customerId)}`
}

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function waitMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0)))
}

function integratedReportStatusValue(value) {
  return String(value || '').trim().toUpperCase()
}

function integratedReportStatusLabel(value) {
  const status = integratedReportStatusValue(value)
  if (status === 'PENDING') return '생성 대기'
  if (status === 'RUNNING') return '생성 중'
  if (status === 'COMPLETED') return '생성 완료'
  if (status === 'FAILED') return '생성 실패'
  if (status === 'DRAFT') return '초안'
  return '확인 필요'
}

function integratedReportIsReady(report) {
  const status = integratedReportStatusValue(report?.status)
  return !status || status === 'COMPLETED' || status === 'DRAFT'
}

const CUSTOMER_COVERAGE_SUMMARY_REPORT_CODE = 'CUSTOMER_COVERAGE_SUMMARY_REPORT'
const CUSTOMER_INTEGRATED_REPORT_CODE = 'CUSTOMER_INTEGRATED_REPORT'
const CUSTOMER_DIAGNOSIS_SIMULATION_REPORT_CODE = 'CUSTOMER_DIAGNOSIS_SIMULATION_REPORT'
const SHOW_DIAGNOSIS_RECOMMENDATIONS = false
const SHOW_DIAGNOSIS_TERMS_SEARCH = false

function isImageOutputTemplate(item) {
  return item?.templateStatus === 'PUBLISHED'
    && ['PNG_OVERLAY', 'HYBRID'].includes(item?.renderMode)
}

function isHtmlOutputTemplate(item) {
  return item?.templateStatus === 'PUBLISHED'
    && ['HTML', 'HYBRID'].includes(item?.renderMode)
}

function outputTemplateLabel(item) {
  return item?.effectiveSelectionLabel || item?.selectionLabel || item?.title || '출력물 템플릿'
}

function preferredOutputTemplateId(items) {
  const rows = Array.isArray(items) ? items : []
  const preferred = rows.find((item) => item?.defaultTemplate) || rows[0]
  return preferred?.id ? String(preferred.id) : ''
}

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function formatCount(value) {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0'
}

function termsDocumentTitle(doc) {
  return doc?.productName || doc?.termsTitle || '약관 문서'
}

function normalizeSearchText(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, '')
}

function buildDiseaseCoverageTerms(diseaseName, diseaseCode) {
  const terms = new Set()
  const name = normalizeSearchText(diseaseName)
  const code = normalizeSearchText(diseaseCode).toUpperCase()
  if (name) terms.add(name)
  if (code) {
    terms.add(code)
    terms.add(code.replace(/\./g, ''))
    const dot = code.indexOf('.')
    if (dot > 1) terms.add(code.slice(0, dot))
    else if (code.length > 3) terms.add(code.slice(0, 3))
  }
  return [...terms].filter(Boolean)
}

function coverageMatchesDiseaseTerms(coverage, terms) {
  if (!terms.length) return false
  const text = normalizeSearchText([
    coverage?.coverageNameRaw,
    coverage?.coverageNameNormalized,
    coverage?.coverageGroup,
    coverage?.note,
  ].filter(Boolean).join(' '))
  return terms.some((term) => text.includes(term))
}

function diseaseJudgementLabel(status, fallback) {
  if (fallback) return fallback
  if (status === 'HIGH_POSSIBILITY') return '보장 가능성 높음'
  if (status === 'REVIEW_REQUIRED') return '검토 필요'
  if (status === 'TERMS_LINK_REQUIRED') return '약관 연결 필요'
  return '관련 낮음'
}

function diseaseJudgementClass(status) {
  if (status === 'HIGH_POSSIBILITY') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (status === 'REVIEW_REQUIRED') return 'border-sky-200 bg-sky-50 text-sky-700'
  if (status === 'TERMS_LINK_REQUIRED') return 'border-amber-200 bg-amber-50 text-amber-800'
  return 'border-slate-200 bg-slate-50 text-slate-600'
}

function openDaumPostcode(onComplete) {
  if (!window?.daum?.Postcode) {
    throw new Error('daum_postcode_not_loaded')
  }
  new window.daum.Postcode({
    oncomplete(data) {
      onComplete?.(data)
    },
  }).open()
}

function normalizeYmdDateForApi(display, label = '날짜') {
  const trimmed = String(display || '').trim()
  if (!trimmed) return { ok: true, value: null }
  const d = trimmed.replace(/\D/g, '')
  if (d.length !== 8) {
    return { ok: false, message: `${label}는 숫자 8자리(YYYYMMDD)까지 입력해 주세요.` }
  }
  const y = Number(d.slice(0, 4))
  const m = Number(d.slice(4, 6))
  const day = Number(d.slice(6, 8))
  if (m < 1 || m > 12 || day < 1 || day > 31) {
    return { ok: false, message: `${label}를 확인해 주세요.` }
  }
  const dt = new Date(y, m - 1, day)
  if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== day) {
    return { ok: false, message: `${label}를 확인해 주세요.` }
  }
  return { ok: true, value: `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` }
}

function calendarDateValue(value) {
  const normalized = normalizeYmdDateForApi(value)
  return normalized.ok && normalized.value ? normalized.value : ''
}

function CustomerYmdInput({ value, onChange, disabled, placeholder, ariaLabel }) {
  const calendarLabel = `${ariaLabel || '날짜'} 캘린더`
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_2.75rem] gap-1">
      <input
        type="text"
        className="min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums"
        value={value}
        onChange={(e) => onChange(formatBirthYmdInput(e.target.value))}
        placeholder={placeholder || 'YYYY-MM-DD'}
        inputMode="numeric"
        disabled={disabled}
      />
      <div
        className={`relative flex min-w-0 items-center justify-center rounded border border-slate-300 transition focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100 ${disabled ? 'bg-slate-100 text-slate-400' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
        title={calendarLabel}
      >
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
        <input
          type="date"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          value={calendarDateValue(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={calendarLabel}
        />
      </div>
    </div>
  )
}

function customerNamePhoneLabel(item) {
  const name = String(item?.customerName || '').trim() || '(이름 없음)'
  const phone = formatKoreanPhoneInput(item?.customerPhone || '')
  return phone ? `${name}(${phone})` : name
}

function koreanManAge(birthYmd) {
  const m = String(birthYmd || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2]) - 1
  const d = Number(m[3])
  const birth = new Date(y, mo, d)
  if (Number.isNaN(birth.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const md = today.getMonth() - birth.getMonth()
  if (md < 0 || (md === 0 && today.getDate() < birth.getDate())) age -= 1
  return age
}

function occurrenceOriginalFromResource(ev) {
  if (!ev?.occurrenceKey || typeof ev.occurrenceKey !== 'string') return null
  const k = ev.occurrenceKey
  const pipe = k.indexOf('|')
  if (pipe < 0) return null
  return k.slice(pipe + 1)
}

/** @param {any} detail GET /calendar/events/:id */
/** @param {any} listRow 목록 occurrence 요약 */
/** @param {string|null|undefined} newDescription */
function buildCalendarPatchPayload(detail, listRow, newDescription) {
  const useDef = detail.useDefaultReminders !== false
  let reminderOffsetsMinutes = null
  if (!useDef) {
    let arr = []
    try {
      if (detail.reminderOffsetsJson) arr = JSON.parse(detail.reminderOffsetsJson)
    } catch {
      arr = []
    }
    reminderOffsetsMinutes = Array.isArray(arr) && arr.length ? arr.map(Number) : []
  }
  const startAt = listRow?.startAt ?? detail.startAt
  const endAt = listRow?.endAt ?? detail.endAt
  const base = {
    title: detail.title,
    description: newDescription ?? null,
    startAt: typeof startAt === 'string' ? startAt : new Date(startAt).toISOString(),
    endAt: typeof endAt === 'string' ? endAt : new Date(endAt).toISOString(),
    allDay: !!detail.allDay,
    timeZone: detail.timeZone || 'Asia/Seoul',
    useDefaultReminders: useDef,
    reminderOffsetsMinutes,
    rrule: detail.rrule && String(detail.rrule).trim() ? detail.rrule : null,
    occurrenceOriginalStart: null,
  }
  const hasRrule = detail.rrule && String(detail.rrule).trim()
  const orig = occurrenceOriginalFromResource(listRow)
  if (hasRrule && orig) {
    base.occurrenceOriginalStart = orig
  }
  if (detail.eventKind === 'ORG') {
    const orgTargets = Array.isArray(detail.orgTargets)
      ? detail.orgTargets.map((t) => ({
        orgId: Number(t.orgId),
        includeDescendants: !!t.includeDescendants,
      }))
      : []
    const explicitUserIds = Array.isArray(detail.explicitUserIds) ? detail.explicitUserIds.map(Number) : []
    return { ...base, orgTargets, explicitUserIds }
  }
  return { ...base, orgTargets: null, explicitUserIds: null }
}

function formatRangeLabel(isoStart, isoEnd, allDay) {
  if (!isoStart) return ''
  if (allDay) {
    const s = formatDate(isoStart)
    const e = formatDate(isoEnd)
    if (s === '-' || e === '-') return String(isoStart || '')
    return `${s} ~ ${e} (종일)`
  }
  const s = formatDateTimeMinute(isoStart)
  const e = formatDateTimeMinute(isoEnd)
  if (s === '-' || e === '-') return String(isoStart || '')
  return `${s} ~ ${e}`
}

function isContactEvent(row) {
  return String(row?.title || '').trim().startsWith('[연락]')
}

function isConsultationEvent(row) {
  return String(row?.title || '').trim().startsWith('[상담]')
}

const DISCLOSURE_GROUPS = [
  {
    title: '최근 3개월',
    items: [
      '진찰·검사·치료·투약 이력 확인',
      '추가검사 또는 재검사 소견 확인',
      '건강검진 이상 소견 또는 추적 관찰 확인',
    ],
  },
  {
    title: '최근 1년',
    items: [
      '질병 확정 진단 또는 계속 치료 확인',
      '수술·입원 예정 또는 정밀검사 예정 확인',
      '동일 증상 반복 진료 확인',
    ],
  },
  {
    title: '최근 5년',
    items: [
      '입원·수술·7일 이상 치료·30일 이상 투약 확인',
      '암·심장·뇌혈관·당뇨 등 주요 질환 이력 확인',
      '현재 복약 또는 정기 추적 관찰 확인',
    ],
  },
]

const QUESTION_PURPOSES = [
  {
    id: 'first',
    label: '첫 상담',
    questions: [
      '이번 상담에서 가장 먼저 해결하고 싶은 걱정은 무엇인가요?',
      '현재 가입 중인 보험에서 잘 이해되지 않는 부분이 있나요?',
      '월 보험료는 어느 정도가 부담 없는 범위인가요?',
      '최근 병원 진료, 검사, 약 처방 이력이 있었나요?',
    ],
  },
  {
    id: 'coverage',
    label: '보장 점검',
    questions: [
      '현재 가장 걱정되는 보장 공백은 진단비, 수술비, 실손, 운전자 중 어디인가요?',
      '기존 보험에서 갱신형 담보나 보험료 인상 부담을 느낀 적이 있나요?',
      '가족력이나 직업상 특별히 신경 쓰는 위험이 있나요?',
      '기존 계약을 유지하면서 보완하고 싶은지, 전체 재정리를 원하는지 확인해도 될까요?',
    ],
  },
  {
    id: 'claim',
    label: '청구 상담',
    questions: [
      '진료일, 병원명, 진단명 또는 주요 증상은 무엇인가요?',
      '입원, 수술, 통원, 약 처방 중 어떤 내용이 있었나요?',
      '영수증, 세부내역서, 처방전 등 준비된 서류가 있나요?',
      '기존에 같은 사고나 질병으로 청구한 이력이 있나요?',
    ],
  },
]

function flattenText(value) {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(flattenText).join(' ')
  if (typeof value === 'object') return Object.values(value).map(flattenText).join(' ')
  return ''
}

function parseJsonMaybe(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function formatHealthYmd(value) {
  const raw = String(value || '').replace(/\D/g, '')
  if (raw.length >= 8) return `${raw.slice(0, 4)}.${raw.slice(4, 6)}.${raw.slice(6, 8)}`
  return String(value || '').trim() || '날짜 확인'
}

function dateFromHealthValue(value) {
  const raw = String(value || '').replace(/\D/g, '')
  if (raw.length < 8) return null
  const d = new Date(Number(raw.slice(0, 4)), Number(raw.slice(4, 6)) - 1, Number(raw.slice(6, 8)))
  return Number.isNaN(d.getTime()) ? null : d
}

function daysBetweenToday(date) {
  if (!date) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return Math.floor((today.getTime() - d.getTime()) / (24 * 60 * 60 * 1000))
}

function normalizeDiagnosisCode(value) {
  let raw = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (/^[A-Z][A-Z]\d/.test(raw)) raw = raw.slice(1)
  const m = raw.match(/^([A-Z])(\d{2})(\d*)$/)
  if (!m) return raw
  return m[3] ? `${m[1]}${m[2]}.${m[3]}` : `${m[1]}${m[2]}`
}

function isGenericDiagnosisName(value) {
  const text = String(value || '').trim()
  return !text || text === '진료' || text === '진료내역' || text === '처방/조제'
}

function collectHealthEpisodes(value, out = []) {
  if (value == null) return out
  if (Array.isArray(value)) {
    value.forEach((item) => collectHealthEpisodes(item, out))
    return out
  }
  if (typeof value !== 'object') return out
  const hasEpisodeShape = value.startDate || value.endDate || value.hospitalName || value.diagnosisCode || value.diagnosisName || value.treatType || value.totalDrugDays
  if (hasEpisodeShape) out.push(value)
  Object.values(value).forEach((item) => {
    if (item && typeof item === 'object') collectHealthEpisodes(item, out)
  })
  return out
}

function disclosurePeriodLabels(daysAgo, ep) {
  const drugDays = Number(ep?.totalDrugDays || ep?.drugDays || 0)
  const text = flattenText(ep)
  const labels = []
  if (daysAgo != null && daysAgo <= 93) labels.push('최근 3개월')
  if (daysAgo != null && daysAgo <= 366 && /재검|추가검사|정밀검사|진단|계속|반복|검사|치료|외래|통원/.test(text)) labels.push('최근 1년')
  if (/입원|수술|처치/.test(text) || drugDays >= 7 || /암|악성|심장|심근|협심증|뇌혈관|뇌졸중|당뇨|고혈압|정신/.test(text)) labels.push('최근 5년')
  return labels
}

function summarizeHealthEpisodeForDisclosure(ep, periodLabel) {
  const dateValue = ep?.startDate || ep?.endDate || ep?.treatStartDate || ep?.resTreatStartDate
  const code = normalizeDiagnosisCode(ep?.diagnosisCode || ep?.diseaseCode || '')
  const name = String(ep?.diagnosisName || ep?.diseaseName || ep?.treatTypeDetail || ep?.treatType || '진료내역').trim()
  const hospital = String(ep?.hospitalName || ep?.resHospitalName || '').trim()
  const treatType = String(ep?.treatType || '').trim()
  const detail = String(ep?.treatTypeDetail || '').trim()
  const drugDays = Number(ep?.totalDrugDays || ep?.drugDays || 0)
  const treatmentDays = Number(ep?.totalTreatmentDays || ep?.visitDays || 0)
  const flags = []
  const merged = `${treatType} ${detail} ${name}`
  if (/입원/.test(merged)) flags.push('입원')
  else if (/외래|통원/.test(merged)) flags.push('외래/통원')
  if (/수술|처치/.test(merged)) flags.push('처치/수술')
  if (/검사|관찰판정|재검|정밀검사/.test(merged)) flags.push('검사')
  if (drugDays > 0) flags.push(`투약 ${drugDays}일`)
  if (treatmentDays > 0 && !flags.some((x) => x.includes('진료'))) flags.push(`진료 ${treatmentDays}일`)
  return {
    periodLabel,
    dateLabel: formatHealthYmd(dateValue),
    code,
    name,
    hospital,
    flags,
    line: `- ${periodLabel}: [${formatHealthYmd(dateValue)}]${code ? ` (${code})` : ''} ${name}${hospital ? `, ${hospital}` : ''}${flags.length ? `, ${flags.join('/')}` : ''}`,
  }
}

function numberOrZero(value) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0
}

function healthRangeLabel(group) {
  const start = group?.startDate || group?.endDate
  const end = group?.endDate || group?.startDate
  if (!start && !end) return ''
  const a = start ? formatHealthYmd(start) : '-'
  const b = end ? formatHealthYmd(end) : '-'
  return a === b ? a : `${a}~${b}`
}

function disclosureGroupTitle(group) {
  const name = String(group?.diagnosisName || group?.diseaseName || '진료내역').trim()
  const code = normalizeDiagnosisCode(group?.diagnosisCode || group?.diseaseCode || '')
  return code ? `${name} [${code}]` : name
}

function disclosureGroupLine(periodLabel, group, option = {}) {
  const treat = numberOrZero(group?.sumTreatmentDays ?? group?.totalTreatmentDays)
  const drug = numberOrZero(group?.sumDrugDays ?? group?.totalDrugDays)
  const inpatient = numberOrZero(group?.sumInpatientDays ?? group?.inpatientDays)
  const surgery = numberOrZero(group?.surgeryCount)
  const parts = []
  if (option.showHosp) {
    if (inpatient > 0) parts.push(`입원·재원 ${inpatient}일`)
    if (surgery > 0) parts.push(`수술(시술) ${surgery}건`)
  }
  if (option.showTreatDrug !== false && (treat > 0 || drug > 0)) {
    parts.push(`진료${treat} / 투약${drug}`)
  }
  const range = healthRangeLabel(group)
  return {
    periodLabel,
    name: String(group?.diagnosisName || ''),
    code: normalizeDiagnosisCode(group?.diagnosisCode || ''),
    flags: parts,
    line: `- ${periodLabel}: ${disclosureGroupTitle(group)}${parts.length ? ` (${parts.join(' / ')})` : ''}${range ? ` / 기간: ${range}` : ''}`,
  }
}

function buildDisclosureAutoSummaryFromViews(summary) {
  const profile = summary?.profile || {}
  const hv = profile?.healthyView || {}
  const sv = profile?.substandardView || {}
  const rows = []
  const addGroups = (periodLabel, groups, option = {}) => {
    if (!Array.isArray(groups)) return
    groups.forEach((group) => {
      if (!group) return
      rows.push(disclosureGroupLine(periodLabel, group, option))
    })
  }

  addGroups('최근 3개월(진료/검사/치료/입원/수술/투약)', hv['3M'], { limitReason: '3m' })
  addGroups('최근 1년(검사/추가검사/재검사)', hv['1Y_CHECK'], { limitReason: '1y' })
  addGroups('최근 5년(같은 원인 7일+ 치료 또는 30일+ 투약)', hv['DRUG_7_30_5Y'])
  addGroups('최근 5년(입원/수술)', hv['HOSP_5Y'], { showHosp: true })
  addGroups('최근 5년(11대 중대질병)', hv['MAJOR11_5Y'])
  if (rows.length === 0) {
    addGroups('유병자 기준 최근 5년 입원/수술', sv['inpatientOrSurgeryLike5Y'], { showHosp: true })
    addGroups('유병자 기준 최근 5년 6대질병', sv['MAJOR6_5Y'])
  }

  const seen = new Set()
  const deduped = rows.filter((row) => {
    const key = `${row.periodLabel}|${row.code}|${row.name}|${row.line}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  const order = [
    '최근 3개월(진료/검사/치료/입원/수술/투약)',
    '최근 1년(검사/추가검사/재검사)',
    '최근 5년(같은 원인 7일+ 치료 또는 30일+ 투약)',
    '최근 5년(입원/수술)',
    '최근 5년(11대 중대질병)',
    '유병자 기준 최근 5년 입원/수술',
    '유병자 기준 최근 5년 6대질병',
  ]
  const sorted = deduped
    .sort((a, b) => order.indexOf(a.periodLabel) - order.indexOf(b.periodLabel))
    .slice(0, 10)

  const questions = []
  if (sorted.some((r) => r.periodLabel.includes('최근 3개월'))) {
    questions.push('최근 3개월 내 진료·검사·치료·입원·수술·투약 이력이 고지 대상인지, 치료가 종료됐는지 확인하세요.')
  }
  if (sorted.some((r) => r.periodLabel.includes('최근 1년'))) {
    questions.push('최근 1년 내 추가검사·재검사·정밀검사 권유 여부와 검사 결과를 확인하세요.')
  }
  if (sorted.some((r) => r.periodLabel.includes('7일+') || r.periodLabel.includes('30일+'))) {
    questions.push('최근 5년 내 같은 원인으로 7일 이상 치료 또는 30일 이상 투약에 해당하는지 기간을 확인하세요.')
  }
  if (sorted.some((r) => r.periodLabel.includes('입원/수술'))) {
    questions.push('최근 5년 내 입원·수술 이력은 입원일수, 수술명, 치료 종료 여부를 확인하세요.')
  }
  if (sorted.some((r) => r.periodLabel.includes('중대질병') || r.periodLabel.includes('6대질병'))) {
    questions.push('주요 질환 항목은 확정 진단 여부, 치료 기간, 현재 복약·추적관찰 여부를 분리해서 확인하세요.')
  }
  return { rows: sorted, questions }
}

function buildDisclosureAutoSummary(summary) {
  const fromViews = buildDisclosureAutoSummaryFromViews(summary)
  if (fromViews.rows.length) return fromViews

  const episodes = collectHealthEpisodes(summary)
  const rowMap = new Map()
  const mergeRow = (row) => {
    const key = `${row.periodLabel}|${row.dateLabel}|${row.code || row.name}`
    const prev = rowMap.get(key)
    if (!prev) {
      rowMap.set(key, row)
      return
    }
    const flags = [...prev.flags]
    row.flags.forEach((flag) => {
      if (!flags.includes(flag)) flags.push(flag)
    })
    const name = isGenericDiagnosisName(prev.name) && !isGenericDiagnosisName(row.name) ? row.name : prev.name
    const hospital = prev.hospital || row.hospital
    const merged = { ...prev, name, hospital, flags }
    merged.line = `- ${merged.periodLabel}: [${merged.dateLabel}]${merged.code ? ` (${merged.code})` : ''} ${merged.name}${merged.hospital ? `, ${merged.hospital}` : ''}${merged.flags.length ? `, ${merged.flags.join('/')}` : ''}`
    rowMap.set(key, merged)
  }
  episodes.forEach((ep) => {
    if (!ep || isPharmacyOnlyEpisode(ep)) return
    const dateValue = ep.startDate || ep.endDate || ep.treatStartDate || ep.resTreatStartDate
    const daysAgo = daysBetweenToday(dateFromHealthValue(dateValue))
    if (daysAgo != null && daysAgo > 365 * 5 + 30) return
    const periodLabels = disclosurePeriodLabels(daysAgo, ep)
    if (!periodLabels.length) return
    periodLabels.forEach((periodLabel) => mergeRow(summarizeHealthEpisodeForDisclosure(ep, periodLabel)))
  })
  const order = { '최근 3개월': 0, '최근 1년': 1, '최근 5년': 2 }
  const sorted = [...rowMap.values()]
    .sort((a, b) => (order[a.periodLabel] - order[b.periodLabel]) || String(b.dateLabel).localeCompare(String(a.dateLabel)))
    .slice(0, 7)
  const questions = []
  if (sorted.some((r) => r.periodLabel === '최근 3개월')) {
    questions.push('최근 3개월 내 위 진료와 관련해 진찰·검사·치료·투약을 받은 사실이 맞는지, 현재 치료가 종료됐는지 확인하세요.')
  }
  if (sorted.some((r) => r.periodLabel === '최근 1년')) {
    questions.push('최근 1년 내 추가검사·재검사·정밀검사 권유 또는 동일 증상 반복 진료가 있었는지 확인하세요.')
  }
  if (sorted.some((r) => r.periodLabel === '최근 5년' && r.flags.some((f) => /입원|수술|투약/.test(f)))) {
    questions.push('최근 5년 내 입원·수술·7일 이상 치료·30일 이상 투약에 해당하는지 기간과 치료 내용을 확인하세요.')
  }
  if (sorted.some((r) => /암|악성|심장|심근|협심증|뇌혈관|뇌졸중|당뇨|고혈압|정신/.test(`${r.name} ${r.code}`))) {
    questions.push('주요 질환 의심 키워드는 확정 진단 여부, 치료 기간, 현재 추적 관찰 여부를 분리해서 확인하세요.')
  }
  if (!questions.length && sorted.length) {
    questions.push('위 진료내역별로 진료일, 병원명, 진료 사유, 치료 종료 여부, 투약 기간을 고객에게 확인하세요.')
  }
  return { rows: sorted, questions }
}

function isPharmacyOnlyEpisode(ep) {
  const source = String(ep?.source || '').trim()
  const hospital = String(ep?.hospitalName || '').trim()
  const treatType = String(ep?.treatType || '').trim()
  return source === 'PRESCRIBE_DRUG' || hospital.includes('약국') || treatType.includes('조제')
}

function consultationMemoSummary(raw) {
  const text = String(raw || '').trim()
  if (!text) return null
  const sentences = text.split(/[\n.。!?]+/).map((s) => s.trim()).filter(Boolean)
  const summary = sentences.slice(0, 2).join(' / ') || text.slice(0, 80)
  const needs = sentences.filter((s) => ['원함', '희망', '걱정', '부담', '필요', '관심'].some((k) => s.includes(k))).slice(0, 3)
  const actions = sentences.filter((s) => ['다음', '확인', '전달', '발송', '예약', '전화', '연락'].some((k) => s.includes(k))).slice(0, 3)
  return [
    `상담 요약: ${summary}`,
    `고객 니즈: ${needs.length ? needs.join(' / ') : '추가 확인 필요'}`,
    '확인 필요: 고지사항, 기존 계약, 보험료 부담 가능 범위',
    `다음 액션: ${actions.length ? actions.join(' / ') : '후속 연락 일정과 필요 서류 확인'}`,
    '후속 메시지 초안: 오늘 상담 내용 기준으로 필요한 자료를 정리해 다시 안내드리겠습니다.',
  ]
}

function disclosureLinesFromChecked(checked, evidence = {}) {
  const selected = DISCLOSURE_GROUPS.flatMap((group) => (
    group.items
      .filter((item) => checked?.[`${group.title}:${item}`])
      .flatMap((item) => {
        const key = `${group.title}:${item}`
        const lines = [`- ${group.title}: ${item}`]
        const refs = Array.isArray(evidence[key]) ? evidence[key] : []
        refs.slice(0, 3).forEach((ref) => lines.push(`  · 확인 근거: ${ref}`))
        return lines
      })
  ))
  if (!selected.length) return ['체크된 고지 확인 항목이 없습니다.']
  return ['[고지사항 상담 확인]', ...selected, '확인 필요: 위 근거 항목별 진료일, 병원명, 치료/검사 내용, 투약 기간을 고객에게 추가 확인']
}

function disclosureLinesFromAutoSummary(rows = [], questions = []) {
  if (!rows.length) return []
  return [
    '[고지사항 상담 확인]',
    ...rows.map((row) => row.line),
    '확인 질문 방향',
    ...questions.map((q) => `- ${q}`),
  ]
}

const ACTIVITY_PAGE_SIZE = 10

const SECTION_TABS = [
  { id: 'integrated-report', label: '통합 리포트' },
  { id: 'contracts', label: '보험 계약' },
  { id: 'maturity', label: '만기·갱신' },
  { id: 'diagnosis-simulation', label: '진단시뮬레이션' },
  { id: 'claims', label: '보험금 청구' },
  { id: 'health', label: '진료정보분석' },
  { id: 'hidden-insurance', label: '숨은보험금' },
  // { id: 'fire', label: '화재보험설계' },
  // { id: 'request', label: '설계의뢰' },
  // { id: 'consultation', label: '상담' },
  // { id: 'inquiries', label: '고객 문의' },
  // { id: 'sales', label: '상품 판매' },
  { id: 'schedule', label: '일정' },
  { id: 'contact', label: '연락' },
  // { id: 'history', label: '이력' },
]

const INTEGRATED_REPORT_SECTION_OPTIONS = [
  { id: 'summary', label: '핵심요약', hint: '고객 기준값과 전체 지표', requiredText: '핵심요약' },
  { id: 'premiumBenchmark', label: '보험료 부담 비교', hint: '월소득·공개통계 기준 비교', requiredText: '보험료 부담 비교' },
  { id: 'portfolio', label: '보험료·납입 현황', hint: '월보험료, 납입흐름', requiredText: '보험료·납입 진단' },
  { id: 'contractList', label: '보험계약 목록', hint: '보유계약 요약 표', requiredText: '보험계약 목록' },
  { id: 'contractDetails', label: '계약별 상세 담보', hint: '계약별 담보 목록', requiredText: '계약별 상세 담보' },
  { id: 'standardCoverage', label: '표준담보·카테고리 진단', hint: '권장·가입·과부족과 보장요약 연결', requiredText: '주요 담보별 과부족 현황' },
  { id: 'diseaseBalance', label: '질병통계 대비 보장 균형', hint: '질병군별 기준 대비 차트', requiredText: '질병통계 대비 보장 균형' },
  { id: 'diagnosisScenarios', label: '진단 시나리오', hint: '부족 보장 TOP 시나리오', requiredText: '고객 맞춤 진단 시나리오' },
  { id: 'healthEvidence', label: '진료내역 기반 확인', hint: '5년 진료비와 관련 보장', requiredText: '진료내역 기반 확인' },
  { id: 'hiddenInsurance', label: '숨은보험금 결과', hint: '청구 가능액·수령 비교', requiredText: '숨은보험금 점검 요약' },
  { id: 'maturitySurvey', label: '만기·설문 확인', hint: '만기/갱신과 상담 설문', requiredText: '만기/갱신' },
  { id: 'recommendations', label: '최종 확인·다음 액션', hint: '상담 마무리 체크', requiredText: '최종 확인 포인트' },
  { id: 'closing', label: '설계사 마무리', hint: '고객 전달 멘트와 안내 문구', requiredText: '맞춘 보장 점검' },
]

const DEFAULT_INTEGRATED_REPORT_SECTION_IDS = INTEGRATED_REPORT_SECTION_OPTIONS.map((item) => item.id)
const COUNSELING_INTEGRATED_REPORT_SECTION_IDS = ['summary', 'premiumBenchmark', 'portfolio', 'contractList', 'standardCoverage', 'diseaseBalance', 'healthEvidence', 'hiddenInsurance', 'recommendations', 'closing']
const SHOW_CUSTOMER_CARD_NEWS_BUTTONS = false

function normalizeIntegratedReportSections(value) {
  const allowed = new Set(DEFAULT_INTEGRATED_REPORT_SECTION_IDS)
  const arr = Array.isArray(value) ? value : DEFAULT_INTEGRATED_REPORT_SECTION_IDS
  const selected = arr.filter((id) => allowed.has(id))
  return selected.length ? selected : DEFAULT_INTEGRATED_REPORT_SECTION_IDS
}

const CUSTOMER_PAGE_VIEWS = [
  { id: 'info', label: '고객 정보' },
  { id: 'work', label: '고객 업무' },
]

const HASH_TO_WORK_TAB = {
  '#integrated-report': 'integrated-report',
  '#maturity': 'maturity',
  '#diagnosis-simulation': 'diagnosis-simulation',
  '#claims': 'claims',
  '#health': 'health',
  '#hidden-insurance': 'hidden-insurance',
  '#schedule': 'schedule',
  '#coverage': 'contracts',
  '#contact': 'contact',
  '#consultation': 'consultation',
}

const WORK_TAB_TO_HASH = {
  'integrated-report': '#integrated-report',
  contracts: '#coverage',
  maturity: '#maturity',
  'diagnosis-simulation': '#diagnosis-simulation',
  claims: '#claims',
  health: '#health',
  'hidden-insurance': '#hidden-insurance',
  schedule: '#schedule',
  contact: '#contact',
  consultation: '#consultation',
}

function hashForWorkTab(tab) {
  return WORK_TAB_TO_HASH[tab] || '#integrated-report'
}

const EVENT_CONSULTATION_TABS = [
  { id: 'assist', label: '상담준비' },
  { id: 'consult-history', label: '상담이력' },
  { id: 'survey', label: '상담설문' },
  { id: 'lead', label: '리드' },
]

const ACTIVITY_TYPE_LABEL = {
  SURVEY_SESSION_LINKED: '상담 설문 연결',
  HEALTH_JOB_CREATED: '진료분석 Job 생성',
  HIDDEN_INSURANCE_LOOKUP_CREATED: '숨은보험금찾기',
  LEAD_CATEGORY_LANDING: '카테고리 랜딩 리드',
  LEAD_PUBLIC_LANDING: '개인 랜딩 리드',
  CUSTOMER_MANUAL_CREATED: '고객 직접 등록',
  CUSTOMER_PROFILE_UPDATED: '기본 정보 수정',
  CUSTOMER_MEMO_UPDATED: '메모 수정',
  CUSTOMER_BUSINESS_CARD_UPSERTED: '명함 이미지 등록·교체',
  CUSTOMER_BUSINESS_CARD_REMOVED: '명함 이미지 삭제',
  CUSTOMER_ANNIVERSARY_CREATED: '기념일 추가',
  CUSTOMER_ANNIVERSARY_UPDATED: '기념일 수정',
  CUSTOMER_ANNIVERSARY_REMOVED: '기념일 삭제',
  CUSTOMER_FAMILY_UPDATED: '가족 정보 변경',
  COVERAGE_ANALYSIS_CREATED: '보장분석 계약 분석',
  COVERAGE_CONTRACTS_REVIEWED: '보장분석 계약 저장',
  COVERAGE_CONTRACTS_EXCEL_IMPORTED: '보유계약 엑셀 반영',
  CUSTOMER_INTEGRATED_REPORT_GENERATE: '통합 리포트 생성',
  INTEGRATED_REPORT_CREATED: '통합 리포트 생성',
  CUSTOMER_INTEGRATED_REPORT_CREATED: '통합 리포트 생성',
  DIAGNOSIS_SIMULATION_GENERATE: '진단시뮬레이션 생성',
  DIAGNOSIS_SIMULATION_CREATED: '진단시뮬레이션 생성',
  MATURITY_POLICY_CREATED: '만기대장 추가',
  MATURITY_POLICY_UPDATED: '만기대장 수정',
  MATURITY_POLICY_CLOSED: '만기대장 종료',
  MATURITY_POLICIES_EXCEL_IMPORTED: '만기·갱신 엑셀 반영',
  CUSTOMER_PIPELINE_STAGE_CHANGED: '관리 단계 변경',
  CUSTOMER_PIPELINE_STAGE_NOTE: '이동 메모',
}

const ACTIVITY_PIPELINE_STAGE_LABEL = {
  LEAD: '잠재',
  CONSULTING: '상담',
  PROPOSAL: '제안',
  JOINED: '가입',
  RENEWAL: '갱신',
  HOLD: '보류',
  MANAGED: '관리',
  MANAGEMENT_ENDED: '관리 종료',
}

function activityPipelineStageLabel(value) {
  const key = String(value || '').trim().toUpperCase()
  if (!key) return '미지정'
  return ACTIVITY_PIPELINE_STAGE_LABEL[key] || String(value || '').trim()
}

const AI_MESSAGE_TYPES = [
  { value: 'FIRST_CONSULT', label: '첫 상담 유도' },
  { value: 'REMINDER', label: '상담 리마인드' },
  { value: 'HEALTH_ANALYSIS', label: '진료 분석 안내' },
  { value: 'CLAIM_GUIDE', label: '청구 안내' },
  { value: 'DESIGN_PROPOSAL', label: '설계 제안 안내' },
  { value: 'RECONTACT', label: '장기 미응답 재접촉' },
  { value: 'ANNIVERSARY', label: '기념일/이벤트' },
]

const AI_MESSAGE_TONES = [
  { value: 'POLITE', label: '정중하게' },
  { value: 'FRIENDLY', label: '친근하게' },
  { value: 'SHORT', label: '짧게' },
  { value: 'DETAILED', label: '자세히' },
]

const SHOW_CUSTOMER_SALES_PIPELINE = false

const SALES_PIPELINE_ACTION_BTN =
  'mt-2 flex w-full items-center justify-center rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors'

function salesPipelineActionClass(current) {
  return current
    ? `${SALES_PIPELINE_ACTION_BTN} border-teal-200 bg-teal-50 text-teal-900 hover:bg-teal-100`
    : `${SALES_PIPELINE_ACTION_BTN} border-slate-300 bg-white text-slate-800 hover:bg-slate-50`
}

const SALES_PIPELINE_DEFS = [
  { id: 'lead', label: '고객등록', action: '리드 출처와 고객 니즈를 확인하세요.', cta: '리드 확인', path: '/sales/consultations' },
  { id: 'contact', label: '초기응대', action: '첫 연락 또는 상담 일정을 남기세요.', cta: '연락하기', actionKind: 'contact' },
  { id: 'consult', label: '상담준비', action: '상담 설문과 고객 메모를 기준으로 질문을 준비하세요.', cta: '상담 메모', actionKind: 'tab', targetTab: 'consultation' },
  { id: 'analysis', label: '진단/분석', action: '기존 계약·진료·화재 리스크 자료를 정리하세요.', cta: '보장분석', actionKind: 'tab', targetTab: 'contracts' },
  { id: 'proposal', label: '제안', action: '설계의뢰나 고객용 설명문으로 제안 흐름을 만드세요.', cta: '설계의뢰', path: '/requests/new' },
  { id: 'maintenance', label: '유지관리', action: '만기·기념일·후속 연락 일정을 관리하세요.', cta: '만기 확인', actionKind: 'tab', targetTab: 'maturity' },
]

function buildCustomerSalesPipeline({ customerId, highlights, latestSurvey, latestHealth, coverageContracts, maturityRows, scheduleRows, contactRows, consultationRows }) {
  const publicLeads = Array.isArray(highlights?.recentPublicLeads) ? highlights.recentPublicLeads : []
  const landingLeads = Array.isArray(highlights?.recentLandingLeads) ? highlights.recentLandingLeads : []
  const designRequests = Array.isArray(highlights?.recentDesignRequests) ? highlights.recentDesignRequests : []
  const latestFire = highlights?.latestFireHistory
  const done = {
    lead: publicLeads.length > 0 || landingLeads.length > 0,
    contact: contactRows.length > 0 || scheduleRows.length > 0,
    consult: Boolean(latestSurvey?.id) || consultationRows.length > 0,
    analysis: Boolean(latestHealth?.id) || Boolean(latestFire?.id) || coverageContracts.length > 0,
    proposal: designRequests.length > 0,
    maintenance: maturityRows.length > 0,
  }
  let currentIdx = SALES_PIPELINE_DEFS.findIndex((step) => !done[step.id])
  if (currentIdx < 0) currentIdx = SALES_PIPELINE_DEFS.length - 1
  return SALES_PIPELINE_DEFS.map((step, idx) => ({
    ...step,
    ctaPath: step.path ? customerPath(step.path, customerId) : null,
    status: done[step.id] ? 'done' : idx === currentIdx ? 'current' : 'upcoming',
  }))
}

function hiddenLookupStatusLabel(status) {
  if (status === 'AUTH_REQUESTED') return '인증요청'
  if (status === 'AUTH_WAITING') return '인증 대기'
  if (status === 'ANALYSIS_RUNNING') return '분석 중'
  if (status === 'COMPLETED') return '조회 완료'
  if (status === 'FAILED') return '실패'
  return '처리 중'
}

function claimApplicationStatusLabel(status) {
  if (status === 'DRAFT') return '임시저장'
  if (status === 'SUBMITTED') return '접수'
  if (status === 'UNDER_REVIEW') return '심사중'
  if (status === 'APPROVED') return '승인'
  if (status === 'REJECTED') return '반려'
  if (status === 'COMPLETED') return '완료'
  return '진행 중'
}

function claimApplicationStatusClass(status) {
  if (status === 'DRAFT') return 'bg-slate-100 text-slate-700'
  if (status === 'SUBMITTED') return 'bg-sky-100 text-sky-800'
  if (status === 'UNDER_REVIEW') return 'bg-amber-100 text-amber-800'
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-800'
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-800'
  if (status === 'COMPLETED') return 'bg-slate-100 text-slate-700'
  return 'bg-slate-100 text-slate-700'
}

function claimApplicationTitle(row) {
  const item = String(row?.claimItem || row?.claimSection || '').trim()
  if (item) return item
  if (row?.insurerOrgName) return `${row.insurerOrgName} 청구`
  return '보험금 청구'
}

function activityTypeLabel(t) {
  if (!t) return '-'
  return ACTIVITY_TYPE_LABEL[t] || t
}

/** payload_json 에 summary 등이 있으면 한 줄 설명으로 */
function activityPayloadSummary(payloadJson) {
  if (payloadJson == null || String(payloadJson).trim() === '') return null
  try {
    const o = JSON.parse(String(payloadJson))
    if (o && typeof o.summary === 'string' && o.summary.trim()) return o.summary.trim()
    if (o && (o.stageLabel || o.stage) && typeof o.note === 'string' && o.note.trim()) {
      return `${o.stageLabel || activityPipelineStageLabel(o.stage)} 이동 메모: ${o.note.trim()}`
    }
    if (o && (Object.prototype.hasOwnProperty.call(o, 'from') || Object.prototype.hasOwnProperty.call(o, 'to'))) {
      return `관리 단계 변경: ${activityPipelineStageLabel(o.from)} → ${activityPipelineStageLabel(o.to)}`
    }
    if (o && (o.previousStageLabel || o.nextStageLabel)) {
      return `관리 단계 변경: ${o.previousStageLabel || '미지정'} → ${o.nextStageLabel || '미지정'}`
    }
  } catch {
    return null
  }
  return null
}

function formatActivityWhen(iso) {
  if (iso == null || String(iso).trim() === '') return '—'
  return formatDateTimeMinute(iso)
}

function parseYmdLocal(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(d.getTime())) return null
  return d
}

function daysUntilAnnualDate(ymd) {
  const d = parseYmdLocal(ymd)
  if (!d) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  let next = new Date(today.getFullYear(), d.getMonth(), d.getDate())
  if (next < today) next = new Date(today.getFullYear() + 1, d.getMonth(), d.getDate())
  return Math.round((next.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
}

function anniversaryDDayLabel(ymd) {
  const days = daysUntilAnnualDate(ymd)
  if (days == null) return '날짜 확인 필요'
  if (days === 0) return '오늘'
  return `D-${days}`
}

function daysUntilYmd(ymd) {
  const d = parseYmdLocal(ymd)
  if (!d) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
}

function maturityLineLabel(v) {
  if (v === 'AUTO') return '자동차'
  if (v === 'GENERAL') return '일반'
  return v || '—'
}

const emptyMaturityForm = {
  policyLine: 'AUTO',
  nextMaturityDate: '',
  insurerName: '',
  productLabel: '',
  enrollmentChannel: '',
  vehicleNumber: '',
  driverRestriction: '',
  memo: '',
}

function formatWon(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '—'
  return `${n.toLocaleString('ko-KR')}원`
}

function formatManWon(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '—'
  const man = Math.round(n / 10000)
  if (man <= 0) return '1만원 미만'
  return `${man.toLocaleString('ko-KR')}만원`
}

function formatCoverageAmountKorean(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '—'
  const man = Math.round(n / 10000)
  if (man <= 0) return '1만원 미만'
  const eok = Math.floor(man / 10000)
  const restMan = man % 10000
  const fmt = (v) => Number(v).toLocaleString('ko-KR')
  if (eok <= 0) return `${fmt(man)}만원`
  if (restMan <= 0) return `${fmt(eok)}억원`
  const rest = restMan >= 1000 && restMan % 1000 === 0
    ? `${fmt(restMan / 1000)}천만원`
    : `${fmt(restMan)}만원`
  return `${fmt(eok)}억 ${rest}`
}

function isCoverageSummaryOtherRow(row) {
  return row?.coverageGroup === '기타' || row?.normalizedCoverageGroup === '기타'
}

function coverageSummaryOtherProductText(row, items) {
  const rowName = String(row?.coverageName || '').trim()
  const genericNames = new Set(['', '기타', '기타보장'])
  const lines = []
  asArray(items).forEach((item) => {
    const detail = String(item?.coverageDetailLines || '').trim()
    const note = String(item?.note || '').trim()
    const coverageNames = String(item?.coverageNames || '').trim()
    const amount = Number(item?.amount || 0)
    const source = detail || note
    if (source) {
      source
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line && !line.replace(/\s/g, '').startsWith('합계'))
        .forEach((line) => lines.push(line))
      return
    }
    if (coverageNames && coverageNames !== rowName && !genericNames.has(coverageNames)) {
      lines.push(amount > 0 ? `${coverageNames} ${formatCoverageAmountKorean(amount)}` : coverageNames)
      return
    }
    if (amount > 0) {
      lines.push(formatCoverageAmountKorean(amount))
    }
  })
  return Array.from(new Set(lines)).join('\n')
}

function incomeWonToMan(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return ''
  return String(Math.round(n / 10000))
}

function incomeManToWon(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) return 0
  return Number(digits) * 10000
}

function coverageRuleAmountToManWon(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return ''
  if (n <= 1) return String(Math.max(0, n))
  return String(Math.round(n / 10000))
}

function coverageRuleManWonToWon(value, originalValue) {
  const n = Number(String(value ?? '').replace(/,/g, ''))
  if (!Number.isFinite(n) || n < 0) return 0
  if (Number(originalValue) <= 1 && n <= 1) return Math.round(n)
  return Math.round(n * 10000)
}

function normalizeCoverageRuleSettingItem(row) {
  return {
    ...row,
    minAmountManWon: row?.minAmountManWon ?? coverageRuleAmountToManWon(row?.minAmount),
    targetAmountManWon: row?.targetAmountManWon ?? coverageRuleAmountToManWon(row?.targetAmount),
    maxAmountManWon: row?.maxAmountManWon ?? coverageRuleAmountToManWon(row?.maxAmount),
  }
}

function coverageRuleSettingPayload(row) {
  return {
    category: row.category || '',
    label: row.label || '',
    importance: row.importance || '권장',
    minAmount: coverageRuleManWonToWon(row.minAmountManWon, row.minAmount),
    targetAmount: coverageRuleManWonToWon(row.targetAmountManWon, row.targetAmount),
    maxAmount: coverageRuleManWonToWon(row.maxAmountManWon, row.maxAmount),
    weight: Number(row.weight || 5),
    ageAdjustmentPercent: Number(row.ageAdjustmentPercent ?? 10),
    keywords: row.keywords || '',
    displayOrder: Number(row.displayOrder || 0),
    active: row.active !== false,
    note: row.note || '',
  }
}

function coverageRuleTemplateLabel(row) {
  if (!row) return '추천 템플릿'
  return [row.name, row.ageBandLabel].filter(Boolean).join(' · ') || '추천 템플릿'
}

function escapeReportHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function reportListHtml(items = []) {
  const list = Array.isArray(items) ? items.filter(Boolean) : []
  if (list.length === 0) return '<p class="muted">확인된 내용이 없습니다.</p>'
  return `<ul>${list.map((item) => `<li>${escapeReportHtml(item)}</li>`).join('')}</ul>`
}

function reportAiNarrative(report) {
  const ai = report?.aiNarrative
  return ai?.status === 'SUCCESS' ? ai : {}
}

function reportPreferredList(primary, fallback = []) {
  const p = Array.isArray(primary) ? primary.filter(Boolean) : []
  if (p.length) return p
  return Array.isArray(fallback) ? fallback.filter(Boolean) : []
}

function reportChunkRows(items, size) {
  const list = Array.isArray(items) ? items : []
  const chunkSize = Math.max(1, Number(size) || 1)
  const chunks = []
  for (let i = 0; i < list.length; i += chunkSize) {
    chunks.push(list.slice(i, i + chunkSize))
  }
  return chunks
}

function reportAvoidSingleTailChunks(chunks) {
  if (chunks.length > 1 && chunks[chunks.length - 1].length === 1 && chunks[chunks.length - 2].length > 1) {
    const last = chunks[chunks.length - 1]
    const previous = chunks[chunks.length - 2]
    last.unshift(previous.pop())
  }
  return chunks.filter((chunk) => chunk.length)
}

function reportChunkRowsAvoidSingleTail(items, size) {
  return reportAvoidSingleTailChunks(reportChunkRows(items, size))
}

const REPORT_STANDARD_CATEGORY_ORDER = [
  '사망/장해',
  '치매/간병',
  '암 진단',
  '뇌/심장 진단',
  '실손의료비',
  '수술/입원',
  '운전자/기타',
]

const REPORT_STANDARD_CATEGORY_META = {
  '사망/장해': {
    imageFile: 'stat_01_death_disability.svg',
    subtitle: '남겨진 가족을 위한 기본 생활자금과 후유장해 대응을 봅니다.',
    statTitle: '가족 생활자금 관점',
    visualKicker: '소득 공백 지표',
    visualValue: '가족생활비',
    visualUnit: '우선 점검',
    visualCaption: '사망·후유장해는 치료비보다 장기 소득 공백 영향이 큽니다.',
    visualBars: [
      { label: '생활비', value: 92 },
      { label: '부채상환', value: 72 },
      { label: '돌봄비', value: 58 },
    ],
    visualTags: ['사망보험금', '후유장해', '가족생활비'],
    statLines: ['경제활동기 사망·장해는 소득 공백이 가장 큰 리스크입니다.', '사망보험금은 남은 가족의 생활비와 부채 상환 여력을 함께 봐야 합니다.', '후유장해는 치료비보다 장기 소득 감소와 돌봄 부담이 커질 수 있습니다.'],
    source: '가계·보건 공개통계와 보장분석 권장금액 기준',
  },
  '치매/간병': {
    imageFile: 'stat_02_dementia_care.svg',
    subtitle: '장기요양, 간병인, 간호간병 일당처럼 장기간 지출될 수 있는 비용을 봅니다.',
    statTitle: '간병비·돌봄 수요',
    visualKicker: '반복 지출 지표',
    visualValue: '장기',
    visualUnit: '월별 부담',
    visualCaption: '간병은 1회 진단금보다 매월 반복되는 비용 구조가 핵심입니다.',
    visualBars: [
      { label: '간병비', value: 94 },
      { label: '장기요양', value: 82 },
      { label: '가족돌봄', value: 76 },
    ],
    visualTags: ['장기요양', '간병인', '간호간병'],
    statLines: ['고령화로 장기요양과 간병 수요는 꾸준히 커지는 영역입니다.', '간병비는 진단금 1회보다 매월 반복 지출되는 성격이 강합니다.', '가족 돌봄 부담을 줄이려면 간병비와 일당 보장을 분리해 봐야 합니다.'],
    source: '장기요양·간병비 공개자료 및 내부 표준가입금액 기준',
  },
  '암 진단': {
    imageFile: 'stat_03_cancer.svg',
    subtitle: '진단금, 유사암, 고액암, 항암치료비를 나눠 치료비와 소득 공백을 봅니다.',
    statTitle: '암 치료비와 치료방식 변화',
    visualKicker: '치료 단계 지표',
    visualValue: '진단·치료',
    visualUnit: '동시 점검',
    visualCaption: '암은 진단금, 수술, 항암, 회복기간 생활비를 나누어 봐야 합니다.',
    visualBars: [
      { label: '진단금', value: 88 },
      { label: '항암치료', value: 74 },
      { label: '생활비', value: 66 },
    ],
    visualTags: ['일반암', '유사암', '항암치료'],
    statLines: ['암은 진단 이후 수술·항암·방사선·표적치료로 비용 구조가 나뉩니다.', '표적·면역항암 등 고가 치료 가능성은 별도 보장 점검 포인트입니다.', '진단금은 치료비뿐 아니라 회복기간 생활비 역할도 함께 봐야 합니다.'],
    source: '국가암정보센터·심평원 공개 보건통계 참고',
  },
  '뇌/심장 진단': {
    imageFile: 'stat_04_brain_heart.svg',
    subtitle: '뇌혈관, 뇌졸중, 뇌출혈, 허혈성심장질환의 진단 범위 차이를 봅니다.',
    statTitle: '중대질병·후유증 관점',
    visualKicker: '후유증 지표',
    visualValue: '진단범위',
    visualUnit: '차이 큼',
    visualCaption: '뇌·심장은 담보명에 따라 보장 범위가 크게 달라질 수 있습니다.',
    visualBars: [
      { label: '진단금', value: 86 },
      { label: '수술비', value: 64 },
      { label: '재활/소득', value: 78 },
    ],
    visualTags: ['뇌혈관', '허혈성심장', '후유증'],
    statLines: ['뇌·심장 질환은 진단 범위가 좁은 담보와 넓은 담보의 차이가 큽니다.', '치료 이후 재활, 소득 공백, 후유장해 가능성을 함께 봐야 합니다.', '연령이 높아질수록 뇌혈관·심혈관 질환 점검 비중이 커집니다.'],
    source: '사망원인·진료비 공개 통계와 내부 권장금액 기준',
  },
  '실손의료비': {
    imageFile: 'stat_05_medical_expense.svg',
    subtitle: '상해·질병 입원/통원, 비급여 항목과 자기부담 조건을 봅니다.',
    statTitle: '의료비 지출 방어',
    visualKicker: '의료비 방어 지표',
    visualValue: '입원·통원',
    visualUnit: '기본축',
    visualCaption: '실손은 실제 지출 의료비를 방어하는 가장 기본적인 보장입니다.',
    visualBars: [
      { label: '입원', value: 90 },
      { label: '통원', value: 70 },
      { label: '비급여', value: 82 },
    ],
    visualTags: ['입원', '통원', '비급여'],
    statLines: ['실손은 실제 의료비 지출을 방어하는 기본 축입니다.', '가입 세대에 따라 자기부담률, 갱신주기, 비급여 보장 방식이 달라집니다.', '진료내역과 실손 수령내역을 같이 보면 미청구 가능성도 확인할 수 있습니다.'],
    source: '실손보험 제도 기준 및 숨은보험금/진료내역 대조',
  },
  '수술/입원': {
    imageFile: 'stat_06_surgery_hospitalization.svg',
    subtitle: '수술비, 입원일당, 질병·상해 수술 담보의 지급 단위를 봅니다.',
    statTitle: '수술·입원 비용 구조',
    visualKicker: '반복 청구 지표',
    visualValue: '회당·일당',
    visualUnit: '분리 점검',
    visualCaption: '수술비와 입원일당은 진단금으로 부족한 반복 비용을 보완합니다.',
    visualBars: [
      { label: '수술비', value: 84 },
      { label: '입원일당', value: 58 },
      { label: '회복비', value: 62 },
    ],
    visualTags: ['질병수술', '상해수술', '입원일당'],
    statLines: ['수술 보장은 진단금으로 메우기 어려운 반복·회당 비용을 보완합니다.', '입원일당은 장기 입원보다 회복기간 현금흐름 보완 관점으로 봐야 합니다.', '질병수술과 상해수술은 지급 범위와 수술 분류표를 함께 확인해야 합니다.'],
    source: '심평원 진료비 통계와 주요 수술 보장분석 기준',
  },
  '운전자/기타': {
    imageFile: 'stat_07_driver_other.svg',
    subtitle: '자동차보험이 보장하지 않는 형사·행정 비용과 생활 배상을 봅니다.',
    statTitle: '자동차보험 밖 비용',
    visualKicker: '생활위험 지표',
    visualValue: '형사·행정',
    visualUnit: '별도 보장',
    visualCaption: '운전자보험은 자동차보험으로 해결되지 않는 개인 부담 비용을 봅니다.',
    visualBars: [
      { label: '형사합의', value: 82 },
      { label: '벌금', value: 76 },
      { label: '변호사', value: 68 },
    ],
    visualTags: ['벌금', '교통사고', '배상책임'],
    statLines: ['운전자보험은 자동차보험의 대인·대물 보상과 목적이 다릅니다.', '벌금, 형사합의, 변호사 비용처럼 사고 후 개인이 부담할 수 있는 비용을 봅니다.', '일상생활배상, 골절, 화상 등 생활 리스크는 가족 구성과 직업을 함께 봐야 합니다.'],
    source: '운전자보험 담보 구조와 생활위험 보장분석 기준',
  },
}

const REPORT_STANDARD_CATEGORY_ALIASES = {
  사망: '사망/장해',
  장해: '사망/장해',
  후유장해: '사망/장해',
  치매: '치매/간병',
  간병: '치매/간병',
  장기요양: '치매/간병',
  암: '암 진단',
  뇌: '뇌/심장 진단',
  심장: '뇌/심장 진단',
  뇌심장: '뇌/심장 진단',
  실손: '실손의료비',
  실비: '실손의료비',
  의료비: '실손의료비',
  수술: '수술/입원',
  입원: '수술/입원',
  운전자: '운전자/기타',
  기타: '운전자/기타',
}

const REPORT_STANDARD_CATEGORY_GROUP_TERMS = {
  '사망/장해': ['사망', '장해', '후유장해'],
  '치매/간병': ['치매', '장기요양', '간병', '간호간병'],
  '암 진단': ['암', '유사암', '소액암', '고액암', '항암', '방사선', '표적'],
  '뇌/심장 진단': ['뇌', '뇌혈관', '뇌졸중', '뇌출혈', '심장', '심근', '허혈', '심혈관'],
  실손의료비: ['실손', '실비', '의료비', '비급여', '상해입원', '질병입원', '상해통원', '질병통원'],
  '수술/입원': ['수술', '입원일당', '상해수술', '질병수술', '암수술', '입원'],
  '운전자/기타': ['운전자', '교통', '벌금', '변호사', '자동차사고', '자부상', '골절', '화상', '배상', '치아', '보철', '화재'],
}

function reportStandardCoverageTone(status) {
  if (status === 'missing' || status === 'low') return 'warn'
  if (status === 'excess') return 'alert'
  return 'ok'
}

function reportStandardCoverageStatusRank(status) {
  if (status === 'missing') return 1
  if (status === 'low') return 2
  if (status === 'excess') return 3
  return 4
}

function reportStandardVisualLevel(value) {
  const numeric = reportSafeNumber(value)
  if (numeric >= 86) return '매우 높음'
  if (numeric >= 70) return '높음'
  if (numeric >= 55) return '중간'
  return '확인'
}

function reportPublicAssetUrl(path) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${path || ''}`
  return `${base}${normalizedPath}`
}

function reportNormalizeCoverageText(value) {
  return String(value || '').replace(/\s+/g, '').toLowerCase()
}

function reportNormalizeStandardCategory(category) {
  const raw = String(category || '').trim()
  if (!raw) return '운전자/기타'
  if (REPORT_STANDARD_CATEGORY_ORDER.includes(raw)) return raw
  const compact = raw.replace(/\s+/g, '')
  return REPORT_STANDARD_CATEGORY_ALIASES[compact] || raw
}

function reportCategoryOrderIndex(category) {
  const idx = REPORT_STANDARD_CATEGORY_ORDER.indexOf(reportNormalizeStandardCategory(category))
  return idx >= 0 ? idx : REPORT_STANDARD_CATEGORY_ORDER.length
}

function reportCoverageCategoryTerms(category, rows) {
  const normalizedCategory = reportNormalizeStandardCategory(category)
  const terms = new Set(REPORT_STANDARD_CATEGORY_GROUP_TERMS[normalizedCategory] || [])
  terms.add(category)
  terms.add(normalizedCategory)
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.label) terms.add(row.label)
    ;(Array.isArray(row?.matchSamples) ? row.matchSamples : []).forEach((sample) => {
      if (sample?.coverageGroup) terms.add(sample.coverageGroup)
      if (sample?.coverageName) terms.add(sample.coverageName)
    })
  })
  return [...terms].map(reportNormalizeCoverageText).filter(Boolean)
}

function reportCoverageRowsForCategory(report, category, rows) {
  const coverages = Array.isArray(report?.coverageDetails?.coverageRows) ? report.coverageDetails.coverageRows : []
  const terms = reportCoverageCategoryTerms(category, rows)
  if (!terms.length) return []
  return coverages
    .filter((row) => {
      const text = reportNormalizeCoverageText([row.coverageGroup, row.coverageName].filter(Boolean).join(' '))
      return text && terms.some((term) => text.includes(term) || term.includes(text))
    })
    .sort((a, b) => reportSafeNumber(b.insuredAmount) - reportSafeNumber(a.insuredAmount))
}

function buildReportCoverageCategoryDiagnostics(report) {
  const reviewRows = Array.isArray(report?.standardCoverageReview?.rows) ? report.standardCoverageReview.rows : []
  if (!reviewRows.length) return []
  const groups = new Map()
  reviewRows.forEach((row) => {
    const category = reportNormalizeStandardCategory(row?.category || '기타')
    if (!groups.has(category)) groups.set(category, [])
    groups.get(category).push({ ...row, normalizedCategory: category })
  })
  return [...groups.entries()]
    .map(([category, rows]) => {
      const sortedRows = rows.slice().sort((a, b) => (
        reportStandardCoverageStatusRank(a.status) - reportStandardCoverageStatusRank(b.status)
        || reportSafeNumber(b.weight) - reportSafeNumber(a.weight)
        || reportSafeNumber(b.gapAmount) - reportSafeNumber(a.gapAmount)
      ))
      const linkedRows = reportCoverageRowsForCategory(report, category, sortedRows)
      const total = sortedRows.length
      const okCount = sortedRows.filter((row) => row.status === 'ok').length
      const issueCount = sortedRows.filter((row) => row.status !== 'ok').length
      const missingCount = sortedRows.filter((row) => row.status === 'missing').length
      const lowCount = sortedRows.filter((row) => row.status === 'low').length
      const excessCount = sortedRows.filter((row) => row.status === 'excess').length
      const linkedAmount = linkedRows.reduce((sum, row) => sum + reportSafeNumber(row.insuredAmount), 0)
      const score = total ? Math.round((okCount / total) * 100) : 0
      return {
        category,
        rows: sortedRows,
        linkedRows,
        linkedAmount,
        total,
        okCount,
        issueCount,
        missingCount,
        lowCount,
        excessCount,
        score,
        meta: REPORT_STANDARD_CATEGORY_META[category] || {
          subtitle: '등록 담보와 권장금액을 비교해 보장 상태를 점검합니다.',
          statTitle: '보장 점검 포인트',
          statLines: ['가입금액과 지급 조건을 함께 확인해야 합니다.', '담보명 매칭 결과는 약관의 보장 범위와 함께 검토합니다.', '고객 상황에 따라 권장금액은 조정될 수 있습니다.'],
          source: '내부 표준가입금액 기준',
        },
      }
    })
    .sort((a, b) => reportCategoryOrderIndex(a.category) - reportCategoryOrderIndex(b.category) || a.category.localeCompare(b.category))
}

function reportCoverageCategoryStatusLabel(group) {
  if (!group) return '점검'
  if (group.missingCount > 0) return '미가입 확인'
  if (group.lowCount > 0) return '보강 검토'
  if (group.excessCount > 0) return '과다'
  return '균형권'
}

function reportContractAiRoleMap(report) {
  const rows = Array.isArray(reportAiNarrative(report)?.contractRoleSummaries)
    ? reportAiNarrative(report).contractRoleSummaries
    : []
  return rows.reduce((acc, row) => {
    const key = String(row?.contractNo || '')
    if (key) acc[key] = row
    return acc
  }, {})
}

function reportExecutiveSummaryHtml(report) {
  const summary = report?.executiveSummary || {}
  const aiSummary = reportAiNarrative(report)?.executiveSummary || {}
  const groups = [
    { title: '우선 점검 3가지', tone: 'warn', items: reportPreferredList(aiSummary.problemPoints, summary.problemPoints) },
    { title: '유지할 강점 3가지', tone: 'ok', items: reportPreferredList(aiSummary.strengthPoints, summary.strengthPoints) },
    { title: '다음 확인 3가지', tone: 'focus', items: reportPreferredList(aiSummary.nextCheckPoints, summary.nextCheckPoints) },
  ].map((group) => ({
    ...group,
    items: Array.isArray(group.items) ? group.items.filter(Boolean).slice(0, 3) : [],
  }))
  if (!groups.some((group) => group.items.length)) return ''
  return `
  <div class="executive-summary-grid">
    ${groups.map((group) => `<div class="executive-summary-card ${group.tone}">
      <h3>${escapeReportHtml(group.title)}</h3>
      ${reportListHtml(group.items)}
    </div>`).join('')}
  </div>`
}

function reportSafeNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

function reportPercent(value, total) {
  const v = reportSafeNumber(value)
  const t = reportSafeNumber(total)
  if (t <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((v / t) * 1000) / 10))
}

function reportWidth(value, total, min = 0) {
  const pct = reportPercent(value, total)
  if (pct <= 0) return 0
  return Math.max(min, Math.min(100, pct))
}

function reportSectionSet(options = {}) {
  return new Set(normalizeIntegratedReportSections(options.selectedSections))
}

function reportOverallDashboardHtml(report) {
  const summary = report?.summary || {}
  const premium = report?.premiumBenchmark || {}
  const review = report?.standardCoverageReview || {}
  const health = report?.healthAnalysis || {}
  const hidden = report?.hiddenInsuranceAnalysis || {}
  const hiddenGuide = hidden?.guide || {}
  const hiddenReportSummary = hidden?.reportSummary || {}
  const healthCost = health?.costStats || {}
  const maturityCount = Number(summary.maturityCount || 0)
  const cards = [
    {
      label: '보험료 부담',
      value: premium.burdenLevel || '미산출',
      sub: summary.monthlyPremium ? `월 ${formatWon(summary.monthlyPremium)}` : '월 보험료 미입력',
      tone: premium.burdenLevel && /높|점검|과다/.test(String(premium.burdenLevel)) ? 'warn' : 'ok',
    },
    {
      label: '표준담보 준비',
      value: review.score != null ? `${review.score}점` : '미산출',
      sub: review.scoreLabel || '보장분석 기준',
      tone: Number(review.score || 0) >= 70 ? 'ok' : 'warn',
    },
    {
      label: '진료내역 신호',
      value: health.riskLabel || '미확인',
      sub: reportSafeNumber(healthCost.totalMedicalCost) ? `5년 ${formatWon(healthCost.totalMedicalCost)}` : '진료통계 미연결',
      tone: health.riskLabel && /높|주의|위험/.test(String(health.riskLabel)) ? 'warn' : 'focus',
    },
    {
      label: '숨은보험금',
      value: hiddenReportSummary.possibilityLabel || '미확인',
      sub: reportSafeNumber(hiddenGuide.unclaimedHint) ? `미청구 여지 ${formatWon(hiddenGuide.unclaimedHint)}` : '조회 결과 확인',
      tone: reportSafeNumber(hiddenGuide.unclaimedHint) > 0 ? 'warn' : 'focus',
    },
    {
      label: '만기·갱신',
      value: maturityCount ? `${maturityCount}건` : '확인 필요',
      sub: maturityCount ? '일정 관리 대상' : '만기정보 미확인',
      tone: maturityCount ? 'warn' : 'focus',
    },
  ]
  return `
  <div class="overall-dashboard">
    ${cards.map((card) => `<div class="${escapeReportHtml(card.tone)}">
      <span>${escapeReportHtml(card.label)}</span>
      <strong>${escapeReportHtml(card.value)}</strong>
      <em>${escapeReportHtml(card.sub)}</em>
    </div>`).join('')}
  </div>`
}

function reportInsurancePortfolioVisualHtml(report, options = {}) {
  const includePortfolio = options.includePortfolio !== false
  const includePremiumBenchmark = options.includePremiumBenchmark !== false
  const details = report?.coverageDetails || {}
  const contracts = Array.isArray(details.contractRows) ? details.contractRows : []
  const b = report?.premiumBenchmark || {}
  const customerPremium = Number(b.customerMonthlyPremium || 0)
  const averagePremium = Number(b.privateMedicalHouseholdAverageMonthlyPremium || 0)
  const aiSections = reportAiNarrative(report)?.sectionNarratives || {}
  const judgmentLines = reportPreferredList(aiSections.premiumBenchmark, b.judgmentLines).slice(0, 3)
  const benchmarkMax = Math.max(customerPremium, averagePremium, 1)
  const customerBenchmarkWidth = Math.max(3, Math.min(100, Math.round((customerPremium / benchmarkMax) * 100)))
  const averageBenchmarkWidth = Math.max(3, Math.min(100, Math.round((averagePremium / benchmarkMax) * 100)))
  if (!contracts.length && !includePremiumBenchmark) return ''
  const chart = buildCoveragePortfolioChartData(contracts, report?.customer?.birthDate)
  const portfolio = buildCoverageContractsPortfolioSummary(contracts)
  const premiumRows = Array.isArray(chart.monthlyPremiumMix) ? chart.monthlyPremiumMix : []
  const paidTotal = reportSafeNumber(portfolio.totalPaidEstimate)
  const futureTotal = reportSafeNumber(portfolio.totalFutureEstimate)
  const paymentTotal = paidTotal + futureTotal
  return `
  <div class="detail-section portfolio-visual-section">
    <div class="block-title">
      <span>보험료·납입 진단</span>
      <strong>월 보험료, 누적 납입, 통계 비교를 한 번에 확인합니다</strong>
    </div>
    ${includePortfolio ? `<div class="portfolio-metrics">
      <div><span>월 보험료</span><strong>${escapeReportHtml(formatWon(portfolio.totalMonthlyPremium))}</strong></div>
      <div><span>기납입 보험료</span><strong>${escapeReportHtml(formatWon(paidTotal))}</strong></div>
      <div><span>향후 납입 보험료</span><strong>${escapeReportHtml(formatWon(futureTotal))}</strong></div>
      <div><span>총 납입 예상</span><strong>${escapeReportHtml(formatWon(paymentTotal))}</strong></div>
    </div>` : ''}
    ${includePortfolio && premiumRows.length ? `<div class="portfolio-panel portfolio-row-block">
      <h3>월 보험료 구성</h3>
      <div class="premium-segment-bar">
        ${premiumRows.map((row, idx) => `<i style="width:${reportWidth(row.value, chart.monthlyPremiumTotal, 1)}%;background:${COVERAGE_CHART_COLORS[idx % COVERAGE_CHART_COLORS.length]}"></i>`).join('')}
      </div>
      <div class="legend-list">
        ${premiumRows.map((row, idx) => `<div>
          <span><em style="background:${COVERAGE_CHART_COLORS[idx % COVERAGE_CHART_COLORS.length]}"></em>${escapeReportHtml(row.name)}</span>
          <b>${escapeReportHtml(formatWon(row.value))} · ${escapeReportHtml(reportPercent(row.value, chart.monthlyPremiumTotal))}%</b>
        </div>`).join('')}
      </div>
    </div>` : ''}
    ${includePortfolio ? `<div class="portfolio-panel portfolio-row-block">
      <h3>전체 납입보험료 흐름</h3>
      <div class="future-premium-callout">
        <span>현재 보유계약 기준 앞으로 낼 보험료</span>
        <strong>${escapeReportHtml(formatWon(futureTotal))}</strong>
        <em>기납입 ${escapeReportHtml(formatWon(paidTotal))} · 총 납입 예상 ${escapeReportHtml(formatWon(paymentTotal))}</em>
      </div>
      <div class="payment-total-bar large">
        <i class="paid" style="width:${reportWidth(paidTotal, paymentTotal, paidTotal > 0 ? 2 : 0)}%"></i>
        <i class="future" style="width:${reportWidth(futureTotal, paymentTotal, futureTotal > 0 ? 2 : 0)}%"></i>
      </div>
      <div class="payment-legend"><span>기납입 ${escapeReportHtml(formatWon(paidTotal))} · ${escapeReportHtml(reportPercent(paidTotal, paymentTotal))}%</span><span>남은 보험료 ${escapeReportHtml(formatWon(futureTotal))} · ${escapeReportHtml(reportPercent(futureTotal, paymentTotal))}%</span></div>
    </div>` : ''}
    ${includePremiumBenchmark ? `<div class="portfolio-panel portfolio-row-block premium-benchmark-panel">
      <h3>보험료 부담 비교</h3>
      <p class="muted">${escapeReportHtml(b.summary || '등록된 보험료를 공개 통계와 비교해 현재 부담 수준을 확인합니다.')}</p>
      <div class="benchmark-status-line">
        <span>판단</span>
        <strong>${escapeReportHtml(b.burdenLevel || '미산출')}</strong>
      </div>
      <div class="bar-row compact">
        <span>고객 월 보험료</span>
        <div class="bar-track"><div class="bar-fill primary" style="width:${customerBenchmarkWidth}%"></div></div>
        <b>${escapeReportHtml(formatWon(customerPremium))}</b>
      </div>
      <div class="bar-row compact">
        <span>통계 평균</span>
        <div class="bar-track"><div class="bar-fill neutral" style="width:${averageBenchmarkWidth}%"></div></div>
        <b>${escapeReportHtml(formatWon(averagePremium))}</b>
      </div>
      <div class="guide-band compact">
        <span>월소득 대비 보험료</span>
        <strong>${b.premiumToIncomePct != null ? `고객 ${escapeReportHtml(b.premiumToIncomePct)}%${b.incomeBasisLabel ? ` · ${escapeReportHtml(b.incomeBasisLabel)}` : ''}` : '월소득 미입력'}</strong>
      </div>
      <p class="footnote">${b.premiumToIncomePct != null ? `권장 상담 구간은 월소득 대비 ${escapeReportHtml(b.incomeRatioGuideMinPct || 5)}~${escapeReportHtml(b.incomeRatioGuideMaxPct || 10)}%입니다.` : '월소득이 미입력되어 월소득 대비 보험료 비중은 계산되지 않았습니다.'}</p>
      ${judgmentLines.length ? `<div class="judgment-list compact">${judgmentLines.map((line) => `<div>${escapeReportHtml(line)}</div>`).join('')}</div>` : ''}
    </div>` : ''}
  </div>`
}

function reportIntegratedEvidenceHtml(report, options = {}) {
  const includeHealth = options.includeHealth !== false
  const includeHidden = options.includeHidden !== false
  if (!includeHealth && !includeHidden) return ''
  const health = report?.healthAnalysis || {}
  const hidden = report?.hiddenInsuranceAnalysis || {}
  const evidence = report?.evidenceSummary || {}
  const aiSections = reportAiNarrative(report)?.sectionNarratives || {}
  const cost = health.costStats || {}
  const hiddenTotals = hidden.totals || {}
  const topDiagnosis = Array.isArray(health.topDiagnosis) ? health.topDiagnosis.slice(0, 5) : []
  const signals = Array.isArray(health.signals) ? health.signals.slice(0, 4) : []
  const yearly = Array.isArray(cost.yearly) ? cost.yearly.slice(-5) : []
  const totalMedical = reportSafeNumber(cost.totalMedicalCost)
  const insuranceBenefit = reportSafeNumber(cost.insuranceBenefit)
  const outOfPocket = reportSafeNumber(cost.outOfPocket)
  const maxDiagnosisCost = Math.max(1, ...topDiagnosis.map((row) => reportSafeNumber(row.totalMedicalCost)))
  const maxYearCost = Math.max(1, ...yearly.map((row) => reportSafeNumber(row.totalMedicalCost)))
  const hiddenGuide = hidden.guide || {}
  const hiddenReportSummary = hidden.reportSummary || {}
  const hiddenCounts = hidden.counts || {}
  const hiddenInterpretation = hidden.interpretation || {}
  const hiddenMedicalItems = Array.isArray(hidden.medicalItems) ? hidden.medicalItems.slice(0, 4) : []
  const hiddenReimbursementItems = Array.isArray(hidden.reimbursementItems) ? hidden.reimbursementItems.slice(0, 4) : []
  const hiddenHighlights = Array.isArray(hidden.highlights) ? hidden.highlights.slice(0, 3) : []
  const healthJudgmentLines = reportPreferredList(aiSections.healthEvidence, evidence.healthJudgmentLines).slice(0, 5)
  const hiddenJudgmentLines = reportPreferredList(aiSections.hiddenInsurance, evidence.hiddenJudgmentLines).slice(0, 5)
  const hiddenClaimCandidateRows = Array.isArray(evidence.hiddenClaimCandidateRows) ? evidence.hiddenClaimCandidateRows.slice(0, 4) : []
  const claimRatio = hiddenGuide.claimRatio
  const hiddenClaimTotal = Math.max(reportSafeNumber(hiddenGuide.estimatedClaimable), reportSafeNumber(hiddenGuide.reimbursementTotal), 1)
  const hiddenBreakdown = hiddenReportSummary.claimableBreakdown || {}
  const hiddenBreakdownLines = Array.isArray(hiddenBreakdown.lines) ? hiddenBreakdown.lines : []
  const targetYearsLabel = Array.isArray(hidden.targetYears) && hidden.targetYears.length ? `${hidden.targetYears.join(', ')}년` : '최근 연말정산 자료'
  const healthFacts = [
    { label: '진료 위험도', value: health.riskLabel || '미확인', sub: health.riskScore != null ? `위험점수 ${health.riskScore}` : '진료정보분석 기준' },
    { label: '최근 5년 총 진료비', value: formatWon(totalMedical), sub: outOfPocket ? `본인부담 ${formatWon(outOfPocket)}` : '진료비 통계 미확인' },
    { label: '상위 질병', value: topDiagnosis[0]?.diagnosisName || topDiagnosis[0]?.diagnosisCode || '미확인', sub: topDiagnosis.length ? `상위 ${topDiagnosis.length}개 질병 확인` : '진료 질병 통계 미확인' },
    { label: '보험급여', value: formatWon(insuranceBenefit), sub: totalMedical ? `진료비 대비 ${reportPercent(insuranceBenefit, totalMedical)}%` : '급여 통계 미확인' },
  ]
  const hiddenFacts = [
    { label: '의료비 합계', value: formatWon(hiddenTotals.medicalExpense), sub: `진료·약국 ${hiddenTotals.visitCount ?? hiddenCounts.medicalFiltered ?? hidden.medicalExpenseCount ?? 0}건` },
    { label: '실손 수령 합계', value: formatWon(hiddenTotals.reimbursement), sub: `${hiddenCounts.reimbursements ?? hidden.reimbursementCount ?? 0}건` },
    { label: '추정 청구 가능액', value: formatWon(hiddenGuide.estimatedClaimable), sub: hiddenReportSummary.generationLabel || '기본 실손 기준' },
    { label: '미청구 여지', value: formatWon(hiddenGuide.unclaimedHint), sub: claimRatio != null ? `수령 비율 ${claimRatio}%` : '수령 비율 미산출' },
  ]
  const rowTitle = (row) => row?.title || row?.diagnosisName || row?.resMemberStoreName || row?.providerName || row?.institutionName || row?.companyName || row?.resCompanyNm || '-'
  const rowSubtitle = (row) => [row?.subtitle, row?.visitTypeLabel, row?.institutionGradeLabel, row?.account, row?.date || row?.resDate].filter(Boolean).join(' · ')
  const rowAmount = (row) => reportSafeNumber(row?.amount ?? row?.totalMedicalCost ?? row?.myTotalCost ?? row?.resAmountPayment ?? row?.paymentAmount)
  return `
  ${includeHealth ? `<div class="evidence-block">
    <div class="block-title">
      <span>진료내역 기반 확인</span>
      <strong>최근 5년 진료정보분석 기준</strong>
    </div>
    <div class="fact-grid">
      ${healthFacts.map((item) => `<div class="fact"><span>${escapeReportHtml(item.label)}</span><strong>${escapeReportHtml(item.value)}</strong><em>${escapeReportHtml(item.sub)}</em></div>`).join('')}
    </div>
    ${healthJudgmentLines.length ? `<div class="evidence-judgment-box">
      <b>진료내역 판단 요약</b>
      ${healthJudgmentLines.map((line) => `<p>${escapeReportHtml(line)}</p>`).join('')}
    </div>` : ''}
    <div class="health-cost-stack">
      <i class="benefit" style="width:${reportWidth(insuranceBenefit, totalMedical, insuranceBenefit > 0 ? 2 : 0)}%"></i>
      <i class="pocket" style="width:${reportWidth(outOfPocket, totalMedical, outOfPocket > 0 ? 2 : 0)}%"></i>
    </div>
    <div class="payment-legend"><span>보험급여 ${escapeReportHtml(formatWon(insuranceBenefit))}</span><span>본인부담 ${escapeReportHtml(formatWon(outOfPocket))}</span></div>
    <div class="evidence-columns">
      <div class="portfolio-panel">
        <h3>진료비 상위 질병 비중</h3>
        ${topDiagnosis.length ? `<div class="diagnosis-share-list">
          ${topDiagnosis.map((row) => `<div class="diagnosis-share-row">
            <div><span>${escapeReportHtml(row.diagnosisName || row.diagnosisCode || '질병')}</span><b>${escapeReportHtml(formatWon(row.totalMedicalCost))}${totalMedical ? ` · ${escapeReportHtml(reportPercent(row.totalMedicalCost, totalMedical))}%` : ''}</b></div>
            <div class="share-track"><i style="width:${reportWidth(row.totalMedicalCost, maxDiagnosisCost, 4)}%"></i></div>
          </div>`).join('')}
        </div>` : '<p class="muted">진료 질병 통계가 없습니다.</p>'}
      </div>
      <div class="portfolio-panel">
        <h3>연도별 진료비 흐름</h3>
        ${yearly.length ? `<div class="yearly-cost-bars">
          ${yearly.map((row) => `<div><b>${escapeReportHtml(row.year || row.label || '-')}</b><i style="height:${Math.max(8, reportWidth(row.totalMedicalCost, maxYearCost, 8))}%"></i><span>${escapeReportHtml(formatWonCompact(row.totalMedicalCost))}</span></div>`).join('')}
        </div>` : '<p class="muted">연도별 진료비 흐름을 표시할 데이터가 없습니다.</p>'}
      </div>
    </div>
    ${signals.length ? `<div class="signal-chip-list">${signals.map((signal) => `<span>${escapeReportHtml(signal.title || signal.label || signal.key || '확인 필요')}</span>`).join('')}</div>` : ''}
  </div>` : ''}
  ${includeHidden ? `<div class="evidence-block hidden-evidence-block hidden-summary-block">
    <div class="block-title">
      <span>숨은보험금 점검 요약</span>
      <strong>${escapeReportHtml(targetYearsLabel)} · ${escapeReportHtml(hiddenReportSummary.filtersLabel || '기본 필터 기준')}</strong>
    </div>
    <div class="fact-grid">
      ${hiddenFacts.map((item) => `<div class="fact"><span>${escapeReportHtml(item.label)}</span><strong>${escapeReportHtml(item.value)}</strong><em>${escapeReportHtml(item.sub)}</em></div>`).join('')}
    </div>
    <div class="hidden-onepage-summary">
      <div>
        <span>조회 판단</span>
        <strong>${escapeReportHtml(hiddenReportSummary.possibilityLabel || '숨은보험금 가능성 점검')}</strong>
      </div>
      <div>
        <span>미청구 여지</span>
        <strong>${escapeReportHtml(formatWon(hiddenGuide.unclaimedHint))}</strong>
      </div>
      <div>
        <span>수령 비율</span>
        <strong>${claimRatio != null ? `${escapeReportHtml(claimRatio)}%` : '미산출'}</strong>
      </div>
    </div>
    ${hiddenJudgmentLines.length ? `<div class="evidence-judgment-box">
      <b>숨은보험금 판단 요약</b>
      ${hiddenJudgmentLines.map((line) => `<p>${escapeReportHtml(line)}</p>`).join('')}
    </div>` : ''}
  </div>
  <div class="evidence-block hidden-evidence-block hidden-claim-block">
    <div class="block-title">
      <span>숨은보험금 청구 가능성 비교</span>
      <strong>${escapeReportHtml(hiddenInterpretation.headline || '조회 결과 점검')}</strong>
    </div>
    <div class="claim-compare-box">
      <div class="claim-compare-head"><span>${escapeReportHtml(hiddenReportSummary.possibilityLabel || '숨은보험금 가능성')}</span><b>${escapeReportHtml(hiddenInterpretation.headline || '조회 결과 점검')}</b></div>
      <div class="claim-compare-row"><span>추정 청구 가능액</span><div class="share-track"><i style="width:${reportWidth(hiddenGuide.estimatedClaimable, hiddenClaimTotal, 4)}%"></i></div><b>${escapeReportHtml(formatWon(hiddenGuide.estimatedClaimable))}</b></div>
      <div class="claim-compare-row received"><span>실손 수령 합계</span><div class="share-track"><i style="width:${reportWidth(hiddenGuide.reimbursementTotal, hiddenClaimTotal, 4)}%"></i></div><b>${escapeReportHtml(formatWon(hiddenGuide.reimbursementTotal))}</b></div>
      <div class="claim-compare-row gap"><span>미청구 여지</span><div class="share-track"><i style="width:${reportWidth(hiddenGuide.unclaimedHint, hiddenClaimTotal, hiddenGuide.unclaimedHint > 0 ? 4 : 0)}%"></i></div><b>${escapeReportHtml(formatWon(hiddenGuide.unclaimedHint))}</b></div>
      ${hiddenBreakdownLines.length ? `<div class="claim-breakdown-list">
        ${hiddenBreakdownLines.map((line) => `<div><span>${escapeReportHtml(line.label)}</span><b>${escapeReportHtml(formatWon(line.amount))}</b></div>`).join('')}
        ${hiddenBreakdown.totalReduction > 0 ? `<div class="total"><span>제외·공제 합계</span><b>${escapeReportHtml(formatWon(hiddenBreakdown.totalReduction))}</b></div>` : ''}
      </div>` : ''}
      ${hiddenInterpretation.body ? `<p>${escapeReportHtml(hiddenInterpretation.body)}</p>` : ''}
    </div>
    ${hiddenClaimCandidateRows.length ? `<div class="portfolio-panel claim-candidate-panel">
      <h3>우선 대조할 청구 후보 구간</h3>
      <div class="claim-candidate-list">
        ${hiddenClaimCandidateRows.map((row) => `<div>
          <span>${escapeReportHtml(row.title || '의료비 구간')}<em>${escapeReportHtml(row.subtitle || '')}${row.visitCount ? ` · ${escapeReportHtml(row.visitCount)}건` : ''}</em></span>
          <b>미청구 여지 ${escapeReportHtml(formatWon(row.unclaimedHint))}</b>
          <strong>추정 ${escapeReportHtml(formatWon(row.estimatedClaimable))} / 수령 ${escapeReportHtml(formatWon(row.reimbursementAmount))}</strong>
        </div>`).join('')}
      </div>
    </div>` : ''}
  </div>
  <div class="evidence-block hidden-evidence-block hidden-detail-block">
    <div class="block-title">
      <span>숨은보험금 주요 내역</span>
      <strong>의료비·실손 수령 내역 대조</strong>
    </div>
    <div class="evidence-columns">
      <div class="portfolio-panel">
        <h3>주요 의료비 내역</h3>
        ${hiddenMedicalItems.length ? `<div class="compact-result-list">
          ${hiddenMedicalItems.map((row) => `<div><span>${escapeReportHtml(rowTitle(row))}<em>${escapeReportHtml(rowSubtitle(row))}</em></span><b>${escapeReportHtml(formatWon(rowAmount(row)))}</b></div>`).join('')}
        </div>` : hiddenHighlights.length ? `<div class="compact-result-list">
          ${hiddenHighlights.map((row) => `<div><span>${escapeReportHtml(row.title || '-')}<em>${escapeReportHtml(row.subtitle || '')}</em></span><b>${escapeReportHtml(formatWon(row.amount))}</b></div>`).join('')}
        </div>` : '<p class="muted">표시할 의료비 내역이 없습니다.</p>'}
      </div>
      <div class="portfolio-panel">
        <h3>실손 수령 내역</h3>
        ${hiddenReimbursementItems.length ? `<div class="compact-result-list">
          ${hiddenReimbursementItems.map((row) => `<div><span>${escapeReportHtml(rowTitle(row))}<em>${escapeReportHtml(rowSubtitle(row))}</em></span><b>${escapeReportHtml(formatWon(rowAmount(row)))}</b></div>`).join('')}
        </div>` : '<p class="muted">표시할 실손 수령 내역이 없습니다.</p>'}
      </div>
    </div>
    <div class="document-chip-list">
      <span>확인 서류</span>
      <b>진료비 영수증</b>
      <b>진료비 세부내역서</b>
      <b>약제비 영수증</b>
      <b>실손 수령내역</b>
    </div>
  </div>` : ''}`
}

function reportDiseaseCoverageBalanceHtml(report) {
  const balance = report?.diseaseCoverageBalance || {}
  const rows = Array.isArray(balance.rows) ? balance.rows : []
  if (!rows.length) return ''
  const tone = (status) => {
    if (status === 'missing' || status === 'needs_review') return 'warn'
    if (status === 'unit_review') return 'warn'
    if (status === 'excess') return 'alert'
    return 'ok'
  }
  const label = (row) => {
    if (row.status === 'missing') return '부족 후보'
    if (row.status === 'needs_review') return '보강 검토'
    if (row.status === 'unit_review') return '금액체계 확인'
    if (row.status === 'excess') return '과다'
    return '균형권'
  }
  const name = (value) => ({
    Cancer: '암',
    Cerebrovascular: '뇌혈관',
    Cardiovascular: '심혈관',
    Hospitalization: '입원',
    Surgery: '수술',
    'Medical expense': '실손/의료비',
  }[value] || value)
  const insuredText = (row) => row.insuredAmountText || (row.insuredAmount ? formatCoverageAmountKorean(row.insuredAmount) : '미확인')
  const referenceText = (row) => row.referenceText || '기준 미확인'
  const rowHtml = (row) => {
    const amount = Number(row.insuredAmount || 0)
    const min = Number(row.referenceMin || 0)
    const max = Number(row.referenceMax || 0)
    const scale = Math.max(amount, max, min, 1)
    const amountWidth = reportWidth(amount, scale, amount > 0 ? 2 : 0)
    const maxWidth = reportWidth(max, scale, max > 0 ? 2 : 0)
    if (row.key === 'REAL_LOSS') {
      return `<div class="balance-card">
        <div class="balance-card-head">
          <div>
            <span class="balance-name">${escapeReportHtml(name(row.label))}</span>
            <strong>${escapeReportHtml(label(row))}</strong>
          </div>
          <span class="pill ${tone(row.status)}">${escapeReportHtml(label(row))}</span>
        </div>
        <div class="real-loss-check ${row.status === 'sufficient' ? 'ok' : 'warn'}">${row.status === 'sufficient' ? '실손/의료비 관련 담보가 확인됩니다.' : '실손/의료비 관련 담보 가입 여부 확인이 필요합니다.'}</div>
        <p class="balance-msg">${escapeReportHtml(row.message || '')}</p>
      </div>`
    }
    return `<div class="balance-card">
      <div class="balance-card-head">
        <div>
          <span class="balance-name">${escapeReportHtml(name(row.label))}</span>
          <strong>${escapeReportHtml(label(row))}</strong>
        </div>
        <span class="pill ${tone(row.status)}">${escapeReportHtml(label(row))}</span>
      </div>
      <div class="coverage-compare-bars">
        <div class="coverage-compare-row">
          <span>가입금액</span>
          <div class="coverage-compare-track"><i class="${tone(row.status)}" style="width:${amountWidth}%"></i></div>
          <b>${escapeReportHtml(insuredText(row))}</b>
        </div>
        <div class="coverage-compare-row reference">
          <span>기준상한</span>
          <div class="coverage-compare-track"><i style="width:${maxWidth}%"></i></div>
          <b>${escapeReportHtml(max ? formatCoverageAmountKorean(max) : referenceText(row))}</b>
        </div>
      </div>
      <div class="balance-amount-line">
        <span>가입금액 <b>${escapeReportHtml(insuredText(row))}</b></span>
        <span>기준금액 <b>${escapeReportHtml(referenceText(row))}</b></span>
      </div>
      <p class="balance-msg">${escapeReportHtml(row.message || '')}</p>
    </div>`
  }
  const chunks = reportChunkRowsAvoidSingleTail(rows, 3)
  return chunks.map((chunk, idx) => `
  <div class="stat-card disease-balance-card" data-report-page-break="true">
    <div class="stat-card-head">
      <div>
        <h2>${idx === 0 ? '질병통계 대비 보장 균형' : '질병통계 대비 보장 균형 (계속)'}</h2>
        <p class="muted">${escapeReportHtml(balance.basis || '등록 담보와 내부 권장 범위를 기준으로 주요 질병 보장 균형을 점검합니다.')}</p>
      </div>
      <strong>${escapeReportHtml(chunks.length > 1 ? `${idx + 1}/${chunks.length}` : (balance.summary || '점검'))}</strong>
    </div>
    <div class="balance-table">
      ${chunk.map(rowHtml).join('')}
    </div>
  </div>`).join('')
}

function reportCoverageDetailsHtml(report, options = {}) {
  const includeList = options.includeList !== false
  const includeDetails = options.includeDetails !== false
  if (!includeList && !includeDetails) return ''
  const details = report?.coverageDetails || {}
  const contracts = Array.isArray(details.contractRows) ? details.contractRows.slice(0, 18) : []
  const coverages = Array.isArray(details.coverageRows) ? details.coverageRows : []
  if (!contracts.length && !coverages.length) return ''
  const aiRoleMap = reportContractAiRoleMap(report)
  const coveragesByContractNo = coverages.reduce((acc, row) => {
    const key = String(row.contractNo || '')
    if (!acc[key]) acc[key] = []
    acc[key].push(row)
    return acc
  }, {})
  const roleByContractNo = contracts.reduce((acc, contract) => {
    const key = String(contract.no || '')
    acc[key] = buildContractRoleInsight(contract, coveragesByContractNo[key] || [], aiRoleMap[key])
    return acc
  }, {})
  return `
  ${includeList ? `<div class="detail-section page-break-soft">
    <div class="block-title">
      <span>보험계약 목록</span>
      <strong>보장분석 업로드/저장 계약 기준</strong>
    </div>
    ${contracts.length ? `<table class="report-table">
      <thead><tr><th>No</th><th>보험사</th><th>구분</th><th>상품명</th><th>계약일</th><th>월 보험료</th><th>납입/보장기간</th><th>계약 역할</th></tr></thead>
      <tbody>
        ${contracts.map((row) => {
          const role = roleByContractNo[String(row.no || '')] || buildContractRoleInsight(row, [])
          return `<tr>
          <td class="num">${escapeReportHtml(row.no)}</td>
          <td>${escapeReportHtml(row.insurerName)}</td>
          <td>${escapeReportHtml(row.insuranceCategory)}</td>
          <td class="wide">${escapeReportHtml(row.productName)}</td>
          <td class="num">${escapeReportHtml(row.contractDate)}</td>
          <td class="num">${escapeReportHtml(formatWon(row.monthlyPremium))}</td>
          <td>${escapeReportHtml(`${row.paymentPeriod || '-'} / ${row.coveragePeriod || '-'}`)}</td>
          <td><span class="contract-role-mini">${escapeReportHtml(role.primaryLabel)}</span><div class="contract-role-count">담보 ${escapeReportHtml(row.coverageCount || 0)}개</div></td>
        </tr>`
        }).join('')}
      </tbody>
    </table>` : '<p class="muted">등록된 계약 상세가 없습니다.</p>'}
  </div>` : ''}
  ${includeDetails ? `<div class="detail-section contract-detail-section">
    <div class="block-title">
      <span>${includeList ? '계약별 상세 담보' : '부록 · 계약별 상세 담보'}</span>
      <strong>계약 단위로 주요 담보와 가입금액을 확인합니다.</strong>
    </div>
    ${contracts.length ? contracts.map((contract) => {
      const rows = coveragesByContractNo[String(contract.no || '')] || []
      const role = roleByContractNo[String(contract.no || '')] || buildContractRoleInsight(contract, rows)
      if (!rows.length) return ''
      const rowsToShow = rows.slice(0, 54)
      const firstChunk = rowsToShow.slice(0, 18)
      const chunks = firstChunk.length ? reportAvoidSingleTailChunks([firstChunk, ...reportChunkRows(rowsToShow.slice(18), 22)]) : []
      const tableHtml = (chunk) => `<table class="report-table coverage-detail-table contract-coverage-table">
          <thead><tr><th>담보그룹</th><th>담보명</th><th>가입금액</th><th>갱신</th></tr></thead>
          <tbody>
            ${chunk.map((row) => `<tr>
              <td>${escapeReportHtml(row.coverageGroup || '미분류')}</td>
              <td class="wide">${escapeReportHtml(row.coverageName || '-')}</td>
              <td class="num">${escapeReportHtml(row.insuredAmount ? formatCoverageAmountKorean(row.insuredAmount) : '-')}</td>
              <td>${escapeReportHtml(row.renewalType || '-')}</td>
            </tr>`).join('')}
          </tbody>
        </table>`
      return chunks.map((chunk, chunkIdx) => `<section class="contract-detail-page ${chunkIdx > 0 ? 'contract-continuation-page' : ''}">
        ${chunkIdx === 0 ? `
        <div class="contract-detail-head">
          <div>
            <span>계약 ${escapeReportHtml(contract.no || '-')}</span>
            <h3>${escapeReportHtml(contract.productName || '-')}</h3>
            <p>${escapeReportHtml(contract.insurerName || '-')} · ${escapeReportHtml(contract.insuranceCategory || '-')}</p>
          </div>
          <div class="contract-detail-meta">
            <b>${escapeReportHtml(formatWon(contract.monthlyPremium))}</b>
            <span>월 보험료</span>
          </div>
        </div>
        <div class="contract-summary-grid">
          <div><span>계약일</span><strong>${escapeReportHtml(contract.contractDate || '-')}</strong></div>
          <div><span>납입기간</span><strong>${escapeReportHtml(contract.paymentPeriod || '-')}</strong></div>
          <div><span>보장기간</span><strong>${escapeReportHtml(contract.coveragePeriod || '-')}</strong></div>
          <div><span>담보 수</span><strong>${escapeReportHtml(rows.length || contract.coverageCount || 0)}개</strong></div>
        </div>
        <div class="contract-role-card">
          <div class="contract-role-main">
            <span>${escapeReportHtml(role.primaryBadge || '보장')}</span>
            <strong>${escapeReportHtml(role.primaryLabel || '계약 역할')}</strong>
            <p>${escapeReportHtml(role.summary)}</p>
          </div>
          <div class="contract-role-side">
            ${role.secondaryLabels.length ? `<div class="contract-role-tags">
              ${role.secondaryLabels.map((label) => `<span>${escapeReportHtml(label)}</span>`).join('')}
            </div>` : ''}
            ${role.topCoverages.length ? `<div class="contract-key-coverage-list">
              ${role.topCoverages.map((row) => `<div><span>${escapeReportHtml(row.name)}</span><b>${escapeReportHtml(row.amountText)}</b></div>`).join('')}
            </div>` : ''}
          </div>
        </div>
        ${role.checkPoints.length ? `<div class="contract-check-points">
          ${role.checkPoints.map((line) => `<div>${escapeReportHtml(line)}</div>`).join('')}
        </div>` : ''}` : `<div class="contract-continuation-head">
          <div>
            <span>계약 ${escapeReportHtml(contract.no || '-')} 계속</span>
            <h3>${escapeReportHtml(contract.productName || '-')}</h3>
            <p>${escapeReportHtml(contract.insurerName || '-')} · ${escapeReportHtml(contract.insuranceCategory || '-')}</p>
          </div>
          <strong>${escapeReportHtml(chunkIdx + 1)} / ${escapeReportHtml(chunks.length)}</strong>
        </div>`}
        ${chunk.length ? tableHtml(chunk) : '<p class="muted">등록된 담보 상세가 없습니다.</p>'}
        ${chunkIdx === chunks.length - 1 && rows.length > rowsToShow.length ? `<p class="footnote">담보가 많아 상위 ${escapeReportHtml(rowsToShow.length)}개만 표시했습니다. 전체 담보는 계약 원문 또는 보장분석 화면에서 확인하세요.</p>` : ''}
      </section>`).join('')
    }).join('') || '<p class="muted">상세 담보가 등록된 계약이 없습니다.</p>' : '<p class="muted">등록된 계약 상세가 없습니다.</p>'}
  </div>` : ''}
  `
}

function reportStandardCoverageReviewHtml(report) {
  const review = report?.standardCoverageReview || {}
  const rows = Array.isArray(review.rows) ? review.rows : []
  if (!rows.length) return ''
  const matching = review.matchingSummary || {}
  const score = Number(review.score || 0)
  const categoryGroups = buildReportCoverageCategoryDiagnostics(report)
  const standardAmount = (row) => reportSafeNumber(row?.currentAmount ?? row?.insuredAmount ?? row?.totalAmount)
  const standardAmountText = (row) => {
    const direct = row?.currentAmountText || row?.insuredAmountText || row?.totalAmountText
    if (direct && direct !== '-') return direct
    const amount = standardAmount(row)
    return amount > 0 ? formatCoverageAmountKorean(amount) : '-'
  }
  const standardRowKey = (row) => [
    reportNormalizeStandardCategory(row?.category || row?.normalizedCategory || ''),
    row?.label || row?.ruleCode || row?.standardCoverageCode || row?.standardCoverageName || '',
  ].join('|')
  const uniqueRows = (items) => {
    const seen = new Set()
    return (Array.isArray(items) ? items : []).filter((row) => {
      const key = standardRowKey(row)
      if (!key.trim() || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }
  const sortStandardRows = (a, b) => (
    reportStandardCoverageStatusRank(a.status) - reportStandardCoverageStatusRank(b.status)
    || reportSafeNumber(b.weight) - reportSafeNumber(a.weight)
    || reportSafeNumber(b.gapAmount) - reportSafeNumber(a.gapAmount)
    || standardAmount(b) - standardAmount(a)
    || String(a.label || '').localeCompare(String(b.label || ''), 'ko')
  )
  const matchItems = (row) => {
    const samples = Array.isArray(row.matchSamples) ? row.matchSamples : []
    const items = samples.map((sample) => {
      const amount = reportSafeNumber(sample?.insuredAmount ?? sample?.amount ?? sample?.totalAmount)
      const amountText = sample?.insuredAmountText || sample?.amountText || sample?.totalAmountText || (amount > 0 ? formatCoverageAmountKorean(amount) : '')
      const productName = sample?.productName || ''
      const coverageName = sample?.coverageName || sample?.standardCoverageName || sample?.coverageGroup || ''
      return {
        title: productName || coverageName || '가입 담보',
        detail: productName && coverageName && coverageName !== productName ? coverageName : '',
        amountText,
      }
    }).filter((item) => item.title || item.amountText)
    if (items.length) return items
    const amountText = standardAmountText(row)
    return amountText !== '-' ? [{ title: '보장요약 연결', detail: '', amountText }] : []
  }
  const sortedRows = rows.slice().sort(sortStandardRows)
  const issueRows = rows.filter((row) => row.status !== 'ok').sort(sortStandardRows)
  const amountRows = rows.filter((row) => standardAmount(row) > 0).sort(sortStandardRows)
  const displayRows = uniqueRows([
    ...issueRows,
    ...amountRows,
    ...sortedRows,
  ])
  const overviewRows = displayRows.slice(0, 12)
  const tableRows = overviewRows.slice(0, 10)
  const standardTableRows = reportAvoidSingleTailChunks(reportChunkRows(displayRows.slice(0, 24), 6))
  const statusPill = (row) => `<span class="table-pill ${reportStandardCoverageTone(row.status)}">${escapeReportHtml(row.statusLabel || '-')}</span>`
  const rowCompareBar = (row, compact = false) => {
    const current = standardAmount(row)
    const min = reportSafeNumber(row.minAmount)
    const target = reportSafeNumber(row.targetAmount || row.maxAmount || row.minAmount)
    const max = reportSafeNumber(row.maxAmount || target || min)
    const scale = Math.max(current, max, target, min, 1)
    return `<div class="${compact ? 'standard-mini-bars compact' : 'standard-mini-bars'}">
      <div><span>가입</span><i class="${reportStandardCoverageTone(row.status)}" style="width:${reportWidth(current, scale, current > 0 ? 2 : 0)}%"></i><b>${escapeReportHtml(standardAmountText(row))}</b></div>
      <div><span>권장</span><i class="ref" style="width:${reportWidth(target || max || min, scale, 2)}%"></i><b>${escapeReportHtml(row.targetText || row.referenceText || '-')}</b></div>
    </div>`
  }
  const standardDetailTableHtml = (chunk) => chunk.length ? `<table class="report-table standard-table">
      <thead><tr><th>분류</th><th>표준담보</th><th>중요도</th><th>상태</th><th>현재</th><th>기준/과부족</th><th>가입내역/판단</th></tr></thead>
      <tbody>
        ${chunk.map((row) => {
          const sampleItems = matchItems(row)
          return `<tr>
          <td>${escapeReportHtml(row.normalizedCategory || reportNormalizeStandardCategory(row.category) || '-')}</td>
          <td class="wide">${escapeReportHtml(row.label || '-')}</td>
          <td>${escapeReportHtml(row.importance || '-')}</td>
          <td>${statusPill(row)}</td>
          <td class="num">${escapeReportHtml(standardAmountText(row))}</td>
          <td class="num">
            <div>${escapeReportHtml(row.referenceText || row.targetText || '-')}</div>
            <div class="standard-gap-line">${escapeReportHtml(row.gapText || '기준 확인')}</div>
          </td>
          <td>
            <div>${escapeReportHtml(row.message || '-')}</div>
            ${row.recommendationText ? `<div class="recommendation-line">${escapeReportHtml(row.recommendationText)}</div>` : ''}
            <div class="match-basis"><b>가입내역</b>${sampleItems.length ? `<ul class="match-basis-list">
              ${sampleItems.map((item) => `<li><span><strong>${escapeReportHtml(item.title)}</strong>${item.detail ? `<em>${escapeReportHtml(item.detail)}</em>` : ''}</span><i>${escapeReportHtml(item.amountText || '-')}</i></li>`).join('')}
            </ul>` : '<span class="match-empty">매칭 담보 없음</span>'}</div>
          </td>
        </tr>`
        }).join('')}
      </tbody>
    </table>` : '<p class="muted">우선 점검할 표준담보 항목이 없습니다.</p>'
  const overviewHtml = `
  <div class="engine-section standard-review-section standard-overview-section" data-report-page-break="true">
    <div class="engine-head">
      <div>
        <h2>주요 담보별 과부족 현황</h2>
        <p>${escapeReportHtml(review.basis || '표준담보별 권장 범위와 현재 보장을 비교합니다. 실제 판단은 약관의 지급 단위와 담보명 매칭 결과를 함께 확인합니다.')}</p>
      </div>
      <div class="score-dial">
        <strong>${escapeReportHtml(score)}</strong>
        <span>${escapeReportHtml(review.scoreLabel || '점검')}</span>
      </div>
    </div>
    <div class="score-track"><div style="width:${Math.max(4, Math.min(100, score))}%"></div></div>
    <div class="issue-strip">
      <span>부족 ${escapeReportHtml(review.missingCount || 0)}건</span>
      <span>보강 ${escapeReportHtml(review.lowCount || 0)}건</span>
      <span>과다 ${escapeReportHtml(review.excessCount || 0)}건</span>
      <span>매칭 ${escapeReportHtml(matching.matchedRuleCount || 0)}/${escapeReportHtml(matching.ruleCount || rows.length)}개 규칙</span>
      <span>검토담보 ${escapeReportHtml(matching.checkedCoverageCount || 0)}개</span>
    </div>
    <div class="coverage-gap-overview">
      <div class="coverage-gap-table-wrap">
        <table class="report-table coverage-gap-table">
          <thead><tr><th>분류</th><th>담보명</th><th>권장금액</th><th>가입금액</th><th>과부족</th><th>상태</th></tr></thead>
          <tbody>
            ${tableRows.map((row) => `<tr>
              <td>${escapeReportHtml(row.normalizedCategory || reportNormalizeStandardCategory(row.category) || '-')}</td>
              <td class="wide">${escapeReportHtml(row.label || '-')}</td>
              <td class="num">${escapeReportHtml(row.targetText || row.referenceText || '-')}</td>
              <td class="num">${escapeReportHtml(standardAmountText(row))}</td>
              <td class="num ${reportStandardCoverageTone(row.status)}">${escapeReportHtml(row.gapText || '-')}</td>
              <td>${statusPill(row)}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
      <div class="coverage-category-overview">
        <h3>카테고리별 준비 상태</h3>
        ${categoryGroups.map((group) => `<div class="category-score-row">
          <div><span>${escapeReportHtml(group.category)}</span><b>${escapeReportHtml(reportCoverageCategoryStatusLabel(group))}</b></div>
          <div class="category-score-track"><i class="${group.score >= 70 ? 'ok' : group.score >= 40 ? 'warn' : 'alert'}" style="width:${Math.max(4, Math.min(100, group.score))}%"></i></div>
          <strong>${escapeReportHtml(group.score)}%</strong>
        </div>`).join('')}
      </div>
    </div>
  </div>`
  const detailHtml = standardTableRows.length ? standardTableRows.map((chunk, idx) => `
  <div class="engine-section standard-review-section ${idx > 0 ? 'continuation-section' : ''}">
    ${idx === 0 ? `<div class="engine-continuation-head">
      <h2>표준담보 세부 진단</h2>
      <span>보장요약 연결</span>
    </div>` : `<div class="engine-continuation-head">
      <h2>표준담보 세부 진단 계속</h2>
      <span>${escapeReportHtml(idx + 1)} / ${escapeReportHtml(standardTableRows.length)}</span>
    </div>`}
    ${standardDetailTableHtml(chunk)}
  </div>`).join('') : ''
  const categoryHtml = categoryGroups.map((group) => {
    const cardRows = group.rows.slice(0, 4)
    const linkedRows = group.linkedRows.slice(0, 3)
    const sourceText = group.meta.source || '내부 표준가입금액 기준'
    const visualBars = Array.isArray(group.meta.visualBars) ? group.meta.visualBars.slice(0, 3) : []
    const visualTags = Array.isArray(group.meta.visualTags) ? group.meta.visualTags.slice(0, 3) : []
    const statImageSrc = group.meta.imageFile ? reportPublicAssetUrl(`/static-assets/report/${group.meta.imageFile}`) : ''
    return `
  <div class="engine-section coverage-category-diagnosis-section" data-report-page-break="true">
    <div class="category-diagnosis-head">
      <div>
        <h2>보장요약 카테고리 진단</h2>
        <div class="category-diagnosis-category">${escapeReportHtml(group.category)}</div>
        <p>${escapeReportHtml(group.meta.subtitle)}</p>
      </div>
      <div class="category-diagnosis-score ${group.score >= 70 ? 'ok' : group.score >= 40 ? 'warn' : 'alert'}">
        <strong>${escapeReportHtml(group.score)}%</strong>
        <span>${escapeReportHtml(reportCoverageCategoryStatusLabel(group))}</span>
      </div>
    </div>
    <div class="category-diagnosis-strip">
      <span>점검 ${escapeReportHtml(group.total)}개</span>
      <span>충분 ${escapeReportHtml(group.okCount)}개</span>
      <span>부족 ${escapeReportHtml(group.missingCount)}개</span>
      <span>보강 ${escapeReportHtml(group.lowCount)}개</span>
      <span>과다 ${escapeReportHtml(group.excessCount)}개</span>
      <span>연결담보 ${escapeReportHtml(group.linkedRows.length)}개</span>
    </div>
    <div class="category-diagnosis-card-grid">
      ${cardRows.map((row) => `<div class="category-diagnosis-card ${reportStandardCoverageTone(row.status)}">
        <div class="category-card-head">
          <span>${escapeReportHtml(row.importance || '권장')}</span>
          ${statusPill(row)}
        </div>
        <strong>${escapeReportHtml(row.label || '-')}</strong>
        ${rowCompareBar(row, true)}
        <div class="category-card-gap">${escapeReportHtml(row.gapText || '기준 확인')}</div>
      </div>`).join('')}
    </div>
    <div class="category-diagnosis-bottom ${statImageSrc ? 'with-source-image' : ''}">
      <div class="category-linked-coverage">
        <div class="category-stat-kicker">보장요약 연결</div>
        <h3>현재 연결된 담보</h3>
        <div class="linked-coverage-total">
          <span>연결 가입금액 합계</span>
          <strong>${escapeReportHtml(group.linkedAmount ? formatCoverageAmountKorean(group.linkedAmount) : '미확인')}</strong>
        </div>
        ${linkedRows.length ? `<div class="linked-coverage-list">
          ${linkedRows.map((row) => `<div><span>${escapeReportHtml(row.coverageName || row.coverageGroup || '담보')}</span><b>${escapeReportHtml(row.insuredAmount ? formatCoverageAmountKorean(row.insuredAmount) : '-')}</b></div>`).join('')}
        </div>` : '<p class="muted">보장요약에 연결된 담보가 확인되지 않았습니다.</p>'}
      </div>
      <div class="category-stat-visual">
        ${statImageSrc ? `<div class="category-stat-source-image-wrap">
          <img class="category-stat-source-image" src="${escapeReportHtml(statImageSrc)}" alt="${escapeReportHtml(group.category)} 통계 이미지" />
        </div>
        <div class="category-stat-caption">${escapeReportHtml(group.meta.visualCaption || '공개통계와 보장분석 기준을 상담용으로 재구성했습니다.')}</div>` : `<div class="category-stat-image">
          <div class="category-stat-main">
            <span>${escapeReportHtml(group.meta.visualKicker || '점검 지표')}</span>
            <strong>${escapeReportHtml(group.meta.visualValue || group.category)}</strong>
            <em>${escapeReportHtml(group.meta.visualUnit || '확인')}</em>
          </div>
          <div class="category-stat-bars">
            ${visualBars.map((bar) => `<div>
              <span>${escapeReportHtml(bar.label || '-')}</span>
              <i><b style="width:${reportWidth(bar.value, 100, 8)}%"></b></i>
              <strong>${escapeReportHtml(bar.level || reportStandardVisualLevel(bar.value))}</strong>
            </div>`).join('')}
          </div>
          ${visualTags.length ? `<div class="category-stat-tags">${visualTags.map((tag) => `<span>${escapeReportHtml(tag)}</span>`).join('')}</div>` : ''}
        </div>
        <div class="category-stat-caption">${escapeReportHtml(group.meta.visualCaption || '')}</div>
        <div class="category-stat-lines">
          ${group.meta.statLines.slice(0, 3).map((line, idx) => `<div><b>${escapeReportHtml(idx + 1)}</b><span>${escapeReportHtml(line)}</span></div>`).join('')}
        </div>`}
        <p>${escapeReportHtml(sourceText)}</p>
      </div>
    </div>
  </div>`
  }).join('')
  return `${overviewHtml}${detailHtml}${categoryHtml}`
}

function reportDiagnosisScenarioHighlightsHtml(report) {
  const rows = Array.isArray(report?.diagnosisScenarioHighlights) ? report.diagnosisScenarioHighlights.slice(0, 4) : []
  if (!rows.length) return ''
  const pct = (value, max) => Math.max(0, Math.min(100, Math.round((Number(value || 0) / Math.max(Number(max || 0), 1)) * 100)))
  const chunks = reportChunkRowsAvoidSingleTail(rows, 4)
  const cardsHtml = (chunk, offset) => `<div class="scenario-highlight-grid">
      ${chunk.map((row, localIdx) => {
        const idx = offset + localIdx
        const need = Number(row.estimatedNeed || 0)
        const benefit = Number(row.estimatedInsuranceBenefit || 0)
        const shortfall = Number(row.estimatedShortfall || 0)
        const scale = Math.max(need, benefit + shortfall, 1)
        const benefitPct = pct(benefit, scale)
        const shortfallPct = pct(shortfall, scale)
        const reasons = Array.isArray(row.reasons) ? row.reasons.slice(0, 2) : []
        const shortfallRatio = need > 0 ? reportPercent(shortfall, need) : 0
        const benefitRatio = need > 0 ? reportPercent(benefit, need) : 0
        return `<div class="scenario-highlight-card">
          <div class="scenario-card-top">
            <span>TOP ${escapeReportHtml(row.rank || idx + 1)} · ${escapeReportHtml(row.category || '질병 시나리오')}</span>
            <b>${escapeReportHtml(row.score != null ? `${row.score}점` : '추천')}</b>
          </div>
          <strong>${escapeReportHtml(row.title || row.diagnosisName || '-')}</strong>
          <div class="scenario-impact-head ${shortfall > 0 ? 'warn' : 'ok'}">
            <span>${shortfall > 0 ? '부족 예상' : '보장 여유'}</span>
            <b>${escapeReportHtml(formatWon(shortfall > 0 ? shortfall : benefit - need))}</b>
            <em>필요자금 대비 보유담보 ${escapeReportHtml(benefitRatio)}%${shortfall > 0 ? ` · 부족 ${escapeReportHtml(shortfallRatio)}%` : ''}</em>
          </div>
          <p>${escapeReportHtml(row.summary || row.message || '')}</p>
          <div class="scenario-money-bar">
            <i class="covered" style="width:${Math.max(2, benefitPct)}%"></i>
            <i class="gap" style="width:${Math.max(shortfall > 0 ? 2 : 0, shortfallPct)}%"></i>
          </div>
          <div class="scenario-money-grid">
            <span>필요자금 <b>${escapeReportHtml(formatWon(need))}</b></span>
            <span>보유담보 <b>${escapeReportHtml(formatWon(benefit))}</b></span>
            <span>부족예상 <b>${escapeReportHtml(formatWon(shortfall))}</b></span>
          </div>
          ${row.coverageTriggerLabel ? `<div class="scenario-trigger">
            <span>${escapeReportHtml(row.coverageTriggerLabel)} · ${escapeReportHtml(row.coverageStatusLabel || '점검')}</span>
            <b>${escapeReportHtml(row.coverageCurrentAmountText || '-')} / 목표 ${escapeReportHtml(row.coverageTargetText || '-')}</b>
            ${row.coverageGapText ? `<em>${escapeReportHtml(row.coverageGapText)}</em>` : ''}
          </div>` : ''}
          ${reasons.length ? `<ul class="scenario-reasons">${reasons.map((reason) => `<li>${escapeReportHtml(reason)}</li>`).join('')}</ul>` : ''}
        </div>`
      }).join('')}
    </div>`
  let renderedOffset = 0
  return chunks.map((chunk, idx) => {
    const offset = renderedOffset
    renderedOffset += chunk.length
    return `
  <div class="engine-section scenario-highlight-section ${idx > 0 ? 'continuation-section' : ''}">
    ${idx === 0 ? `<div class="engine-head">
      <div>
        <h2>고객 맞춤 진단 시나리오 TOP ${escapeReportHtml(rows.length)}</h2>
        <p>현재 연령, 성별, 진료정보, 보유계약을 함께 반영해 고객에게 설명하기 쉬운 진단 시나리오를 추천합니다.</p>
      </div>
      <div class="score-dial">
        <strong>${escapeReportHtml(rows.length)}</strong>
        <span>시나리오</span>
      </div>
    </div>` : `<div class="engine-continuation-head">
      <h2>고객 맞춤 진단 시나리오 계속</h2>
      <span>${escapeReportHtml(idx + 1)} / ${escapeReportHtml(chunks.length)}</span>
    </div>`}
    ${cardsHtml(chunk, offset)}
  </div>`
  }).join('')
}

function reportNarrativeSectionsHtml(report) {
  const sections = Array.isArray(report?.sections) ? report.sections : []
  const visibleTitles = new Set(['만기/갱신', '상담 설문'])
  const visible = sections.filter((section) => visibleTitles.has(section?.title))
  if (!visible.length) return ''
  return `
  <div class="grid narrative-grid">
    ${visible.map((section) => `<div class="section"><h2>${escapeReportHtml(section?.title || '')}</h2>${reportListHtml(section?.lines)}</div>`).join('')}
  </div>`
}

function reportFinalActionsHtml(report) {
  const ai = reportAiNarrative(report)
  const recommendations = reportPreferredList(ai.recommendations, report?.recommendations)
  const nextActions = reportPreferredList(ai.nextActions, report?.nextActions)
  return `
  <div class="engine-section final-actions-page" data-report-page-break="true">
    <div class="engine-head">
      <div>
        <h2>최종 확인 포인트</h2>
        <p>고객에게 설명하기 전 마지막으로 확인할 상담 포인트입니다.</p>
      </div>
    </div>
    <div class="final-action-grid">
      <div class="section">
        <h2>확인 포인트</h2>
        ${reportListHtml(recommendations)}
      </div>
      <div class="section">
        <h2>다음 상담 액션</h2>
        ${reportListHtml(nextActions)}
      </div>
    </div>
  </div>`
}

function reportClosingHtml(report) {
  const advisor = report?.advisorComments || {}
  const aiClosing = reportAiNarrative(report)?.closing || {}
  const customer = report?.customer || {}
  const customerName = customer.customerName || '고객'
  const note = advisor.customerNote || aiClosing.customerNote || '이번 리포트는 현재 등록된 자료를 기준으로 작성했습니다. 실제 보장 판단은 약관, 보험사 기준, 고객님의 추가 확인 내용에 따라 달라질 수 있어 상담에서 함께 확인해드리겠습니다.'
  const nextStep = advisor.nextStep || aiClosing.nextStep || '다음 상담에서는 보완이 필요한 항목, 유지할 보장, 보험료 부담 가능 범위를 함께 정리합니다.'
  return `
  <div class="closing-page">
    <div class="closing-badge">설계EZ 상담 리포트</div>
    <h2>${escapeReportHtml(customerName)}님께 맞춘 보장 점검을 이어가겠습니다.</h2>
    <p>${escapeReportHtml(note)}</p>
    <div class="closing-next"><span>다음 확인</span><strong>${escapeReportHtml(nextStep)}</strong></div>
    <p class="closing-disclaimer">${escapeReportHtml(report?.disclaimer || '')}</p>
  </div>`
}

function buildDiagnosisSimulationPrintHtml(simulation, options = {}) {
  const { autoPrint = false, showToolbar = true } = options
  const customer = simulation?.customer || {}
  const messages = Array.isArray(simulation?.keyMessages) ? simulation.keyMessages : []
  const actions = Array.isArray(simulation?.nextActions) ? simulation.nextActions : []
  const coverages = Array.isArray(simulation?.matchedCoverages) ? simulation.matchedCoverages : []
  const referenceCoverages = Array.isArray(simulation?.referenceCoverages) ? simulation.referenceCoverages : []
  const costAssumptions = Array.isArray(simulation?.costAssumptions) ? simulation.costAssumptions : []
  const termsEvidence = simulation?.termsEvidence || null
  const termsEvidenceContracts = Array.isArray(termsEvidence?.contracts) ? termsEvidence.contracts : []
  const title = simulation?.title || '진단시뮬레이션'
  const numberValue = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? Math.max(0, n) : 0
  }
  const expectedMedicalCost = numberValue(simulation?.expectedMedicalCost)
  const estimatedOutOfPocket = numberValue(simulation?.estimatedOutOfPocket)
  const recoveryLivingCost = numberValue(simulation?.recoveryLivingCost)
  const expectedNeed = numberValue(simulation?.expectedNeed || estimatedOutOfPocket + recoveryLivingCost)
  const estimatedInsuranceBenefit = numberValue(simulation?.estimatedInsuranceBenefit)
  const estimatedShortfall = numberValue(simulation?.estimatedShortfall)
  const estimatedSurplus = numberValue(simulation?.estimatedSurplus)
  const healthBenefitEstimate = Math.max(0, expectedMedicalCost - estimatedOutOfPocket)
  const pct = (part, total) => {
    if (!total || total <= 0) return 0
    return Math.max(0, Math.min(100, Math.round((part / total) * 1000) / 10))
  }
  const needPocketPct = pct(estimatedOutOfPocket, expectedNeed)
  const needLivingPct = expectedNeed > 0 ? Math.max(0, 100 - needPocketPct) : 0
  const coverageTotal = Math.max(expectedNeed, estimatedInsuranceBenefit)
  const coverageBenefitPct = pct(estimatedInsuranceBenefit, coverageTotal)
  const coverageGapPct = coverageTotal > 0 ? Math.max(0, 100 - coverageBenefitPct) : 0
  const impactMax = Math.max(estimatedOutOfPocket, recoveryLivingCost, expectedNeed, 1)
  const impactWidth = (value) => {
    const width = pct(value, impactMax)
    return value > 0 ? Math.max(7, width) : 0
  }
  const monthlyIncome = numberValue(customer?.monthlyIncome)
  const recoveryMonths = monthlyIncome > 0 && recoveryLivingCost > 0
    ? Math.max(1, Math.round(recoveryLivingCost / monthlyIncome))
    : 0
  const customerName = customer.customerName || '고객'
  const diagnosisName = simulation?.diagnosisName || title
  const scenarioEventLabel = diagnosisName.includes('진단')
    ? `${diagnosisName}을 받으면`
    : `${diagnosisName} 상황이 발생하면`
  const shortfallLabel = estimatedShortfall > 0 ? '부족 예상 금액' : '보장 여유 금액'
  const shortfallAmount = estimatedShortfall > 0 ? estimatedShortfall : estimatedSurplus
  const coverageTableHtml = (rows, emptyMessage) => rows.length
    ? `<table><thead><tr><th>보험사</th><th>상품</th><th>담보</th><th>가입금액</th></tr></thead><tbody>${rows.map((row) => `<tr><td>${escapeReportHtml(row.insurerName || '-')}</td><td>${escapeReportHtml(row.productName || '-')}</td><td>${escapeReportHtml(row.coverageName || '-')}</td><td class="num">${escapeReportHtml(formatCoverageAmountKorean(row.insuredAmount))}</td></tr>`).join('')}</tbody></table>`
    : `<p>${escapeReportHtml(emptyMessage)}</p>`
  const assumptionRows = costAssumptions.length ? costAssumptions : [
    { label: '예상 총진료비', amount: expectedMedicalCost, basis: '공개 진료비 통계를 기준금액으로 반영했습니다.', note: '실제 치료 조합과 병원 청구 구조에 따라 달라질 수 있습니다.' },
    { label: '본인부담·비급여 가정', amount: estimatedOutOfPocket, basis: '건강보험으로 보전되지 않을 수 있는 개인 부담액을 별도 가정했습니다.', note: '실손 보상 여부와 비급여 선택에 따라 달라질 수 있습니다.' },
    { label: '회복·생활비 필요액', amount: recoveryLivingCost, basis: '치료·회복 기간의 소득 공백과 생활비를 반영했습니다.', note: '직업, 가족 돌봄, 치료 기간에 따라 달라질 수 있습니다.' },
    { label: '진단비 예상보험금', amount: estimatedInsuranceBenefit, basis: '계산 반영 담보의 가입금액을 합산했습니다.', note: '수술·항암·방사선 담보는 참고용으로 분리했습니다.' },
  ]
  const sourceLine = [simulation?.sourceName, simulation?.sourceUrl].filter(Boolean).join(' ')
  const statBasisNotes = [
    simulation?.statBasis ? `통계/가정 근거: ${simulation.statBasis}` : '',
    sourceLine ? `출처: ${sourceLine}` : '',
  ].filter(Boolean)
  const termsEvidenceStatusClass = {
    FOUND: 'ok',
    LINKED_NO_MATCH: 'warn',
    TERMS_LINK_REQUIRED: 'warn',
    SERVICE_UNAVAILABLE: 'muted',
  }[termsEvidence?.status] || 'muted'
  const termsEvidenceChunksHtml = (row) => {
    const documents = Array.isArray(row?.documents) ? row.documents : []
    const chunks = documents.flatMap((documentRow) => {
      const doc = documentRow?.document || {}
      const matchedChunks = Array.isArray(documentRow?.matchedChunks) ? documentRow.matchedChunks : []
      return matchedChunks.map((chunk) => ({ doc, chunk }))
    }).slice(0, 3)
    if (!chunks.length) return '<div class="terms-evidence-empty">약관 본문 근거는 아직 확인되지 않았습니다.</div>'
    return chunks.map(({ doc, chunk }) => {
      const pageLabel = chunk?.pageStart ? `p.${chunk.pageStart}${chunk.pageEnd && chunk.pageEnd !== chunk.pageStart ? `-${chunk.pageEnd}` : ''}` : '본문'
      return `<div class="terms-evidence-text"><span>${escapeReportHtml(termsDocumentTitle(doc))} · ${escapeReportHtml(pageLabel)}</span>${escapeReportHtml(chunk?.text || '-')}</div>`
    }).join('')
  }
  const termsEvidenceTableHtml = termsEvidenceContracts.length
    ? `<table><thead><tr><th>계약</th><th>확인상태</th><th>약관 본문 근거</th></tr></thead><tbody>${termsEvidenceContracts.map((row) => {
      const contract = row?.contract || {}
      const contractName = [contract.insurerName, contract.productName].filter(Boolean).join(' · ') || '보유계약'
      return `<tr><td>${escapeReportHtml(contractName)}</td><td>${escapeReportHtml(row.judgementLabel || termsEvidence?.statusLabel || '-')}</td><td>${termsEvidenceChunksHtml(row)}</td></tr>`
    }).join('')}</tbody></table>`
    : ''
  const imagePayload = JSON.stringify({
    customerName: customer.customerName || '고객',
    title,
    diagnosisName: simulation?.diagnosisName || title,
    summary: simulation?.summary || '',
    statBasis: simulation?.statBasis || '',
    sourceName: simulation?.sourceName || '',
    expectedMedicalCost,
    healthBenefitEstimate,
    estimatedOutOfPocket,
    recoveryLivingCost,
    expectedNeed,
    estimatedInsuranceBenefit,
    estimatedShortfall,
    estimatedSurplus,
    messages: messages.slice(0, 3).map((message) => String(message || '')),
    actions: actions.slice(0, 2).map((action) => String(action || '')),
    coverages: coverages.slice(0, 3).map((row) => ({
      insurerName: row.insurerName || '',
      productName: row.productName || '',
      coverageName: row.coverageName || '',
      insuredAmount: numberValue(row.insuredAmount),
    })),
  })
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
  const autoPrintScript = autoPrint ? `
  <script>
    window.addEventListener('load', function () {
      window.setTimeout(function () { window.print(); }, 250);
    });
  </script>` : ''
  const initialPreviewMode = autoPrint ? 'advisor' : 'customer'
  const imageDownloadScript = `
  <script>
    (function () {
      var payload = ${imagePayload};
      var W = 1080;
      var H = 1350;
      var currentMode = '${initialPreviewMode}';

      function esc(value) {
        var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return String(value == null ? '' : value).replace(/[&<>"']/g, function (m) { return map[m]; });
      }

      function money(value) {
        var n = Number(value || 0);
        if (!Number.isFinite(n)) n = 0;
        if (Math.abs(n) >= 10000) return Math.round(n / 10000).toLocaleString('ko-KR') + '만원';
        return Math.round(n).toLocaleString('ko-KR') + '원';
      }

      function pct(part, total) {
        if (!total || total <= 0) return 0;
        return Math.max(0, Math.min(1, Number(part || 0) / total));
      }

      function splitLongWord(word, max) {
        var out = [];
        var text = String(word || '');
        while (text.length > max) {
          out.push(text.slice(0, max));
          text = text.slice(max);
        }
        if (text) out.push(text);
        return out;
      }

      function wrap(value, max, maxLines) {
        var rawWords = String(value || '').replace(/\\s+/g, ' ').trim().split(' ').filter(Boolean);
        var words = [];
        rawWords.forEach(function (word) {
          if (word.length > max) words = words.concat(splitLongWord(word, max));
          else words.push(word);
        });
        var lines = [];
        var line = '';
        words.forEach(function (word) {
          var next = line ? line + ' ' + word : word;
          if (next.length > max && line) {
            lines.push(line);
            line = word;
          } else {
            line = next;
          }
        });
        if (line) lines.push(line);
        if (!lines.length) lines.push('');
        if (maxLines && lines.length > maxLines) {
          lines = lines.slice(0, maxLines);
          lines[lines.length - 1] = lines[lines.length - 1].replace(/…$/, '') + '…';
        }
        return lines;
      }

      function textLine(value, x, y, size, color, weight, anchor) {
        return '<text x="' + x + '" y="' + y + '" fill="' + color + '" font-family="Noto Sans KR, Arial, sans-serif" font-size="' + size + '" font-weight="' + (weight || 500) + '" text-anchor="' + (anchor || 'start') + '">' + esc(value) + '</text>';
      }

      function textBlock(lines, x, y, size, color, weight, lineHeight) {
        return lines.map(function (line, index) {
          return textLine(line, x, y + (index * lineHeight), size, color, weight);
        }).join('');
      }

      function card(x, y, w, h, fill, stroke, rx) {
        return '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + (rx || 18) + '" fill="' + fill + '" stroke="' + (stroke || '#e2e8f0') + '"/>';
      }

      function pill(value, x, y, w, fill, color) {
        return '<rect x="' + x + '" y="' + (y - 25) + '" width="' + w + '" height="36" rx="18" fill="' + fill + '"/>' +
          textLine(value, x + (w / 2), y, 17, color, 900, 'middle');
      }

      function metricBox(x, y, w, h, label, value, sub, color, bg, stroke) {
        var out = card(x, y, w, h, bg || '#ffffff', stroke || '#dbe3ef', 18);
        out += textLine(label, x + 24, y + 42, 19, '#64748b', 800);
        out += textLine(value, x + 24, y + 88, 33, color || '#0f172a', 900);
        if (sub) out += textLine(sub, x + 24, y + 120, 16, '#64748b', 700);
        return out;
      }

      function hbar(x, y, w, h, ratio, color, trackColor) {
        var r = Math.max(0, Math.min(1, Number(ratio || 0)));
        var out = '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + (h / 2) + '" fill="' + (trackColor || '#e2e8f0') + '"/>';
        if (r > 0) out += '<rect x="' + x + '" y="' + y + '" width="' + Math.max(h, w * r) + '" height="' + h + '" rx="' + (h / 2) + '" fill="' + color + '"/>';
        return out;
      }

      function bar(x, y, w, h, segments) {
        var out = '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + (h / 2) + '" fill="#e2e8f0"/>';
        var offset = 0;
        segments.forEach(function (seg) {
          var width = Math.max(0, Math.min(w - offset, w * Number(seg.ratio || 0)));
          if (width <= 0) return;
          out += '<rect x="' + (x + offset) + '" y="' + y + '" width="' + width + '" height="' + h + '" fill="' + seg.color + '"/>';
          offset += width;
        });
        return out;
      }

      function ledgerRow(label, value, y, color) {
        return textLine(label, 612, y, 25, '#475569', 700) + textLine(value, 940, y, 29, color || '#0f172a', 900, 'end');
      }

      function renderCustomerSvg() {
        var expectedNeed = Number(payload.expectedNeed || 0);
        var benefit = Number(payload.estimatedInsuranceBenefit || 0);
        var shortfall = Number(payload.estimatedShortfall || 0);
        var surplus = Number(payload.estimatedSurplus || 0);
        var expectedMedicalCost = Number(payload.expectedMedicalCost || 0);
        var outOfPocket = Number(payload.estimatedOutOfPocket || 0);
        var livingCost = Number(payload.recoveryLivingCost || 0);
        var isShortfall = shortfall > 0;
        var resultColor = isShortfall ? '#dc2626' : '#047857';
        var resultBg = isShortfall ? '#fff1f2' : '#f0fdf4';
        var resultStroke = isShortfall ? '#fecaca' : '#bbf7d0';
        var diagnosisName = payload.diagnosisName || '진단';
        var scenarioEvent = diagnosisName.indexOf('진단') >= 0 ? diagnosisName + '을 받으면' : diagnosisName + ' 상황이 발생하면';
        var coverages = (payload.coverages || []).filter(Boolean);
        var preparedPct = pct(benefit, expectedNeed);
        var svg = [];

        svg.push('<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + ' ' + H + '">');
        svg.push('<defs>');
        svg.push('<linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#eef6ff"/><stop offset="1" stop-color="#f8fafc"/></linearGradient>');
        svg.push('<linearGradient id="hero" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#dc2626"/><stop offset=".6" stop-color="#ef4444"/><stop offset="1" stop-color="#fb5a5f"/></linearGradient>');
        svg.push('<filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="14" stdDeviation="18" flood-color="#0f172a" flood-opacity=".12"/></filter>');
        svg.push('</defs>');
        svg.push('<rect width="' + W + '" height="' + H + '" fill="url(#bg)"/>');
        svg.push('<rect x="54" y="48" width="972" height="1254" rx="30" fill="#ffffff" stroke="#dbeafe" filter="url(#softShadow)"/>');

        svg.push('<rect x="54" y="48" width="972" height="310" rx="30" fill="url(#hero)"/>');
        svg.push('<circle cx="540" cy="124" r="38" fill="#fef3c7"/>');
        svg.push(textLine('진', 540, 138, 31, '#b45309', 900, 'middle'));
        svg.push(textLine((payload.customerName || '고객') + '님이 ' + scenarioEvent, 540, 206, 28, '#fee2e2', 900, 'middle'));
        svg.push(textLine(money(expectedNeed), 540, 284, 76, '#ffffff', 900, 'middle'));
        svg.push(textLine('필요 자금이 예상됩니다', 540, 330, 27, '#fff7ed', 900, 'middle'));

        var impactMax = Math.max(expectedMedicalCost, outOfPocket, livingCost, expectedNeed, 1);
        svg.push(card(100, 392, 880, 390, '#ffffff', '#dbe3ef', 22));
        svg.push(textLine('예상 재정 현황', 136, 448, 34, '#0f172a', 900));
        svg.push(textLine('총 진료비', 136, 494, 23, '#334155', 900));
        svg.push(textLine(money(expectedMedicalCost), 936, 494, 28, '#0f172a', 900, 'end'));
        svg.push(hbar(136, 512, 800, 24, pct(expectedMedicalCost, impactMax), '#94a3b8', '#f1f5f9'));
        svg.push(textLine('본인부담·비급여 예상', 136, 566, 23, '#334155', 900));
        svg.push(textLine(money(outOfPocket), 936, 566, 28, '#ef4444', 900, 'end'));
        svg.push(hbar(136, 584, 800, 24, pct(outOfPocket, impactMax), '#ef4444', '#f1f5f9'));
        svg.push(textLine('회복·생활비 필요액', 136, 638, 23, '#334155', 900));
        svg.push(textLine(money(livingCost), 936, 638, 28, '#f59e0b', 900, 'end'));
        svg.push(hbar(136, 656, 800, 24, pct(livingCost, impactMax), '#f59e0b', '#f1f5f9'));
        svg.push(textLine('총 필요 자금', 136, 710, 24, '#0f172a', 900));
        svg.push(textLine(money(expectedNeed), 936, 710, 31, '#7c3aed', 900, 'end'));
        svg.push(hbar(136, 728, 800, 24, pct(expectedNeed, impactMax), '#7c3aed', '#f1f5f9'));

        svg.push(card(100, 812, 880, 250, '#ffffff', '#dbe3ef', 22));
        svg.push(textLine('진단비 담보', 136, 862, 29, '#0f172a', 900));
        svg.push(textLine('진단비 예상액', 136, 914, 22, '#475569', 800));
        svg.push(textLine(money(benefit), 936, 914, 30, '#2563eb', 900, 'end'));
        svg.push('<line x1="136" y1="944" x2="936" y2="944" stroke="#e2e8f0"/>');
        if (coverages.length) {
          coverages.slice(0, 3).forEach(function (coverage, index) {
            var y = 980 + (index * 34);
            svg.push(textBlock(wrap(coverage.coverageName || '진단비 담보', 42, 1), 136, y, 18, '#334155', 800, 20));
            svg.push(textLine(money(coverage.insuredAmount || 0), 936, y, 20, '#0f172a', 900, 'end'));
          });
        } else {
          svg.push(textBlock(wrap('진단비 담보가 확인되지 않았습니다.', 42, 2), 136, 980, 20, '#475569', 800, 24));
        }

        svg.push(card(100, 1092, 880, 170, resultBg, resultStroke, 22));
        svg.push(textLine(isShortfall ? '보험금 반영 후 공백' : '보험금 반영 후 여유', 540, 1138, 30, resultColor, 900, 'middle'));
        svg.push(textLine(money(isShortfall ? shortfall : surplus), 540, 1190, 54, resultColor, 900, 'middle'));
        svg.push(textLine(isShortfall ? '현재 진단비 예상액으로 채워지지 않는 예상 부족액입니다.' : '현재 진단비 예상액이 필요 자금을 초과합니다.', 540, 1228, 21, '#334155', 800, 'middle'));
        svg.push(hbar(136, 1244, 800, 16, preparedPct, '#2563eb', '#e2e8f0'));

        svg.push('<line x1="100" y1="1270" x2="980" y2="1270" stroke="#e2e8f0"/>');
        svg.push(textBlock(wrap('출처: ' + (payload.sourceName || '공개 통계') + ' · 실제 보험금은 진단명, 약관, 심사 결과를 추가 확인해야 합니다.', 84, 1), 100, 1294, 17, '#64748b', 700, 22));
        svg.push('</svg>');
        return svg.join('');
      }

      function renderAdvisorSvg() {
        var expectedNeed = Number(payload.expectedNeed || 0);
        var benefit = Number(payload.estimatedInsuranceBenefit || 0);
        var shortfall = Number(payload.estimatedShortfall || 0);
        var surplus = Number(payload.estimatedSurplus || 0);
        var expectedMedicalCost = Number(payload.expectedMedicalCost || 0);
        var healthBenefit = Number(payload.healthBenefitEstimate || 0);
        var outOfPocket = Number(payload.estimatedOutOfPocket || 0);
        var livingCost = Number(payload.recoveryLivingCost || 0);
        var isShortfall = shortfall > 0;
        var resultColor = isShortfall ? '#dc2626' : '#047857';
        var resultBg = isShortfall ? '#fff1f2' : '#f0fdf4';
        var resultStroke = isShortfall ? '#fecaca' : '#bbf7d0';
        var diagnosisName = payload.diagnosisName || '진단';
        var title = (payload.customerName || '고객') + '님 ' + diagnosisName + ' 시나리오';
        var messages = (payload.messages || []).filter(Boolean);
        if (!messages.length && payload.summary) messages = [payload.summary];
        var actions = (payload.actions || []).filter(Boolean);
        var coverages = (payload.coverages || []).filter(Boolean);
        var preparedPct = pct(benefit, expectedNeed);
        var healthPct = pct(healthBenefit, expectedMedicalCost);
        var pocketPct = pct(outOfPocket, expectedMedicalCost);
        var topMessage = messages[0] || payload.summary || ((payload.diagnosisName || '해당 진단') + ' 발생 시 필요한 자금과 현재 보장을 비교했습니다.');
        var actionText = actions.length ? actions.join(' ') : '진단비, 수술비, 입원일당 등 실제 약관상 지급 조건을 함께 확인해보면 좋습니다.';
        var svg = [];

        svg.push('<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + ' ' + H + '">');
        svg.push('<defs>');
        svg.push('<linearGradient id="advisorBg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#eef6ff"/><stop offset="1" stop-color="#f8fafc"/></linearGradient>');
        svg.push('<linearGradient id="advisorHero" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0b1220"/><stop offset=".55" stop-color="#0f2a4d"/><stop offset="1" stop-color="#17446f"/></linearGradient>');
        svg.push('<filter id="advisorShadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="14" stdDeviation="18" flood-color="#0f172a" flood-opacity=".12"/></filter>');
        svg.push('</defs>');
        svg.push('<rect width="' + W + '" height="' + H + '" fill="url(#advisorBg)"/>');
        svg.push('<rect x="54" y="48" width="972" height="1254" rx="30" fill="#ffffff" stroke="#dbeafe" filter="url(#advisorShadow)"/>');

        svg.push('<rect x="54" y="48" width="972" height="270" rx="30" fill="url(#advisorHero)"/>');
        svg.push('<rect x="54" y="276" width="972" height="42" fill="#17446f"/>');
        svg.push(pill('설계사용 진단 리포트', 100, 112, 210, '#dbeafe', '#1e3a8a'));
        svg.push(textBlock(wrap(title, 25, 2), 100, 176, 42, '#ffffff', 900, 50));
        svg.push(textBlock(wrap('계산 반영 담보, 비용 가정, 다음 확인 포인트를 상담 검토용으로 정리했습니다.', 43, 2), 100, 266, 23, '#dbeafe', 700, 29));

        svg.push(card(100, 360, 880, 150, resultBg, resultStroke, 22));
        svg.push(textLine(isShortfall ? '부족 예상 금액' : '보장 여유 금액', 142, 420, 30, resultColor, 900));
        svg.push(textLine(money(isShortfall ? shortfall : surplus), 936, 422, 54, resultColor, 900, 'end'));
        svg.push(textLine(isShortfall ? '현재 보유 담보로 채워지지 않는 예상 공백입니다.' : '현재 보유 담보가 예상 필요자금보다 큽니다.', 142, 480, 22, '#334155', 800));

        svg.push(metricBox(100, 536, 272, 126, '예상 필요자금', money(expectedNeed), '진료비와 회복 기간 비용', '#0f172a', '#ffffff'));
        svg.push(metricBox(404, 536, 272, 126, '진단비 예상액', money(benefit), '계산 반영 담보', '#2563eb', '#ffffff'));
        svg.push(metricBox(708, 536, 272, 126, isShortfall ? '부족 예상액' : '보장 여유액', money(isShortfall ? shortfall : surplus), isShortfall ? '추가 확인 필요' : '현재 기준 여유', resultColor, resultBg, resultStroke));

        svg.push(card(100, 700, 880, 164, '#f8fafc', '#dbe3ef', 22));
        svg.push(textLine('보장 대응력', 136, 752, 28, '#0f172a', 900));
        svg.push(textLine(Math.round(preparedPct * 100) + '% 준비', 936, 752, 27, preparedPct >= 1 ? '#047857' : '#dc2626', 900, 'end'));
        svg.push(hbar(136, 780, 800, 26, preparedPct, '#2563eb', '#e2e8f0'));
        svg.push(textLine('필요자금', 136, 838, 21, '#475569', 800));
        svg.push(textLine(money(expectedNeed), 282, 838, 24, '#0f172a', 900));
        svg.push(textLine('진단비', 440, 838, 21, '#475569', 800));
        svg.push(textLine(money(benefit), 586, 838, 24, '#2563eb', 900));
        svg.push(textLine(isShortfall ? '부족' : '여유', 744, 838, 21, '#475569', 800));
        svg.push(textLine(money(isShortfall ? shortfall : surplus), 936, 838, 24, resultColor, 900, 'end'));

        svg.push(card(100, 900, 424, 166, '#ffffff', '#dbe3ef', 22));
        svg.push(textLine('예상 비용 구조', 132, 944, 26, '#0f172a', 900));
        svg.push(bar(132, 970, 360, 20, [
          { ratio: healthPct, color: '#10b981' },
          { ratio: pocketPct, color: '#fb7185' },
        ]));
        svg.push(textLine('총진료비', 132, 1016, 19, '#475569', 800));
        svg.push(textLine(money(expectedMedicalCost), 492, 1016, 22, '#0f172a', 900, 'end'));
        svg.push(textLine('개인 부담/비급여', 132, 1050, 19, '#475569', 800));
        svg.push(textLine(money(outOfPocket), 492, 1050, 22, '#dc2626', 900, 'end'));

        svg.push(card(556, 900, 424, 166, '#ffffff', '#dbe3ef', 22));
        svg.push(textLine('핵심 안내', 588, 944, 26, '#0f172a', 900));
        svg.push(textBlock(wrap(topMessage, 27, 2), 588, 984, 20, '#334155', 800, 27));
        svg.push(textLine('회복·생활비 필요액', 588, 1044, 19, '#475569', 800));
        svg.push(textLine(money(livingCost), 940, 1044, 22, '#d97706', 900, 'end'));

        svg.push(card(100, 1090, 424, 142, '#f8fafc', '#dbe3ef', 18));
        svg.push(textLine('확인 포인트', 128, 1128, 20, '#0f172a', 900));
        svg.push(textBlock(wrap(actionText, 35, 4), 128, 1154, 16, '#475569', 700, 20));

        svg.push(card(556, 1090, 424, 142, '#f8fafc', '#dbe3ef', 18));
        svg.push(textLine('진단비 담보', 584, 1128, 20, '#0f172a', 900));
        if (coverages.length) {
          var coverage = coverages[0] || {};
          svg.push(textBlock(wrap((coverage.coverageName || '진단비 담보') + ' ' + money(coverage.insuredAmount || 0), 28, 3), 584, 1154, 18, '#334155', 900, 22));
        } else {
          svg.push(textBlock(wrap('진단비 담보 미확인 · 보장 공백 가능성 점검', 28, 3), 584, 1154, 18, '#475569', 800, 22));
        }

        svg.push('<line x1="100" y1="1260" x2="980" y2="1260" stroke="#e2e8f0"/>');
        svg.push(textLine('출처: ' + (payload.sourceName || '공개 통계') + ' · 실제 지급 여부는 약관, 심사 결과에 따라 달라질 수 있습니다.', 100, 1290, 17, '#64748b', 700));
        svg.push('</svg>');
        return svg.join('');
      }

      function svgForMode(mode) {
        return mode === 'advisor' ? renderAdvisorSvg() : renderCustomerSvg();
      }

      function downloadImage(mode) {
        var svg = svgForMode(mode || currentMode);
        var blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var img = new Image();
        img.onload = function () {
          var canvas = document.createElement('canvas');
          canvas.width = W;
          canvas.height = H;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, W, H);
          ctx.drawImage(img, 0, 0, W, H);
          URL.revokeObjectURL(url);
          canvas.toBlob(function (pngBlob) {
            if (!pngBlob) return;
            var a = document.createElement('a');
            var suffix = (mode || currentMode) === 'advisor' ? '설계사용_리포트' : '고객용_요약';
            var filename = ((payload.customerName || '고객') + '_' + (payload.diagnosisName || '진단시뮬레이션') + '_' + suffix + '.png').replace(/[\\\\/:*?"<>|]/g, '_');
            a.href = URL.createObjectURL(pngBlob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.setTimeout(function () {
              URL.revokeObjectURL(a.href);
              a.remove();
            }, 500);
          }, 'image/png');
        };
        img.onerror = function () {
          URL.revokeObjectURL(url);
          alert('이미지 생성 중 오류가 발생했습니다.');
        };
        img.src = url;
      }

      function mountCustomerPreview() {
        var target = document.getElementById('diagnosisCustomerSvgPreview');
        if (target) target.innerHTML = renderCustomerSvg();
      }

      function setMode(mode) {
        currentMode = mode === 'advisor' ? 'advisor' : 'customer';
        document.body.setAttribute('data-preview-mode', currentMode);
        var customerPanel = document.getElementById('diagnosisCustomerPanel');
        var advisorPanel = document.getElementById('diagnosisAdvisorPanel');
        if (customerPanel) customerPanel.hidden = currentMode !== 'customer';
        if (advisorPanel) advisorPanel.hidden = currentMode !== 'advisor';
        ['customer', 'advisor'].forEach(function (key) {
          var tab = document.getElementById('diagnosisTab' + (key === 'customer' ? 'Customer' : 'Advisor'));
          if (!tab) return;
          var active = key === currentMode;
          tab.classList.toggle('active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        var description = document.getElementById('diagnosisToolbarDescription');
        if (description) description.textContent = currentMode === 'customer'
          ? '고객에게 바로 보여줄 수 있는 한눈 요약 이미지입니다.'
          : '설계사가 검토할 계산 근거와 담보 리포트입니다.';
      }

      mountCustomerPreview();
      setMode(currentMode);
      var customerButton = document.getElementById('diagnosisCustomerImageDownloadBtn');
      var advisorButton = document.getElementById('diagnosisAdvisorImageDownloadBtn');
      var customerTab = document.getElementById('diagnosisTabCustomer');
      var advisorTab = document.getElementById('diagnosisTabAdvisor');
      var printButton = document.getElementById('diagnosisPrintBtn');
      if (customerButton) customerButton.addEventListener('click', function () { downloadImage('customer'); });
      if (advisorButton) advisorButton.addEventListener('click', function () { downloadImage('advisor'); });
      if (customerTab) customerTab.addEventListener('click', function () { setMode('customer'); });
      if (advisorTab) advisorTab.addEventListener('click', function () { setMode('advisor'); });
      if (printButton) printButton.addEventListener('click', function () { setMode('advisor'); window.print(); });
    })();
  </script>`
  const toolbarHtml = showToolbar ? `
  <div class="toolbar">
    <div><strong>${escapeReportHtml(title)}</strong><span id="diagnosisToolbarDescription">고객에게 바로 보여줄 수 있는 한눈 요약 이미지입니다.</span></div>
    <div class="toolbar-tabs" role="tablist" aria-label="진단시뮬레이션 미리보기 유형">
      <button type="button" id="diagnosisTabCustomer" role="tab" aria-controls="diagnosisCustomerPanel">고객용</button>
      <button type="button" id="diagnosisTabAdvisor" role="tab" aria-controls="diagnosisAdvisorPanel">설계사용</button>
    </div>
    <div class="toolbar-actions">
      <button type="button" id="diagnosisCustomerImageDownloadBtn">고객용 PNG</button>
      <button type="button" id="diagnosisAdvisorImageDownloadBtn">설계사용 PNG</button>
      <button type="button" id="diagnosisPrintBtn">인쇄/PDF</button>
      <button type="button" class="secondary" onclick="window.close()">닫기</button>
    </div>
  </div>` : ''
  return `<!doctype html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <title>${escapeReportHtml(title)}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #eef2f7; color: #172033; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans KR", sans-serif; line-height: 1.55; }
    .toolbar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid #dbe3ef; background: rgba(255,255,255,.96); padding: 12px 18px; }
    .toolbar strong { display: block; color: #0f172a; font-size: 14px; }
    .toolbar span { display: block; margin-top: 2px; color: #64748b; font-size: 11px; }
    .toolbar-tabs { display: inline-flex; border: 1px solid #dbe3ef; border-radius: 8px; background: #f8fafc; padding: 3px; }
    .toolbar-tabs button { border: 0; border-radius: 6px; background: transparent; color: #475569; padding: 7px 12px; }
    .toolbar-tabs button.active { background: #0f2a4d; color: #fff; box-shadow: 0 4px 12px rgba(15, 42, 77, .18); }
    .toolbar-actions { display: flex; gap: 8px; }
    .toolbar button { border: 1px solid #0f2a4d; background: #0f2a4d; color: #fff; cursor: pointer; font-weight: 700; font-size: 12px; padding: 7px 12px; }
    .toolbar button.secondary { border-color: #cbd5e1; background: #fff; color: #334155; }
    .toolbar .toolbar-tabs button { border: 0; background: transparent; color: #475569; padding: 7px 12px; }
    .toolbar .toolbar-tabs button.active { background: #0f2a4d; color: #fff; }
    .preview-panel[hidden] { display: none; }
    .customer-preview-stage { width: min(100%, 920px); margin: 18px auto 36px; padding: 18px; border: 1px solid #dbe3ef; background: #fff; box-shadow: 0 16px 48px rgba(15, 23, 42, .12); }
    .customer-preview-stage svg { display: block; width: 100%; max-width: 720px; height: auto; margin: 0 auto; border-radius: 18px; box-shadow: 0 10px 26px rgba(15,23,42,.12); }
    .sheet { width: min(100%, 920px); margin: 18px auto 36px; background: #fff; box-shadow: 0 16px 48px rgba(15, 23, 42, .12); }
    .cover { padding: 34px; background: linear-gradient(135deg, #0f2a4d 0%, #15406f 62%, #e8f4fb 62%, #f8fbff 100%); color: #fff; }
    .cover .eyebrow { color: #bfdbfe; font-size: 12px; font-weight: 800; }
    .cover h1 { margin: 6px 0 0; max-width: 620px; font-size: 30px; line-height: 1.25; }
    .cover p { margin: 12px 0 0; max-width: 620px; color: #dbeafe; font-size: 13px; }
    .content { padding: 28px 34px 34px; }
    .scenario-visual { overflow: hidden; border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; box-shadow: 0 12px 30px rgba(15,23,42,.08); break-inside: avoid; }
    .scenario-hero { display: grid; place-items: center; gap: 8px; padding: 30px 24px 32px; text-align: center; color: #fff; background: linear-gradient(135deg, #dc2626 0%, #ef4444 58%, #fb5a5f 100%); }
    .scenario-mark { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 999px; background: #fef3c7; color: #b45309; font-size: 20px; font-weight: 900; box-shadow: inset 0 -4px 0 rgba(180, 83, 9, .14); }
    .scenario-hero-kicker { color: #fee2e2; font-size: 15px; font-weight: 800; }
    .scenario-hero strong { display: block; font-size: 46px; line-height: 1; font-weight: 900; letter-spacing: 0; }
    .scenario-hero span { color: #fff7ed; font-size: 14px; font-weight: 800; }
    .scenario-visual-body { display: grid; gap: 14px; padding: 18px; }
    .impact-card { border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; padding: 17px 18px; }
    .impact-card h2 { margin: 0 0 16px; color: #0f172a; font-size: 19px; }
    .impact-row { margin-top: 14px; }
    .impact-row:first-of-type { margin-top: 0; }
    .impact-row-head { display: flex; justify-content: space-between; gap: 14px; align-items: baseline; margin-bottom: 8px; color: #475569; font-size: 12px; }
    .impact-row-head b { color: #0f172a; font-size: 13px; }
    .impact-row-head strong { color: #0f172a; font-size: 15px; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .impact-track { height: 25px; overflow: hidden; border-radius: 999px; background: #f1f5f9; }
    .impact-fill { display: block; height: 100%; min-width: 0; border-radius: 999px; }
    .impact-fill.red { background: linear-gradient(90deg, #ef4444, #fb7185); }
    .impact-fill.orange { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
    .impact-fill.blue { background: linear-gradient(90deg, #2563eb, #60a5fa); }
    .impact-fill.purple { background: linear-gradient(90deg, #7c3aed, #a855f7); }
    .impact-fill.slate { background: #cbd5e1; }
    .impact-split { display: flex; height: 21px; margin-top: 12px; overflow: hidden; border-radius: 999px; background: #e2e8f0; }
    .impact-split i { display: block; height: 100%; min-width: 2px; }
    .impact-legend { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; color: #64748b; font-size: 10.5px; }
    .impact-legend span::before { content: ""; display: inline-block; width: 7px; height: 7px; border-radius: 999px; margin-right: 4px; vertical-align: 1px; background: var(--dot); }
    .impact-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(260px, .72fr); gap: 14px; align-items: stretch; }
    .insurance-impact { display: grid; align-content: center; gap: 10px; border-color: #fecaca; background: #fff1f2; text-align: center; }
    .insurance-impact.surplus { border-color: #bbf7d0; background: #f0fdf4; }
    .insurance-impact span { color: #991b1b; font-size: 13px; font-weight: 900; }
    .insurance-impact.surplus span { color: #047857; }
    .insurance-impact strong { display: block; color: #dc2626; font-size: 32px; line-height: 1.05; font-weight: 900; font-variant-numeric: tabular-nums; }
    .insurance-impact.surplus strong { color: #047857; }
    .insurance-impact p { color: #475569; font-size: 12px; font-weight: 700; }
    .coverage-stack { display: flex; height: 18px; overflow: hidden; border-radius: 999px; background: #e2e8f0; }
    .coverage-stack i { display: block; height: 100%; min-width: 2px; }
    .income-impact { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .income-impact div { border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; padding: 12px; text-align: center; }
    .income-impact span { display: block; color: #64748b; font-size: 11px; }
    .income-impact strong { display: block; margin-top: 5px; color: #0f172a; font-size: 18px; font-variant-numeric: tabular-nums; }
    .cost-ledger { display: grid; gap: 8px; }
    .cost-line { display: flex; justify-content: space-between; gap: 14px; align-items: baseline; color: #334155; font-size: 12px; }
    .cost-line strong { color: #0f172a; font-size: 14px; font-variant-numeric: tabular-nums; }
    .cost-line.plus strong { color: #2563eb; }
    .cost-line.minus strong { color: #dc2626; }
    .cost-line.green strong { color: #059669; }
    .profile-context-box { margin-top: 10px; border: 1px solid #dbe3ef; background: #f8fafc; padding: 11px; break-inside: avoid; }
    .profile-facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 7px; }
    .profile-facts div { border: 1px solid #e2e8f0; background: #fff; padding: 7px 8px; }
    .profile-facts span { display: block; color: #64748b; font-size: 10px; }
    .profile-facts strong { display: block; margin-top: 2px; color: #0f172a; font-size: 12px; }
    .profile-context-box ul { margin-top: 8px; }
    .profile-context-box li { color: #475569; font-size: 11px; }
    .section { margin-top: 16px; border: 1px solid #dbe3ef; padding: 14px; break-inside: avoid; }
    .section h2 { margin: 0 0 8px; color: #0f172a; font-size: 15px; }
    .section p, li { color: #334155; font-size: 12px; }
    .section-note { margin: 0 0 8px; color: #64748b; font-size: 11px; }
    .assumption-note { margin-top: 3px; color: #64748b; font-size: 10.5px; }
    .assumption-source-note { margin: 8px 0 0; }
    .terms-status { display: inline-flex; align-items: center; min-height: 22px; margin-bottom: 8px; border: 1px solid #cbd5e1; background: #f8fafc; color: #475569; padding: 2px 8px; font-size: 11px; font-weight: 800; }
    .terms-status.ok { border-color: #bbf7d0; background: #f0fdf4; color: #047857; }
    .terms-status.warn { border-color: #fde68a; background: #fffbeb; color: #92400e; }
    .terms-evidence-text { margin: 4px 0; color: #334155; font-size: 10.5px; line-height: 1.45; }
    .terms-evidence-text span { display: block; margin-bottom: 2px; color: #64748b; font-weight: 800; }
    .terms-evidence-empty { color: #64748b; font-size: 10.5px; }
    ul { margin: 0; padding-left: 18px; }
    li { margin: 3px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
    th { background: #f1f5f9; color: #334155; border: 1px solid #dbe3ef; padding: 6px; text-align: left; }
    td { border: 1px solid #e2e8f0; padding: 6px; color: #334155; }
    .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .disclaimer { margin-top: 18px; color: #64748b; font-size: 10.5px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
    @media (max-width: 760px) { .toolbar { align-items: stretch; flex-direction: column; } .toolbar-actions, .toolbar-tabs { flex-wrap: wrap; } .impact-grid, .income-impact { grid-template-columns: 1fr; } .scenario-hero strong { font-size: 36px; } }
    @media print { body { background: #fff; } .toolbar, #diagnosisCustomerPanel { display: none !important; } #diagnosisAdvisorPanel { display: block !important; } .sheet { width: auto; margin: 0; box-shadow: none; } .cover, .scenario-hero, .impact-fill, .impact-split i, .coverage-stack i, .insurance-impact { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body data-preview-mode="${escapeReportHtml(initialPreviewMode)}">
${toolbarHtml}
<section id="diagnosisCustomerPanel" class="preview-panel" aria-labelledby="diagnosisTabCustomer"${initialPreviewMode === 'advisor' ? ' hidden' : ''}>
  <div class="customer-preview-stage">
    <div id="diagnosisCustomerSvgPreview"></div>
  </div>
</section>
<section id="diagnosisAdvisorPanel" class="preview-panel" aria-labelledby="diagnosisTabAdvisor"${initialPreviewMode === 'customer' ? ' hidden' : ''}>
<main class="sheet">
  <section class="cover">
    <div class="eyebrow">설계EZ 진단 시뮬레이션</div>
    <h1>${escapeReportHtml(title)}</h1>
    <p>${escapeReportHtml(customer.customerName || '고객')}님의 보유계약 담보를 기준으로 ${escapeReportHtml(simulation?.diagnosisName || '선택 진단')} 발생 시 예상 필요자금과 보장 대응력을 점검합니다.</p>
  </section>
  <section class="content">
    <div class="scenario-visual">
      <div class="scenario-hero">
        <div class="scenario-mark">진</div>
        <div class="scenario-hero-kicker">${escapeReportHtml(customerName)}님이 ${escapeReportHtml(scenarioEventLabel)}</div>
        <strong>${escapeReportHtml(formatManWon(expectedNeed))}</strong>
        <span>필요 자금이 예상됩니다</span>
      </div>
      <div class="scenario-visual-body">
        <div class="impact-grid">
          <div class="impact-card">
            <h2>예상 재정 영향</h2>
            <div class="impact-row">
              <div class="impact-row-head"><b>본인부담·비급여 예상</b><strong>${escapeReportHtml(formatManWon(estimatedOutOfPocket))}</strong></div>
              <div class="impact-track"><i class="impact-fill red" style="width:${impactWidth(estimatedOutOfPocket)}%"></i></div>
            </div>
            <div class="impact-row">
              <div class="impact-row-head"><b>회복·생활비 필요액</b><strong>${escapeReportHtml(formatManWon(recoveryLivingCost))}</strong></div>
              <div class="impact-track"><i class="impact-fill orange" style="width:${impactWidth(recoveryLivingCost)}%"></i></div>
            </div>
            <div class="impact-row">
              <div class="impact-row-head"><b>총 필요 자금</b><strong>${escapeReportHtml(formatManWon(expectedNeed))}</strong></div>
              <div class="impact-track"><i class="impact-fill purple" style="width:${impactWidth(expectedNeed)}%"></i></div>
            </div>
            <div class="impact-split">
              <i class="impact-fill red" style="width:${needPocketPct}%"></i>
              <i class="impact-fill orange" style="width:${needLivingPct}%"></i>
            </div>
            <div class="impact-legend">
              <span style="--dot:#ef4444">본인부담·비급여</span>
              <span style="--dot:#f59e0b">회복·생활비</span>
              <span style="--dot:#7c3aed">총 필요 자금</span>
            </div>
          </div>
          <div class="impact-card insurance-impact ${estimatedShortfall > 0 ? '' : 'surplus'}">
            <span>${estimatedShortfall > 0 ? '보험금 반영 후 공백' : '보험금 반영 후 여유'}</span>
            <strong>${escapeReportHtml(formatManWon(shortfallAmount))}</strong>
            <p>${estimatedShortfall > 0 ? '현재 진단비 예상액으로 채워지지 않는 예상 부족액입니다.' : '현재 진단비 예상액이 필요 자금을 초과합니다.'}</p>
            <div class="coverage-stack">
              <i class="impact-fill blue" style="width:${coverageBenefitPct}%"></i>
              <i class="impact-fill slate" style="width:${coverageGapPct}%"></i>
            </div>
            <div class="impact-legend">
              <span style="--dot:#2563eb">진단비 예상액 ${escapeReportHtml(formatManWon(estimatedInsuranceBenefit))}</span>
              <span style="--dot:#cbd5e1">${escapeReportHtml(shortfallLabel)} ${escapeReportHtml(formatManWon(shortfallAmount))}</span>
            </div>
          </div>
        </div>
        <div class="income-impact">
          <div><span>예상 총진료비</span><strong>${escapeReportHtml(formatManWon(expectedMedicalCost))}</strong></div>
          <div><span>월소득 기준</span><strong>${monthlyIncome > 0 ? escapeReportHtml(formatManWon(monthlyIncome)) : '미입력'}</strong></div>
          <div><span>회복 기간 가정</span><strong>${recoveryMonths > 0 ? `${recoveryMonths}개월` : '별도 가정'}</strong></div>
        </div>
        <div class="impact-card">
          <h2>세부 계산 흐름</h2>
          <div class="cost-ledger">
            <div class="cost-line"><span>총 진료비</span><strong>${escapeReportHtml(formatWon(expectedMedicalCost))}</strong></div>
            <div class="cost-line green"><span>건강보험/통계상 급여 가정</span><strong>-${escapeReportHtml(formatWon(healthBenefitEstimate))}</strong></div>
            <div class="cost-line minus"><span>개인 부담금</span><strong>-${escapeReportHtml(formatWon(estimatedOutOfPocket))}</strong></div>
            <div class="cost-line minus"><span>회복·생활비 필요액</span><strong>-${escapeReportHtml(formatWon(recoveryLivingCost))}</strong></div>
            <div class="cost-line"><span>필요 자금</span><strong>${escapeReportHtml(formatWon(expectedNeed))}</strong></div>
            <div class="cost-line plus"><span>진단비 예상보험금</span><strong>+${escapeReportHtml(formatWon(estimatedInsuranceBenefit))}</strong></div>
            <div class="cost-line ${estimatedShortfall > 0 ? 'minus' : 'green'}"><span>${escapeReportHtml(shortfallLabel)}</span><strong>${estimatedShortfall > 0 ? '-' : '+'}${escapeReportHtml(formatWon(shortfallAmount))}</strong></div>
          </div>
        </div>
      </div>
    </div>
    <div class="section"><h2>핵심 메시지</h2>${reportListHtml(messages)}</div>
    <div class="section">
      <h2>금액 산정 메모</h2>
      <table>
        <thead><tr><th>항목</th><th>금액</th><th>산정 메모</th></tr></thead>
        <tbody>
          ${assumptionRows.map((row) => `<tr><td>${escapeReportHtml(row.label || '-')}</td><td class="num">${escapeReportHtml(formatWon(row.amount))}</td><td>${escapeReportHtml(row.basis || '-')}<div class="assumption-note">${escapeReportHtml(row.note || '')}</div></td></tr>`).join('')}
        </tbody>
      </table>
      ${statBasisNotes.length ? `<p class="section-note assumption-source-note">${statBasisNotes.map((line) => escapeReportHtml(line)).join('<br/>')}</p>` : ''}
    </div>
    <div class="section">
      <h2>진단비 담보</h2>
      <p class="section-note">아래 담보만 진단비 예상보험금 계산에 반영했습니다.</p>
      ${coverageTableHtml(coverages, '진단비 담보가 확인되지 않았습니다.')}
    </div>
    ${referenceCoverages.length ? `<div class="section">
      <h2>암 관련 참고 담보</h2>
      <p class="section-note">수술·항암·방사선 등 참고용 담보입니다. 위 예상보험금에는 합산하지 않았습니다.</p>
      ${coverageTableHtml(referenceCoverages, '암 관련 참고 담보가 확인되지 않았습니다.')}
    </div>` : ''}
    ${termsEvidence ? `<div class="section">
      <h2>약관 근거 확인</h2>
      <span class="terms-status ${termsEvidenceStatusClass}">${escapeReportHtml(termsEvidence.statusLabel || '약관 근거 미확인')}</span>
      <p class="section-note">${escapeReportHtml(termsEvidence.summary || '계약에 연결된 약관 본문을 함께 확인합니다.')}</p>
      ${termsEvidenceTableHtml}
    </div>` : ''}
    <div class="section"><h2>다음 상담 액션</h2>${reportListHtml(actions)}</div>
    <p class="disclaimer">${escapeReportHtml(simulation?.disclaimer || '')}</p>
  </section>
</main>
</section>
${imageDownloadScript}
${autoPrintScript}
</body>
</html>`
}

function buildIntegratedReportPrintHtml(report, options = {}) {
  const { autoPrint = false, showToolbar = true } = options
  const selectedSections = reportSectionSet(options)
  const hasSection = (id) => selectedSections.has(id)
  const customer = report?.customer || {}
  const reportTitle = '설계EZ통합분석 리포트'
  const customerName = customer.customerName || '고객'
  const reportFileTitle = `${String(customerName).replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '') || '고객'}_설계EZ통합분석리포트`
  const toolbarHtml = showToolbar ? `
  <div class="toolbar">
    <div>
      <strong>${escapeReportHtml(reportTitle)}</strong>
      <span>고객 제공 전 내용을 확인한 뒤 인쇄 또는 PDF 저장을 진행하세요.</span>
    </div>
    <div class="toolbar-actions">
      <button type="button" class="secondary" id="reportPrevPage">이전</button>
      <span class="page-counter" id="reportPageCounter">1 / 1</span>
      <button type="button" class="secondary" id="reportNextPage">다음</button>
      <button type="button" onclick="window.print()">인쇄/PDF</button>
      <button type="button" class="secondary" onclick="window.close()">닫기</button>
    </div>
  </div>` : ''
  const autoPrintScript = autoPrint ? `
  <script>
    window.addEventListener('load', function () {
      window.setTimeout(function () { window.print(); }, 250);
    });
  </script>` : ''
  const previewScript = `
  <script>
    (function () {
      function titleElementFor(node) {
        return node.querySelector && (node.querySelector('.block-title span') || node.querySelector('h2') || node.querySelector('h3'));
      }
      function titleFor(node, fallback) {
        var h = titleElementFor(node);
        return h ? h.textContent.trim() : fallback;
      }
      function removeTitleExplanation(container) {
        if (!container || !container.querySelectorAll) return;
        Array.prototype.slice.call(container.querySelectorAll('h2, h3, p')).forEach(function (item) {
          item.remove();
        });
        if (!container.textContent.trim() && !container.querySelector('strong, .score-dial, .category-diagnosis-score')) {
          container.remove();
        }
      }
      function stripRepeatedTitle(node, pageTitle) {
        if (!node || !pageTitle) return node;
        var titleEl = titleElementFor(node);
        if (!titleEl || titleEl.textContent.trim() !== String(pageTitle).trim()) return node;
        var blockTitle = titleEl.closest && titleEl.closest('.block-title');
        if (blockTitle && node.contains(blockTitle)) {
          blockTitle.remove();
          return node;
        }
        var titleHead = titleEl.closest && titleEl.closest('.stat-card-head, .engine-head, .engine-continuation-head, .category-diagnosis-head, .section, .portfolio-panel');
        if (titleHead && node.contains(titleHead)) {
          var textBox = titleEl.parentElement;
          if (textBox && textBox !== titleHead) {
            removeTitleExplanation(textBox);
          } else {
            titleEl.remove();
          }
        } else {
          titleEl.remove();
        }
        return node;
      }
      function makePage(nodes, title, extraClass) {
        var page = document.createElement('section');
        page.className = 'preview-page' + (extraClass ? ' ' + extraClass : '');
        if (title) {
          var head = document.createElement('div');
          head.className = 'preview-page-title';
          head.innerHTML = '<strong>' + title + '</strong><span>설계EZ통합분석 리포트</span>';
          page.appendChild(head);
        }
        nodes.forEach(function (node) { page.appendChild(node); });
        return page;
      }
      function contentNodeCount(page) {
        return Array.prototype.slice.call(page.children).filter(function (child) {
          return !(child.classList && child.classList.contains('preview-page-title'));
        }).length;
      }
      function makeEmptyPage(title, extraClass) {
        var page = makePage([], title, extraClass);
        page.style.display = 'block';
        page.style.visibility = 'hidden';
        return page;
      }
      function setup() {
        var main = document.querySelector('main.page');
        var cover = document.querySelector('.cover');
        var content = document.querySelector('.content');
        if (!main || !cover || !content) return;
        var pages = document.createElement('div');
        pages.className = 'preview-pages';
        pages.appendChild(makePage([cover], '', 'cover-preview'));
        main.replaceWith(pages);
        var currentPage = null;
        function appendPacked(node, title, extraClass) {
          var pageTitle = title || titleFor(node, '분석 상세');
          var needsTitleStrip = !currentPage;
          if (!currentPage) {
            stripRepeatedTitle(node, pageTitle);
            currentPage = makeEmptyPage(pageTitle, extraClass || 'analysis-preview packed-preview');
            pages.appendChild(currentPage);
          }
          currentPage.appendChild(node);
          if (currentPage.scrollHeight > currentPage.clientHeight && contentNodeCount(currentPage) > 1) {
            currentPage.removeChild(node);
            currentPage = makeEmptyPage(pageTitle, extraClass || 'analysis-preview packed-preview');
            pages.appendChild(currentPage);
            if (!needsTitleStrip) stripRepeatedTitle(node, pageTitle);
            currentPage.appendChild(node);
          }
        }
        function startFreshPage() {
          currentPage = null;
        }
        var skipNextCoverageFlat = false;
        Array.prototype.slice.call(content.children).forEach(function (node) {
          if (node.classList && node.classList.contains('disease-balance-card')) {
            startFreshPage();
            appendPacked(node, titleFor(node, '질병통계 대비 보장 균형'), 'analysis-preview packed-preview disease-balance-preview');
            startFreshPage();
            return;
          }
          if (node.classList && node.classList.contains('contract-detail-section')) {
            startFreshPage();
            var title = node.querySelector('.block-title');
            if (title) {
              title.remove();
            }
            Array.prototype.slice.call(node.querySelectorAll('.contract-detail-page')).forEach(function (contractNode) {
              pages.appendChild(makePage([contractNode], '계약별 상세 담보', 'contract-preview'));
            });
            skipNextCoverageFlat = true;
            return;
          }
          if (node.classList && node.classList.contains('coverage-category-diagnosis-section')) {
            startFreshPage();
            appendPacked(node, titleFor(node, '보장요약 카테고리 진단'), 'analysis-preview packed-preview category-preview');
            startFreshPage();
            return;
          }
          if (skipNextCoverageFlat && node.classList && node.classList.contains('detail-section') && node.querySelector('.coverage-detail-table') && !node.querySelector('.compact-table')) {
            skipNextCoverageFlat = false;
            return;
          }
          skipNextCoverageFlat = false;
          appendPacked(node, currentPage ? null : titleFor(node, '핵심요약'), currentPage ? null : 'summary-preview packed-preview');
        });
        Array.prototype.slice.call(pages.querySelectorAll('.preview-page')).forEach(function (page) {
          page.style.display = '';
          page.style.visibility = '';
        });
        var pageList = Array.prototype.slice.call(document.querySelectorAll('.preview-page'));
        var idx = 0;
        var counter = document.getElementById('reportPageCounter');
        var prev = document.getElementById('reportPrevPage');
        var next = document.getElementById('reportNextPage');
        function show(nextIdx) {
          idx = Math.max(0, Math.min(pageList.length - 1, nextIdx));
          pageList.forEach(function (page, pageIdx) { page.classList.toggle('active', pageIdx === idx); });
          if (counter) counter.textContent = (idx + 1) + ' / ' + pageList.length;
          if (prev) prev.disabled = idx === 0;
          if (next) next.disabled = idx >= pageList.length - 1;
        }
        if (prev) prev.addEventListener('click', function () { show(idx - 1); });
        if (next) next.addEventListener('click', function () { show(idx + 1); });
        window.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowLeft') show(idx - 1);
          if (event.key === 'ArrowRight') show(idx + 1);
        });
        show(0);
      }
      window.addEventListener('load', setup);
    })();
  </script>`
  return `<!doctype html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <title>${escapeReportHtml(reportFileTitle)}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    * { box-sizing: border-box; }
    :root {
      --report-primary: #2563EB;
      --report-primary-dark: #1D4ED8;
      --report-primary-light: #3B82F6;
      --report-primary-soft: #EFF6FF;
      --report-primary-border: #BFDBFE;
      --report-ink: #0F172A;
      --report-muted: #64748B;
      --report-border: #E2E8F0;
    }
    body { margin: 0; background: #eef2f7; color: #172033; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans KR", sans-serif; line-height: 1.55; }
    .toolbar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid #dbe3ef; background: rgba(255,255,255,.96); padding: 12px 18px; }
    .toolbar strong { display: block; color: #0f172a; font-size: 14px; }
    .toolbar span { display: block; margin-top: 2px; color: #64748b; font-size: 11px; }
    .toolbar-actions { display: flex; gap: 8px; }
    .toolbar button { border: 1px solid var(--report-primary); background: var(--report-primary); color: #fff; cursor: pointer; font-weight: 700; font-size: 12px; padding: 7px 12px; }
    .toolbar button.secondary { border-color: #cbd5e1; background: #fff; color: #334155; }
    .toolbar button:disabled { cursor: not-allowed; opacity: .45; }
    .page-counter { display: inline-flex; min-width: 58px; align-items: center; justify-content: center; border: 1px solid #cbd5e1; background: #f8fafc; color: #334155; font-size: 12px; font-weight: 800; padding: 7px 10px; }
    .page { width: min(100%, 960px); margin: 18px auto; background: #fff; padding: 0; box-shadow: 0 16px 48px rgba(15, 23, 42, .12); }
    .preview-pages { width: min(100%, 960px); margin: 18px auto 36px; }
    .preview-page { position: relative; display: none; width: 210mm; height: 297mm; min-height: 297mm; margin: 0 auto; background: #fff; box-shadow: 0 16px 48px rgba(15, 23, 42, .16); padding: 14mm; overflow: hidden; }
    .preview-page::after { content: ""; position: absolute; right: 10mm; bottom: 8mm; width: 18mm; height: 18mm; background: url("${escapeReportHtml(sgezLogo)}") center / contain no-repeat; opacity: .045; filter: grayscale(.35) saturate(.7); pointer-events: none; z-index: 4; }
    .preview-page.active { display: block; }
    .preview-page.cover-preview { padding: 0; }
    .preview-page.cover-preview .cover { min-height: 297mm; }
    .packed-preview .detail-section:first-of-type,
    .packed-preview .engine-section:first-of-type,
    .packed-preview .evidence-block:first-of-type,
    .packed-preview .stat-card:first-of-type { margin-top: 0; }
    .preview-page-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 2px solid var(--report-primary); margin-bottom: 12px; padding-bottom: 8px; color: var(--report-ink); font-size: 13px; font-weight: 900; }
    .preview-page-title span { color: #64748b; font-size: 10px; font-weight: 800; }
    .category-preview { padding: 9mm 8mm 8mm; }
    .category-preview .preview-page-title { margin-bottom: 8px; padding-bottom: 6px; }
    .category-preview .engine-section { margin-top: 0; padding: 8px; }
    .category-preview .coverage-category-diagnosis-section { display: grid; grid-template-rows: 31mm 8mm 39mm 1fr; height: 254mm; overflow: hidden; }
    .category-preview .category-diagnosis-head { min-height: 0; padding-bottom: 8px; overflow: hidden; }
    .category-preview .category-diagnosis-head p { max-height: 12mm; margin-top: 4px; overflow: hidden; font-size: 11px; }
    .category-preview .category-diagnosis-category { font-size: 30px; }
    .category-preview .category-diagnosis-strip { min-height: 0; margin-top: 5px; overflow: hidden; align-content: start; }
    .category-preview .category-diagnosis-card-grid { min-height: 0; margin-top: 6px; }
    .category-preview .category-diagnosis-card { min-height: 0; height: 100%; overflow: hidden; }
    .category-preview .category-diagnosis-bottom.with-source-image { display: grid; grid-template-rows: 28mm 1fr; min-height: 0; margin-top: 7px; overflow: hidden; }
    .category-preview .category-diagnosis-bottom.with-source-image .category-linked-coverage { height: 28mm; overflow: hidden; }
    .category-preview .category-diagnosis-bottom.with-source-image .category-stat-visual { display: grid; grid-template-rows: 75mm auto auto; min-height: 0; overflow: hidden; }
    .category-preview .category-stat-source-image-wrap { display: flex; align-items: center; justify-content: center; height: 75mm; border-color: #dbe3ef; }
    .category-preview .category-stat-source-image { width: 100%; height: 100%; object-fit: contain; }
    .cover { position: relative; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; min-height: 297mm; padding: 38mm 32mm 28mm; background: linear-gradient(135deg, #f8fbff 0%, var(--report-primary-soft) 56%, #fff 100%); color: var(--report-ink); }
    .cover:before { content: ""; position: absolute; inset: 0 auto 0 0; width: 18mm; background: linear-gradient(180deg, var(--report-primary), var(--report-primary-dark)); }
    .cover:after { content: ""; position: absolute; right: 28mm; top: 32mm; width: 116mm; height: 116mm; border: 18px solid rgba(37,99,235,.08); border-radius: 999px; }
    .cover-brand, .cover-main, .cover-footer { position: relative; z-index: 1; }
    .cover-brand { display: inline-flex; align-items: center; gap: 10px; color: var(--report-primary-dark); font-size: 16px; font-weight: 900; letter-spacing: 0; }
    .cover-brand img { width: 44px; height: 44px; border-radius: 12px; box-shadow: 0 10px 24px rgba(37,99,235,.18); }
    .cover-brand span { display: inline-block; color: var(--report-primary-dark); }
    .cover-kicker { color: var(--report-primary); font-size: 14px; font-weight: 900; }
    .cover h1 { margin-top: 12px; max-width: 640px; color: var(--report-ink); font-size: 43px; line-height: 1.15; font-weight: 900; }
    .cover-subtitle { max-width: 560px; margin-top: 18px; color: #475569; font-size: 15px; line-height: 1.65; }
    .cover-footer { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; border-top: 2px solid var(--report-primary); padding-top: 16px; }
    .cover-footer div { border-left: 4px solid var(--report-primary); padding: 4px 0 4px 12px; }
    .cover-footer span { display: block; color: #64748b; font-size: 11px; font-weight: 900; }
    .cover-footer strong { display: block; margin-top: 4px; color: #0f172a; font-size: 16px; }
    .content { padding: 28px 34px 34px; }
    h1, h2, h3, p { margin: 0; }
    .eyebrow { color: #64748b; font-size: 12px; font-weight: 700; letter-spacing: 0; }
    h1 { margin-top: 6px; font-size: 25px; line-height: 1.25; }
    .summary { margin-top: 12px; color: #334155; font-size: 13px; }
    .meta { margin-top: 10px; color: #64748b; font-size: 11px; }
    .metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 20px; }
    .metric { border: 1px solid #dbe3ef; padding: 10px; }
    .metric span { display: block; color: #64748b; font-size: 11px; }
    .metric strong { display: block; margin-top: 4px; color: #0f172a; font-size: 17px; }
    .overall-dashboard { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 7px; margin-top: 12px; break-inside: avoid; }
    .overall-dashboard div { border: 1px solid #dbe3ef; background: #f8fafc; padding: 9px; min-height: 82px; }
    .overall-dashboard span { display: block; color: #64748b; font-size: 10px; font-weight: 900; }
    .overall-dashboard strong { display: block; margin-top: 5px; color: #0f172a; font-size: 14px; line-height: 1.2; }
    .overall-dashboard em { display: block; margin-top: 5px; color: #64748b; font-size: 9.5px; font-style: normal; line-height: 1.35; }
    .overall-dashboard .ok { border-color: #bbf7d0; background: #f0fdf4; }
    .overall-dashboard .warn { border-color: #fde68a; background: #fffbeb; }
    .overall-dashboard .focus { border-color: #bfdbfe; background: #eff6ff; }
    .executive-summary-grid { display: grid; grid-template-columns: 1fr; gap: 10px; margin-top: 14px; break-inside: avoid; }
    .executive-summary-card { border: 1px solid #dbe3ef; border-left: 5px solid #cbd5e1; background: #fff; padding: 13px 16px; min-height: 0; }
    .executive-summary-card h3 { margin: 0 0 8px; color: #0f172a; font-size: 15px; }
    .executive-summary-card ul { padding-left: 18px; }
    .executive-summary-card li { font-size: 12.4px; line-height: 1.55; }
    .executive-summary-card.warn { border-color: #fde68a; border-left-color: #f59e0b; background: #fffbeb; }
    .executive-summary-card.ok { border-color: #bbf7d0; border-left-color: #10b981; background: #f0fdf4; }
    .executive-summary-card.focus { border-color: #bfdbfe; border-left-color: #2563eb; background: #eff6ff; }
    .band { margin-top: 18px; padding: 14px 16px; border: 1px solid #d7e7ff; background: #f5f9ff; break-inside: avoid; }
    .stat-card { margin-top: 16px; padding: 14px; border: 1px solid #dbe3ef; background: #fbfdff; break-inside: avoid; }
    .stat-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
    .stat-card-head h2 { margin-bottom: 4px; }
    .stat-card-head strong { white-space: nowrap; border: 1px solid #bae6fd; background: #e0f2fe; color: #075985; padding: 4px 8px; font-size: 12px; }
    .bar-row { display: grid; grid-template-columns: 140px 1fr 92px; align-items: center; gap: 10px; margin-top: 8px; font-size: 12px; }
    .bar-row span { color: #475569; }
    .bar-row b { text-align: right; color: #0f172a; font-size: 12px; }
    .bar-track { height: 13px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .bar-track.thick { height: 18px; }
    .bar-fill { height: 100%; border-radius: 999px; }
    .bar-fill.primary { background: var(--report-primary); }
    .bar-fill.neutral { background: #94a3b8; }
    .bar-fill.accent { background: #0f766e; }
    .portfolio-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-top: 12px; }
    .portfolio-metrics div { border: 1px solid #e2e8f0; background: #f8fafc; padding: 9px 10px; }
    .portfolio-metrics span { display: block; color: #64748b; font-size: 10.5px; }
    .portfolio-metrics strong { display: block; margin-top: 3px; color: #0f172a; font-size: 14px; font-variant-numeric: tabular-nums; }
    .portfolio-grid, .evidence-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px; }
    .portfolio-panel { border: 1px solid #e2e8f0; background: #fff; padding: 11px; break-inside: avoid; }
    .portfolio-row-block { margin-top: 10px; }
    .portfolio-panel h3 { margin: 0 0 8px; color: #0f172a; font-size: 12px; }
    .premium-segment-bar, .payment-total-bar, .health-cost-stack { display: flex; height: 18px; margin-top: 10px; overflow: hidden; border-radius: 999px; background: #e2e8f0; }
    .premium-segment-bar i, .payment-total-bar i, .health-cost-stack i { display: block; min-width: 0; height: 100%; }
    .payment-total-bar.large { height: 22px; }
    .payment-total-bar .paid, .payment-stack .paid, .health-cost-stack .benefit { background: #0f766e; }
    .payment-total-bar .future, .payment-stack .future { background: #f59e0b; }
    .health-cost-stack .pocket { background: #fb7185; }
    .future-premium-callout { display: grid; grid-template-columns: 1fr auto; gap: 2px 12px; border: 1px solid #fde68a; background: #fffbeb; padding: 10px 12px; }
    .future-premium-callout span { color: #92400e; font-size: 11px; font-weight: 900; }
    .future-premium-callout strong { grid-row: span 2; align-self: center; color: #0f172a; font-size: 20px; font-variant-numeric: tabular-nums; }
    .future-premium-callout em { color: #64748b; font-size: 10.5px; font-style: normal; }
    .premium-benchmark-panel .muted { margin-top: 3px; }
    .benchmark-status-line { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; border: 1px solid #bfdbfe; background: #eff6ff; padding: 7px 9px; }
    .benchmark-status-line span { color: var(--report-primary-dark); font-size: 10.5px; font-weight: 900; }
    .benchmark-status-line strong { color: #0f172a; font-size: 13px; }
    .legend-list { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 12px; margin-top: 9px; }
    .legend-list div { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #475569; font-size: 10.5px; }
    .legend-list span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .legend-list em { display: inline-block; width: 8px; height: 8px; margin-right: 5px; border-radius: 999px; vertical-align: 0; }
    .legend-list b { color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .payment-legend { display: flex; justify-content: space-between; gap: 8px; margin-top: 6px; color: #64748b; font-size: 10.5px; }
    .payment-contract-list { display: grid; gap: 7px; margin-top: 10px; }
    .payment-contract-list.top3 { gap: 10px; }
    .payment-contract-title { display: flex; justify-content: space-between; gap: 10px; color: #334155; font-size: 10.5px; }
    .payment-contract-title span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .payment-contract-title b { color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .payment-stack { display: flex; height: 9px; margin-top: 4px; overflow: hidden; border-radius: 999px; background: #e2e8f0; }
    .payment-stack i { display: block; height: 100%; }
    .payment-contract-sub { display: flex; justify-content: space-between; gap: 8px; margin-top: 3px; color: #64748b; font-size: 9.5px; }
    .maturity-bar-list { display: grid; gap: 7px; }
    .maturity-row { display: grid; grid-template-columns: 48px 1fr 32px; align-items: center; gap: 7px; color: #475569; font-size: 10.5px; }
    .maturity-row b { text-align: right; color: #0f172a; font-variant-numeric: tabular-nums; }
    .maturity-track { height: 10px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .maturity-track i { display: block; height: 100%; border-radius: 999px; background: #2563eb; }
    .maturity-contracts { display: grid; gap: 5px; margin-top: 10px; }
    .maturity-contracts div { display: flex; justify-content: space-between; gap: 8px; border-top: 1px solid #eef2f7; padding-top: 5px; font-size: 10px; }
    .maturity-contracts span { color: #64748b; white-space: nowrap; }
    .maturity-contracts b { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #0f172a; }
    .visual-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 16px; break-inside: avoid; }
    .evidence-block { margin-top: 16px; border: 1px solid #cbd5e1; background: #fff; padding: 14px; break-inside: avoid; }
    .block-title { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; border-bottom: 2px solid var(--report-primary); padding-bottom: 8px; }
    .report-flow-title { margin-bottom: 12px; }
    .block-title span { color: #0f172a; font-size: 15px; font-weight: 900; }
    .block-title strong { color: #64748b; font-size: 11px; font-weight: 700; }
    .fact-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 12px; }
    .fact { border: 1px solid #e2e8f0; background: #f8fafc; padding: 10px; min-height: 78px; }
    .fact span { display: block; color: #64748b; font-size: 10.5px; }
    .fact strong { display: block; margin-top: 4px; color: #0f172a; font-size: 16px; }
    .fact em { display: block; margin-top: 4px; color: #475569; font-size: 10.5px; font-style: normal; }
    .evidence-judgment-box { display: grid; gap: 5px; margin-top: 10px; border: 1px solid #dbeafe; background: #eff6ff; padding: 9px 10px; }
    .evidence-judgment-box b { color: var(--report-primary-dark); font-size: 11px; }
    .evidence-judgment-box p { margin: 0; color: #334155; font-size: 10.8px; line-height: 1.45; }
    .mini-table { margin-top: 10px; border: 1px solid #e2e8f0; }
    .mini-table-title { background: #f1f5f9; color: #334155; font-size: 11px; font-weight: 900; padding: 6px 8px; }
    .mini-table div:not(.mini-table-title) { display: flex; justify-content: space-between; gap: 10px; border-top: 1px solid #e2e8f0; padding: 6px 8px; font-size: 11px; }
    .mini-table b { color: #0f172a; font-variant-numeric: tabular-nums; }
    .diagnosis-share-list { display: grid; gap: 8px; }
    .diagnosis-share-row div:first-child { display: flex; justify-content: space-between; gap: 10px; color: #334155; font-size: 10.5px; }
    .diagnosis-share-row span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .diagnosis-share-row b { color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .share-track { height: 10px; margin-top: 4px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .share-track i { display: block; height: 100%; border-radius: 999px; background: var(--report-primary); }
    .health-type-panel { margin-top: 10px; }
    .yearly-cost-bars { display: grid; grid-template-columns: repeat(5, 1fr); align-items: end; gap: 7px; min-height: 132px; padding-top: 8px; }
    .yearly-cost-bars div { display: grid; grid-template-rows: 16px 90px 16px; align-items: end; text-align: center; color: #64748b; font-size: 9.5px; }
    .yearly-cost-bars b { align-self: start; color: #334155; font-size: 10px; }
    .yearly-cost-bars i { display: block; width: 100%; min-height: 8px; border-radius: 6px 6px 0 0; background: linear-gradient(180deg, var(--report-primary), var(--report-primary-dark)); }
    .yearly-cost-bars span { align-self: end; color: #475569; }
    .signal-chip-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
    .signal-chip-list span { border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 4px 7px; font-size: 10.5px; font-weight: 800; }
    .related-coverage-panel { margin-top: 10px; }
    .related-coverage-list { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
    .related-coverage-list div { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 8px; border: 1px solid #e2e8f0; background: #f8fafc; padding: 7px 8px; }
    .related-coverage-list span { min-width: 0; color: #0f172a; font-size: 10.5px; font-weight: 900; }
    .related-coverage-list em { display: block; margin-top: 1px; color: #64748b; font-size: 9.5px; font-style: normal; font-weight: 500; }
    .related-coverage-list b { color: #0f172a; font-size: 10.5px; white-space: nowrap; }
    .related-coverage-list strong { grid-column: 2; color: #475569; font-size: 9.5px; text-align: right; }
    .related-coverage-list small { grid-column: 1 / -1; color: #64748b; font-size: 9.5px; line-height: 1.35; }
    .claim-compare-box { margin-top: 12px; border: 1px solid #dbe3ef; background: #f8fafc; padding: 11px; }
    .claim-compare-head { display: flex; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
    .claim-compare-head span { border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 2px 6px; font-size: 10.5px; font-weight: 900; }
    .claim-compare-head b { color: #0f172a; font-size: 12px; }
    .hidden-onepage-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 10px; }
    .hidden-onepage-summary div { border: 1px solid #dbe3ef; background: #f8fafc; padding: 9px; }
    .hidden-onepage-summary span { display: block; color: #64748b; font-size: 10.5px; }
    .hidden-onepage-summary strong { display: block; margin-top: 3px; color: #0f172a; font-size: 14px; }
    .claim-compare-row { display: grid; grid-template-columns: 92px 1fr 86px; align-items: center; gap: 8px; margin-top: 7px; color: #475569; font-size: 10.5px; }
    .claim-compare-row b { text-align: right; color: #0f172a; font-variant-numeric: tabular-nums; }
    .claim-compare-row.received .share-track i { background: #2563eb; }
    .claim-compare-row.gap .share-track i { background: #fb7185; }
    .claim-compare-box p { margin-top: 9px; color: #64748b; font-size: 11px; }
    .claim-breakdown-list { display: grid; gap: 4px; margin-top: 9px; border-top: 1px solid #e2e8f0; padding-top: 8px; }
    .claim-breakdown-list div { display: flex; justify-content: space-between; gap: 10px; color: #64748b; font-size: 10px; }
    .claim-breakdown-list div.total { border-top: 1px solid #e2e8f0; margin-top: 2px; padding-top: 5px; color: #334155; font-weight: 900; }
    .claim-breakdown-list b { color: #0f172a; font-variant-numeric: tabular-nums; }
    .claim-candidate-panel { margin-top: 10px; }
    .claim-candidate-list { display: grid; gap: 7px; }
    .claim-candidate-list div { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 10px; border: 1px solid #e2e8f0; background: #fff; padding: 8px; }
    .claim-candidate-list span { min-width: 0; color: #0f172a; font-size: 10.5px; font-weight: 900; }
    .claim-candidate-list em { display: block; margin-top: 2px; color: #64748b; font-size: 9.5px; font-style: normal; font-weight: 500; }
    .claim-candidate-list b { color: #be123c; font-size: 10.5px; white-space: nowrap; }
    .claim-candidate-list strong { grid-column: 1 / -1; color: #475569; font-size: 9.5px; }
    .document-chip-list { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 10px; border-top: 1px solid #e2e8f0; padding-top: 9px; }
    .document-chip-list span { color: #64748b; font-size: 10.5px; font-weight: 900; }
    .document-chip-list b { border: 1px solid #dbe3ef; background: #f8fafc; color: #334155; padding: 4px 7px; font-size: 10.5px; }
    .compact-result-list { display: grid; gap: 6px; }
    .compact-result-list div { display: flex; justify-content: space-between; gap: 10px; border-bottom: 1px solid #eef2f7; padding-bottom: 6px; font-size: 10.5px; }
    .compact-result-list div:last-child { border-bottom: 0; padding-bottom: 0; }
    .compact-result-list span { min-width: 0; color: #0f172a; font-weight: 800; }
    .compact-result-list em { display: block; margin-top: 2px; overflow: hidden; color: #64748b; font-size: 9.5px; font-style: normal; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
    .compact-result-list b { color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .detail-section { margin-top: 16px; border: 1px solid #cbd5e1; background: #fff; padding: 14px; break-inside: avoid; }
    .engine-section { margin-top: 16px; border: 2px solid var(--report-primary); background: #fff; padding: 16px; break-inside: avoid; }
    .engine-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
    .engine-head h2 { font-size: 16px; margin-bottom: 4px; }
    .engine-head p { color: #64748b; font-size: 12px; }
    .engine-continuation-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 2px solid var(--report-primary); padding-bottom: 8px; }
    .engine-continuation-head h2 { margin: 0; color: var(--report-ink); font-size: 15px; }
    .engine-continuation-head span { border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 3px 7px; font-size: 10.5px; font-weight: 900; }
    .score-dial { width: 92px; min-height: 82px; display: grid; place-items: center; border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); text-align: center; }
    .score-dial strong { display: block; color: var(--report-primary-dark); font-size: 30px; line-height: 1; }
    .score-dial span { display: block; margin-top: 4px; color: var(--report-primary-dark); font-size: 11px; font-weight: 900; }
    .score-track { height: 14px; margin-top: 12px; background: #e2e8f0; overflow: hidden; }
    .score-track div { height: 100%; background: linear-gradient(90deg, var(--report-primary-light), var(--report-primary-dark)); }
    .issue-strip { display: flex; gap: 6px; margin-top: 10px; }
    .issue-strip span { border: 1px solid #e2e8f0; background: #f8fafc; color: #334155; padding: 4px 7px; font-size: 11px; font-weight: 900; }
    .standard-overview-section { break-inside: avoid; page-break-inside: avoid; }
    .coverage-gap-overview { display: grid; grid-template-columns: 1.28fr .72fr; gap: 12px; margin-top: 12px; align-items: start; }
    .coverage-gap-table { margin-top: 0; font-size: 10px; }
    .coverage-gap-table td.warn, .coverage-gap-table td.alert { color: #be123c; font-weight: 900; }
    .coverage-gap-table td.ok { color: #166534; font-weight: 900; }
    .coverage-category-overview { border: 1px solid #dbe3ef; background: #f8fafc; padding: 10px; }
    .coverage-category-overview h3 { color: #0f172a; font-size: 13px; margin-bottom: 8px; }
    .category-score-row { display: grid; grid-template-columns: 96px 1fr 38px; gap: 7px; align-items: center; padding: 6px 0; border-bottom: 1px solid #e2e8f0; }
    .category-score-row:last-child { border-bottom: 0; }
    .category-score-row span { display: block; color: #0f172a; font-size: 10.5px; font-weight: 900; }
    .category-score-row b { display: block; margin-top: 1px; color: #64748b; font-size: 9.5px; }
    .category-score-row strong { text-align: right; color: #0f172a; font-size: 10.5px; font-variant-numeric: tabular-nums; }
    .category-score-track { height: 8px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .category-score-track i { display: block; height: 100%; border-radius: 999px; }
    .category-score-track i.ok { background: #0f766e; }
    .category-score-track i.warn { background: #f59e0b; }
    .category-score-track i.alert { background: #e11d48; }
    .standard-mini-bars { display: grid; gap: 5px; margin-top: 7px; }
    .standard-mini-bars div { display: grid; grid-template-columns: 30px 1fr 68px; gap: 6px; align-items: center; color: #64748b; font-size: 9.8px; }
    .standard-mini-bars.compact div { grid-template-columns: 28px 1fr 62px; }
    .standard-mini-bars i { display: block; height: 8px; border-radius: 999px; background: #0f766e; }
    .standard-mini-bars i.warn { background: #f59e0b; }
    .standard-mini-bars i.alert { background: #e11d48; }
    .standard-mini-bars i.ref { background: #93c5fd; }
    .standard-mini-bars b { color: #0f172a; text-align: right; font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .coverage-category-diagnosis-section { break-inside: avoid; page-break-inside: avoid; }
    .category-diagnosis-head { display: grid; grid-template-columns: 1fr 104px; gap: 14px; align-items: start; border-bottom: 2px solid var(--report-primary); padding-bottom: 10px; }
    .category-diagnosis-head h2 { margin: 0; color: var(--report-primary); font-size: 13px; font-weight: 900; }
    .category-diagnosis-category { margin-top: 5px; color: #0f172a; font-size: 28px; font-weight: 900; line-height: 1.12; }
    .category-diagnosis-head p { margin-top: 6px; color: #475569; font-size: 12px; }
    .category-diagnosis-score { border: 1px solid #dbe3ef; background: #f8fafc; padding: 9px; text-align: center; }
    .category-diagnosis-score strong { display: block; color: #0f172a; font-size: 23px; font-variant-numeric: tabular-nums; }
    .category-diagnosis-score span { display: block; margin-top: 2px; color: #334155; font-size: 10px; }
    .category-diagnosis-score.ok { border-color: #bbf7d0; background: #f0fdf4; }
    .category-diagnosis-score.warn { border-color: #fde68a; background: #fffbeb; }
    .category-diagnosis-score.alert { border-color: #fecaca; background: #fff1f2; }
    .category-diagnosis-strip { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
    .category-diagnosis-strip span { border: 1px solid #e2e8f0; background: #fff; color: #475569; padding: 3px 6px; font-size: 10px; font-weight: 900; }
    .category-diagnosis-card-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; margin-top: 9px; }
    .category-diagnosis-card { min-height: 98px; border: 1px solid #dbe3ef; background: #fff; padding: 8px; break-inside: avoid; }
    .category-diagnosis-card.warn { border-color: #fbbf24; background: #fffbeb; }
    .category-diagnosis-card.alert { border-color: #fda4af; background: #fff1f2; }
    .category-diagnosis-card.ok { border-color: #bbf7d0; background: #f0fdf4; }
    .category-card-head { display: flex; justify-content: space-between; gap: 6px; align-items: center; }
    .category-card-head > span { color: #64748b; font-size: 9.8px; font-weight: 900; }
    .category-diagnosis-card strong { display: block; margin-top: 6px; color: #0f172a; font-size: 12px; line-height: 1.25; }
    .category-card-gap { margin-top: 7px; border-top: 1px solid rgba(15,23,42,.08); padding-top: 5px; color: #be123c; font-size: 10.5px; font-weight: 900; }
    .category-diagnosis-card.ok .category-card-gap { color: #166534; }
    .category-diagnosis-card.more { border-style: dashed; background: #f8fafc; color: #475569; }
    .category-diagnosis-card.more p { margin-top: 8px; font-size: 10.5px; color: #64748b; }
    .category-diagnosis-bottom { display: grid; grid-template-columns: 1.12fr .88fr; gap: 10px; margin-top: 11px; }
    .category-diagnosis-bottom.with-source-image { grid-template-columns: 1fr; gap: 6px; margin-top: 8px; }
    .category-stat-visual, .category-linked-coverage { border: 1px solid #dbe3ef; background: #f8fafc; padding: 11px; min-height: 190px; }
    .category-diagnosis-bottom.with-source-image .category-linked-coverage { min-height: 0; padding: 7px 9px; }
    .category-diagnosis-bottom.with-source-image .category-stat-visual { min-height: 0; padding: 0; border: 0; background: #fff; }
    .category-stat-kicker { color: var(--report-primary); font-size: 10px; font-weight: 900; }
    .category-stat-visual h3, .category-linked-coverage h3 { margin-top: 4px; color: #0f172a; font-size: 14px; }
    .category-stat-source-image-wrap { border: 1px solid #e2e8f0; background: #fff; overflow: hidden; padding: 0; }
    .category-stat-source-image { display: block; width: 100%; max-width: 100%; max-height: 700px; object-fit: contain; image-rendering: auto; }
    .category-stat-image { display: grid; grid-template-columns: 116px 1fr; gap: 10px; margin-top: 8px; border: 1px solid #bfdbfe; background: linear-gradient(135deg, #eff6ff 0%, #ffffff 62%); padding: 9px; }
    .category-stat-main { border-right: 1px solid #dbeafe; padding-right: 9px; }
    .category-stat-main span { display: block; color: #1d4ed8; font-size: 9.8px; font-weight: 900; }
    .category-stat-main strong { display: block; margin-top: 6px; color: #0f172a; font-size: 18px; line-height: 1.1; }
    .category-stat-main em { display: inline-block; margin-top: 6px; background: var(--report-primary); color: #fff; padding: 3px 6px; font-size: 9.5px; font-style: normal; font-weight: 900; }
    .category-stat-bars { display: grid; gap: 5px; align-content: center; }
    .category-stat-bars div { display: grid; grid-template-columns: 50px 1fr 30px; gap: 6px; align-items: center; }
    .category-stat-bars span { color: #334155; font-size: 9.8px; font-weight: 900; }
    .category-stat-bars i { display: block; height: 8px; background: #dbeafe; border-radius: 999px; overflow: hidden; }
    .category-stat-bars b { display: block; height: 100%; background: linear-gradient(90deg, #2563eb, #0f766e); border-radius: 999px; }
    .category-stat-bars strong { color: #0f172a; text-align: right; font-size: 9.5px; font-variant-numeric: tabular-nums; }
    .category-stat-tags { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 4px; border-top: 1px solid #dbeafe; padding-top: 6px; }
    .category-stat-tags span { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 2px 5px; font-size: 9.5px; font-weight: 900; }
    .category-stat-caption { margin-top: 8px; color: #334155; font-size: 12px; line-height: 1.35; font-weight: 800; }
    .category-stat-lines { display: grid; gap: 4px; margin-top: 8px; }
    .category-stat-lines div { display: grid; grid-template-columns: 20px 1fr; gap: 6px; align-items: start; }
    .category-stat-lines b { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 999px; background: var(--report-primary); color: #fff; font-size: 9.5px; }
    .category-stat-lines span { color: #334155; font-size: 10px; line-height: 1.28; }
    .category-stat-visual p { margin-top: 8px; border-top: 1px solid #e2e8f0; padding-top: 6px; color: #64748b; font-size: 11px; }
    .category-diagnosis-bottom.with-source-image .category-stat-caption { margin-top: 4px; font-size: 10.5px; line-height: 1.25; }
    .category-diagnosis-bottom.with-source-image .category-stat-visual p { margin-top: 4px; padding-top: 4px; font-size: 9.5px; text-align: right; }
    .linked-coverage-total { display: flex; justify-content: space-between; gap: 10px; margin-top: 10px; border: 1px solid #bfdbfe; background: #eff6ff; padding: 8px; }
    .category-diagnosis-bottom.with-source-image .linked-coverage-total { margin-top: 4px; padding: 5px 7px; }
    .linked-coverage-total span { color: var(--report-primary-dark); font-size: 10.5px; font-weight: 900; }
    .linked-coverage-total strong { color: #0f172a; font-size: 12px; font-variant-numeric: tabular-nums; }
    .linked-coverage-list { display: grid; gap: 5px; margin-top: 8px; }
    .category-diagnosis-bottom.with-source-image .linked-coverage-list { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 3px 8px; margin-top: 4px; }
    .linked-coverage-list div { display: flex; justify-content: space-between; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; color: #475569; font-size: 10.5px; }
    .linked-coverage-list div:last-child { border-bottom: 0; padding-bottom: 0; }
    .linked-coverage-list span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .linked-coverage-list b { color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .scenario-highlight-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-top: 12px; }
    .scenario-highlight-card { border: 1px solid #dbe3ef; background: #f8fafc; padding: 10px; }
    .scenario-card-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 3px; }
    .scenario-card-top span { color: var(--report-primary); font-size: 10.5px; font-weight: 900; }
    .scenario-card-top b { border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 2px 6px; font-size: 10.5px; }
    .scenario-highlight-card strong { display: block; margin-top: 2px; color: #0f172a; font-size: 13px; }
    .scenario-highlight-card p { margin-top: 5px; color: #475569; font-size: 11px; }
    .scenario-impact-head { display: grid; grid-template-columns: 62px 1fr; gap: 2px 8px; align-items: center; margin-top: 8px; border: 1px solid #fecaca; background: #fff1f2; padding: 7px 8px; }
    .scenario-impact-head.ok { border-color: #bbf7d0; background: #f0fdf4; }
    .scenario-impact-head span { color: #be123c; font-size: 10.5px; font-weight: 900; }
    .scenario-impact-head.ok span { color: #166534; }
    .scenario-impact-head b { color: #0f172a; font-size: 15px; font-variant-numeric: tabular-nums; }
    .scenario-impact-head em { grid-column: 1 / -1; color: #64748b; font-size: 9.8px; font-style: normal; }
    .scenario-money-bar { display: flex; height: 10px; margin-top: 8px; overflow: hidden; border-radius: 999px; background: #e2e8f0; }
    .scenario-money-bar i { display: block; height: 100%; }
    .scenario-money-bar .covered { background: var(--report-primary); }
    .scenario-money-bar .gap { background: #fb7185; }
    .scenario-money-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; margin-top: 7px; }
    .scenario-money-grid span { border: 1px solid #e2e8f0; background: #fff; color: #64748b; padding: 5px 6px; font-size: 10.5px; }
    .scenario-money-grid b { display: block; margin-top: 2px; color: #0f172a; font-size: 11px; }
    .scenario-trigger { display: grid; gap: 2px; margin-top: 8px; border: 1px solid #fde68a; background: #fffbeb; padding: 7px 8px; }
    .scenario-trigger span { color: #92400e; font-size: 10.5px; font-weight: 900; }
    .scenario-trigger b { color: #0f172a; font-size: 11px; }
    .scenario-trigger em { color: #64748b; font-size: 9.5px; font-style: normal; }
    .scenario-reasons { margin-top: 7px; padding-left: 16px; color: #475569; }
    .scenario-reasons li { font-size: 10.5px; }
    .page-break-soft { break-before: auto; }
    .report-table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 10.5px; }
    .report-table th { background: #f1f5f9; color: #334155; border: 1px solid #dbe3ef; padding: 6px; text-align: left; font-weight: 900; }
    .report-table td { border: 1px solid #e2e8f0; padding: 6px; color: #334155; vertical-align: top; }
    .report-table td.wide { color: #0f172a; font-weight: 700; }
    .report-table .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .compact-table th:nth-child(5), .compact-table td:nth-child(5) { width: 42%; }
    .coverage-detail-table th:nth-child(3), .coverage-detail-table td:nth-child(3) { width: 22%; }
    .coverage-detail-table th:nth-child(5), .coverage-detail-table td:nth-child(5) { width: 28%; }
    .contract-detail-page { margin-top: 14px; border: 1px solid #dbe3ef; background: #fff; padding: 14px; break-inside: avoid; page-break-inside: avoid; }
    .contract-detail-page + .contract-detail-page { page-break-before: always; }
    .contract-detail-head { display: flex; justify-content: space-between; gap: 16px; border-bottom: 2px solid var(--report-primary); padding-bottom: 10px; }
    .contract-detail-head span { color: var(--report-primary); font-size: 11px; font-weight: 900; }
    .contract-detail-head h3 { margin: 2px 0 2px; color: #0f172a; font-size: 16px; }
    .contract-detail-head p { margin: 0; color: #64748b; font-size: 11px; }
    .contract-detail-meta { min-width: 120px; text-align: right; }
    .contract-detail-meta b { display: block; color: #0f172a; font-size: 14px; }
    .contract-detail-meta span { color: #64748b; font-size: 10px; }
    .contract-role-mini { display: inline-block; border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 2px 5px; font-size: 10px; font-weight: 900; white-space: nowrap; }
    .contract-role-count { margin-top: 3px; color: #64748b; font-size: 9.5px; }
    .contract-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin: 10px 0; }
    .contract-summary-grid div { border: 1px solid #e2e8f0; background: #f8fafc; padding: 7px 8px; }
    .contract-summary-grid span { display: block; color: #64748b; font-size: 10px; }
    .contract-summary-grid strong { display: block; margin-top: 2px; color: #0f172a; font-size: 11px; }
    .contract-role-card { display: grid; grid-template-columns: 1.15fr .85fr; gap: 10px; margin: 10px 0; border: 1px solid #bfdbfe; background: #eff6ff; padding: 11px; break-inside: avoid; }
    .contract-role-main span { display: inline-block; border: 1px solid #93c5fd; background: #fff; color: #1d4ed8; padding: 2px 6px; font-size: 10px; font-weight: 900; }
    .contract-role-main strong { display: block; margin-top: 6px; color: #0f172a; font-size: 14px; }
    .contract-role-main p { margin-top: 5px; color: #334155; font-size: 11px; line-height: 1.45; }
    .contract-role-side { display: grid; gap: 7px; align-content: start; }
    .contract-role-tags { display: flex; flex-wrap: wrap; gap: 4px; }
    .contract-role-tags span { border: 1px solid #dbeafe; background: #fff; color: #334155; padding: 3px 6px; font-size: 9.8px; font-weight: 900; }
    .contract-key-coverage-list { display: grid; gap: 4px; }
    .contract-key-coverage-list div { display: flex; justify-content: space-between; gap: 8px; border-bottom: 1px solid #dbeafe; padding-bottom: 4px; color: #475569; font-size: 10px; }
    .contract-key-coverage-list div:last-child { border-bottom: 0; padding-bottom: 0; }
    .contract-key-coverage-list span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .contract-key-coverage-list b { color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .contract-check-points { display: grid; gap: 5px; margin-bottom: 10px; }
    .contract-check-points div { border: 1px solid #fde68a; background: #fffbeb; color: #92400e; padding: 6px 8px; font-size: 10.5px; font-weight: 800; }
    .contract-continuation-head { display: flex; justify-content: space-between; gap: 12px; border-bottom: 2px solid var(--report-primary); padding-bottom: 9px; }
    .contract-continuation-head span { color: var(--report-primary); font-size: 11px; font-weight: 900; }
    .contract-continuation-head h3 { margin: 2px 0; color: #0f172a; font-size: 15px; }
    .contract-continuation-head p { margin: 0; color: #64748b; font-size: 10.5px; }
    .contract-continuation-head strong { align-self: flex-start; border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 3px 7px; font-size: 10.5px; }
    .report-table tr, .scenario-highlight-card, .contract-detail-page { break-inside: avoid; page-break-inside: avoid; }
    .contract-coverage-table th:nth-child(1), .contract-coverage-table td:nth-child(1) { width: 20%; }
    .contract-coverage-table th:nth-child(2), .contract-coverage-table td:nth-child(2) { width: 48%; }
    .contract-coverage-table th:nth-child(3), .contract-coverage-table td:nth-child(3) { width: 18%; }
    .contract-coverage-table th:nth-child(4), .contract-coverage-table td:nth-child(4) { width: 14%; }
    .table-pill { display: inline-block; border: 1px solid #fde68a; background: #fffbeb; color: #92400e; padding: 2px 5px; font-weight: 900; white-space: nowrap; }
    .table-pill.ok { border-color: #bbf7d0; background: #f0fdf4; color: #166534; }
    .table-pill.warn { border-color: #fde68a; background: #fffbeb; color: #92400e; }
    .table-pill.alert { border-color: #fecaca; background: #fff1f2; color: #be123c; }
    .standard-table th:nth-child(7), .standard-table td:nth-child(7) { width: 36%; }
    .match-basis { display: grid; gap: 4px; margin-top: 5px; color: #64748b; font-size: 9.8px; line-height: 1.35; }
    .match-basis > b { color: var(--report-primary-dark); font-size: 9.5px; }
    .match-empty { color: #94a3b8; }
    .match-basis-list { display: grid; gap: 3px; margin: 0; padding: 0; list-style: none; }
    .match-basis-list li { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 6px; align-items: start; border-top: 1px solid #e2e8f0; padding-top: 3px; color: #475569; }
    .match-basis-list li:first-child { border-top: 0; padding-top: 0; }
    .match-basis-list span { min-width: 0; }
    .match-basis-list strong { display: block; overflow: hidden; color: #334155; font-size: 9.8px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
    .match-basis-list em { display: block; overflow: hidden; color: #64748b; font-size: 9.2px; font-style: normal; text-overflow: ellipsis; white-space: nowrap; }
    .match-basis-list i { color: #0f172a; font-size: 9.8px; font-style: normal; font-weight: 900; white-space: nowrap; }
    .standard-gap-line { margin-top: 3px; color: #be123c; font-size: 9.8px; font-weight: 900; }
    .recommendation-line { margin-top: 4px; color: #334155; font-size: 10px; font-weight: 700; }
    .duplicate-table { margin-top: 12px; }
    .bar-row.compact { grid-template-columns: 96px 1fr 78px; }
    .check-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; margin-top: 12px; }
    .check-grid span { border: 1px solid #e2e8f0; background: #fff; padding: 5px 7px; font-size: 11px; color: #64748b; }
    .check-grid span.ready { border-color: #bbf7d0; background: #f0fdf4; color: #166534; font-weight: 700; }
    .check-grid span.missing { border-color: #e2e8f0; background: #f8fafc; color: #64748b; }
    .disease-balance-preview .disease-balance-card { padding: 12px; }
    .disease-balance-card .stat-card-head { margin-bottom: 9px; }
    .balance-table { display: grid; gap: 8px; margin-top: 10px; }
    .balance-card { border: 1px solid #e2e8f0; background: #fff; padding: 9px 10px; break-inside: avoid; }
    .balance-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
    .balance-card-head strong { display: block; margin-top: 2px; color: #0f172a; font-size: 12px; }
    .balance-name { display: block; font-weight: 900; color: #0f172a; font-size: 13px; }
    .balance-amount-line { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px; color: #475569; font-size: 11px; }
    .balance-amount-line span { border: 1px solid #e2e8f0; background: #f8fafc; padding: 6px 8px; }
    .balance-amount-line b { float: right; color: #0f172a; font-variant-numeric: tabular-nums; }
    .balance-msg { margin-top: 7px; color: #64748b; font-size: 10.6px; line-height: 1.42; }
    .balance-scale { position: relative; height: 16px; margin-top: 10px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .balance-reference { position: absolute; top: 4px; height: 8px; border-radius: 999px; background: #93c5fd; opacity: .85; }
    .balance-current { position: absolute; top: 2px; width: 12px; height: 12px; transform: translateX(-50%); border: 2px solid #fff; border-radius: 999px; background: var(--report-primary); box-shadow: 0 0 0 1px rgba(37,99,235,.35); }
    .balance-current.warn { background: #f59e0b; }
    .balance-current.alert { background: #e11d48; }
    .balance-current.ok { background: #0f766e; }
    .real-loss-check { margin-top: 9px; border: 1px solid #e2e8f0; background: #f8fafc; color: #475569; padding: 7px 8px; font-size: 11px; font-weight: 800; }
    .real-loss-check.ok { border-color: #bbf7d0; background: #f0fdf4; color: #166534; }
    .real-loss-check.warn { border-color: #fde68a; background: #fffbeb; color: #92400e; }
    .coverage-compare-bars { display: grid; gap: 7px; margin-top: 10px; }
    .coverage-compare-row { display: grid; grid-template-columns: 54px 1fr 84px; align-items: center; gap: 8px; color: #475569; font-size: 10.5px; }
    .coverage-compare-row b { text-align: right; color: #0f172a; font-variant-numeric: tabular-nums; }
    .coverage-compare-track { height: 11px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .coverage-compare-track i { display: block; height: 100%; border-radius: 999px; background: var(--report-primary); }
    .coverage-compare-row.reference .coverage-compare-track i { background: #93c5fd; }
    .coverage-compare-track i.warn { background: #f59e0b; }
    .coverage-compare-track i.alert { background: #e11d48; }
    .coverage-compare-track i.ok { background: #0f766e; }
    .pill { border: 1px solid #cbd5e1; background: #f8fafc; color: #334155; padding: 3px 6px; text-align: center; font-weight: 800; font-size: 10.5px; }
    .pill.ok { border-color: #bbf7d0; background: #f0fdf4; color: #166534; }
    .pill.warn { border-color: #fde68a; background: #fffbeb; color: #92400e; }
    .pill.alert { border-color: #fecaca; background: #fff1f2; color: #be123c; }
    .guide-band { display: flex; justify-content: space-between; gap: 10px; margin-top: 12px; border: 1px dashed #cbd5e1; background: #fff; padding: 8px 10px; font-size: 12px; }
    .guide-band.compact { margin-top: 9px; padding: 7px 9px; }
    .guide-band span { color: #475569; }
    .guide-band strong { color: #0f172a; }
    .footnote { margin-top: 8px; color: #64748b; font-size: 10.5px; }
    .judgment-list { display: grid; gap: 5px; margin-top: 10px; }
    .judgment-list div { border: 1px solid #e2e8f0; background: #fff; color: #334155; padding: 6px 8px; font-size: 11px; }
    .grid { display: grid; grid-template-columns: 1fr; gap: 10px; margin-top: 16px; }
    .section { border: 1px solid #e2e8f0; padding: 12px; break-inside: avoid; }
    h2 { margin-bottom: 8px; color: #0f172a; font-size: 14px; }
    ul { margin: 0; padding-left: 18px; }
    li { margin: 3px 0; font-size: 12px; }
    .muted { color: #64748b; font-size: 12px; }
    .comment { margin-top: 16px; padding: 12px; border: 1px solid #fde68a; background: #fffbeb; break-inside: avoid; }
    .disclaimer { margin-top: 18px; color: #64748b; font-size: 10.5px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
    .closing-page { margin-top: 16px; min-height: 210px; border: 2px solid var(--report-primary); background: linear-gradient(135deg, #f8fbff 0%, var(--report-primary-soft) 100%); padding: 22px; break-inside: avoid; }
    .final-actions-page { break-before: page; page-break-before: always; }
    .final-action-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 12px; }
    .closing-badge { display: inline-block; border: 1px solid var(--report-primary-border); background: var(--report-primary-soft); color: var(--report-primary-dark); padding: 4px 8px; font-size: 11px; font-weight: 900; }
    .closing-page h2 { margin-top: 12px; color: #0f172a; font-size: 22px; }
    .closing-page p { margin-top: 10px; color: #334155; font-size: 13px; }
    .closing-next { margin-top: 16px; border: 1px solid #cbd5e1; background: #fff; padding: 12px; }
    .closing-next span { display: block; color: #64748b; font-size: 11px; font-weight: 900; }
    .closing-next strong { display: block; margin-top: 4px; color: #0f172a; font-size: 14px; }
    .closing-disclaimer { border-top: 1px solid #cbd5e1; padding-top: 10px; color: #64748b !important; font-size: 10.5px !important; }
    @media print {
      body { background: #fff; }
      .toolbar { display: none; }
      .page { width: auto; margin: 0; padding: 0; box-shadow: none; }
      .preview-pages { width: auto; margin: 0; }
      .preview-page { display: block !important; width: auto; height: auto; min-height: 260mm; margin: 0; padding: 0; box-shadow: none; overflow: visible; page-break-after: always; }
      .preview-page::after { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .preview-page-title { margin-top: 0; }
      .cover { min-height: 260mm; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .preview-page.cover-preview .cover { min-height: 260mm; }
      .content { padding: 20px 0 0; }
      .stat-card, .engine-section, .detail-section, .contract-detail-page, .closing-page { break-inside: auto; page-break-inside: auto; }
      .preview-page-title { break-after: avoid; page-break-after: avoid; }
      .contract-detail-page + .contract-detail-page { page-break-before: auto; }
    }
  </style>
</head>
<body>
${toolbarHtml}
<main class="page">
  <section class="cover">
    <div class="cover-brand">
      <img src="${escapeReportHtml(sgezLogo)}" alt="설계EZ 로고" />
      <span>설계EZ</span>
    </div>
    <div class="cover-main">
      <div class="cover-kicker">고객 상담용 분석 문서</div>
      <h1>${escapeReportHtml(reportTitle)}</h1>
      <p class="cover-subtitle">진료정보, 보험계약, 숨은보험금 조회 결과를 바탕으로 상담에 필요한 확인 내용을 정리한 리포트입니다.</p>
    </div>
    <div class="cover-footer">
      <div><span>고객명</span><strong>${escapeReportHtml(customerName)}</strong></div>
      <div><span>작성일</span><strong>${escapeReportHtml(report?.reportDate || '-')}</strong></div>
    </div>
  </section>
  <section class="content">
    ${hasSection('summary') ? `${reportOverallDashboardHtml(report)}
    ${reportExecutiveSummaryHtml(report)}` : ''}
    ${(hasSection('portfolio') || hasSection('premiumBenchmark')) ? reportInsurancePortfolioVisualHtml(report, { includePortfolio: hasSection('portfolio'), includePremiumBenchmark: hasSection('premiumBenchmark') }) : ''}
    ${reportCoverageDetailsHtml(report, { includeList: hasSection('contractList'), includeDetails: false })}
    ${reportCoverageDetailsHtml(report, { includeList: false, includeDetails: hasSection('contractDetails') })}
    ${hasSection('standardCoverage') ? reportStandardCoverageReviewHtml(report) : ''}
    ${hasSection('diseaseBalance') ? reportDiseaseCoverageBalanceHtml(report) : ''}
    ${hasSection('diagnosisScenarios') ? reportDiagnosisScenarioHighlightsHtml(report) : ''}
    ${reportIntegratedEvidenceHtml(report, { includeHealth: hasSection('healthEvidence'), includeHidden: hasSection('hiddenInsurance') })}
    ${hasSection('maturitySurvey') ? reportNarrativeSectionsHtml(report) : ''}
    ${hasSection('recommendations') ? reportFinalActionsHtml(report) : ''}
    ${hasSection('closing') ? reportClosingHtml(report) : ''}
  </section>
</main>
${previewScript}
${autoPrintScript}
</body>
</html>`
}

function validateIntegratedReportPrintHtml(html, selectedSections) {
  const normalizedSections = normalizeIntegratedReportSections(selectedSections)
  const warnings = []
  if (!html.includes('설계EZ통합분석 리포트')) warnings.push('리포트 제목이 확인되지 않습니다.')
  if (/SGEZ\s+Integrated\s+Report/i.test(html)) warnings.push('영문 기본 제목이 남아 있습니다.')
  if (/[�]/.test(html)) warnings.push('깨진 문자(�)가 포함되어 있습니다.')
  if (/\bundefined\b/.test(html)) warnings.push('미정의 텍스트가 포함되어 있을 수 있습니다.')
  INTEGRATED_REPORT_SECTION_OPTIONS
    .filter((section) => normalizedSections.includes(section.id))
    .forEach((section) => {
      if (section.requiredText && !html.includes(section.requiredText)) {
        warnings.push(`${section.label} 섹션이 출력 HTML에서 확인되지 않습니다.`)
      }
    })
  return warnings
}

/** 계약 목록 표시용: 음수 불가, 0원 허용 */
function formatRoundedWonNonNegative(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return '—'
  return `${Math.round(n).toLocaleString('ko-KR')}원`
}

/** 전체계약목록 납입기간 칼럼: "월납 / 20년 / 55세만기" → "20년" 등 년·개월만 표기 */
function shortenPaymentPeriodForList(raw) {
  if (raw == null || String(raw).trim() === '') return '—'
  const full = String(raw).trim()
  const parts = full.split(/\s*\/\s*/)
  for (const part of parts) {
    const p = part.trim()
    if (/세\s*만기/.test(p) && !/\d+\s*년/.test(p)) continue
    const y = p.match(/(\d+)\s*년/)
    if (y) return `${y[1]}년`
  }
  const y2 = full.match(/(\d+)\s*년/)
  if (y2) return `${y2[1]}년`
  const mo = full.match(/(\d+)\s*개월/)
  if (mo) return `${mo[1]}개월`
  return full
}

function startOfDayLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** 계약일 등 YYYY-MM-DD / YYYY.MM.DD / YYYY/MM/DD */
function parseFlexibleContractYmd(raw) {
  if (raw == null || raw === '') return null
  const s = String(raw).trim()
  const m = s.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})/)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const day = Number(m[3])
  const dt = new Date(y, mo - 1, day)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== day) return null
  return dt
}

function formatYmdDash(date) {
  if (!date) return ''
  const y = date.getFullYear()
  const mo = String(date.getMonth() + 1).padStart(2, '0')
  const da = String(date.getDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

function addMonthsSafe(date, months) {
  const d = new Date(date.getTime())
  const expected = d.getDate()
  d.setMonth(d.getMonth() + months)
  if (d.getDate() !== expected) d.setDate(0)
  return d
}

function addYearsSafe(date, years) {
  const d = new Date(date.getTime())
  const expected = d.getDate()
  d.setFullYear(d.getFullYear() + years)
  if (d.getDate() !== expected) d.setDate(0)
  return d
}

/** 보장기간 문자열에서 '~' 구간의 종료일만 추출 */
function parseCoveragePeriodRangeEndStr(coveragePeriod) {
  if (!coveragePeriod) return null
  const s = String(coveragePeriod).replace(/\s+/g, ' ')
  const range = s.match(/(\d{4}[.\-/]\d{1,2}[.\-/]\d{1,2})\s*[~\-～〜]\s*(\d{4}[.\-/]\d{1,2}[.\-/]\d{1,2})/)
  if (range) {
    const endRaw = range[2].replace(/\./g, '-').replace(/\//g, '-')
    const d = parseFlexibleContractYmd(endRaw)
    return d ? formatYmdDash(d) : range[2].trim()
  }
  return null
}

/** 납입 개월 수 (월납·연납 환산용). 종신 등만 있으면 null */
function parsePaymentHorizonMonths(paymentPeriod, coveragePeriod) {
  const blob = `${paymentPeriod || ''} ${coveragePeriod || ''}`
  if (/종신|평생/.test(blob)) return null

  let m = blob.match(/(\d+)\s*년\s*(?:납입|납)?/)
  if (!m) m = blob.match(/(?:납입\s*)?(?:기간\s*)?(\d+)\s*년(?:\s*납)?/)
  if (m) return Number(m[1]) * 12
  m = blob.match(/(\d+)\s*개월/)
  if (m) return Number(m[1])

  return null
}

function inferCoveragePaymentCycle(contract) {
  const blob = `${contract.paymentPeriod || ''} ${contract.coveragePeriod || ''}`
  if (/일시\s*납|일납|일시불/.test(blob)) return '일시납'
  if (/연\s*납|연납/.test(blob)) return '연납'
  return '월납'
}

function countPaymentsMade(contractStart, today, paymentCycle, totalUnits) {
  if (!contractStart || totalUnits == null || totalUnits <= 0) return 0
  const start = startOfDayLocal(contractStart)
  const end = startOfDayLocal(today)
  if (end < start) return 0

  let paid = 0
  let cursor = new Date(start.getTime())
  if (paymentCycle === '연납') {
    while (paid < totalUnits && cursor <= end) {
      paid++
      cursor = addYearsSafe(cursor, 1)
    }
    return paid
  }
  // 월납 · 일시납은 호출부에서 처리
  while (paid < totalUnits && cursor <= end) {
    paid++
    cursor = addMonthsSafe(cursor, 1)
  }
  return paid
}

/**
 * 행 표시와 합계에 공통으로 쓰는 추정 금액(원). 계산 불가 시 paidWon·futureWon·monthlyWon 은 null.
 */
function deriveCoverageContractFinancialNumeric(contract) {
  const premium = Number(contract.monthlyPremium)
  const premiumOk = Number.isFinite(premium) && premium > 0
  const contractStart = parseFlexibleContractYmd(contract.contractDate)
  const totalMonths = parsePaymentHorizonMonths(contract.paymentPeriod, contract.coveragePeriod)
  const paymentCycle = inferCoveragePaymentCycle(contract)
  const today = startOfDayLocal(new Date())

  const monthlyWon = premiumOk ? premium : null

  let paidWon = null
  let futureWon = null

  if (premiumOk && contractStart) {
    if (paymentCycle === '일시납') {
      const started = today >= startOfDayLocal(contractStart)
      paidWon = started ? premium : 0
      futureWon = started ? 0 : premium
    } else if (totalMonths != null && totalMonths > 0) {
      if (paymentCycle === '연납') {
        const totalYears = Math.max(1, Math.ceil(totalMonths / 12))
        const yearlyPremium = premium * 12
        const paidYears = countPaymentsMade(contractStart, today, '연납', totalYears)
        const remainingYears = Math.max(0, totalYears - paidYears)
        paidWon = paidYears * yearlyPremium
        futureWon = remainingYears * yearlyPremium
      } else {
        const paidMonths = countPaymentsMade(contractStart, today, '월납', totalMonths)
        const remainingMonths = Math.max(0, totalMonths - paidMonths)
        paidWon = paidMonths * premium
        futureWon = remainingMonths * premium
      }
    }
  }

  return { monthlyWon, paidWon, futureWon }
}

/** 보험사명 기준 생명/손해 구분 (요약 건수용 휴리스틱) */
function classifyCoverageInsurerProductLine(insurerName) {
  const s = String(insurerName || '').trim()
  if (!s) return 'unknown'
  if (/생명/.test(s)) return 'life'
  if (/손해|화재|해상|손보/.test(s)) return 'damage'
  return 'unknown'
}

function buildCoverageContractsPortfolioSummary(contracts) {
  const list = Array.isArray(contracts) ? contracts : []
  let life = 0
  let damage = 0
  let unknown = 0
  let totalMonthly = 0
  let totalPaidEst = 0
  let totalFutureEst = 0

  for (const c of list) {
    const kind = classifyCoverageInsurerProductLine(c.insurerName)
    if (kind === 'life') life++
    else if (kind === 'damage') damage++
    else unknown++

    const fin = deriveCoverageContractFinancialNumeric(c)
    if (fin.monthlyWon != null) totalMonthly += fin.monthlyWon
    if (fin.paidWon != null) totalPaidEst += fin.paidWon
    if (fin.futureWon != null) totalFutureEst += fin.futureWon
  }

  return {
    totalCount: list.length,
    lifeCount: life,
    damageCount: damage,
    unknownCount: unknown,
    totalMonthlyPremium: totalMonthly,
    totalPaidEstimate: totalPaidEst,
    totalFutureEstimate: totalFutureEst,
  }
}

/** 합계 행·요약용 (0원 표시 허용) */
function formatWonSummaryAmount(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return '—'
  return `${Math.round(n).toLocaleString('ko-KR')}원`
}

const COVERAGE_CHART_COLORS = ['#1e3a8a', '#0f766e', '#f97316', '#be123c', '#2563eb', '#7c3aed', '#64748b', '#ca8a04']

function formatWonCompact(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '0'
  if (n >= 100_000_000) {
    const v = n / 100_000_000
    return `${v >= 10 ? Math.round(v).toLocaleString('ko-KR') : v.toFixed(1)}억`
  }
  if (n >= 10_000) return `${Math.round(n / 10_000).toLocaleString('ko-KR')}만`
  return `${Math.round(n).toLocaleString('ko-KR')}`
}

function coverageContractChartName(contract, index) {
  const product = String(contract?.productName || '').trim()
  const insurer = String(contract?.insurerName || '').trim()
  const base = product || insurer || `계약 ${index + 1}`
  return base.length > 18 ? `${base.slice(0, 17)}…` : base
}

function deriveCoverageContractMaturityDate(contract) {
  const coverageEndStr = parseCoveragePeriodRangeEndStr(contract?.coveragePeriod)
  if (coverageEndStr) {
    const end = parseFlexibleContractYmd(coverageEndStr)
    if (end) return end
  }
  const contractStart = parseFlexibleContractYmd(contract?.contractDate)
  const totalMonths = parsePaymentHorizonMonths(contract?.paymentPeriod, contract?.coveragePeriod)
  if (contractStart && totalMonths != null && totalMonths > 0) return addMonthsSafe(contractStart, totalMonths)
  return null
}

function ageAtDate(birthYmd, targetDate) {
  const birth = parseFlexibleContractYmd(birthYmd)
  if (!birth || !targetDate) return null
  let age = targetDate.getFullYear() - birth.getFullYear()
  const md = targetDate.getMonth() - birth.getMonth()
  if (md < 0 || (md === 0 && targetDate.getDate() < birth.getDate())) age -= 1
  return Number.isFinite(age) ? age : null
}

function coverageMaturityAgeBand(age) {
  if (!Number.isFinite(age)) return { label: '연령 미산출', sort: 999 }
  if (age < 20) return { label: '20세 미만', sort: 10 }
  if (age >= 90) return { label: '90세 이상', sort: 90 }
  const decade = Math.floor(age / 10) * 10
  return { label: `${decade}대`, sort: decade }
}

function buildCoveragePortfolioChartData(contracts, birthYmd) {
  const list = Array.isArray(contracts) ? contracts : []

  const premiumRows = list
    .map((contract, index) => {
      const fin = deriveCoverageContractFinancialNumeric(contract)
      const value = fin.monthlyWon || 0
      return {
        id: contract?.id || `contract-${index}`,
        name: coverageContractChartName(contract, index),
        fullName: contract?.productName || contract?.insurerName || `계약 ${index + 1}`,
        insurerName: contract?.insurerName || '',
        value,
      }
    })
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)

  const premiumTop = premiumRows.slice(0, 7)
  const premiumOtherValue = premiumRows.slice(7).reduce((sum, row) => sum + row.value, 0)
  const monthlyPremiumMix = premiumOtherValue > 0
    ? [...premiumTop, { id: 'other', name: '기타', fullName: '기타 계약', insurerName: '', value: premiumOtherValue }]
    : premiumTop
  const monthlyPremiumTotal = premiumRows.reduce((sum, row) => sum + row.value, 0)

  const paymentRows = list
    .map((contract, index) => {
      const fin = deriveCoverageContractFinancialNumeric(contract)
      const paidWon = fin.paidWon || 0
      const futureWon = fin.futureWon || 0
      return {
        id: contract?.id || `payment-${index}`,
        name: coverageContractChartName(contract, index),
        fullName: contract?.productName || contract?.insurerName || `계약 ${index + 1}`,
        paidWon,
        futureWon,
        totalWon: paidWon + futureWon,
      }
    })
    .filter((row) => row.totalWon > 0)
    .sort((a, b) => b.totalWon - a.totalWon)
    .slice(0, 8)

  const birth = parseFlexibleContractYmd(birthYmd)
  const maturityMap = new Map()
  list.forEach((contract, index) => {
    const maturityDate = deriveCoverageContractMaturityDate(contract)
    if (!maturityDate) return
    const age = birth ? ageAtDate(birthYmd, maturityDate) : null
    const band = birth ? coverageMaturityAgeBand(age) : { label: `${maturityDate.getFullYear()}년`, sort: maturityDate.getFullYear() }
    const key = band.label
    if (!maturityMap.has(key)) {
      maturityMap.set(key, {
        name: key,
        sort: band.sort,
        count: 0,
        monthlyPremium: 0,
        contracts: [],
      })
    }
    const row = maturityMap.get(key)
    const fin = deriveCoverageContractFinancialNumeric(contract)
    row.count += 1
    row.monthlyPremium += fin.monthlyWon || 0
    row.contracts.push({
      name: coverageContractChartName(contract, index),
      fullName: contract?.productName || contract?.insurerName || `계약 ${index + 1}`,
      maturityDate: formatYmdDash(maturityDate),
      age,
      monthlyWon: fin.monthlyWon || 0,
    })
  })

  const maturityRows = Array.from(maturityMap.values()).sort((a, b) => a.sort - b.sort)

  return {
    monthlyPremiumMix,
    monthlyPremiumTotal,
    paymentRows,
    maturityRows,
    maturityBasis: birth ? 'AGE' : 'YEAR',
  }
}

const CONTRACT_ROLE_DEFINITIONS = [
  {
    id: 'cancer',
    label: '암 보장',
    badge: '암',
    pattern: /암|악성|신생물|항암|방사선|표적|유사암|소액암|고액암|전이암/i,
    summary: '암 진단비와 치료비 공백을 줄이는 역할이 큽니다.',
    checkPoint: '일반암, 유사암, 항암·방사선·수술 담보의 지급 조건과 감액기간을 확인하세요.',
  },
  {
    id: 'brainHeart',
    label: '뇌·심혈관 보장',
    badge: '3대질병',
    pattern: /뇌|뇌혈관|뇌졸중|심장|심혈관|심근|협심증|허혈|급성심근/i,
    summary: '뇌혈관·심혈관 진단과 치료 리스크를 보완하는 역할이 확인됩니다.',
    checkPoint: '뇌혈관질환과 뇌졸중, 허혈성심장질환과 급성심근경색의 보장 범위 차이를 확인하세요.',
  },
  {
    id: 'surgeryHospital',
    label: '입원·수술 보장',
    badge: '치료비',
    pattern: /입원|수술|시술|처치|상급병실|간호|간병|통원/i,
    summary: '입원·수술·치료 과정에서 반복 발생할 수 있는 비용을 보완하는 성격입니다.',
    checkPoint: '질병/상해 구분, 1회 지급 한도, 동일 질병 반복 지급 제한을 확인하세요.',
  },
  {
    id: 'realLoss',
    label: '실손·의료비',
    badge: '실손',
    pattern: /실손|실비|의료비|급여|비급여|입원통원|통원의료/i,
    summary: '실제 지출 의료비를 보전하는 기본 의료비 대응 역할입니다.',
    checkPoint: '세대별 실손 구조, 자기부담률, 비급여 특약과 기존 수령 이력을 함께 확인하세요.',
  },
  {
    id: 'driverLiability',
    label: '운전자·배상책임',
    badge: '운전자',
    pattern: /운전자|벌금|교통|변호사|형사합의|자동차사고|배상책임|일상생활|대인|대물/i,
    summary: '운전 중 사고나 배상책임 리스크를 줄이는 목적의 계약입니다.',
    checkPoint: '벌금, 변호사비, 교통사고처리지원금의 한도와 보장 대상 차량 범위를 확인하세요.',
  },
  {
    id: 'deathDisability',
    label: '사망·후유장해',
    badge: '가족보장',
    pattern: /사망|종신|정기|후유|장해|재해|상해사망/i,
    summary: '가족 생활비와 장해 발생 시 소득 공백을 대비하는 역할이 있습니다.',
    checkPoint: '사망보험금 목적, 수익자, 후유장해 지급률과 현재 가족 책임 범위를 함께 확인하세요.',
  },
  {
    id: 'child',
    label: '자녀·성장기 보장',
    badge: '자녀',
    pattern: /자녀|어린이|태아|키즈|주니어|성장|교육/i,
    summary: '자녀의 성장기 질병·상해와 보호자 부담을 함께 보는 계약입니다.',
    checkPoint: '자녀 연령 변화에 따라 성인기 보장으로 이어지는 담보와 만기 시점을 확인하세요.',
  },
  {
    id: 'savingsPension',
    label: '저축·연금',
    badge: '자산',
    pattern: /연금|저축|적립|환급|IRP|노후|생활자금/i,
    summary: '보장보다는 장기 자금 마련과 환급 구조 확인이 중요한 계약입니다.',
    checkPoint: '보장성 보험료와 저축성 보험료를 분리해 유지 목적과 해지환급 구조를 확인하세요.',
  },
]

function textMatchesContractRole(text, role) {
  if (!text) return false
  return role.pattern.test(String(text))
}

function buildContractRoleInsight(contract, rows = [], aiRole) {
  const productText = [
    contract?.productName,
    contract?.insuranceCategory,
    contract?.insurerName,
  ].filter(Boolean).join(' ')
  const roleStats = CONTRACT_ROLE_DEFINITIONS.map((role) => ({
    ...role,
    score: textMatchesContractRole(productText, role) ? 4 : 0,
    count: 0,
    amount: 0,
    samples: [],
  }))

  const fallbackAmountRows = []
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const text = [row.coverageGroup, row.coverageName].filter(Boolean).join(' ')
    const amount = reportSafeNumber(row.insuredAmount)
    fallbackAmountRows.push(row)
    roleStats.forEach((role) => {
      if (!textMatchesContractRole(text, role)) return
      role.count += 1
      role.amount += amount
      role.score += 10 + Math.min(6, Math.round(amount / 10_000_000))
      if (role.samples.length < 3) role.samples.push(row.coverageName || row.coverageGroup || role.label)
    })
  })

  const ranked = roleStats
    .filter((role) => role.score > 0)
    .sort((a, b) => b.score - a.score || b.amount - a.amount)
  const primary = ranked[0] || {
    id: 'general',
    label: contract?.insuranceCategory || '종합 보장',
    badge: contract?.insuranceCategory || '보장',
    summary: '여러 담보가 섞여 있어 종합적인 보장 점검이 필요한 계약입니다.',
    checkPoint: '담보별 지급 조건과 갱신 여부를 나눠서 유지 목적을 확인하세요.',
    samples: [],
  }
  const secondary = ranked.slice(1, 4)
  const topCoverages = fallbackAmountRows
    .slice()
    .sort((a, b) => reportSafeNumber(b.insuredAmount) - reportSafeNumber(a.insuredAmount))
    .slice(0, 4)
    .map((row) => ({
      name: row.coverageName || row.coverageGroup || '담보',
      amountText: row.insuredAmount ? formatCoverageAmountKorean(row.insuredAmount) : '-',
    }))
  const summary = aiRole?.summary || `이 계약은 ${primary.label}을 중심으로 구성된 계약입니다. ${primary.summary}`
  const checkPoints = reportPreferredList(aiRole?.checkPoints, [
    primary.checkPoint,
    /갱신/.test(String(contract?.renewalType || '')) ? '갱신형 담보가 있다면 향후 보험료 변동 가능성과 갱신 주기를 함께 확인하세요.' : '',
    secondary.length ? `${secondary.map((role) => role.label).join(', ')}도 함께 포함되어 있어 중복·보완 관계를 같이 점검하면 좋습니다.` : '',
  ]).slice(0, 3)

  return {
    primaryLabel: primary.label,
    primaryBadge: primary.badge,
    secondaryLabels: secondary.map((role) => role.label),
    summary,
    checkPoints,
    topCoverages,
    sampleNames: primary.samples || [],
  }
}

/**
 * 전체계약목록 표시용 파생값 (스키마에 만기·납입주기 필드 없음 → 텍스트·계약일 기반 추정)
 */
function deriveCoverageContractRowPresentation(contract) {
  const premium = Number(contract.monthlyPremium)
  const premiumOk = Number.isFinite(premium) && premium > 0
  const contractStart = parseFlexibleContractYmd(contract.contractDate)
  const totalMonths = parsePaymentHorizonMonths(contract.paymentPeriod, contract.coveragePeriod)
  const coverageEndStr = parseCoveragePeriodRangeEndStr(contract.coveragePeriod)
  const paymentCycle = inferCoveragePaymentCycle(contract)

  let maturityDisplay = coverageEndStr || '—'
  if (!coverageEndStr && contractStart && totalMonths != null && totalMonths > 0) {
    maturityDisplay = formatYmdDash(addMonthsSafe(contractStart, totalMonths))
  }

  const fin = deriveCoverageContractFinancialNumeric(contract)
  const paidDisplay = fin.paidWon != null ? formatRoundedWonNonNegative(fin.paidWon) : '—'
  const futureDisplay = fin.futureWon != null ? formatRoundedWonNonNegative(fin.futureWon) : '—'

  const cycleLabel =
    paymentCycle === '일시납' ? '일시납' :
      paymentCycle === '연납' ? '연납' :
        '월납'

  return {
    paymentCycleLabel: cycleLabel,
    paymentPeriodShort: shortenPaymentPeriodForList(contract.paymentPeriod),
    maturityDisplay,
    premiumDisplay: premiumOk ? formatWon(premium) : '—',
    paidPremiumDisplay: paidDisplay,
    futurePremiumDisplay: futureDisplay,
  }
}

const COVERAGE_GROUP_OPTIONS = [
  '',
  '암진단비',
  '유사암/소액암',
  '뇌혈관질환',
  '뇌졸중',
  '심장질환',
  '질병수술비',
  '상해수술비',
  '입원일당',
  '후유장해',
  '사망',
  '실손',
  '상해실손',
  '질병실손',
  '운전자',
  '배상책임',
  '골절',
  '화상',
]

/** 계약 단위 보험 구분(API: insuranceCategory). BE 화이트리스트와 동일 순서 유지 */
const COVERAGE_INSURANCE_CATEGORY_OPTIONS = ['실손', '종합보험', '운전자', '종신', '연금', '자동차', '암보험', '자녀']

function normalizeCoverageInsuranceCategory(raw) {
  const s = raw == null ? '' : String(raw).trim()
  if (!s) return ''
  return COVERAGE_INSURANCE_CATEGORY_OPTIONS.includes(s) ? s : ''
}

/** 상품명 키워드로 보험 구분 1차 추론(BE 허용값만). 명확하지 않으면 '' */
function inferCoverageInsuranceCategoryFromProductName(productNameRaw) {
  const name = String(productNameRaw ?? '').trim()
  if (!name) return ''
  const s = name.replace(/\s+/g, '')

  const looksAuto =
    /(자동차|교통|자차|대인배상|대물배상|물적사고|배상책임|무제한보장)/.test(s) ||
    /(티맵|tmap|마일리지|블랙박스)/i.test(s)
  if (looksAuto && !/운전자/.test(s)) return '자동차'

  if (/운전자/.test(s)) return '운전자'

  if (/실손|실비|의료실비|간편실손|표준실손|실손의료|입원통원|간병/.test(s)) return '실손'
  if (/암진단|암보험|유사암|소액암|항암|표적항암|고액암|전이암|암수술/.test(s)) return '암보험'
  if (/종신/.test(s)) return '종신'
  if (/연금|연금형|저축보험|연금저축|IRP/.test(s)) return '연금'
  if (/어린이|태아|자녀|키즈|주니어|교육자금|자녀보험/.test(s)) return '자녀'
  if (/종합|건강보험|유병자|참좋은|실버|미니보험/.test(s)) return '종합보험'

  return ''
}

/** 상품명 변경 시 구분 필드 동기화. 수동 선택은 유지(이전 추론값과 같을 때만 자동 따라감) */
function applyAutoInsuranceCategoryOnProductChange(prevProductName, nextProductName, currentCategory) {
  const cur = normalizeCoverageInsuranceCategory(currentCategory)
  const prevGuess = normalizeCoverageInsuranceCategory(inferCoverageInsuranceCategoryFromProductName(prevProductName))
  const nextGuess = normalizeCoverageInsuranceCategory(inferCoverageInsuranceCategoryFromProductName(nextProductName))

  if (!cur) return nextGuess
  if (prevGuess && cur === prevGuess) return nextGuess
  return undefined
}

/** 구분 미입력 건만 상품명 기반으로 채움 */
function inferCoverageInsuranceCategoriesMissing(contracts) {
  return (Array.isArray(contracts) ? contracts : []).map((contract) => {
    const inferred = normalizeCoverageInsuranceCategory(inferCoverageInsuranceCategoryFromProductName(contract.productName))
    if (!normalizeCoverageInsuranceCategory(contract.insuranceCategory) && inferred) {
      return { ...contract, insuranceCategory: inferred }
    }
    return contract
  })
}

/** 담보명·현재 그룹 기준 그룹 제안 (실손 세분·사망 등). 빈 문자열이면 유지 */
function suggestCoverageGroupFromName(rawName, currentGroup) {
  const name = String(rawName || '').trim()
  if (!name) return ''
  const g = String(currentGroup || '').trim()

  if (/질병수술비/.test(name) || /상해수술비/.test(name)) return ''

  if (/일반상해|전체상해/.test(name)) return '상해실손'
  if (/전체질병/.test(name)) return '질병실손'

  const lossContext = /실손|의료비|간병/.test(name) || g === '실손'
  const excludeOtherBenefit = /수술비|입원일당|입원\s*일당|진단비|후유/.test(name)

  if (lossContext && !excludeOtherBenefit) {
    if (/상해/.test(name)) return '상해실손'
    if (/질병/.test(name)) return '질병실손'
  }

  if (!g && /사망/.test(name)) return '사망'

  return ''
}

/** 서버 보장분석 파서가 담보 note에 넣는 출처 태그(CustomerCoverageAnalysisService와 동기화). 검수 메모 아님. */
const COVERAGE_NOTE_PARSER_TAGS = new Set([
  'KB_DETAIL_LIST',
  'KB_PRODUCT_MATRIX',
  'CONTRACT_SECTION_TABLE',
  'CONTRACT_COVERAGE_TABLE',
  'CONTRACT_SECTION_TEXT',
  'DB_DETAIL_ENROLLMENT',
])

function isCoverageParserProvenanceNote(note) {
  if (note == null || note === '') return false
  return COVERAGE_NOTE_PARSER_TAGS.has(String(note).trim())
}

/** 표시·입력용: 파서 태그는 빈 문자열로 취급해 사용자가 검수 메모를 직접 채우도록 함 */
function normalizeCoverageReviewNoteForUi(note) {
  if (note == null || note === '') return ''
  const s = String(note).trim()
  if (isCoverageParserProvenanceNote(s)) return ''
  return s
}

/** 저장 API용: 빈 값·파서 태그는 null */
function normalizeCoverageReviewNoteForApi(note) {
  const ui = normalizeCoverageReviewNoteForUi(note)
  return ui === '' ? null : ui
}

function stripParserNotesFromCoverageDraftContracts(contracts) {
  return (Array.isArray(contracts) ? contracts : []).map((contract) => ({
    ...contract,
    coverages: (Array.isArray(contract.coverages) ? contract.coverages : []).map((cv) => {
      const note = normalizeCoverageReviewNoteForUi(cv.note)
      const sug = suggestCoverageGroupFromName(cv.coverageNameRaw || cv.coverageNameNormalized || '', cv.coverageGroup)
      return {
        ...cv,
        note,
        ...(sug ? { coverageGroup: sug } : {}),
      }
    }),
  }))
}

function sanitizeCoverageContractsForSave(contracts) {
  return (Array.isArray(contracts) ? contracts : []).map((contract) => {
    const { match, ...rest } = contract || {}
    return {
      ...rest,
      insuranceCategory: normalizeCoverageInsuranceCategory(rest.insuranceCategory),
      targetContractId: rest.targetContractId ?? null,
      matchResolution: rest.matchResolution ?? null,
      coverages: (Array.isArray(rest.coverages) ? rest.coverages : []).map((cv) => ({
        ...cv,
        note: normalizeCoverageReviewNoteForApi(cv.note),
      })),
    }
  })
}

function normalizeCoverageContractsForAutoSave(contracts) {
  return sanitizeCoverageContractsForSave(
    stripParserNotesFromCoverageDraftContracts(
      inferCoverageInsuranceCategoriesMissing(contracts),
    ),
  )
}

function coverageDraftCoverageCount(contracts) {
  return (Array.isArray(contracts) ? contracts : [])
    .reduce((sum, contract) => sum + (Array.isArray(contract?.coverages) ? contract.coverages.length : 0), 0)
}

function coverageDraftContractMatchKey(value) {
  return String(value || '').replace(/[\s()[\].·_-]/g, '').toLowerCase()
}

function coverageDraftContractMatchScore(a, b) {
  if (!a || !b) return 0
  let score = 0
  const insurerA = coverageDraftContractMatchKey(a.insurerName)
  const insurerB = coverageDraftContractMatchKey(b.insurerName)
  const productA = coverageDraftContractMatchKey(a.productName)
  const productB = coverageDraftContractMatchKey(b.productName)
  if (insurerA && insurerB && (insurerA === insurerB || insurerA.includes(insurerB) || insurerB.includes(insurerA))) score += 4
  if (a.contractDate && b.contractDate && String(a.contractDate) === String(b.contractDate)) score += 4
  if (productA && productB && (productA === productB || productA.includes(productB) || productB.includes(productA))) score += 5
  return score
}

function findCoverageDraftMetadataSource(contract, candidates) {
  let best = null
  let bestScore = 0
  for (const candidate of Array.isArray(candidates) ? candidates : []) {
    const score = coverageDraftContractMatchScore(contract, candidate)
    if (score > bestScore) {
      best = candidate
      bestScore = score
    }
  }
  return bestScore >= 5 ? best : null
}

function fillCoverageDraftContractMetadata(target, source) {
  if (!target || !source) return target
  const next = { ...target }
  ;[
    'insurerName',
    'insuranceCategory',
    'productName',
    'policyNumberMasked',
    'contractDate',
    'paymentPeriod',
    'coveragePeriod',
    'renewalType',
    'monthlyPremium',
  ].forEach((field) => {
    const current = next[field]
    const incoming = source[field]
    if ((current == null || current === '') && incoming != null && incoming !== '') {
      next[field] = incoming
    }
  })
  return next
}

function mergeCoverageDraftContractMetadata(primary, fallback) {
  return (Array.isArray(primary) ? primary : []).map((contract) => (
    fillCoverageDraftContractMetadata(contract, findCoverageDraftMetadataSource(contract, fallback))
  ))
}

function selectCoverageContractsForAutoSave(draft) {
  const contracts = Array.isArray(draft?.contracts) ? draft.contracts : []
  const mappedContracts = Array.isArray(draft?.coverageMappedContracts) ? draft.coverageMappedContracts : []
  if (coverageDraftCoverageCount(mappedContracts) > coverageDraftCoverageCount(contracts)) {
    return mergeCoverageDraftContractMetadata(mappedContracts, contracts)
  }
  if (contracts.length === 0 && mappedContracts.length > 0) {
    return mappedContracts
  }
  return mergeCoverageDraftContractMetadata(contracts, mappedContracts)
}

function isCoverageDraftMatchUnresolved(contract) {
  if (!contract || contract.match?.status !== 'REVIEW') return false
  if (contract.matchResolution === 'NEW') return false
  if (contract.targetContractId != null && contract.targetContractId !== '') return false
  return true
}

function coverageDraftToJson(contracts) {
  return JSON.stringify(Array.isArray(contracts) ? contracts : [], null, 2)
}

function coverageSummaryProductKey(product) {
  const id = String(product?.contractId ?? '').trim()
  if (id && id !== '0') return `ID:${id}`
  const explicitKey = String(product?.productKey ?? '').trim()
  if (explicitKey) return explicitKey
  return `${String(product?.insurerName || '').trim()}|${String(product?.productName || '').trim()}`
}

function coverageSummaryProductOrder(product, index = 0) {
  const order = Number(product?.productOrder || product?.sourceColumnIndex || 0)
  return Number.isFinite(order) && order > 0 ? order : Number.MAX_SAFE_INTEGER - 1000 + index
}

function coverageSummaryShortLabel(value, fallback) {
  const text = String(value || fallback || '').trim()
  if (!text) return '항목'
  return text.length > 16 ? `${text.slice(0, 15)}…` : text
}

// eslint-disable-next-line no-unused-vars
function buildCoverageSummaryInsightData(report) {
  const insights = report?.portfolioInsights || {}
  const premiumRows = asArray(insights.premiumRows)
    .map((row, index) => {
      const name = row?.name || row?.productName || row?.insurerName || `계약 ${index + 1}`
      return {
        id: row?.key || `premium-${index}`,
        name: coverageSummaryShortLabel(name, `계약 ${index + 1}`),
        fullName: [row?.insurerName, name].filter(Boolean).join(' · ') || name,
        value: Math.max(0, Number(row?.monthlyPremium || 0)),
        ratio: Number(row?.ratio || 0),
      }
    })
    .filter((row) => row.value > 0)
  const maturityRows = asArray(insights.maturityRows)
    .map((row, index) => ({
      id: `${row?.name || 'maturity'}-${index}`,
      name: row?.name || '-',
      count: Math.max(0, Number(row?.count || 0)),
      monthlyPremium: Math.max(0, Number(row?.monthlyPremium || 0)),
      products: asArray(row?.products),
    }))
    .filter((row) => row.count > 0)
  const timelineAges = asArray(insights.timelineAges)
    .map((age) => Number(age))
    .filter((age) => Number.isFinite(age) && age > 0)
  let timelineMaxAmount = 0
  const coverageTimelineRows = asArray(insights.coverageTimelineRows)
    .map((row, index) => {
      const points = asArray(row?.points)
        .map((point) => {
          const amount = Math.max(0, Number(point?.amount || 0))
          timelineMaxAmount = Math.max(timelineMaxAmount, amount)
          return {
            age: Number(point?.age || 0),
            amount,
          }
        })
        .filter((point) => Number.isFinite(point.age) && point.age > 0)
      return {
        id: row?.coverageCode || `${row?.coverageName || 'coverage'}-${index}`,
        coverageGroup: row?.normalizedCoverageGroup || row?.coverageGroup || '',
        coverageName: row?.coverageName || `보장 ${index + 1}`,
        currentAmount: Math.max(0, Number(row?.currentAmount || 0)),
        points,
      }
    })
    .filter((row) => row.currentAmount > 0 || row.points.some((point) => point.amount > 0))
  return {
    monthlyPremiumTotal: Math.max(0, Number(insights.monthlyPremiumTotal || 0)),
    premiumRows,
    maturityRows,
    maturityBasis: insights.maturityBasis || '',
    timelineAges,
    coverageTimelineRows,
    timelineMaxAmount,
    hasAny: premiumRows.length > 0 || maturityRows.length > 0 || coverageTimelineRows.length > 0,
  }
}

function latestCoverageFileJob(jobs) {
  if (!Array.isArray(jobs)) return null
  return jobs.find((j) => j?.uploadedFileId) || null
}

function readinessTone(status) {
  if (status === 'ready') return 'border-emerald-200 bg-emerald-50 text-emerald-800'
  if (status === 'partial') return 'border-amber-200 bg-amber-50 text-amber-900'
  return 'border-slate-200 bg-slate-50 text-slate-600'
}

function readinessCardTone(row) {
  if (row?.group === '필수') {
    if (row.status === 'ready') return 'border-emerald-200 bg-emerald-50/80'
    if (row.status === 'partial') return 'border-amber-300 bg-amber-50'
    return 'border-rose-300 bg-rose-50'
  }
  if (row?.status === 'ready') return 'border-slate-200 bg-white'
  if (row?.status === 'partial') return 'border-amber-100 bg-amber-50/50'
  return 'border-slate-200 bg-slate-50/70'
}

function readinessBadgeTone(row) {
  if (row?.group === '필수' && row?.status === 'missing') return 'border-rose-200 bg-rose-100 text-rose-800'
  return readinessTone(row?.status)
}

function readinessStatusLabel(status) {
  if (status === 'ready') return '완료'
  if (status === 'partial') return '일부'
  return '부족'
}

function buildIntegratedReportReadiness({ latestHealth, latestHiddenInsurance, coverageContracts, coverageJobs, maturityRows, latestSurvey }) {
  const hiddenCompleted = latestHiddenInsurance?.status === 'COMPLETED'
  const coverageCount = (Array.isArray(coverageContracts) ? coverageContracts : [])
    .reduce((sum, contract) => sum + (Array.isArray(contract?.coverages) ? contract.coverages.length : 0), 0)
  const latestCoverageJob = latestCoverageFileJob(coverageJobs)
  const hasCoverageContracts = Array.isArray(coverageContracts) && coverageContracts.length > 0
  const hasHealthOrHidden = Boolean(latestHealth?.id) || hiddenCompleted
  const rows = [
    {
      key: 'coverage',
      group: '필수',
      title: '보험계약/담보',
      status: hasCoverageContracts ? 'ready' : latestCoverageJob ? 'partial' : 'missing',
      detail: hasCoverageContracts
        ? `계약 ${coverageContracts.length}건, 담보 ${coverageCount}개`
        : latestCoverageJob
          ? '보장분석 파일 있음'
          : '보장분석 업로드 또는 계약 등록 필요',
    },
    {
      key: 'health-hidden',
      group: '필수',
      title: '진료 또는 숨은보험금 자료',
      status: hasHealthOrHidden ? 'ready' : 'missing',
      detail: latestHealth?.id && hiddenCompleted
        ? '진료정보분석과 숨은보험금 조회 모두 있음'
        : latestHealth?.id
          ? '진료정보분석 있음'
          : hiddenCompleted
            ? '숨은보험금 조회 완료'
            : '진료정보분석 또는 숨은보험금 조회 필요',
    },
    {
      key: 'disease-stats',
      group: '권장',
      title: '질병통계 연계',
      status: latestHealth?.id && hasCoverageContracts ? 'ready' : hasHealthOrHidden || hasCoverageContracts ? 'partial' : 'missing',
      detail: latestHealth?.id && hasCoverageContracts
        ? '진료 KCD와 계약 담보를 통계 기준으로 비교 가능'
        : '진료정보와 계약이 함께 있어야 비교 품질이 높아짐',
    },
    {
      key: 'hidden-insurance',
      group: '권장',
      title: '숨은보험금 리포트',
      status: hiddenCompleted ? 'ready' : latestHiddenInsurance ? 'partial' : 'missing',
      detail: hiddenCompleted
        ? '조회 완료'
        : latestHiddenInsurance
          ? `조회 상태: ${hiddenLookupStatusLabel(latestHiddenInsurance.status)}`
          : '숨은보험금 조회 이력 없음',
    },
    {
      key: 'maturity',
      group: '선택',
      title: '만기/갱신 계약',
      status: Array.isArray(maturityRows) && maturityRows.length > 0 ? 'ready' : 'missing',
      detail: Array.isArray(maturityRows) && maturityRows.length > 0 ? `${maturityRows.length}건` : '만기계약 등록 시 갱신 전 상담 포인트 보강',
    },
    {
      key: 'survey',
      group: '선택',
      title: '상담 설문',
      status: latestSurvey?.id ? 'ready' : 'missing',
      detail: latestSurvey?.id ? `최근 설문 세션 #${latestSurvey.id}` : '설문 응답이 있으면 고객 니즈 반영 가능',
    },
  ]
  const required = rows.filter((row) => row.group === '필수')
  const readyRequired = required.filter((row) => row.status === 'ready').length
  const canCreate = readyRequired === required.length
  const readyAll = rows.filter((row) => row.status === 'ready').length
  return {
    rows,
    canCreate,
    readyRequired,
    requiredCount: required.length,
    readyAll,
    totalCount: rows.length,
    qualityLabel: canCreate
      ? readyAll >= 6 ? '정밀 통합 리포트 가능' : '기본 통합 리포트 가능'
      : '통합 리포트 준비 필요',
  }
}

export default function CustomerDetailPage() {
  const { id: idParam } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { hasAnyRole } = useAuth()
  const isOperator = hasAnyRole([Roles.OPERATOR])
  const customerId = Number(String(idParam || '').trim())
  const initialWorkTab = HASH_TO_WORK_TAB[location.hash] || 'integrated-report'
  const initialPageView = HASH_TO_WORK_TAB[location.hash] ? 'work' : 'info'

  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(true)
  const [activityPage, setActivityPage] = useState(0)
  const [activityLoading, setActivityLoading] = useState(false)
  const [err, setErr] = useState('')
  const [memoDraft, setMemoDraft] = useState('')
  const [memoSaving, setMemoSaving] = useState(false)
  const [anniversaryTitle, setAnniversaryTitle] = useState('')
  const [anniversaryDate, setAnniversaryDate] = useState('')
  const [anniversarySaving, setAnniversarySaving] = useState(false)
  const [anniversaryDeletingId, setAnniversaryDeletingId] = useState(null)
  const [aiMessageType, setAiMessageType] = useState('FIRST_CONSULT')
  const [aiMessageTone, setAiMessageTone] = useState('POLITE')
  const [aiMessageAnniversaryId, setAiMessageAnniversaryId] = useState('')
  const [aiMessageContext, setAiMessageContext] = useState('')
  const [aiMessageGenerating, setAiMessageGenerating] = useState(false)
  const [aiMessageDraft, setAiMessageDraft] = useState(null)
  const [aiMessageModalOpen, setAiMessageModalOpen] = useState(false)
  const [easyExplainGenerating, setEasyExplainGenerating] = useState('')
  const [easyExplainDraft, setEasyExplainDraft] = useState(null)
  const [easyExplainModalOpen, setEasyExplainModalOpen] = useState(false)
  const [riskChecking, setRiskChecking] = useState(false)
  const [riskCheckResult, setRiskCheckResult] = useState(null)
  const [riskCheckModalOpen, setRiskCheckModalOpen] = useState(false)
  const [profileName, setProfileName] = useState('')
  const [profileBirth, setProfileBirth] = useState('')
  const [profileGender, setProfileGender] = useState('')
  const [profilePhone, setProfilePhone] = useState('')
  const [profileRegion, setProfileRegion] = useState('')
  const [profileCustomerSegment, setProfileCustomerSegment] = useState('')
  const [profileAffiliation, setProfileAffiliation] = useState('')
  const [profileOccupation, setProfileOccupation] = useState('')
  const [profileHonorific, setProfileHonorific] = useState('')
  const [profileHomeAddress, setProfileHomeAddress] = useState('')
  const [profileCompanyAddress, setProfileCompanyAddress] = useState('')
  const [profileMonthlyIncome, setProfileMonthlyIncome] = useState('')
  const [profileIncomeBasis, setProfileIncomeBasis] = useState('')
  const [profileDualIncomeStatus, setProfileDualIncomeStatus] = useState('')
  const [profileCustomerEmail, setProfileCustomerEmail] = useState('')
  const [profileCustomerSource, setProfileCustomerSource] = useState('')
  const [profilePostcodeError, setProfilePostcodeError] = useState('')
  const [profileSaving, setProfileSaving] = useState(false)
  const [familyName, setFamilyName] = useState('')
  const [familyRelationship, setFamilyRelationship] = useState('')
  const [familyBirthDate, setFamilyBirthDate] = useState('')
  const [familyCohabiting, setFamilyCohabiting] = useState('')
  const [familyMemo, setFamilyMemo] = useState('')
  const [familyLinkedCustomerRefId, setFamilyLinkedCustomerRefId] = useState('')
  const [familyLinkedSearch, setFamilyLinkedSearch] = useState('')
  const [familyLinkCandidates, setFamilyLinkCandidates] = useState([])
  const [familySaving, setFamilySaving] = useState(false)
  const [familyDeletingId, setFamilyDeletingId] = useState(null)
  const [pageView, setPageView] = useState(initialPageView)
  const [sectionTab, setSectionTab] = useState(initialWorkTab)
  const [eventConsultationTab, setEventConsultationTab] = useState('assist')
  const [schedModal, setSchedModal] = useState(null)
  const [schedTitle, setSchedTitle] = useState('')
  const [schedDesc, setSchedDesc] = useState('')
  const [schedStart, setSchedStart] = useState('')
  const [schedEnd, setSchedEnd] = useState('')
  const [schedBusy, setSchedBusy] = useState(false)
  const [contactModal, setContactModal] = useState(null)
  const [contactAt, setContactAt] = useState('')
  const [contactContent, setContactContent] = useState('')
  const [contactBusy, setContactBusy] = useState(false)
  const [contactDraftGenerating, setContactDraftGenerating] = useState(false)
  const [contactDraftInfo, setContactDraftInfo] = useState(null)
  const contactIntentAppliedRef = useRef('')
  const [consultSaving, setConsultSaving] = useState(false)
  const [disclosureChecked, setDisclosureChecked] = useState({})
  const [disclosureEvidence, setDisclosureEvidence] = useState({})
  const [disclosureAutoRows, setDisclosureAutoRows] = useState([])
  const [disclosureAutoQuestions, setDisclosureAutoQuestions] = useState([])
  const [disclosureAutoNote, setDisclosureAutoNote] = useState('')
  const [disclosureAutoLoading, setDisclosureAutoLoading] = useState(false)
  const [questionPurpose, setQuestionPurpose] = useState('first')
  const [postConsultMemo, setPostConsultMemo] = useState('')
  const [integratedReportComment, setIntegratedReportComment] = useState('')
  const [integratedReportCustomerNote, setIntegratedReportCustomerNote] = useState('')
  const [integratedReportNextStep, setIntegratedReportNextStep] = useState('')
  const [integratedReportSections, setIntegratedReportSections] = useState(DEFAULT_INTEGRATED_REPORT_SECTION_IDS)
  const [integratedReportSectionModalOpen, setIntegratedReportSectionModalOpen] = useState(false)
  const [integratedReportSectionDraft, setIntegratedReportSectionDraft] = useState(DEFAULT_INTEGRATED_REPORT_SECTION_IDS)
  const [integratedReportGenerating, setIntegratedReportGenerating] = useState(false)
  const [integratedReportDraft, setIntegratedReportDraft] = useState(null)
  const [integratedReportError, setIntegratedReportError] = useState('')
  const [integratedReportRows, setIntegratedReportRows] = useState([])
  const [integratedReportRowsLoading, setIntegratedReportRowsLoading] = useState(false)
  const [integratedReportTemplateOptions, setIntegratedReportTemplateOptions] = useState([])
  const [integratedReportTemplateId, setIntegratedReportTemplateId] = useState('')
  const [integratedReportTemplateBusy, setIntegratedReportTemplateBusy] = useState(false)
  const [integratedReportHtmlTemplateOptions, setIntegratedReportHtmlTemplateOptions] = useState([])
  const [integratedReportHtmlTemplateId, setIntegratedReportHtmlTemplateId] = useState('')
  const [integratedReportHtmlBusy, setIntegratedReportHtmlBusy] = useState(false)
  const [coverageRuleSettingsOpen, setCoverageRuleSettingsOpen] = useState(false)
  const [coverageRuleSettingsLoading, setCoverageRuleSettingsLoading] = useState(false)
  const [coverageRuleSettings, setCoverageRuleSettings] = useState(null)
  const [coverageRuleTemplateId, setCoverageRuleTemplateId] = useState('')
  const [coverageRuleFilter, setCoverageRuleFilter] = useState('')
  const [coverageRuleApplying, setCoverageRuleApplying] = useState(false)
  const [coverageRuleSavingCode, setCoverageRuleSavingCode] = useState('')
  const [coverageRuleError, setCoverageRuleError] = useState('')
  const [diagnosisOverview, setDiagnosisOverview] = useState(null)
  const [diagnosisLoading, setDiagnosisLoading] = useState(false)
  const [diagnosisGenerating, setDiagnosisGenerating] = useState(false)
  const [diagnosisError, setDiagnosisError] = useState('')
  const [diagnosisSelectedCode, setDiagnosisSelectedCode] = useState('')
  const [diagnosisDraft, setDiagnosisDraft] = useState(null)
  const [diagnosisTemplateOptions, setDiagnosisTemplateOptions] = useState([])
  const [diagnosisTemplateId, setDiagnosisTemplateId] = useState('')
  const [diagnosisTemplateBusy, setDiagnosisTemplateBusy] = useState(false)
  const [diagnosisHtmlTemplateOptions, setDiagnosisHtmlTemplateOptions] = useState([])
  const [diagnosisHtmlTemplateId, setDiagnosisHtmlTemplateId] = useState('')
  const [diagnosisHtmlBusy, setDiagnosisHtmlBusy] = useState(false)
  const [diagnosisRuleModalOpen, setDiagnosisRuleModalOpen] = useState(false)
  const [diagnosisRuleRows, setDiagnosisRuleRows] = useState([])
  const [diagnosisRuleSelectedCode, setDiagnosisRuleSelectedCode] = useState('')
  const [diagnosisRuleFilter, setDiagnosisRuleFilter] = useState('')
  const [diagnosisRuleLoading, setDiagnosisRuleLoading] = useState(false)
  const [diagnosisRuleSavingCode, setDiagnosisRuleSavingCode] = useState('')
  const [diagnosisRuleError, setDiagnosisRuleError] = useState('')
  const [diagnosisTermsSearch, setDiagnosisTermsSearch] = useState({
    q: '',
    diseaseCode: '',
    scope: 'selected',
    contractId: '',
  })
  const [diagnosisTermsResult, setDiagnosisTermsResult] = useState(null)
  const [diagnosisTermsLoading, setDiagnosisTermsLoading] = useState(false)
  const [diagnosisTermsError, setDiagnosisTermsError] = useState('')

  const [scheduleRows, setScheduleRows] = useState([])
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [scheduleError, setScheduleError] = useState('')
  const [expandedOccKey, setExpandedOccKey] = useState(null)
  const [expListRow, setExpListRow] = useState(null)
  const [expDetail, setExpDetail] = useState(null)
  const [expDetailLoading, setExpDetailLoading] = useState(false)
  const [expMemoDraft, setExpMemoDraft] = useState('')
  const [expMemoSaving, setExpMemoSaving] = useState(false)

  const [cardCarouselIdx, setCardCarouselIdx] = useState(0)
  const [cardDeleting, setCardDeleting] = useState(false)
  const [cardPreviewUrl, setCardPreviewUrl] = useState('')
  const [cardWizardOpen, setCardWizardOpen] = useState(false)
  const [cardWizardStep, setCardWizardStep] = useState('front')
  const [cardDraftFrontId, setCardDraftFrontId] = useState('')
  const [cardDraftBackId, setCardDraftBackId] = useState('')
  const [cardWizardBusy, setCardWizardBusy] = useState(false)
  /** 파일 업로드와 별도로, Gemini 추출 API 호출 중임을 UI에 구분해 표시 */
  const [cardExtracting, setCardExtracting] = useState(false)
  const [cardDiffs, setCardDiffs] = useState([])
  const [cardExtracted, setCardExtracted] = useState(null)
  const [coverageLoading, setCoverageLoading] = useState(false)
  const [coverageErr, setCoverageErr] = useState('')
  const [coverageContracts, setCoverageContracts] = useState([])
  const [coverageJobs, setCoverageJobs] = useState([])
  const [coverageContractCandidates, setCoverageContractCandidates] = useState([])
  const [coverageCandidateBusyId, setCoverageCandidateBusyId] = useState(null)
  const [coverageFileId, setCoverageFileId] = useState('')
  const [coverageAnalyzing, setCoverageAnalyzing] = useState(false)
  const [coverageDraftJobId, setCoverageDraftJobId] = useState(null)
  const [coverageDraftContracts, setCoverageDraftContracts] = useState([])
  const [coverageDraftJson, setCoverageDraftJson] = useState('')
  const [coverageJsonOpen, setCoverageJsonOpen] = useState(false)
  const [coverageSaving, setCoverageSaving] = useState(false)
  const [coverageEditingContractId, setCoverageEditingContractId] = useState(null)
  const [coverageDeletingContractId, setCoverageDeletingContractId] = useState(null)
  const [coverageTermsLookupBusyId, setCoverageTermsLookupBusyId] = useState(null)
  const [coverageResetting, setCoverageResetting] = useState(false)
  const [coverageUploadedFileMeta, setCoverageUploadedFileMeta] = useState(null)
  const [coverageNameValidation, setCoverageNameValidation] = useState(null)
  const [coverageExtractionQuality, setCoverageExtractionQuality] = useState(null)
  const [coverageNameMismatchAcknowledged, setCoverageNameMismatchAcknowledged] = useState(false)
  const [coverageSavedTab, setCoverageSavedTab] = useState('all')
  const [coverageExcelBusy, setCoverageExcelBusy] = useState('')
  const [coverageSummaryReport, setCoverageSummaryReport] = useState(null)
  const [coverageSummaryLoading, setCoverageSummaryLoading] = useState(false)
  const [coverageSummarySaving, setCoverageSummarySaving] = useState(false)
  const [coverageSummaryBusy, setCoverageSummaryBusy] = useState('')
  const [coverageSummaryError, setCoverageSummaryError] = useState('')
  const [coverageSummaryTableOpen, setCoverageSummaryTableOpen] = useState(false)
  const [coverageSummaryReportExpanded, setCoverageSummaryReportExpanded] = useState(true)
  const [coverageSummaryTemplateOptions, setCoverageSummaryTemplateOptions] = useState([])
  const [coverageSummaryTemplateId, setCoverageSummaryTemplateId] = useState('')
  const [coverageSummaryHtmlTemplateOptions, setCoverageSummaryHtmlTemplateOptions] = useState([])
  const [coverageSummaryHtmlTemplateId, setCoverageSummaryHtmlTemplateId] = useState('')
  const [maturityRows, setMaturityRows] = useState([])
  const [maturityLoading, setMaturityLoading] = useState(false)
  const [maturityError, setMaturityError] = useState('')
  const [maturitySaving, setMaturitySaving] = useState(false)
  const [maturityExcelBusy, setMaturityExcelBusy] = useState('')
  const [maturityForm, setMaturityForm] = useState(emptyMaturityForm)
  /** 인카 3종확인서: 운영 화이트리스트·운영자 예외 (null이면 아직 조회 전) */
  const [afterContractAllowed, setAfterContractAllowed] = useState(null)
  const visibleSectionTabs = SECTION_TABS

  const replaceCustomerDetailHash = useCallback((hash) => {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: hash || '',
      },
      { replace: true },
    )
  }, [location.pathname, location.search, navigate])

  const selectCustomerPageView = useCallback((viewId) => {
    const nextView = viewId === 'work' ? 'work' : 'info'
    setPageView(nextView)
    replaceCustomerDetailHash(nextView === 'work' ? hashForWorkTab(sectionTab) : '')
  }, [replaceCustomerDetailHash, sectionTab])

  const selectCustomerWorkTab = useCallback((tabId) => {
    const nextTab = visibleSectionTabs.some((tab) => tab.id === tabId) ? tabId : 'integrated-report'
    setPageView('work')
    setSectionTab(nextTab)
    replaceCustomerDetailHash(hashForWorkTab(nextTab))
  }, [replaceCustomerDetailHash, visibleSectionTabs])

  const businessCardRows = useMemo(() => {
    const raw = detail?.businessCards
    if (!Array.isArray(raw)) return []
    return [...raw].sort((a, b) => (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0) || Number(a?.id) - Number(b?.id))
  }, [detail?.businessCards])

  const anniversaryRows = useMemo(() => {
    const raw = detail?.anniversaries
    if (!Array.isArray(raw)) return []
    return [...raw].sort((a, b) => {
      const da = daysUntilAnnualDate(a?.anniversaryDate)
      const db = daysUntilAnnualDate(b?.anniversaryDate)
      if (da == null && db == null) return Number(a?.id || 0) - Number(b?.id || 0)
      if (da == null) return 1
      if (db == null) return -1
      return da - db || Number(a?.id || 0) - Number(b?.id || 0)
    })
  }, [detail?.anniversaries])

  const familyRows = useMemo(() => {
    const raw = detail?.familyMembers
    return Array.isArray(raw) ? raw : []
  }, [detail?.familyMembers])
  const selectedFamilyLink = useMemo(
    () => familyLinkCandidates.find((item) => String(item?.id) === String(familyLinkedCustomerRefId)) || null,
    [familyLinkCandidates, familyLinkedCustomerRefId],
  )

  const coveragePortfolioSummary = useMemo(
    () => buildCoverageContractsPortfolioSummary(coverageContracts),
    [coverageContracts],
  )
  const coveragePortfolioChartData = useMemo(
    () => buildCoveragePortfolioChartData(coverageContracts, detail?.customer?.birthDate),
    [coverageContracts, detail?.customer?.birthDate],
  )
  const coverageContractCandidatePanelVisible = false
  const coverageSummaryInlineEditorVisible = false
  const coverageSummaryBasePdfProducts = useMemo(() => {
    const rows = asArray(coverageSummaryReport?.rows)
    const map = new Map()
    rows.forEach((row) => {
      asArray(row?.products).forEach((product) => {
        const key = coverageSummaryProductKey(product)
        if (!key || key === '|') return
        if (!map.has(key)) {
          map.set(key, {
            key,
            contractId: product?.contractId || null,
            productKey: product?.productKey || '',
            productOrder: product?.productOrder || product?.sourceColumnIndex || null,
            sourceColumnIndex: product?.sourceColumnIndex || null,
            insurerName: product?.insurerName || '',
            productName: product?.productName || '',
            contractDate: product?.contractDate || '',
            paymentPeriod: product?.paymentPeriod || '',
            coveragePeriod: product?.coveragePeriod || '',
            monthlyPremium: product?.monthlyPremium || 0,
          })
        }
      })
    })
    return Array.from(map.values()).sort((a, b) => coverageSummaryProductOrder(a) - coverageSummaryProductOrder(b))
  }, [coverageSummaryReport?.rows])
  const coverageSummaryIncludeNoDetailContracts = Boolean(coverageSummaryReport?.pdfIncludeNoDetailContracts)
  const coverageSummaryIncludedContractIds = useMemo(() => {
    const ids = new Set()
    coverageSummaryBasePdfProducts.forEach((product) => {
      if (product.contractId != null && product.contractId !== '') ids.add(String(product.contractId))
    })
    return ids
  }, [coverageSummaryBasePdfProducts])
  const coverageSummaryOmittedContracts = useMemo(
    () => coverageContracts.filter((contract) => (
      contract?.id
      && !coverageSummaryIncludedContractIds.has(String(contract.id))
      && asArray(contract.coverages).length === 0
    )),
    [coverageContracts, coverageSummaryIncludedContractIds],
  )
  const coverageSummaryNoDetailProducts = useMemo(
    () => coverageSummaryOmittedContracts.map((contract) => ({
      key: coverageSummaryProductKey({ contractId: contract.id }),
      contractId: contract.id,
      productKey: '',
      productOrder: null,
      sourceColumnIndex: null,
      insurerName: contract.insurerName || '',
      productName: contract.productName || '',
      contractDate: contract.contractDate || '',
      paymentPeriod: contract.paymentPeriod || contract.paymentPeriodShort || '',
      coveragePeriod: contract.coveragePeriod || contract.maturityDisplay || '',
      monthlyPremium: contract.monthlyPremium || 0,
      noDetailContract: true,
    })),
    [coverageSummaryOmittedContracts],
  )
  const coverageSummaryPdfProducts = useMemo(() => {
    if (!coverageSummaryIncludeNoDetailContracts) return coverageSummaryBasePdfProducts
    const map = new Map()
    coverageSummaryBasePdfProducts.forEach((product) => map.set(product.key, product))
    coverageSummaryNoDetailProducts.forEach((product) => {
      if (product.key && product.key !== '|') map.set(product.key, product)
    })
    return Array.from(map.values())
  }, [coverageSummaryBasePdfProducts, coverageSummaryIncludeNoDetailContracts, coverageSummaryNoDetailProducts])
  const coverageSummaryPdfProductOptionsVisible = coverageSummaryBasePdfProducts.length > 0
    || coverageSummaryOmittedContracts.length > 0
  const coverageSummaryPdfSelectedKeys = useMemo(
    () => asArray(coverageSummaryReport?.pdfSelectedProductKeys).map(String).filter(Boolean),
    [coverageSummaryReport?.pdfSelectedProductKeys],
  )
  const coverageSummaryPdfEffectiveSelectedKeys = useMemo(
    () => coverageSummaryPdfSelectedKeys.length > 0
      ? coverageSummaryPdfSelectedKeys
      : coverageSummaryPdfProducts.map((product) => product.key),
    [coverageSummaryPdfProducts, coverageSummaryPdfSelectedKeys],
  )
  const coverageSummaryPdfAllSelected = coverageSummaryPdfProducts.length > 0
    && coverageSummaryPdfProducts.every((product) => coverageSummaryPdfEffectiveSelectedKeys.includes(product.key))
  const coverageSummaryTableProducts = useMemo(
    () => coverageSummaryPdfProducts.filter((product) => coverageSummaryPdfEffectiveSelectedKeys.includes(product.key)),
    [coverageSummaryPdfProducts, coverageSummaryPdfEffectiveSelectedKeys],
  )
  const coverageSummaryTableOtherCount = Math.max(0, coverageSummaryPdfProducts.length - coverageSummaryTableProducts.length)
  const coverageSummaryTableProductColumnCount = coverageSummaryTableProducts.length + (coverageSummaryTableOtherCount > 0 ? 1 : 0)
  const coverageSummaryTableWidth = 72 + 240 + 148 + (coverageSummaryTableProductColumnCount * 152)
  const coverageSummaryTableRows = useMemo(
    () => asArray(coverageSummaryReport?.rows).filter((row) => row?.visible !== false),
    [coverageSummaryReport?.rows],
  )
  const coverageSummaryTableGroupSpans = useMemo(() => {
    const spans = new Map()
    let idx = 0
    while (idx < coverageSummaryTableRows.length) {
      const group = coverageSummaryTableRows[idx]?.coverageGroup || ''
      let next = idx + 1
      while (next < coverageSummaryTableRows.length && (coverageSummaryTableRows[next]?.coverageGroup || '') === group) {
        next += 1
      }
      spans.set(idx, next - idx)
      for (let inner = idx + 1; inner < next; inner += 1) spans.set(inner, 0)
      idx = next
    }
    return spans
  }, [coverageSummaryTableRows])
  const diagnosisTermsSelectedContractId = useMemo(() => {
    const selected = String(diagnosisTermsSearch.contractId || '')
    if (selected && coverageContracts.some((contract) => String(contract.id) === selected)) return selected
    return coverageContracts[0]?.id ? String(coverageContracts[0].id) : ''
  }, [coverageContracts, diagnosisTermsSearch.contractId])
  const diagnosisTermsSearchTerms = useMemo(
    () => buildDiseaseCoverageTerms(diagnosisTermsSearch.q, diagnosisTermsSearch.diseaseCode),
    [diagnosisTermsSearch.q, diagnosisTermsSearch.diseaseCode],
  )
  const diagnosisTermsRows = useMemo(() => {
    const resultContracts = asArray(diagnosisTermsResult?.contracts)
    return resultContracts.map((row) => {
      const apiContract = row?.contract || {}
      const savedContract = coverageContracts.find((contract) => String(contract.id) === String(apiContract.id)) || apiContract
      const apiMatchedCoverages = asArray(row?.matchedCoverages)
      const fallbackMatchedCoverages = asArray(savedContract.coverages)
        .filter((coverage) => coverageMatchesDiseaseTerms(coverage, diagnosisTermsSearchTerms))
        .slice(0, 8)
      const matchedCoverages = (apiMatchedCoverages.length ? apiMatchedCoverages : fallbackMatchedCoverages).slice(0, 8)
      const documents = asArray(row?.documents)
      return {
        contract: savedContract,
        documents,
        matchedCoverages,
        matchedChunkCount: Number(row?.matchedChunkCount || 0),
        termsLinked: Boolean(row?.termsLinked || savedContract?.termsLink?.linked),
        termsSearchStatus: row?.termsSearchStatus || '',
        termsSearchMessage: row?.termsSearchMessage || '',
        judgementStatus: row?.judgementStatus || 'LOW_RELEVANCE',
        judgementLabel: row?.judgementLabel,
      }
    })
  }, [coverageContracts, diagnosisTermsResult, diagnosisTermsSearchTerms])

  const latestCoverageFile = useMemo(() => latestCoverageFileJob(coverageJobs), [coverageJobs])
  const displayCoverageFileId = coverageFileId || coverageUploadedFileMeta?.fileId || latestCoverageFile?.uploadedFileId || ''
  const displayCoverageFileName = coverageUploadedFileMeta?.filename || '보장분석 파일'
  const latestCoverageAnalysisDateText = latestCoverageFile
    ? formatApiDateTimeDisplay(
      latestCoverageFile.completedAtDisplay || latestCoverageFile.createdAtDisplay,
      latestCoverageFile.completedAt || latestCoverageFile.createdAt,
    )
    : ''

  const loadDetail = useCallback(async (id, opts = {}) => {
    const actPage = Number.isFinite(opts.activityPage) ? opts.activityPage : 0
    setDetailLoading(true)
    setErr('')
    try {
      const res = await api.get(`/customer-management/customers/${id}`, {
        params: {
          reportPage: 0,
          reportSize: 10,
          sessionPage: 0,
          sessionSize: 10,
          activityPage: actPage,
          activitySize: ACTIVITY_PAGE_SIZE,
        },
      })
      setDetail(unwrap(res) || null)
      setActivityPage(actPage)
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '상세를 불러오지 못했습니다.')
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const loadActivityPage = useCallback(async (page) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const nextPage = Math.max(0, page)
    setActivityLoading(true)
    try {
      const res = await api.get(`/customer-management/customers/${customerId}`, {
        params: {
          reportPage: 0,
          reportSize: 10,
          sessionPage: 0,
          sessionSize: 10,
          activityPage: nextPage,
          activitySize: ACTIVITY_PAGE_SIZE,
        },
        __silent: true,
      })
      const data = unwrap(res)
      setDetail((prev) => (prev ? { ...prev, activities: data?.activities || null } : prev))
      setActivityPage(nextPage)
    } catch (e) {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '이력을 불러오지 못했습니다.', 'error') } catch { }
    } finally {
      setActivityLoading(false)
    }
  }, [customerId])

  const linkFireHistoryToCustomer = useCallback(async (historyId) => {
    if (!historyId || !customerId) return
    try {
      await api.post(`/fire/building-registry/history/${historyId}/link-customer`, { customerRefId: customerId })
      try { window?.toast?.show?.('화재보험설계 이력을 고객과 연결했습니다.', 'success') } catch { }
      loadDetail(customerId)
    } catch (e) {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '연결에 실패했습니다.', 'error') } catch { }
    }
  }, [customerId, loadDetail])

  const linkDesignRequestToCustomer = useCallback(async (requestId) => {
    if (!requestId || !customerId) return
    try {
      await api.post(`/requests/${requestId}/link-customer`, { customerRefId: customerId })
      try { window?.toast?.show?.('설계의뢰를 고객과 연결했습니다.', 'success') } catch { }
      loadDetail(customerId)
    } catch (e) {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '연결에 실패했습니다.', 'error') } catch { }
    }
  }, [customerId, loadDetail])

  const loadScheduleRows = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setScheduleLoading(true)
    setScheduleError('')
    try {
      const from = new Date()
      from.setFullYear(from.getFullYear() - 1)
      const to = new Date()
      to.setFullYear(to.getFullYear() + 1)
      const res = await api.get('/calendar/events', {
        params: {
          from: from.toISOString(),
          to: to.toISOString(),
          includeCancelled: false,
        },
      })
      const data = unwrap(res)
      const arr = Array.isArray(data) ? data : []
      const filtered = arr.filter((ev) => Number(ev.customerRefId) === customerId && !ev.cancelled)
      filtered.sort((a, b) => String(b.startAt).localeCompare(String(a.startAt)))
      setScheduleRows(filtered)
    } catch (e) {
      setScheduleError(e?.response?.data?.message || e?.message || '일정을 불러오지 못했습니다.')
      setScheduleRows([])
    } finally {
      setScheduleLoading(false)
    }
  }, [customerId])

  const loadCoverageContracts = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setCoverageLoading(true)
    setCoverageErr('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-contracts`, { __silent: true })
      const data = unwrap(res) || {}
      const contracts = Array.isArray(data.contracts) ? data.contracts : []
      const jobs = Array.isArray(data.analysisJobs) ? data.analysisJobs : []
      const candidates = Array.isArray(data.contractCandidates) ? data.contractCandidates : []
      setCoverageContracts(contracts)
      setCoverageJobs(jobs)
      setCoverageContractCandidates(candidates)
      return { contracts, analysisJobs: jobs, contractCandidates: candidates }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장분석 계약을 불러오지 못했습니다.')
      setCoverageErr(msg)
      setCoverageContracts([])
      setCoverageJobs([])
      setCoverageContractCandidates([])
      return null
    } finally {
      setCoverageLoading(false)
    }
  }, [customerId])

  const loadCoverageSummaryReport = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setCoverageSummaryLoading(true)
    setCoverageSummaryError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary`, { __silent: true })
      const data = unwrap(res) || null
      setCoverageSummaryReport(data)
      return data
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 리포트를 불러오지 못했습니다.')
      setCoverageSummaryError(msg)
      setCoverageSummaryReport(null)
      return null
    } finally {
      setCoverageSummaryLoading(false)
    }
  }, [customerId])

  const loadMaturityRows = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setMaturityLoading(true)
    setMaturityError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/maturity-policies`, { __silent: true })
      const data = unwrap(res)
      setMaturityRows(Array.isArray(data) ? data : [])
    } catch (e) {
      const msg = getApiErrorMessage(e, '만기·갱신 목록을 불러오지 못했습니다.')
      setMaturityError(msg)
      setMaturityRows([])
    } finally {
      setMaturityLoading(false)
    }
  }, [customerId])

  const loadIntegratedReports = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setIntegratedReportRowsLoading(true)
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/integrated-reports`, { __silent: true })
      const data = unwrap(res)
      const rows = Array.isArray(data) ? data : []
      setIntegratedReportRows(rows)
      return rows
    } catch {
      setIntegratedReportRows([])
      return []
    } finally {
      setIntegratedReportRowsLoading(false)
    }
  }, [customerId])

  const upsertIntegratedReportRow = useCallback((report) => {
    if (!report?.id) return
    const reportId = String(report.id)
    setIntegratedReportRows((prevRows) => {
      const rows = Array.isArray(prevRows) ? prevRows : []
      const existingIndex = rows.findIndex((row) => String(row?.id || '') === reportId)
      if (existingIndex >= 0) {
        return rows.map((row, index) => (
          index === existingIndex ? { ...row, ...report } : row
        ))
      }
      return [report, ...rows].slice(0, Math.max(3, rows.length || 3))
    })
  }, [])

  const loadCoverageRuleSettings = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return null
    setCoverageRuleSettingsLoading(true)
    setCoverageRuleError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/integrated-report/coverage-rule-settings`, { __silent: true })
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items.map(normalizeCoverageRuleSettingItem) : []
      const next = { ...data, items }
      setCoverageRuleSettings(next)
      const recommendedId = data.recommendedTemplate?.id ? String(data.recommendedTemplate.id) : ''
      setCoverageRuleTemplateId((prev) => prev || recommendedId || (Array.isArray(data.templateOptions) ? String(data.templateOptions[0]?.id || '') : ''))
      return next
    } catch (e) {
      setCoverageRuleSettings(null)
      setCoverageRuleError(getApiErrorMessage(e, '표준가입금액 설정을 불러오지 못했습니다.'))
      return null
    } finally {
      setCoverageRuleSettingsLoading(false)
    }
  }, [customerId])

  const openCoverageRuleSettings = async () => {
    setCoverageRuleSettingsOpen(true)
    if (!coverageRuleSettings) {
      await loadCoverageRuleSettings()
    }
  }

  const updateCoverageRuleSettingDraft = (ruleCode, field, value) => {
    setCoverageRuleSettings((prev) => ({
      ...(prev || {}),
      items: asArray(prev?.items).map((row) => (
        row.ruleCode === ruleCode ? { ...row, [field]: value } : row
      )),
    }))
  }

  const applyCoverageRuleTemplate = async () => {
    if (!coverageRuleTemplateId) {
      setCoverageRuleError('불러올 표준가입금액 템플릿을 선택해 주세요.')
      return
    }
    setCoverageRuleApplying(true)
    setCoverageRuleError('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/integrated-report/coverage-rule-settings/apply-template`,
        { templateId: Number(coverageRuleTemplateId) },
      )
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items.map(normalizeCoverageRuleSettingItem) : []
      setCoverageRuleSettings({ ...data, items })
      try { window?.toast?.show?.('표준가입금액 템플릿을 내 설정으로 불러왔습니다.', 'success') } catch { }
    } catch (e) {
      setCoverageRuleError(getApiErrorMessage(e, '표준가입금액 템플릿을 불러오지 못했습니다.'))
    } finally {
      setCoverageRuleApplying(false)
    }
  }

  const saveCoverageRuleSettingItem = async (row) => {
    if (!row?.ruleCode) return
    setCoverageRuleSavingCode(row.ruleCode)
    setCoverageRuleError('')
    try {
      const res = await api.patch(
        `/customer-management/customers/${customerId}/integrated-report/coverage-rule-settings/items/${encodeURIComponent(row.ruleCode)}`,
        coverageRuleSettingPayload(row),
      )
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items.map(normalizeCoverageRuleSettingItem) : []
      setCoverageRuleSettings({ ...data, items })
      try { window?.toast?.show?.('표준가입금액 항목을 저장했습니다.', 'success') } catch { }
    } catch (e) {
      setCoverageRuleError(getApiErrorMessage(e, '표준가입금액 항목을 저장하지 못했습니다.'))
    } finally {
      setCoverageRuleSavingCode('')
    }
  }

  const resetCoverageRuleSettings = async () => {
    if (!window.confirm('내 표준가입금액 설정을 초기화하고 운영자 템플릿 기준으로 되돌릴까요?')) return
    setCoverageRuleApplying(true)
    setCoverageRuleError('')
    try {
      const res = await api.post(`/customer-management/customers/${customerId}/integrated-report/coverage-rule-settings/reset`)
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items.map(normalizeCoverageRuleSettingItem) : []
      setCoverageRuleSettings({ ...data, items })
      try { window?.toast?.show?.('내 표준가입금액 설정을 초기화했습니다.', 'success') } catch { }
    } catch (e) {
      setCoverageRuleError(getApiErrorMessage(e, '표준가입금액 설정을 초기화하지 못했습니다.'))
    } finally {
      setCoverageRuleApplying(false)
    }
  }

  const loadOutputTemplateOptions = useCallback(async (reportCode, predicate = isImageOutputTemplate) => {
    try {
      const res = await api.get('/ops/report-templates', {
        params: { reportCode },
        __silent: true,
      })
      const data = unwrap(res)
      return (Array.isArray(data) ? data : []).filter(predicate)
    } catch {
      return []
    }
  }, [])

  const loadDiagnosisOverview = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setDiagnosisLoading(true)
    setDiagnosisError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/diagnosis-simulations/overview`, { __silent: true })
      const data = unwrap(res) || {}
      setDiagnosisOverview(data)
      const options = Array.isArray(data.scenarios) ? data.scenarios : []
      setDiagnosisSelectedCode((prev) => prev || options[0]?.code || '')
    } catch (e) {
      const msg = getApiErrorMessage(e, '진단시뮬레이션 정보를 불러오지 못했습니다.')
      setDiagnosisError(msg)
      setDiagnosisOverview(null)
    } finally {
      setDiagnosisLoading(false)
    }
  }, [customerId])

  const loadDiagnosisScenarioRules = useCallback(async () => {
    setDiagnosisRuleLoading(true)
    setDiagnosisRuleError('')
    try {
      const res = await api.get('/customer-management/diagnosis-simulations/scenario-rules', { __silent: true })
      const data = unwrap(res)
      const rows = Array.isArray(data) ? data : []
      setDiagnosisRuleRows(rows)
      setDiagnosisRuleSelectedCode((prev) => prev || rows[0]?.scenarioCode || '')
    } catch (e) {
      setDiagnosisRuleError(getApiErrorMessage(e, '진단시뮬레이션 룰을 불러오지 못했습니다.'))
      setDiagnosisRuleRows([])
    } finally {
      setDiagnosisRuleLoading(false)
    }
  }, [])

  const openDiagnosisRuleModal = async () => {
    setDiagnosisRuleModalOpen(true)
    if (diagnosisRuleRows.length === 0) {
      await loadDiagnosisScenarioRules()
    } else if (!diagnosisRuleSelectedCode) {
      setDiagnosisRuleSelectedCode(diagnosisRuleRows[0]?.scenarioCode || '')
    }
  }

  const updateDiagnosisRuleDraft = (scenarioCode, field, value) => {
    setDiagnosisRuleRows((rows) => rows.map((row) => (
      row.scenarioCode === scenarioCode ? { ...row, [field]: value } : row
    )))
  }

  const saveDiagnosisScenarioRule = async (row) => {
    if (!row?.scenarioCode) return
    setDiagnosisRuleSavingCode(row.scenarioCode)
    setDiagnosisRuleError('')
    try {
      const manWonToWon = (manWonValue) => Math.max(0, Math.round(Number(manWonValue || 0) * 10000))
      const payload = {
        title: row.title || '',
        diagnosisName: row.diagnosisName || '',
        category: row.category || '',
        expectedMedicalCost: manWonToWon(row.expectedMedicalCostManWon),
        outOfPocketCost: manWonToWon(row.outOfPocketCostManWon),
        recoveryLivingCost: manWonToWon(row.recoveryLivingCostManWon),
        summary: row.summary || '',
        statBasis: row.statBasis || '',
        sourceName: row.sourceName || '',
        sourceUrl: row.sourceUrl || '',
        coverageKeywords: row.coverageKeywords || '',
        gender: row.gender || '',
        minAge: row.minAge === '' || row.minAge == null ? null : Number(row.minAge),
        maxAge: row.maxAge === '' || row.maxAge == null ? null : Number(row.maxAge),
        displayOrder: Number(row.displayOrder || 0),
        active: row.active !== false,
        note: row.note || '',
      }
      const res = await api.patch(`/customer-management/diagnosis-simulations/scenario-rules/${encodeURIComponent(row.scenarioCode)}`, payload)
      const saved = unwrap(res)
      setDiagnosisRuleRows((rows) => rows.map((item) => item.scenarioCode === row.scenarioCode ? { ...item, ...saved } : item))
      await loadDiagnosisOverview()
    } catch (e) {
      setDiagnosisRuleError(getApiErrorMessage(e, '진단시뮬레이션 룰을 저장하지 못했습니다.'))
    } finally {
      setDiagnosisRuleSavingCode('')
    }
  }

  const resetDiagnosisScenarioRules = async () => {
    if (!window.confirm('진단시뮬레이션 룰을 기본값으로 복원할까요?')) return
    setDiagnosisRuleLoading(true)
    setDiagnosisRuleError('')
    try {
      const res = await api.post('/customer-management/diagnosis-simulations/scenario-rules/reset-defaults')
      const data = unwrap(res)
      const rows = Array.isArray(data) ? data : []
      setDiagnosisRuleRows(rows)
      setDiagnosisRuleSelectedCode(rows[0]?.scenarioCode || '')
      await loadDiagnosisOverview()
    } catch (e) {
      setDiagnosisRuleError(getApiErrorMessage(e, '기본 룰로 복원하지 못했습니다.'))
    } finally {
      setDiagnosisRuleLoading(false)
    }
  }

  useEffect(() => {
    if (!Number.isFinite(customerId) || customerId <= 0) {
      setErr('잘못된 고객 ID입니다.')
      setDetail(null)
      setDetailLoading(false)
      return
    }
    loadDetail(customerId)
  }, [customerId, loadDetail])

  useEffect(() => {
    const tab = HASH_TO_WORK_TAB[location.hash]
    if (tab) {
      setPageView('work')
      setSectionTab(tab)
      return
    }
    if (!location.hash) {
      setPageView('info')
    }
  }, [location.hash])

  /** 숨긴 레거시 탭이 남아도 현재 노출 탭으로 보정 */
  useEffect(() => {
    setSectionTab((t) => {
      if (t === 'events' || t === 'design') return 'health'
      return t
    })
  }, [])

  useEffect(() => {
    if (!isOperator) {
      setCoverageSummaryTemplateOptions([])
      setCoverageSummaryTemplateId('')
      setCoverageSummaryHtmlTemplateOptions([])
      setCoverageSummaryHtmlTemplateId('')
      setIntegratedReportTemplateOptions([])
      setIntegratedReportTemplateId('')
      setIntegratedReportHtmlTemplateOptions([])
      setIntegratedReportHtmlTemplateId('')
      setDiagnosisTemplateOptions([])
      setDiagnosisTemplateId('')
      setDiagnosisHtmlTemplateOptions([])
      setDiagnosisHtmlTemplateId('')
      return undefined
    }
    let cancelled = false
    Promise.all([
      loadOutputTemplateOptions(CUSTOMER_COVERAGE_SUMMARY_REPORT_CODE),
      loadOutputTemplateOptions(CUSTOMER_INTEGRATED_REPORT_CODE),
      loadOutputTemplateOptions(CUSTOMER_DIAGNOSIS_SIMULATION_REPORT_CODE),
      loadOutputTemplateOptions(CUSTOMER_COVERAGE_SUMMARY_REPORT_CODE, isHtmlOutputTemplate),
      loadOutputTemplateOptions(CUSTOMER_INTEGRATED_REPORT_CODE, isHtmlOutputTemplate),
      loadOutputTemplateOptions(CUSTOMER_DIAGNOSIS_SIMULATION_REPORT_CODE, isHtmlOutputTemplate),
    ]).then(([coverageOptions, integratedOptions, diagnosisOptions, coverageHtmlOptions, integratedHtmlOptions, diagnosisHtmlOptions]) => {
      if (cancelled) return
      setCoverageSummaryTemplateOptions(coverageOptions)
      setCoverageSummaryTemplateId((prev) => (
        prev && coverageOptions.some((item) => String(item.id) === String(prev))
          ? prev
          : preferredOutputTemplateId(coverageOptions)
      ))
      setCoverageSummaryHtmlTemplateOptions(coverageHtmlOptions)
      setCoverageSummaryHtmlTemplateId((prev) => (
        prev && coverageHtmlOptions.some((item) => String(item.id) === String(prev))
          ? prev
          : preferredOutputTemplateId(coverageHtmlOptions)
      ))
      setIntegratedReportTemplateOptions(integratedOptions)
      setIntegratedReportTemplateId((prev) => (
        prev && integratedOptions.some((item) => String(item.id) === String(prev))
          ? prev
          : preferredOutputTemplateId(integratedOptions)
      ))
      setIntegratedReportHtmlTemplateOptions(integratedHtmlOptions)
      setIntegratedReportHtmlTemplateId((prev) => (
        prev && integratedHtmlOptions.some((item) => String(item.id) === String(prev))
          ? prev
          : preferredOutputTemplateId(integratedHtmlOptions)
      ))
      setDiagnosisTemplateOptions(diagnosisOptions)
      setDiagnosisTemplateId((prev) => (
        prev && diagnosisOptions.some((item) => String(item.id) === String(prev))
          ? prev
          : preferredOutputTemplateId(diagnosisOptions)
      ))
      setDiagnosisHtmlTemplateOptions(diagnosisHtmlOptions)
      setDiagnosisHtmlTemplateId((prev) => (
        prev && diagnosisHtmlOptions.some((item) => String(item.id) === String(prev))
          ? prev
          : preferredOutputTemplateId(diagnosisHtmlOptions)
      ))
    })
    return () => {
      cancelled = true
    }
  }, [isOperator, loadOutputTemplateOptions])

  useEffect(() => {
    if (sectionTab !== 'schedule' && sectionTab !== 'contact' && sectionTab !== 'consultation') return
    loadScheduleRows()
  }, [sectionTab, loadScheduleRows])

  useEffect(() => {
    if (sectionTab !== 'contracts') return
    loadCoverageContracts()
    loadCoverageSummaryReport()
  }, [sectionTab, loadCoverageContracts, loadCoverageSummaryReport])

  useEffect(() => {
    if (sectionTab !== 'maturity') return
    loadMaturityRows()
  }, [sectionTab, loadMaturityRows])

  useEffect(() => {
    if (sectionTab !== 'diagnosis-simulation') return
    loadDiagnosisOverview()
  }, [sectionTab, loadDiagnosisOverview])

  useEffect(() => {
    if (!coverageContracts[0]?.id) return
    setDiagnosisTermsSearch((prev) => (
      prev.contractId ? prev : { ...prev, contractId: String(coverageContracts[0].id) }
    ))
  }, [coverageContracts])

  useEffect(() => {
    if (sectionTab !== 'integrated-report') return
    loadCoverageContracts()
    loadMaturityRows()
    loadIntegratedReports()
  }, [sectionTab, loadCoverageContracts, loadMaturityRows, loadIntegratedReports])

  useEffect(() => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    try {
      const saved = JSON.parse(window.localStorage.getItem(`sgez.integratedReportDraft.${customerId}`) || '{}')
      setIntegratedReportComment(typeof saved.comment === 'string' ? saved.comment : '')
      setIntegratedReportCustomerNote(typeof saved.customerNote === 'string' ? saved.customerNote : '')
      setIntegratedReportNextStep(typeof saved.nextStep === 'string' ? saved.nextStep : '')
      setIntegratedReportSections(normalizeIntegratedReportSections(saved.sections))
    } catch {
      setIntegratedReportComment('')
      setIntegratedReportCustomerNote('')
      setIntegratedReportNextStep('')
      setIntegratedReportSections(DEFAULT_INTEGRATED_REPORT_SECTION_IDS)
    }
  }, [customerId])

  useEffect(() => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    try {
      window.localStorage.setItem(`sgez.integratedReportDraft.${customerId}`, JSON.stringify({
        comment: integratedReportComment,
        customerNote: integratedReportCustomerNote,
        nextStep: integratedReportNextStep,
        sections: integratedReportSections,
      }))
    } catch {
      // local draft persistence is best effort only
    }
  }, [customerId, integratedReportComment, integratedReportCustomerNote, integratedReportNextStep, integratedReportSections])

  useEffect(() => {
    if (coverageSavedTab === 'all') return
    if (!coverageContracts.some((contract) => String(contract.id) === coverageSavedTab)) {
      setCoverageSavedTab('all')
    }
  }, [coverageContracts, coverageSavedTab])

  useEffect(() => {
    let cancelled = false
      ; (async () => {
        try {
          const res = await api.get('/after-contract/certificates/eligibility')
          const payload = unwrap(res)
          if (!cancelled) setAfterContractAllowed(Boolean(payload?.allowed))
        } catch {
          if (!cancelled) setAfterContractAllowed(false)
        }
      })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const m = detail?.customer?.memo
    setMemoDraft(m != null ? String(m) : '')
  }, [detail?.customer?.id, detail?.customer?.memo])

  useEffect(() => {
    const cu = detail?.customer
    if (!cu) return
    setProfileName(cu.customerName != null ? String(cu.customerName) : '')
    setProfileBirth(formatBirthYmdInput(cu.birthDate != null ? String(cu.birthDate) : ''))
    setProfileGender(cu.gender != null ? String(cu.gender) : '')
    setProfilePhone(formatKoreanPhoneInput(cu.customerPhone != null ? String(cu.customerPhone) : ''))
    setProfileRegion(cu.region != null ? String(cu.region) : '')
    setProfileCustomerSegment(cu.customerSegment != null ? String(cu.customerSegment) : '')
    setProfileAffiliation(cu.affiliation != null ? String(cu.affiliation) : '')
    setProfileOccupation(cu.occupation != null ? String(cu.occupation) : '')
    setProfileHonorific(cu.honorific != null ? String(cu.honorific) : '')
    setProfileHomeAddress(cu.homeAddress != null ? String(cu.homeAddress) : '')
    setProfileCompanyAddress(cu.companyAddress != null ? String(cu.companyAddress) : '')
    setProfileMonthlyIncome(incomeWonToMan(cu.monthlyIncome))
    setProfileIncomeBasis(cu.incomeBasis != null ? String(cu.incomeBasis) : '')
    setProfileDualIncomeStatus(cu.dualIncomeStatus != null ? String(cu.dualIncomeStatus) : '')
    setProfileCustomerEmail(cu.customerEmail != null ? String(cu.customerEmail) : '')
    setProfileCustomerSource(cu.customerSource != null ? String(cu.customerSource) : '')
    setProfilePostcodeError('')
  }, [
    detail?.customer,
    detail?.customer?.id,
    detail?.customer?.customerName,
    detail?.customer?.birthDate,
    detail?.customer?.gender,
    detail?.customer?.customerPhone,
    detail?.customer?.region,
    detail?.customer?.customerSegment,
    detail?.customer?.affiliation,
    detail?.customer?.occupation,
    detail?.customer?.honorific,
    detail?.customer?.homeAddress,
    detail?.customer?.companyAddress,
    detail?.customer?.monthlyIncome,
    detail?.customer?.incomeBasis,
    detail?.customer?.dualIncomeStatus,
    detail?.customer?.customerEmail,
    detail?.customer?.customerSource,
  ])

  const activityItems = useMemo(() => {
    const raw = detail?.activities?.items
    if (!Array.isArray(raw)) return []
    return raw.filter((a) => a && a.activityType !== 'CUSTOMER_MERGED')
  }, [detail])
  const activityTotalPages = useMemo(() => {
    const total = detail?.activities?.totalPages
    return typeof total === 'number' && total > 0 ? total : (activityItems.length > 0 ? 1 : 0)
  }, [detail?.activities?.totalPages, activityItems.length])
  const activityTotalElements = useMemo(() => {
    const total = detail?.activities?.totalElements
    return typeof total === 'number' ? total : activityItems.length
  }, [detail?.activities?.totalElements, activityItems.length])
  const contactRows = useMemo(() => scheduleRows.filter(isContactEvent), [scheduleRows])
  const consultationRows = useMemo(() => scheduleRows.filter(isConsultationEvent), [scheduleRows])
  const highlights = useMemo(() => detail?.activityHighlights || {}, [detail?.activityHighlights])
  const latestHealth = (detail?.healthReports?.items || [])[0] || null
  const latestHiddenInsurance = highlights.latestHiddenInsuranceLookup || null
  const recentClaimApplications = useMemo(
    () => (Array.isArray(highlights.recentClaimApplications) ? highlights.recentClaimApplications : []),
    [highlights.recentClaimApplications],
  )
  const latestClaimApplication = highlights.latestClaimApplication || recentClaimApplications[0] || null
  const latestSurvey = (detail?.surveySessions?.items || [])[0] || null
  const integratedReportReadiness = useMemo(() => buildIntegratedReportReadiness({
    latestHealth,
    latestHiddenInsurance,
    coverageContracts,
    coverageJobs,
    maturityRows,
    latestSurvey,
  }), [latestHealth, latestHiddenInsurance, coverageContracts, coverageJobs, maturityRows, latestSurvey])
  const integratedReportCommentCount = integratedReportComment.trim().length
    + integratedReportCustomerNote.trim().length
    + integratedReportNextStep.trim().length
  const integratedReportSelectedSectionCount = integratedReportSections.length
  const integratedReportDraftSectionCount = normalizeIntegratedReportSections(integratedReportSectionDraft).length

  const openIntegratedReportSectionModal = useCallback(() => {
    setIntegratedReportSectionDraft(normalizeIntegratedReportSections(integratedReportSections))
    setIntegratedReportError('')
    setIntegratedReportSectionModalOpen(true)
  }, [integratedReportSections])

  const toggleIntegratedReportSectionDraft = useCallback((sectionId) => {
    setIntegratedReportSectionDraft((prev) => {
      const current = normalizeIntegratedReportSections(prev)
      if (current.includes(sectionId)) {
        const next = current.filter((id) => id !== sectionId)
        return next.length ? next : current
      }
      return [...current, sectionId]
    })
  }, [])
  const salesPipeline = useMemo(() => buildCustomerSalesPipeline({
    customerId,
    highlights,
    latestSurvey,
    latestHealth,
    coverageContracts,
    maturityRows,
    scheduleRows,
    contactRows,
    consultationRows,
  }), [customerId, highlights, latestSurvey, latestHealth, coverageContracts, maturityRows, scheduleRows, contactRows, consultationRows])
  const nextActions = useMemo(() => {
    const arr = detail?.nextActions
    return Array.isArray(arr) ? arr : []
  }, [detail?.nextActions])
  const disclosureMemoLines = useMemo(() => {
    const autoLines = disclosureLinesFromAutoSummary(disclosureAutoRows, disclosureAutoQuestions)
    if (autoLines.length) return autoLines
    return disclosureLinesFromChecked(disclosureChecked, disclosureEvidence)
  }, [disclosureAutoQuestions, disclosureAutoRows, disclosureChecked, disclosureEvidence])
  const selectedQuestionPurpose = useMemo(
    () => QUESTION_PURPOSES.find((item) => item.id === questionPurpose) || QUESTION_PURPOSES[0],
    [questionPurpose],
  )
  const consultationQuestionLines = useMemo(() => {
    const lines = [...(selectedQuestionPurpose.questions || [])]
    if (profileOccupation) {
      lines.push(`${profileOccupation} 업무 특성상 운전, 현장 작업, 장시간 근무처럼 보험 가입 심사에 참고할 생활·직무 위험이 있나요?`)
    }
    if (memoDraft && /보험료|부담|예산|월/.test(memoDraft)) {
      lines.push('기존 메모에 보험료 부담 관련 내용이 있어요. 이번 상담에서 유지 가능한 월 보험료 범위를 다시 확인할까요?')
    }
    if (memoDraft && /가족|배우자|자녀|부모/.test(memoDraft)) {
      lines.push('가족 관련 니즈가 메모에 있어요. 가족력이나 가족 보장까지 함께 점검할 필요가 있나요?')
    }
    if (latestHealth?.id) {
      const date = formatApiDateTimeDisplay(latestHealth.createdAtDisplay, latestHealth.createdAt).slice(0, 10)
      lines.push(`최근 진료정보분석(${date}) 결과가 있어요. 가입 전 고지해야 할 진료·검사·투약 이력이 있는지 먼저 확인할까요?`)
    }
    if (coverageContracts.length > 0) {
      lines.push(`저장된 보장분석 계약 ${coverageContracts.length}건이 있어요. 기존 계약 중 유지할 담보와 보완할 담보를 구분해볼까요?`)
    } else {
      lines.push('저장된 보장분석 계약이 없어요. 기존 증권이나 보험사 앱 화면을 받아 현재 가입 내용을 먼저 확인할까요?')
    }
    if (consultationRows.length > 0) {
      lines.push('이전 상담 이력이 있어요. 지난 상담의 다음 액션이 완료됐는지 먼저 확인할까요?')
    }
    return lines.filter((line, idx, arr) => arr.indexOf(line) === idx).slice(0, 9)
  }, [coverageContracts.length, consultationRows.length, latestHealth, memoDraft, profileOccupation, selectedQuestionPurpose])
  const postConsultSummaryLines = useMemo(() => consultationMemoSummary(postConsultMemo), [postConsultMemo])

  const manAge = useMemo(() => koreanManAge(profileBirth), [profileBirth])
  const segmentSelectOptions = useMemo(() => segmentOptionsForStored(profileCustomerSegment), [profileCustomerSegment])
  const sourceSelectOptions = useMemo(() => sourceOptionsForStored(profileCustomerSource), [profileCustomerSource])

  const openProfileAddressSearch = (target) => {
    setProfilePostcodeError('')
    try {
      openDaumPostcode((data) => {
        if (!data) return
        const zone = String(data.zonecode || '').trim()
        const road = String(data.roadAddress || '').trim()
        const jibun = String(data.jibunAddress || '').trim()
        const address = [zone ? `(${zone})` : '', road || jibun].filter(Boolean).join(' ').trim()
        if (!address) return
        if (target === 'company') {
          setProfileCompanyAddress(address)
        } else {
          setProfileHomeAddress(address)
        }
      })
    } catch {
      setProfilePostcodeError('주소검색(카카오 우편번호) 스크립트를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.')
    }
  }

  const openScheduleModal = (kind = 'default', preset = {}) => {
    const nm = detail?.customer?.customerName || '(고객)'
    const { startLocal, endLocal } = defaultScheduleRangeLocal()
    setSchedModal({ id: customerId, name: nm })
    setSchedTitle(preset.title || (kind === 'consultation' ? `[상담] ${detail?.customer?.customerName || '고객'}` : `[고객] ${detail?.customer?.customerName || '고객'}`))
    setSchedDesc(preset.description || (kind === 'consultation' ? '상담 목적:\n상담 전 확인:\n후속 조치:' : ''))
    setSchedStart(startLocal)
    setSchedEnd(endLocal)
  }

  const submitSchedule = async () => {
    if (!schedModal?.id) return
    const title = (schedTitle || '').trim()
    if (!title) {
      try { window?.toast?.show?.('일정 제목을 입력해 주세요.', 'error') } catch { }
      return
    }
    let startAt
    let endAt
    try {
      startAt = new Date(schedStart).toISOString()
      endAt = new Date(schedEnd).toISOString()
    } catch {
      try { window?.toast?.show?.('시작·종료 시각을 확인해 주세요.', 'error') } catch { }
      return
    }
    if (!schedStart || !schedEnd || new Date(endAt) <= new Date(startAt)) {
      try { window?.toast?.show?.('종료 시각은 시작보다 이후여야 합니다.', 'error') } catch { }
      return
    }
    setSchedBusy(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title,
          description: (schedDesc || '').trim() || null,
          startAt,
          endAt,
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: true,
          reminderOffsetsMinutes: null,
          rrule: null,
          customerRefId: schedModal.id,
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('개인 일정으로 등록되었습니다. 고객에게 별도 알림은 전송되지 않습니다.', 'success') } catch { }
      setSchedModal(null)
      await loadDetail(customerId)
      if (sectionTab === 'schedule' || sectionTab === 'consultation') await loadScheduleRows()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '일정 등록에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setSchedBusy(false)
    }
  }

  const openContactModal = useCallback((preset = {}) => {
    const nm = detail?.customer?.customerName || '(고객)'
    setContactModal({
      id: customerId,
      name: nm,
      intent: preset.intent || null,
      relatedId: preset.relatedId || null,
    })
    setContactAt(defaultContactAtLocal())
    setContactContent(preset.content || '')
    setContactDraftInfo(preset.draftInfo || null)
  }, [customerId, detail?.customer?.customerName])

  const generateContactDraft = useCallback(async ({ intent = 'GENERAL_FOLLOWUP', relatedId = null, customContext = '', openModal = true } = {}) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    if (openModal) {
      selectCustomerWorkTab('contact')
      openContactModal({
        intent,
        relatedId,
        content: '연락 문구 초안을 생성하는 중입니다...',
        draftInfo: { loading: true, intentLabel: '연락 초안' },
      })
    }
    setContactDraftGenerating(true)
    setErr('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/contact-message-draft`,
        {
          intent,
          relatedId: relatedId ? Number(relatedId) : null,
          tone: 'POLITE',
          customContext,
        },
        { __silent: true },
      )
      const draft = unwrap(res) || {}
      const content = String(draft.recommendedContactContent || draft.detailedMessage || draft.shortMessage || '').trim()
      const genMode = draft.generationMode
      const ruleBased = genMode === 'RULE'
      setContactModal((prev) => prev || {
        id: customerId,
        name: detail?.customer?.customerName || '(고객)',
        intent,
        relatedId,
      })
      setContactContent(content)
      setContactDraftInfo({
        loading: false,
        intent,
        generationMode: genMode,
        intentLabel: ruleBased
          ? `템플릿 · ${draft.intentLabel || '연락 초안'}`
          : (draft.intentLabel || 'AI 연락 초안'),
        aiAvailable: draft.aiAvailable,
        riskNotice: draft.riskNotice || draft.sendPolicy,
        draft,
      })
      try {
        window?.toast?.show?.(
          ruleBased
            ? '템플릿 연락 초안을 반영했습니다. 저장 전 확인해 주세요.'
            : 'AI 연락 문구 초안을 연락 내용에 반영했습니다. 저장 전 확인해 주세요.',
          'success',
        )
      } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, 'AI 연락 문구 생성에 실패했습니다.')
      setErr(msg)
      setContactDraftInfo({ loading: false, error: msg, intentLabel: '연락 초안' })
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setContactDraftGenerating(false)
    }
  }, [customerId, detail?.customer?.customerName, openContactModal, selectCustomerWorkTab])

  useEffect(() => {
    if (!detail || !Number.isFinite(customerId) || customerId <= 0) return
    let sp
    try {
      sp = new URLSearchParams(location.search || '')
    } catch {
      return
    }
    const intent = String(sp.get('contactIntent') || '').trim()
    if (!intent) return
    const maturityPolicyId = String(sp.get('maturityPolicyId') || '').trim()
    const relatedId = maturityPolicyId ? Number(maturityPolicyId) : null
    const signature = `${customerId}:${intent}:${maturityPolicyId}`
    if (contactIntentAppliedRef.current === signature) return
    contactIntentAppliedRef.current = signature
    selectCustomerWorkTab('contact')
    generateContactDraft({
      intent,
      relatedId: Number.isFinite(relatedId) ? relatedId : null,
      openModal: true,
    })
  }, [customerId, detail, generateContactDraft, location.search, selectCustomerWorkTab])

  const runNextAction = (action) => {
    if (!action) return
    if (action.actionKind === 'contact') {
      const payload = action.actionPayload || {}
      generateContactDraft({
        intent: payload.intent || 'GENERAL_FOLLOWUP',
        relatedId: payload.relatedId || null,
        customContext: payload.content || '',
      })
      return
    }
    if (action.actionKind === 'schedule') {
      openScheduleModal('default', {
        title: `[고객] ${detail?.customer?.customerName || '고객'} 후속 연락`,
        description: '고객 출처:\n연락 목적:\n다음 조치:',
        ...(action.actionPayload || {}),
      })
    }
  }

  const runPipelineAction = (step) => {
    if (!step) return
    if (step.actionKind === 'contact') {
      generateContactDraft({
        intent: 'NEW_LEAD',
        customContext: '초기 연락 기록\n상담 가능 시간 확인\n다음 조치:',
      })
      return
    }
    if (step.actionKind === 'tab' && step.targetTab) {
      selectCustomerWorkTab(step.targetTab)
    }
  }

  const submitContact = async () => {
    if (!contactModal?.id) return
    const content = (contactContent || '').trim()
    if (!content) {
      try { window?.toast?.show?.('연락 내용을 입력해 주세요.', 'error') } catch { }
      return
    }
    let start
    try {
      start = new Date(contactAt)
      if (!contactAt || Number.isNaN(start.getTime())) throw new Error('invalid_date')
    } catch {
      try { window?.toast?.show?.('연락 일시를 확인해 주세요.', 'error') } catch { }
      return
    }
    const end = new Date(start.getTime() + 30 * 60 * 1000)
    setContactBusy(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title: `[연락] ${contactModal.name || '고객'}`,
          description: content,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: false,
          reminderOffsetsMinutes: [],
          rrule: null,
          customerRefId: contactModal.id,
          personalCategory: 'OTHER',
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('연락 이력이 등록되었습니다. 일정 메뉴에서도 확인할 수 있습니다.', 'success') } catch { }
      setContactModal(null)
      await loadDetail(customerId)
      if (sectionTab === 'schedule' || sectionTab === 'contact') await loadScheduleRows()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '연락 이력 등록에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setContactBusy(false)
    }
  }

  const saveConsultationHistory = async (titleSuffix, lines) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const content = (Array.isArray(lines) ? lines.join('\n') : String(lines || '')).trim()
    if (!content) {
      try { window?.toast?.show?.('상담 이력에 남길 내용이 없습니다.', 'error') } catch { }
      return
    }
    const nm = detail?.customer?.customerName || '고객'
    const start = new Date()
    start.setSeconds(0, 0)
    const end = new Date(start.getTime() + 30 * 60 * 1000)
    setConsultSaving(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title: `[상담] ${nm}${titleSuffix ? ` · ${titleSuffix}` : ''}`,
          description: content,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: false,
          reminderOffsetsMinutes: [],
          rrule: null,
          customerRefId: customerId,
          personalCategory: 'OTHER',
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('상담 이력에 남겼습니다.', 'success') } catch { }
      await loadDetail(customerId)
      if (sectionTab === 'consultation' || sectionTab === 'schedule') await loadScheduleRows()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '상담 이력 저장에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setConsultSaving(false)
    }
  }

  const fillPostConsultMemoFromCustomerMemo = () => {
    const value = String(memoDraft || '').trim()
    if (!value) {
      try { window?.toast?.show?.('불러올 고객 메모가 없습니다.', 'error') } catch { }
      return
    }
    setPostConsultMemo(value)
  }

  const fillPostConsultMemoFromRecentHistory = () => {
    const rows = [...consultationRows, ...contactRows]
      .sort((a, b) => String(b.startAt || '').localeCompare(String(a.startAt || '')))
      .slice(0, 5)
    if (!rows.length) {
      try { window?.toast?.show?.('불러올 상담/연락 이력이 없습니다.', 'error') } catch { }
      return
    }
    setPostConsultMemo(rows.map((row) => {
      const when = formatRangeLabel(row.startAt, row.endAt, row.allDay)
      const desc = String(row.description || row.memo || '').trim()
      return [`${row.title || '[이력]'} · ${when}`, desc].filter(Boolean).join('\n')
    }).join('\n\n'))
  }

  const applyDisclosureFromLatestHealth = async () => {
    if (!latestHealth?.id) {
      try { window?.toast?.show?.('연결된 진료정보분석 결과가 없습니다.', 'error') } catch { }
      return
    }
    setDisclosureAutoLoading(true)
    setErr('')
    try {
      const res = await api.get(`/health-reports/${latestHealth.id}`)
      const report = unwrap(res) || {}
      const summary = parseJsonMaybe(report.summaryJson) || report.summaryJson || {}
      const next = {}
      const nextEvidence = {}
      const autoSummary = buildDisclosureAutoSummary(summary)
      if (!autoSummary.rows.length) {
        setDisclosureAutoRows([])
        setDisclosureAutoQuestions([])
        setDisclosureAutoNote('최근 진료정보분석 결과에서 자동으로 표시할 핵심 고지 확인 내역을 찾지 못했습니다. 필요 시 기간별 체크리스트를 수동으로 확인해 주세요.')
        try { window?.toast?.show?.('자동으로 표시할 핵심 고지 확인 내역이 없습니다.', 'success') } catch { }
        return
      }
      autoSummary.rows.forEach((row) => {
        const basePeriod = row.periodLabel.startsWith('최근 3개월')
          ? '최근 3개월'
          : row.periodLabel.startsWith('최근 1년')
            ? '최근 1년'
            : row.periodLabel.includes('5년')
              ? '최근 5년'
              : row.periodLabel
        const group = DISCLOSURE_GROUPS.find((g) => g.title === basePeriod)
        if (!group) return
        const item = group.items.find((candidate) => {
          if (basePeriod === '최근 3개월') return /진찰|검사|치료|투약/.test(candidate)
          if (basePeriod === '최근 1년') {
            if (row.periodLabel.includes('검사')) return /수술|입원|정밀검사/.test(candidate)
            return /진단|반복|정밀검사/.test(candidate)
          }
          if (row.periodLabel.includes('입원/수술') || row.periodLabel.includes('7일+') || row.periodLabel.includes('30일+')) {
            return /입원|수술|7일|30일/.test(candidate)
          }
          if (row.periodLabel.includes('중대질병') || row.periodLabel.includes('6대질병')) {
            return /암|심장|뇌혈관|당뇨/.test(candidate)
          }
          if (row.flags.some((flag) => /복약|투약/.test(flag))) return /복약|추적/.test(candidate)
          return /입원|수술|7일|30일/.test(candidate)
        }) || group.items[0]
        const key = `${group.title}:${item}`
        next[key] = true
        nextEvidence[key] = [row.line.replace(/^- /, '')]
      })
      setDisclosureChecked((prev) => ({ ...prev, ...next }))
      setDisclosureEvidence((prev) => ({ ...prev, ...nextEvidence }))
      setDisclosureAutoRows(autoSummary.rows)
      setDisclosureAutoQuestions(autoSummary.questions)
      const reportDate = formatApiDateTimeDisplay(report.createdAtDisplay || latestHealth.createdAtDisplay, report.createdAt || latestHealth.createdAt).slice(0, 10)
      setDisclosureAutoNote(`최근 진료정보분석(${reportDate || '날짜 확인 필요'}) 결과를 기준으로 확인 필요성이 높은 항목을 자동 체크했습니다. 실제 고지 대상 여부는 고객에게 기간·진료일·치료 내용을 다시 확인해 주세요.`)
      try { window?.toast?.show?.('진료정보분석 결과를 바탕으로 고지 체크를 반영했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '진료정보분석 결과를 불러오지 못했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setDisclosureAutoLoading(false)
    }
  }

  const toggleScheduleMemoPanel = async (row) => {
    const occKey = row.occurrenceKey || `${row.id}`
    if (expandedOccKey === occKey) {
      setExpandedOccKey(null)
      setExpListRow(null)
      setExpDetail(null)
      setExpMemoDraft('')
      return
    }
    setExpandedOccKey(occKey)
    setExpListRow(row)
    setExpDetail(null)
    setExpMemoDraft('')
    setExpDetailLoading(true)
    setErr('')
    try {
      const res = await api.get(`/calendar/events/${row.id}`)
      const d = unwrap(res)
      setExpDetail(d)
      setExpMemoDraft(d?.description != null ? String(d.description) : '')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '일정 상세를 불러오지 못했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
      setExpandedOccKey(null)
      setExpListRow(null)
    } finally {
      setExpDetailLoading(false)
    }
  }

  const saveScheduleOccMemo = async () => {
    if (!expListRow || !expDetail) return
    if (expDetail.eventKind !== 'PERSONAL') {
      try { window?.toast?.show?.('조직 일정은 업무 캘린더에서 수정해 주세요.', 'error') } catch { }
      return
    }
    setExpMemoSaving(true)
    setErr('')
    try {
      const payload = buildCalendarPatchPayload(expDetail, expListRow, (expMemoDraft || '').trim() || null)
      await api.patch(`/calendar/events/${expListRow.id}`, payload, { __silent: true })
      try { window?.toast?.show?.('내용을 저장했습니다. 고객 상세와 업무 캘린더에서 확인됩니다.', 'success') } catch { }
      setExpandedOccKey(null)
      setExpListRow(null)
      setExpDetail(null)
      setExpMemoDraft('')
      await loadScheduleRows()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '메모 저장에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setExpMemoSaving(false)
    }
  }

  const setMaturityFormField = (field, value) => {
    setMaturityForm((prev) => ({ ...prev, [field]: value }))
  }

  const submitMaturityPolicy = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    if (!maturityForm.nextMaturityDate) {
      try { window?.toast?.show?.('다음 만기일을 입력해 주세요.', 'error') } catch { }
      return
    }
    setMaturitySaving(true)
    setMaturityError('')
    try {
      await api.post(
        `/customer-management/customers/${customerId}/maturity-policies`,
        {
          policyLine: maturityForm.policyLine,
          nextMaturityDate: maturityForm.nextMaturityDate,
          insurerName: maturityForm.insurerName.trim() || null,
          productLabel: maturityForm.productLabel.trim() || null,
          enrollmentChannel: maturityForm.enrollmentChannel.trim() || null,
          vehicleNumber: maturityForm.vehicleNumber.trim() || null,
          driverRestriction: maturityForm.driverRestriction.trim() || null,
          memo: null,
        },
        { __silent: true },
      )
      setMaturityForm(emptyMaturityForm)
      await loadMaturityRows()
      try { window?.toast?.show?.('만기·갱신 행과 안내 일정을 등록했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '만기·갱신 행 등록에 실패했습니다.')
      setMaturityError(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setMaturitySaving(false)
    }
  }

  const closeMaturityPolicy = async (row) => {
    if (!row?.id || !Number.isFinite(customerId) || customerId <= 0) return
    if (!window.confirm('이 만기·갱신 행을 종료하고 자동 등록된 안내 일정을 취소할까요?')) return
    setMaturitySaving(true)
    setMaturityError('')
    try {
      await api.delete(`/customer-management/customers/${customerId}/maturity-policies/${row.id}`, { __silent: true })
      await loadMaturityRows()
      try { window?.toast?.show?.('만기·갱신 행을 종료했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '만기·갱신 행 종료에 실패했습니다.')
      setMaturityError(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setMaturitySaving(false)
    }
  }

  const saveProfile = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const digits = (profilePhone || '').replace(/\D/g, '')
    if (digits.length > 0 && !(digits.length === 10 || digits.length === 11)) {
      setErr('연락처는 숫자 10~11자리로 입력해 주세요.')
      try { window?.toast?.show?.('연락처는 숫자 10~11자리로 입력해 주세요.', 'error') } catch { }
      return
    }
    const birthNorm = normalizeBirthDateForApi(profileBirth)
    if (!birthNorm.ok) {
      setErr(birthNorm.message)
      try { window?.toast?.show?.(birthNorm.message, 'error') } catch { }
      return
    }
    setProfileSaving(true)
    setErr('')
    try {
      await api.patch(
        `/customer-management/customers/${customerId}`,
        {
          customerName: (profileName || '').trim() || null,
          birthDate: birthNorm.value,
          gender: (profileGender || '').trim(),
          customerPhone: digits.length === 10 || digits.length === 11 ? digits : null,
          region: (profileRegion || '').trim(),
          customerSegment: (profileCustomerSegment || '').trim(),
          affiliation: (profileAffiliation || '').trim(),
          occupation: (profileOccupation || '').trim(),
          honorific: (profileHonorific || '').trim(),
          homeAddress: (profileHomeAddress || '').trim(),
          companyAddress: (profileCompanyAddress || '').trim(),
          monthlyIncome: incomeManToWon(profileMonthlyIncome),
          incomeBasis: (profileIncomeBasis || '').trim(),
          dualIncomeStatus: (profileDualIncomeStatus || '').trim(),
          customerEmail: (profileCustomerEmail || '').trim(),
          customerSource: (profileCustomerSource || '').trim(),
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('고객 정보를 저장했습니다. 저장만으로 고객에게 알림이 전송되지는 않습니다.', 'success') } catch { }
      await loadDetail(customerId)
    } catch (e) {
      const msg = getApiErrorMessage(e, '저장에 실패했습니다.')
      setErr(msg)
      // 409 등은 axios 인터셉터에서도 토스트함 — 중복 방지
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setProfileSaving(false)
    }
  }

  const saveMemo = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setMemoSaving(true)
    setErr('')
    try {
      await api.patch(`/customer-management/customers/${customerId}/memo`, { memo: memoDraft }, { __silent: true })
      try { window?.toast?.show?.('메모가 저장되었습니다. 고객 메모는 내부 관리용이며 고객에게 알림이 가지 않습니다.', 'success') } catch { }
      await loadDetail(customerId)
    } catch (e) {
      const msg = getApiErrorMessage(e, '메모 저장에 실패했습니다.')
      setErr(msg)
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setMemoSaving(false)
    }
  }

  const recoverIntegratedReportAfterTimeout = async (previousLatestId, selectedSections) => {
    const previousId = previousLatestId == null ? '' : String(previousLatestId)
    const delays = [0, 1200, 2500, 4500, 7000, 10000]
    for (const delay of delays) {
      if (delay > 0) await waitMs(delay)
      try {
        const listRes = await api.get(
          `/customer-management/customers/${customerId}/integrated-reports`,
          { __silent: true, __suppressErrorPageRedirect: true },
        )
        const rows = Array.isArray(unwrap(listRes)) ? unwrap(listRes) : []
        setIntegratedReportRows(rows)
        const latest = rows[0] || null
        if (previousId && String(latest?.id || '') === previousId) continue
        if (!latest?.id) continue
        const detailRes = await api.get(
          `/customer-management/customers/${customerId}/integrated-reports/${latest.id}`,
          { __silent: true, __suppressErrorPageRedirect: true },
        )
        const report = unwrap(detailRes) || null
        if (!report) continue
        setIntegratedReportSections(integratedReportOutputSectionsFor(report, selectedSections))
        setIntegratedReportDraft(report)
        return report
      } catch {
        // 생성 요청은 백그라운드에서 계속 완료될 수 있으므로 다음 확인 주기로 넘긴다.
      }
    }
    return null
  }

  const pollIntegratedReportGeneration = async (reportId, selectedSections) => {
    const id = String(reportId || '').trim()
    if (!id) return null
    const delays = [800, 1200, 1600, 2200, 3000, 4000, 5000, 6500, 8000, 10000, 10000, 10000]
    for (const delay of delays) {
      await waitMs(delay)
      try {
        const res = await api.get(
          `/customer-management/customers/${customerId}/integrated-reports/${encodeURIComponent(id)}`,
          { __silent: true, __suppressErrorPageRedirect: true },
        )
        const report = unwrap(res) || null
        const status = integratedReportStatusValue(report?.status)
        upsertIntegratedReportRow(report)
        if (status === 'FAILED') {
          const message = report?.errorMessage || '통합 리포트 생성에 실패했습니다. 잠시 후 다시 시도해주세요.'
          setIntegratedReportError(message)
          try { window?.toast?.show?.(message, 'error', 5200) } catch { }
          return null
        }
        if (integratedReportIsReady(report)) {
          setIntegratedReportSections(integratedReportOutputSectionsFor(report, selectedSections))
          setIntegratedReportDraft(report)
          return report
        }
      } catch {
        // 일시적인 조회 실패는 다음 polling 주기에서 다시 확인한다.
      }
    }
    setIntegratedReportError('통합 리포트 생성이 계속 진행 중입니다. 최근 생성 이력을 새로고침해 완료 여부를 확인해주세요.')
    return null
  }

  const generateIntegratedReport = async (sectionsOverride = null) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const selectedSections = normalizeIntegratedReportSections(sectionsOverride || integratedReportSections)
    const previousLatestId = integratedReportRows?.[0]?.id
    setIntegratedReportGenerating(true)
    setIntegratedReportError('')
    try {
      const res = await api.post(`/customer-management/customers/${customerId}/integrated-report`, {
        internalComment: integratedReportComment,
        customerNote: integratedReportCustomerNote,
        nextStep: integratedReportNextStep,
        sections: selectedSections,
      }, { __silent: true, __suppressErrorPageRedirect: true })
      const report = unwrap(res) || null
      setIntegratedReportSections(selectedSections)
      const status = integratedReportStatusValue(report?.status)
      if (report?.id && (status === 'PENDING' || status === 'RUNNING')) {
        setIntegratedReportDraft(null)
        upsertIntegratedReportRow(report)
        setIntegratedReportError('통합 리포트 생성을 시작했습니다. 완료되면 자동으로 불러옵니다.')
        try { window?.toast?.show?.('통합 리포트 생성을 시작했습니다.', 'info', 3600) } catch { }
        const completed = await pollIntegratedReportGeneration(report.id, selectedSections)
        if (completed) {
          setIntegratedReportError('')
          try { window?.toast?.show?.('통합 리포트 생성이 완료되었습니다.', 'success') } catch { }
        }
        return
      }
      if (integratedReportIsReady(report)) {
        setIntegratedReportDraft(report || null)
        upsertIntegratedReportRow(report)
        try { window?.toast?.show?.('통합 리포트를 생성했습니다.', 'success') } catch { }
        return
      }
      upsertIntegratedReportRow(report)
      setIntegratedReportError('통합 리포트 생성 상태를 확인할 수 없습니다. 최근 생성 이력을 새로고침해 주세요.')
    } catch (e) {
      if (e?.response?.status === 504) {
        const waitingMessage = '리포트 생성 응답이 지연되어 생성 이력을 확인하고 있습니다. 잠시만 기다려주세요.'
        setIntegratedReportError(waitingMessage)
        try { window?.toast?.show?.(waitingMessage, 'info', 5200) } catch { }
        const recovered = await recoverIntegratedReportAfterTimeout(previousLatestId, selectedSections)
        if (recovered) {
          setIntegratedReportError('')
          try { window?.toast?.show?.('통합 리포트 생성이 완료되어 결과를 불러왔습니다.', 'success', 4200) } catch { }
          return
        }
        const timeoutMessage = '리포트 생성 응답이 지연되고 있습니다. 생성이 완료됐을 수 있으니 최근 생성 이력을 새로고침해 확인해주세요.'
        setIntegratedReportError(timeoutMessage)
        try { window?.toast?.show?.(timeoutMessage, 'warning', 6200) } catch { }
        return
      }
      const msg = getApiErrorMessage(e, '통합 리포트 생성에 실패했습니다.')
      setIntegratedReportError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setIntegratedReportGenerating(false)
    }
  }

  const confirmIntegratedReportGenerate = () => {
    const selectedSections = normalizeIntegratedReportSections(integratedReportSectionDraft)
    setIntegratedReportSections(selectedSections)
    setIntegratedReportSectionModalOpen(false)
    generateIntegratedReport(selectedSections)
  }

  const integratedReportOutputSectionsFor = (report, fallback = integratedReportSections) => (
    normalizeIntegratedReportSections(report?.selectedSections || report?.advisorComments?.sections || fallback)
  )

  const writeIntegratedReportPopup = (popupWindow, report, options = {}) => {
    const selectedSections = normalizeIntegratedReportSections(options.selectedSections || integratedReportSections)
    const html = buildIntegratedReportPrintHtml(report, {
      autoPrint: Boolean(options.autoPrint),
      showToolbar: true,
      selectedSections,
    })
    const warnings = validateIntegratedReportPrintHtml(html, selectedSections)
    if (warnings.length) {
      try { window?.toast?.show?.(`리포트 출력 점검: ${warnings[0]}`, 'warning', 5000) } catch { }
      // eslint-disable-next-line no-console
      console.warn('[integrated-report-print-qa]', warnings)
    }
    popupWindow.document.open()
    popupWindow.document.write(html)
    popupWindow.document.close()
    popupWindow.focus()
  }

  const openIntegratedReport = async (reportId, options = {}) => {
    if (!Number.isFinite(customerId) || customerId <= 0 || !reportId) return
    let popupWindow = null
    if (options.popup) {
      popupWindow = window.open('about:blank', '_blank', 'width=980,height=760')
      if (!popupWindow) {
        try { window?.toast?.show?.('팝업 차단을 해제한 뒤 다시 시도해주세요.', 'error', 5000) } catch { }
        return
      }
      popupWindow.document.open()
      popupWindow.document.write('<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>통합 리포트</title></head><body style="font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Noto Sans KR,sans-serif;padding:32px;color:#334155;">통합 리포트를 불러오는 중입니다.</body></html>')
      popupWindow.document.close()
      popupWindow.focus()
    }
    setIntegratedReportError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/integrated-reports/${reportId}`)
      const report = unwrap(res) || null
      upsertIntegratedReportRow(report)
      if (!integratedReportIsReady(report)) {
        const status = integratedReportStatusValue(report?.status)
        const msg = status === 'FAILED'
          ? (report?.errorMessage || '통합 리포트 생성에 실패했습니다. 다시 생성해주세요.')
          : `${integratedReportStatusLabel(status)}입니다. 완료 후 리포트를 볼 수 있습니다.`
        setIntegratedReportError(msg)
        if (popupWindow) {
          popupWindow.document.open()
          popupWindow.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>통합 리포트</title></head><body style="font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Noto Sans KR,sans-serif;padding:32px;color:#334155;">${escapeReportHtml(msg)}</body></html>`)
          popupWindow.document.close()
        }
        try { window?.toast?.show?.(msg, status === 'FAILED' ? 'error' : 'info', 4200) } catch { }
        return
      }
      setIntegratedReportDraft(report)
      if (popupWindow && report) {
        const reportSections = integratedReportOutputSectionsFor(report)
        setIntegratedReportSections(reportSections)
        writeIntegratedReportPopup(popupWindow, report, { autoPrint: false, selectedSections: reportSections })
      }
    } catch (e) {
      const msg = getApiErrorMessage(e, '통합 리포트를 불러오지 못했습니다.')
      setIntegratedReportError(msg)
      if (popupWindow) {
        popupWindow.document.open()
        popupWindow.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>통합 리포트</title></head><body style="font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Noto Sans KR,sans-serif;padding:32px;color:#be123c;">${escapeReportHtml(msg)}</body></html>`)
        popupWindow.document.close()
      }
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    }
  }

  const openIntegratedReportWindow = (autoPrint = false, reportOverride = null) => {
    const report = reportOverride || integratedReportDraft
    if (!report) return
    const reportWindow = window.open('about:blank', '_blank', 'width=980,height=760')
    if (!reportWindow) {
      try { window?.toast?.show?.('팝업 차단을 해제한 뒤 다시 시도해주세요.', 'error', 5000) } catch { }
      return
    }
    const reportSections = integratedReportOutputSectionsFor(report)
    writeIntegratedReportPopup(reportWindow, report, { autoPrint, selectedSections: reportSections })
  }

  const previewIntegratedReport = () => {
    openIntegratedReportWindow(false)
  }

  const printIntegratedReport = () => {
    openIntegratedReportWindow(true)
  }

  const downloadIntegratedReportTemplateImage = async () => {
    const reportId = integratedReportDraft?.id
    if (!Number.isFinite(customerId) || customerId <= 0 || !reportId || integratedReportTemplateBusy) return
    setIntegratedReportTemplateBusy(true)
    setIntegratedReportError('')
    try {
      const res = await api.get(
        `/customer-management/customers/${customerId}/integrated-reports/${encodeURIComponent(reportId)}/template-image`,
        {
          params: integratedReportTemplateId ? { reportTemplateId: integratedReportTemplateId } : undefined,
          responseType: 'blob',
          __silent: true,
        },
      )
      saveBlobResponse(
        res,
        `${safeDownloadNamePart(detail?.customer?.customerName)}_통합리포트_템플릿_${new Date().toISOString().slice(0, 10)}.png`,
        'image/png',
      )
      try { window?.toast?.show?.('통합 리포트 템플릿 이미지를 다운로드했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '통합 리포트 템플릿 이미지 생성에 실패했습니다.')
      setIntegratedReportError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setIntegratedReportTemplateBusy(false)
    }
  }

  const downloadIntegratedReportTemplateHtml = async () => {
    const reportId = integratedReportDraft?.id
    if (!Number.isFinite(customerId) || customerId <= 0 || !reportId || integratedReportHtmlBusy) return
    setIntegratedReportHtmlBusy(true)
    setIntegratedReportError('')
    try {
      const res = await api.get(
        `/customer-management/customers/${customerId}/integrated-reports/${encodeURIComponent(reportId)}/template-html`,
        {
          params: integratedReportHtmlTemplateId ? { reportTemplateId: integratedReportHtmlTemplateId } : undefined,
          responseType: 'blob',
          __silent: true,
        },
      )
      saveBlobResponse(
        res,
        `${safeDownloadNamePart(detail?.customer?.customerName)}_통합리포트_템플릿_${new Date().toISOString().slice(0, 10)}.html`,
        'text/html',
      )
      try { window?.toast?.show?.('통합 리포트 HTML을 다운로드했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '통합 리포트 HTML 생성에 실패했습니다.')
      setIntegratedReportError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setIntegratedReportHtmlBusy(false)
    }
  }

  const downloadDiagnosisSimulationTemplateImage = async () => {
    const simulationId = diagnosisDraft?.id
    if (!Number.isFinite(customerId) || customerId <= 0 || !simulationId || diagnosisTemplateBusy) return
    setDiagnosisTemplateBusy(true)
    setDiagnosisError('')
    try {
      const res = await api.get(
        `/customer-management/customers/${customerId}/diagnosis-simulations/${encodeURIComponent(simulationId)}/template-image`,
        {
          params: diagnosisTemplateId ? { reportTemplateId: diagnosisTemplateId } : undefined,
          responseType: 'blob',
          __silent: true,
        },
      )
      saveBlobResponse(
        res,
        `${safeDownloadNamePart(detail?.customer?.customerName)}_진단시뮬레이션_템플릿_${new Date().toISOString().slice(0, 10)}.png`,
        'image/png',
      )
      try { window?.toast?.show?.('진단시뮬레이션 템플릿 이미지를 다운로드했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '진단시뮬레이션 템플릿 이미지 생성에 실패했습니다.')
      setDiagnosisError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setDiagnosisTemplateBusy(false)
    }
  }

  const downloadDiagnosisSimulationTemplateHtml = async () => {
    const simulationId = diagnosisDraft?.id
    if (!Number.isFinite(customerId) || customerId <= 0 || !simulationId || diagnosisHtmlBusy) return
    setDiagnosisHtmlBusy(true)
    setDiagnosisError('')
    try {
      const res = await api.get(
        `/customer-management/customers/${customerId}/diagnosis-simulations/${encodeURIComponent(simulationId)}/template-html`,
        {
          params: diagnosisHtmlTemplateId ? { reportTemplateId: diagnosisHtmlTemplateId } : undefined,
          responseType: 'blob',
          __silent: true,
        },
      )
      saveBlobResponse(
        res,
        `${safeDownloadNamePart(detail?.customer?.customerName)}_진단시뮬레이션_템플릿_${new Date().toISOString().slice(0, 10)}.html`,
        'text/html',
      )
      try { window?.toast?.show?.('진단시뮬레이션 HTML을 다운로드했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '진단시뮬레이션 HTML 생성에 실패했습니다.')
      setDiagnosisError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setDiagnosisHtmlBusy(false)
    }
  }

  const openDiagnosisSimulationWindow = (simulation, autoPrint = false) => {
    if (!simulation) return
    const reportWindow = window.open('about:blank', '_blank', 'width=980,height=760')
    if (!reportWindow) {
      try { window?.toast?.show?.('팝업 차단을 해제한 뒤 다시 시도해주세요.', 'error', 5000) } catch { }
      return
    }
    reportWindow.document.open()
    reportWindow.document.write(buildDiagnosisSimulationPrintHtml(simulation, { autoPrint, showToolbar: true }))
    reportWindow.document.close()
    reportWindow.focus()
  }

  const generateDiagnosisSimulation = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0 || !diagnosisSelectedCode) return
    setDiagnosisGenerating(true)
    setDiagnosisError('')
    try {
      const res = await api.post(`/customer-management/customers/${customerId}/diagnosis-simulations`, {
        scenarioCode: diagnosisSelectedCode,
        includeTermsEvidence: false,
      })
      const data = unwrap(res) || null
      setDiagnosisDraft(data)
      try { window?.toast?.show?.('진단시뮬레이션을 생성했습니다.', 'success') } catch { }
      if (data) openDiagnosisSimulationWindow(data, false)
      loadDiagnosisOverview().catch(() => {})
    } catch (e) {
      const msg = getApiErrorMessage(e, '진단시뮬레이션 생성에 실패했습니다.')
      setDiagnosisError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setDiagnosisGenerating(false)
    }
  }

  const searchDiagnosisTerms = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const q = String(diagnosisTermsSearch.q || '').trim()
    const diseaseCode = String(diagnosisTermsSearch.diseaseCode || '').trim()
    if (!q && !diseaseCode) {
      setDiagnosisTermsError('질병명 또는 질병코드를 입력해주세요.')
      setDiagnosisTermsResult(null)
      return
    }
    setDiagnosisTermsLoading(true)
    setDiagnosisTermsError('')
    try {
      let availableContracts = Array.isArray(coverageContracts) ? coverageContracts : []
      if (!availableContracts.length) {
        const loaded = await loadCoverageContracts()
        availableContracts = Array.isArray(loaded?.contracts) ? loaded.contracts : []
      }
      if (!availableContracts.length) {
        setDiagnosisTermsResult(null)
        setDiagnosisTermsError('저장된 보장분석 계약이 없어 검색할 수 없습니다.')
        return
      }
      const params = { limit: 5 }
      if (q) params.q = q
      if (diseaseCode) params.diseaseCode = diseaseCode
      const selectedContractId = diagnosisTermsSelectedContractId || availableContracts[0]?.id || ''
      if (diagnosisTermsSearch.scope === 'selected' && selectedContractId) {
        params.contractId = selectedContractId
      }
      const res = await api.get(
        `/customer-management/customers/${customerId}/coverage-contracts/terms-disease-search`,
        { params },
      )
      setDiagnosisTermsResult(unwrap(res) || null)
    } catch (e) {
      setDiagnosisTermsResult(null)
      setDiagnosisTermsError(getApiErrorMessage(e, '질병 기준 약관 검색에 실패했습니다.'))
    } finally {
      setDiagnosisTermsLoading(false)
    }
  }

  const openDiagnosisSimulation = async (simulationId, autoPrint = false) => {
    if (!Number.isFinite(customerId) || customerId <= 0 || !simulationId) return
    setDiagnosisError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/diagnosis-simulations/${simulationId}`)
      const data = unwrap(res) || null
      setDiagnosisDraft(data)
      if (data) openDiagnosisSimulationWindow(data, autoPrint)
    } catch (e) {
      const msg = getApiErrorMessage(e, '진단시뮬레이션 이력을 불러오지 못했습니다.')
      setDiagnosisError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    }
  }

  const openCustomerCardNews = (sourceType = 'integrated') => {
    const opened = openCustomerCardNewsWindow({
      sourceType,
      customer: detail?.customer,
      latestHealth,
      latestHiddenInsurance,
      coverageContracts,
      maturityRows,
      integratedReportDraft,
    })
    if (!opened) {
      try { window?.toast?.show?.('팝업 차단을 해제한 뒤 다시 시도해주세요.', 'error', 5000) } catch { }
    }
  }

  const addAnniversary = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const title = anniversaryTitle.trim()
    const anniversaryDateNorm = normalizeYmdDateForApi(anniversaryDate, '기념일 날짜')
    if (!title || !String(anniversaryDate || '').trim()) {
      try { window?.toast?.show?.('기념일명과 날짜를 입력해 주세요.', 'error') } catch { }
      return
    }
    if (!anniversaryDateNorm.ok) {
      try { window?.toast?.show?.(anniversaryDateNorm.message, 'error') } catch { }
      return
    }
    setAnniversarySaving(true)
    setErr('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/anniversaries`,
        { title, anniversaryDate: anniversaryDateNorm.value },
        { __silent: true },
      )
      const created = unwrap(res)
      if (created) {
        setDetail((prev) => {
          if (!prev) return prev
          const rows = Array.isArray(prev.anniversaries) ? prev.anniversaries : []
          return { ...prev, anniversaries: [...rows, created] }
        })
      }
      setAnniversaryTitle('')
      setAnniversaryDate('')
      try { window?.toast?.show?.('기념일을 추가했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '기념일 추가에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setAnniversarySaving(false)
    }
  }

  const deleteAnniversary = async (row) => {
    const id = row?.id
    if (!Number.isFinite(customerId) || customerId <= 0 || id == null) return
    const title = String(row?.title || '기념일')
    if (!window.confirm(`${title} 기념일을 삭제할까요?`)) return
    setAnniversaryDeletingId(id)
    setErr('')
    try {
      await api.delete(`/customer-management/customers/${customerId}/anniversaries/${encodeURIComponent(String(id))}`, { __silent: true })
      setDetail((prev) => {
        if (!prev) return prev
        const rows = Array.isArray(prev.anniversaries) ? prev.anniversaries : []
        return { ...prev, anniversaries: rows.filter((r) => String(r?.id) !== String(id)) }
      })
      try { window?.toast?.show?.('기념일을 삭제했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '기념일 삭제에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setAnniversaryDeletingId(null)
    }
  }

  const resetFamilyForm = () => {
    setFamilyName('')
    setFamilyRelationship('')
    setFamilyBirthDate('')
    setFamilyCohabiting('')
    setFamilyMemo('')
    setFamilyLinkedCustomerRefId('')
    setFamilyLinkedSearch('')
    setFamilyLinkCandidates([])
  }

  const searchFamilyLinkCandidates = async () => {
    const q = familyLinkedSearch.trim()
    if (!q) {
      setFamilyLinkCandidates([])
      return
    }
    try {
      const res = await api.get('/customer-management/customers', { params: { q, page: 0, size: 8 }, __silent: true })
      const data = unwrap(res)
      const items = Array.isArray(data?.items) ? data.items : []
      setFamilyLinkCandidates(items.filter((item) => String(item.id) !== String(customerId)))
    } catch {
      setFamilyLinkCandidates([])
    }
  }

  const addFamilyMember = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const name = familyName.trim()
    if (!name && !familyLinkedCustomerRefId) {
      try { window?.toast?.show?.('가족 이름을 입력하거나 기존 고객을 연결해 주세요.', 'error') } catch { }
      return
    }
    setFamilySaving(true)
    try {
      const birthNorm = normalizeYmdDateForApi(familyBirthDate, '생년월일')
      if (!birthNorm.ok) {
        try { window?.toast?.show?.(birthNorm.message || '생년월일을 확인해 주세요.', 'error') } catch { }
        return
      }
      const res = await api.post(`/customer-management/customers/${customerId}/family-members`, {
        name,
        relationship: familyRelationship.trim(),
        birthDate: birthNorm.value,
        cohabiting: familyCohabiting === '' ? null : familyCohabiting === 'YES',
        memo: familyMemo.trim(),
        linkedCustomerRefId: familyLinkedCustomerRefId ? Number(familyLinkedCustomerRefId) : null,
      }, { __silent: true })
      const created = unwrap(res)
      setDetail((prev) => {
        if (!prev || !created) return prev
        const rows = Array.isArray(prev.familyMembers) ? prev.familyMembers : []
        return { ...prev, familyMembers: [...rows, created] }
      })
      resetFamilyForm()
      try { window?.toast?.show?.('가족 정보를 추가했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '가족 정보 추가에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setFamilySaving(false)
    }
  }

  const deleteFamilyMember = async (row) => {
    const id = row?.id
    if (!Number.isFinite(customerId) || customerId <= 0 || id == null) return
    if (!window.confirm(`${row?.name || '가족'} 정보를 삭제할까요?`)) return
    setFamilyDeletingId(id)
    try {
      await api.delete(`/customer-management/customers/${customerId}/family-members/${encodeURIComponent(String(id))}`, { __silent: true })
      setDetail((prev) => {
        if (!prev) return prev
        const rows = Array.isArray(prev.familyMembers) ? prev.familyMembers : []
        return { ...prev, familyMembers: rows.filter((r) => String(r?.id) !== String(id)) }
      })
      try { window?.toast?.show?.('가족 정보를 삭제했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '가족 정보 삭제에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setFamilyDeletingId(null)
    }
  }

  const generateAiMessageDraft = async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    setAiMessageGenerating(true)
    setErr('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/ai-message-draft`,
        {
          messageType: aiMessageType,
          tone: aiMessageTone,
          anniversaryId: aiMessageType === 'ANNIVERSARY' && aiMessageAnniversaryId ? Number(aiMessageAnniversaryId) : null,
          customContext: aiMessageContext,
        },
        { __silent: true },
      )
      const d = unwrap(res) || null
      setAiMessageDraft(d)
      setAiMessageModalOpen(true)
      try {
        window?.toast?.show?.(
          d?.generationMode === 'RULE'
            ? '템플릿 메시지 초안을 생성했습니다. 발송 전 내용을 꼭 확인해 주세요.'
            : 'AI 메시지 초안을 생성했습니다. 발송 전 내용을 꼭 확인해 주세요.',
          'success',
        )
      } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, 'AI 메시지 생성에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setAiMessageGenerating(false)
    }
  }

  const generateEasyExplanation = async (sourceType, relatedId = null) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const key = `${sourceType}:${relatedId || ''}`
    setEasyExplainGenerating(key)
    setErr('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/easy-explanation`,
        { sourceType, relatedId },
        { __silent: true },
      )
      setEasyExplainDraft(unwrap(res) || null)
      setEasyExplainModalOpen(true)
      try { window?.toast?.show?.('고객용 쉬운 설명을 생성했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '고객용 설명 생성에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setEasyExplainGenerating('')
    }
  }

  const checkComplianceRisk = async (textOverride) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    const text = String(textOverride || '').trim()
    if (!text) {
      try { window?.toast?.show?.('검사할 문구를 입력해 주세요.', 'error') } catch { }
      return
    }
    setRiskChecking(true)
    setErr('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/compliance-risk-check`,
        { text },
        { __silent: true },
      )
      setRiskCheckResult(normalizeComplianceCheckResult(res?.data) || null)
      setRiskCheckModalOpen(true)
      try { window?.toast?.show?.('표현 리스크 검사를 완료했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '표현 리스크 검사에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setRiskChecking(false)
    }
  }

  const copyAiMessage = async (text) => {
    const value = String(text || '').trim()
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      try { window?.toast?.show?.('문구를 복사했습니다.', 'success') } catch { }
    } catch {
      try { window?.toast?.show?.('복사에 실패했습니다. 직접 선택해 복사해 주세요.', 'error') } catch { }
    }
  }

  useEffect(() => {
    if (businessCardRows.length === 0) return
    if (cardCarouselIdx >= businessCardRows.length) {
      setCardCarouselIdx(0)
    }
  }, [businessCardRows.length, cardCarouselIdx, businessCardRows])

  useEffect(() => {
    let cancel = false
    let blobUrl = ''
    const revoke = () => {
      if (blobUrl) {
        try { URL.revokeObjectURL(blobUrl) } catch { }
        blobUrl = ''
      }
    }
    setCardPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try { URL.revokeObjectURL(prev) } catch { }
      }
      return ''
    })
    if (!businessCardRows.length) {
      return () => revoke()
    }
    const i = Math.min(Math.max(0, cardCarouselIdx), businessCardRows.length - 1)
    const fid = businessCardRows[i]?.fileId
    if (!fid) {
      return () => revoke()
    }
    ; (async () => {
      try {
        const res = await api.get(`/files/${encodeURIComponent(fid)}/preview`, { responseType: 'blob' })
        const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/jpeg' })
        blobUrl = URL.createObjectURL(blob)
        if (!cancel) setCardPreviewUrl(blobUrl)
      } catch {
        if (!cancel) setCardPreviewUrl('')
      }
    })()
    return () => {
      cancel = true
      revoke()
    }
  }, [businessCardRows, cardCarouselIdx])

  const uploadCardFile = async (file) => {
    const form = new FormData()
    form.append('file', file)
    const up = await api.post('/files', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      __silent: true,
    })
    const payload = up?.data?.data ?? up?.data ?? {}
    const fid = payload.fileId || payload.id
    if (!fid) throw new Error('no_file_id')
    return fid
  }

  const uploadCoverageFile = async (file) => {
    const form = new FormData()
    form.append('file', file)
    const up = await api.post('/files/upload/coverage-analysis', form, {
      params: Number.isFinite(customerId) ? { customerId } : undefined,
      headers: { 'Content-Type': 'multipart/form-data' },
      __silent: true,
    })
    const payload = up?.data?.data ?? up?.data ?? {}
    const fid = payload.fileId || payload.id
    if (!fid) throw new Error('no_file_id')
    return { ...payload, fileId: fid }
  }

  const handleCoverageFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setCoverageErr('')
    const savedCoverageCount = Array.isArray(coverageContracts) ? coverageContracts.length : 0
    let resetConfirmed = false
    if (savedCoverageCount > 0) {
      resetConfirmed = window.confirm(
        `새 보장분석 PDF를 업로드하면 기존 저장된 보험계약 ${savedCoverageCount}건이 초기화되고 새 분석 결과로 다시 생성됩니다.\n\n업로드 전 엑셀 다운로드로 백업해 주세요. 계속할까요?`,
      )
      if (!resetConfirmed) {
        try { event.target.value = '' } catch { }
        return
      }
    }
    try {
      const uploaded = await uploadCoverageFile(file)
      const fid = uploaded.fileId
      setCoverageFileId(fid)
      setCoverageUploadedFileMeta({
        fileId: fid,
        filename: uploaded.originalFilename || uploaded.filename || file.name,
        contentType: uploaded.contentType || file.type,
        size: uploaded.size || file.size,
        uploadedAt: uploaded.createdAt || new Date().toISOString(),
      })
      setCoverageNameValidation(null)
      setCoverageExtractionQuality(null)
      setCoverageNameMismatchAcknowledged(false)
      await runCoverageAnalysis(fid, { resetConfirmed })
    } catch (e) {
      const msg = getApiErrorMessage(e, '파일 업로드에 실패했습니다.')
      setCoverageErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      try { event.target.value = '' } catch { }
    }
  }

  const openCoverageFilePreview = async (fileId = displayCoverageFileId) => {
    if (!fileId) return
    try {
      const res = await api.get(`/files/${encodeURIComponent(fileId)}/preview`, { responseType: 'blob', __silent: true })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/pdf' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => {
        try { URL.revokeObjectURL(url) } catch { }
      }, 60_000)
    } catch (e) {
      const msg = getApiErrorMessage(e, '파일 미리보기를 열지 못했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    }
  }

  const downloadCoverageFile = async (fileId = displayCoverageFileId) => {
    if (!fileId) return
    try {
      const res = await api.get(`/files/${encodeURIComponent(fileId)}/download`, { responseType: 'blob', __silent: true })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = displayCoverageFileName || `coverage-analysis-${fileId}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = getApiErrorMessage(e, '파일 다운로드에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    }
  }

  function filenameFromDisposition(headers, fallback) {
    const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'] || ''
    if (typeof cd === 'string') {
      const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
      if (star?.[1]) {
        try { return decodeURIComponent(star[1]) } catch { }
      }
      const plain = cd.match(/filename="([^"]+)"/i)
      if (plain?.[1]) return plain[1]
    }
    return fallback
  }

  function safeDownloadNamePart(value, fallback = '고객') {
    const text = String(value || '').trim().replace(/[\\/:*?"<>|\r\n]+/g, '_').replace(/\s+/g, '')
    return text || fallback
  }

  function saveBlobResponse(res, fallback, defaultContentType = 'application/octet-stream') {
    const blob = new Blob([res.data], { type: res.headers?.['content-type'] || defaultContentType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filenameFromDisposition(res.headers, fallback)
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const downloadCustomerInsuranceExcel = async (kind) => {
    const isMaturity = kind === 'maturity'
    const setBusy = isMaturity ? setMaturityExcelBusy : setCoverageExcelBusy
    const endpoint = isMaturity
      ? `/customer-management/customers/${customerId}/maturity-policies/excel`
      : `/customer-management/customers/${customerId}/coverage-contracts/excel`
    const date = new Date().toISOString().slice(0, 10)
    const fallback = isMaturity
      ? `만기갱신목록_${date}.xlsx`
      : `${safeDownloadNamePart(detail?.customer?.customerName)}_보유계약목록_${date}.xlsx`
    setBusy('download')
    try {
      const res = await api.get(endpoint, { responseType: 'blob', __silent: true })
      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filenameFromDisposition(res.headers, fallback)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = getApiErrorMessage(e, '엑셀 다운로드에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setBusy('')
    }
  }

  const uploadCustomerInsuranceExcel = async (kind, event) => {
    const file = event.target.files?.[0]
    if (!file) return
    const isMaturity = kind === 'maturity'
    const setBusy = isMaturity ? setMaturityExcelBusy : setCoverageExcelBusy
    const endpoint = isMaturity
      ? `/customer-management/customers/${customerId}/maturity-policies/excel`
      : `/customer-management/customers/${customerId}/coverage-contracts/excel`
    const form = new FormData()
    form.append('file', file)
    setBusy('upload')
    try {
      const res = await api.post(endpoint, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        __silent: true,
      })
      const data = res?.data?.data || {}
      if (isMaturity) {
        await loadMaturityRows()
        const created = Number(data.created || 0)
        const updated = Number(data.updated || 0)
        const closed = Number(data.closed || 0)
        try { window?.toast?.show?.(`만기·갱신 엑셀 반영 완료: 추가 ${created}건, 수정 ${updated}건, 종료 ${closed}건`, 'success', 5000) } catch { }
      } else {
        await loadCoverageContracts()
        await Promise.allSettled([loadDiagnosisOverview(), loadIntegratedReports()])
        const created = Number(data.created || 0)
        const updated = Number(data.updated || 0)
        const deleted = Number(data.deleted || 0)
        const coverageSaved = Number(data.coverageSaved || 0)
        try { window?.toast?.show?.(`보유계약 엑셀 반영 완료: 추가 ${created}건, 수정 ${updated}건, 삭제 ${deleted}건, 담보 ${coverageSaved}건`, 'success', 5000) } catch { }
      }
    } catch (e) {
      const msg = getApiErrorMessage(e, '엑셀 업로드 반영에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setBusy('')
      try { event.target.value = '' } catch { }
    }
  }

  const openCoverageSummaryReport = async () => {
    setCoverageSummaryTableOpen(true)
    if (!coverageSummaryReport && !coverageSummaryLoading) {
      await loadCoverageSummaryReport()
    }
  }

  const updateCoverageSummaryRow = (rowIndex, field, value) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const rows = Array.isArray(prev.rows) ? prev.rows : []
      return {
        ...prev,
        rows: rows.map((row, idx) => (idx === rowIndex ? { ...row, [field]: value } : row)),
      }
    })
  }

  const updateCoverageSummaryProduct = (rowIndex, productIndex, field, value) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const rows = Array.isArray(prev.rows) ? prev.rows : []
      return {
        ...prev,
        rows: rows.map((row, idx) => {
          if (idx !== rowIndex) return row
          const products = Array.isArray(row.products) ? row.products : []
          return {
            ...row,
            products: products.map((product, pIdx) => (pIdx === productIndex ? { ...product, [field]: value } : product)),
          }
        }),
      }
    })
  }

  const updateCoverageSummaryProductContract = (rowIndex, productIndex, contractId) => {
    const selected = coverageContracts.find((contract) => String(contract.id) === String(contractId))
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const rows = Array.isArray(prev.rows) ? prev.rows : []
      return {
        ...prev,
        rows: rows.map((row, idx) => {
          if (idx !== rowIndex) return row
          const products = Array.isArray(row.products) ? row.products : []
          return {
            ...row,
            products: products.map((product, pIdx) => {
              if (pIdx !== productIndex) return product
              if (!selected) {
                return {
                  ...product,
                  contractId: null,
                  insurerName: '',
                  productName: '',
                  contractDate: '',
                  paymentPeriod: '',
                  coveragePeriod: '',
                  monthlyPremium: 0,
                }
              }
              return {
                ...product,
                contractId: selected.id,
                insurerName: selected.insurerName || '',
                productName: selected.productName || '',
                contractDate: selected.contractDate || '',
                paymentPeriod: selected.paymentPeriod || '',
                coveragePeriod: selected.coveragePeriod || '',
                monthlyPremium: selected.monthlyPremium || 0,
              }
            }),
          }
        }),
      }
    })
  }

  const addCoverageSummaryProduct = (rowIndex) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const rows = Array.isArray(prev.rows) ? prev.rows : []
      return {
        ...prev,
        rows: rows.map((row, idx) => {
          if (idx !== rowIndex) return row
          const products = Array.isArray(row.products) ? row.products : []
          return {
            ...row,
            products: [
              ...products,
              {
                contractId: null,
                insurerName: '',
                productName: '',
                contractDate: '',
                paymentPeriod: '',
                coveragePeriod: '',
                monthlyPremium: 0,
                amount: 0,
                coverageNames: row.coverageName || '',
                note: '',
              },
            ],
          }
        }),
      }
    })
  }

  const removeCoverageSummaryProduct = (rowIndex, productIndex) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const rows = Array.isArray(prev.rows) ? prev.rows : []
      return {
        ...prev,
        rows: rows.map((row, idx) => {
          if (idx !== rowIndex) return row
          const products = Array.isArray(row.products) ? row.products : []
          return {
            ...row,
            products: products.filter((_, pIdx) => pIdx !== productIndex),
          }
        }),
      }
    })
  }

  const setCoverageSummaryPdfProductSelected = (key, selected) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const current = asArray(prev.pdfSelectedProductKeys).map(String).filter(Boolean)
      const base = current.length > 0 ? current : coverageSummaryPdfProducts.map((product) => product.key)
      const next = selected
        ? Array.from(new Set([...base, key]))
        : base.filter((item) => item !== key)
      if (!selected && current.length > 0 && next.length === 0) return prev
      return { ...prev, pdfSelectedProductKeys: next }
    })
  }

  const setCoverageSummaryPdfAllProducts = (selected) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        pdfSelectedProductKeys: selected ? coverageSummaryPdfProducts.map((product) => product.key) : [],
      }
    })
  }

  const setCoverageSummaryIncludeNoDetailContracts = (selected) => {
    setCoverageSummaryReport((prev) => {
      if (!prev) return prev
      const current = asArray(prev.pdfSelectedProductKeys).map(String).filter(Boolean)
      const noDetailKeys = coverageSummaryNoDetailProducts.map((product) => product.key).filter(Boolean)
      const noDetailKeySet = new Set(noDetailKeys)
      let nextKeys = current
      if (selected && current.length > 0) {
        nextKeys = Array.from(new Set([...current, ...noDetailKeys]))
      } else if (!selected && current.length > 0) {
        nextKeys = current.filter((key) => !noDetailKeySet.has(key))
      }
      return {
        ...prev,
        pdfIncludeNoDetailContracts: selected,
        pdfSelectedProductKeys: nextKeys,
      }
    })
  }

  const saveCoverageSummaryReport = async () => {
    if (!coverageSummaryReport || coverageSummarySaving) return
    setCoverageSummarySaving(true)
    setCoverageSummaryError('')
    try {
      const res = await api.put(
        `/customer-management/customers/${customerId}/coverage-summary`,
        { report: coverageSummaryReport },
        { __silent: true },
      )
      setCoverageSummaryReport(unwrap(res) || null)
      try { window?.toast?.show?.('보장요약 리포트를 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 리포트 저장에 실패했습니다.')
      setCoverageSummaryError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummarySaving(false)
    }
  }

  const regenerateCoverageSummaryReport = async () => {
    if (coverageSummaryBusy) return
    if (coverageSummaryReport?.id && !window.confirm('현재 보장요약 리포트를 저장된 계약 기준으로 새로 생성할까요? 기존 편집값은 새 리포트로 대체됩니다.')) return
    setCoverageSummaryBusy('regenerate')
    setCoverageSummaryError('')
    try {
      const res = await api.post(`/customer-management/customers/${customerId}/coverage-summary/regenerate`, {}, { __silent: true })
      setCoverageSummaryReport(unwrap(res) || null)
      try { window?.toast?.show?.('저장된 계약 기준으로 보장요약 리포트를 생성했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 리포트 재생성에 실패했습니다.')
      setCoverageSummaryError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummaryBusy('')
    }
  }

  const downloadCoverageSummaryExcel = async () => {
    setCoverageSummaryBusy('excel-download')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary/excel`, { responseType: 'blob', __silent: true })
      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filenameFromDisposition(res.headers, `${safeDownloadNamePart(detail?.customer?.customerName)}_보장요약리포트_${new Date().toISOString().slice(0, 10)}.xlsx`)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 엑셀 다운로드에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummaryBusy('')
    }
  }

  const uploadCoverageSummaryExcel = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    const form = new FormData()
    form.append('file', file)
    setCoverageSummaryBusy('excel-upload')
    try {
      const res = await api.post(`/customer-management/customers/${customerId}/coverage-summary/excel`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        __silent: true,
      })
      setCoverageSummaryReport(unwrap(res) || null)
      try { window?.toast?.show?.('보장요약 엑셀을 반영했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 엑셀 업로드에 실패했습니다.')
      setCoverageSummaryError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummaryBusy('')
      try { event.target.value = '' } catch { }
    }
  }

  const downloadCoverageSummaryPdf = async () => {
    setCoverageSummaryBusy('pdf')
    try {
      if (coverageSummaryReport) {
        const saveRes = await api.put(
          `/customer-management/customers/${customerId}/coverage-summary`,
          { report: coverageSummaryReport },
          { __silent: true },
        )
        setCoverageSummaryReport(unwrap(saveRes) || coverageSummaryReport)
      }
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary/pdf`, { responseType: 'blob', __silent: true })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filenameFromDisposition(res.headers, `${safeDownloadNamePart(detail?.customer?.customerName)}_보장요약리포트_${new Date().toISOString().slice(0, 10)}.pdf`)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약표 출력에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummaryBusy('')
    }
  }

  const downloadCoverageSummaryTemplateImage = async () => {
    setCoverageSummaryBusy('template-image')
    setCoverageSummaryError('')
    try {
      if (coverageSummaryReport) {
        const saveRes = await api.put(
          `/customer-management/customers/${customerId}/coverage-summary`,
          { report: coverageSummaryReport },
          { __silent: true },
        )
        setCoverageSummaryReport(unwrap(saveRes) || coverageSummaryReport)
      }
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary/template-image`, {
        params: coverageSummaryTemplateId ? { reportTemplateId: coverageSummaryTemplateId } : undefined,
        responseType: 'blob',
        __silent: true,
      })
      saveBlobResponse(
        res,
        `${safeDownloadNamePart(detail?.customer?.customerName)}_보장요약_템플릿_${new Date().toISOString().slice(0, 10)}.png`,
        'image/png',
      )
      try { window?.toast?.show?.('보장요약 템플릿 이미지를 다운로드했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 템플릿 이미지 생성에 실패했습니다.')
      setCoverageSummaryError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummaryBusy('')
    }
  }

  const downloadCoverageSummaryTemplateHtml = async () => {
    setCoverageSummaryBusy('template-html')
    setCoverageSummaryError('')
    try {
      if (coverageSummaryReport) {
        const saveRes = await api.put(
          `/customer-management/customers/${customerId}/coverage-summary`,
          { report: coverageSummaryReport },
          { __silent: true },
        )
        setCoverageSummaryReport(unwrap(saveRes) || coverageSummaryReport)
      }
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary/template-html`, {
        params: coverageSummaryHtmlTemplateId ? { reportTemplateId: coverageSummaryHtmlTemplateId } : undefined,
        responseType: 'blob',
        __silent: true,
      })
      saveBlobResponse(
        res,
        `${safeDownloadNamePart(detail?.customer?.customerName)}_보장요약_템플릿_${new Date().toISOString().slice(0, 10)}.html`,
        'text/html',
      )
      try { window?.toast?.show?.('보장요약 HTML을 다운로드했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장요약 HTML 생성에 실패했습니다.')
      setCoverageSummaryError(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSummaryBusy('')
    }
  }

  const syncCoverageDraftContracts = (contracts, opts = {}) => {
    const raw = Array.isArray(contracts) ? contracts : []
    const base = opts.stripParserNotes ? stripParserNotesFromCoverageDraftContracts(raw) : raw
    const next = inferCoverageInsuranceCategoriesMissing(base)
    setCoverageDraftContracts(next)
    setCoverageDraftJson(coverageDraftToJson(next))
  }

  const updateCoverageDraftContract = (contractIndex, field, value) => {
    const next = coverageDraftContracts.map((contract, idx) => {
      if (idx !== contractIndex) return contract
      let updated = { ...contract, [field]: value }
      if (field === 'productName') {
        const catAdj = applyAutoInsuranceCategoryOnProductChange(contract.productName, value, contract.insuranceCategory)
        if (catAdj !== undefined) updated = { ...updated, insuranceCategory: catAdj }
      }
      return updated
    })
    syncCoverageDraftContracts(next)
  }

  const updateCoverageDraftMatch = (contractIndex, value) => {
    const next = coverageDraftContracts.map((contract, idx) => {
      if (idx !== contractIndex) return contract
      if (value === 'NEW') {
        return { ...contract, targetContractId: null, matchResolution: 'NEW' }
      }
      const parsed = Number(value)
      return {
        ...contract,
        targetContractId: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
        matchResolution: null,
      }
    })
    syncCoverageDraftContracts(next)
  }

  const updateCoverageDraftCoverage = (contractIndex, coverageIndex, field, value) => {
    const next = coverageDraftContracts.map((contract, idx) => {
      if (idx !== contractIndex) return contract
      const coverages = Array.isArray(contract.coverages) ? contract.coverages : []
      return {
        ...contract,
        coverages: coverages.map((coverage, cIdx) => (
          cIdx === coverageIndex ? { ...coverage, [field]: value } : coverage
        )),
      }
    })
    syncCoverageDraftContracts(next)
  }

  const applyCoverageRenewalToContract = (contractIndex, renewalType) => {
    const next = coverageDraftContracts.map((contract, idx) => {
      if (idx !== contractIndex) return contract
      const coverages = Array.isArray(contract.coverages) ? contract.coverages : []
      return {
        ...contract,
        renewalType,
        coverages: coverages.map((coverage) => ({ ...coverage, renewalType })),
      }
    })
    syncCoverageDraftContracts(next)
  }

  const removeCoverageDraftContract = (contractIndex) => {
    syncCoverageDraftContracts(coverageDraftContracts.filter((_, idx) => idx !== contractIndex))
  }

  const addCoverageDraftCoverage = (contractIndex) => {
    const next = coverageDraftContracts.map((contract, idx) => {
      if (idx !== contractIndex) return contract
      const coverages = Array.isArray(contract.coverages) ? contract.coverages : []
      return {
        ...contract,
        coverages: [
          ...coverages,
          {
            coverageNameRaw: '',
            sourceCoverageName: '',
            standardCoverageName: '',
            coverageNameNormalized: '',
            coverageGroup: '',
            insuredAmount: null,
            premium: null,
            paymentCondition: '',
            renewalType: '',
            note: '',
            confidence: 1,
          },
        ],
      }
    })
    syncCoverageDraftContracts(next)
  }

  const removeCoverageDraftCoverage = (contractIndex, coverageIndex) => {
    const next = coverageDraftContracts.map((contract, idx) => {
      if (idx !== contractIndex) return contract
      const coverages = Array.isArray(contract.coverages) ? contract.coverages : []
      return { ...contract, coverages: coverages.filter((_, cIdx) => cIdx !== coverageIndex) }
    })
    syncCoverageDraftContracts(next)
  }

  const applyCoverageDraftJson = () => {
    try {
      const parsed = JSON.parse(coverageDraftJson || '[]')
      if (!Array.isArray(parsed)) throw new Error('not_array')
      syncCoverageDraftContracts(parsed, { stripParserNotes: true })
      try { window?.toast?.show?.('JSON 내용을 검수 표에 반영했습니다.', 'success') } catch { }
    } catch {
      try { window?.toast?.show?.('계약 JSON 형식을 확인해 주세요.', 'error') } catch { }
    }
  }

  const runCoverageAnalysis = async (uploadedFileIdOverride, options = {}) => {
    if (!Number.isFinite(customerId)) return
    const fileId = typeof uploadedFileIdOverride === 'string' ? uploadedFileIdOverride : coverageFileId
    if (!fileId) {
      try { window?.toast?.show?.('먼저 보장분석 PDF를 업로드해 주세요.', 'error') } catch { }
      return
    }
    const savedCoverageCount = Array.isArray(coverageContracts) ? coverageContracts.length : 0
    if (savedCoverageCount > 0 && !options?.resetConfirmed) {
      const confirmed = window.confirm(
        `새 분석을 저장하면 기존 저장된 보험계약 ${savedCoverageCount}건이 초기화되고 새 분석 결과로 다시 생성됩니다.\n\n진행 전 엑셀 다운로드로 백업해 주세요. 계속할까요?`,
      )
      if (!confirmed) return
    }
    setCoverageAnalyzing(true)
    setCoverageErr('')
    try {
      const res = await api.post(`/customer-management/customers/${customerId}/coverage-analysis`, {
        uploadedFileId: fileId,
        rawText: '',
        rawTextMode: 'REPLACE',
        aiModel: 'manual-text-parser',
        focusPoints: ['진단비', '수술비', '입원일당', '갱신형', '운전자/배상책임'],
      }, { __silent: true })
      const data = unwrap(res) || {}
      const contracts = normalizeCoverageContractsForAutoSave(selectCoverageContractsForAutoSave(data.draft))
      if (!Array.isArray(contracts) || contracts.length === 0) {
        throw new Error('저장할 계약 초안이 없습니다.')
      }
      let deletedBeforeSave = 0
      if (savedCoverageCount > 0) {
        const resetRes = await api.post(
          `/customer-management/customers/${customerId}/coverage-contracts/reset`,
          { confirmReset: true },
          { __silent: true },
        )
        const resetData = unwrap(resetRes) || {}
        deletedBeforeSave = Number(resetData.deletedContracts ?? savedCoverageCount)
        setCoverageSummaryReport(null)
        setCoverageSummaryError('')
      }
      const saveRes = await api.post(`/customer-management/customers/${customerId}/coverage-contracts`, {
        analysisJobId: data.job?.id || null,
        contracts,
      }, { __silent: true })
      const saved = unwrap(saveRes) || {}
      const savedCount = Array.isArray(saved.contracts) ? saved.contracts.length : contracts.length
      const replacedCount = Number(saved.replacedCount || 0)
      syncCoverageDraftContracts([])
      setCoverageDraftJobId(null)
      setCoverageEditingContractId(null)
      setCoverageJsonOpen(false)
      setCoverageNameValidation(null)
      setCoverageExtractionQuality(null)
      setCoverageNameMismatchAcknowledged(false)
      await loadCoverageContracts()
      await loadDetail(customerId)
      try {
        window?.toast?.show?.(
          `분석이 완료되었습니다. ${deletedBeforeSave ? `기존 ${deletedBeforeSave}건 초기화 · ` : ''}저장 ${savedCount}건${!deletedBeforeSave && replacedCount ? ` · 갱신 ${replacedCount}건` : ''}. PDF 원문과 대조해 한 번 확인해 주세요. 추가·수정 사항은 엑셀 다운로드 후 수정하여 업로드하면 됩니다.`,
          'success',
          8000,
        )
      } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보장분석 저장에 실패했습니다.')
      setCoverageErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageAnalyzing(false)
    }
  }

  const saveCoverageDraft = async () => {
    if (!Number.isFinite(customerId)) return
    const contracts = sanitizeCoverageContractsForSave(coverageDraftContracts)
    if (!Array.isArray(contracts) || contracts.length === 0) {
      try { window?.toast?.show?.('저장할 계약 초안이 없습니다.', 'error') } catch { }
      return
    }
    if (!coverageEditingContractId && contracts.some(isCoverageDraftMatchUnresolved)) {
      try { window?.toast?.show?.('매칭 확인이 필요한 계약이 있습니다. 기존 계약 연결 또는 신규 저장을 선택해 주세요.', 'error', 5000) } catch { }
      return
    }
    if (coverageNameValidation?.checked && coverageNameValidation?.matched === false && !coverageNameMismatchAcknowledged) {
      const currentName = coverageNameValidation.currentCustomerName || detail?.customer?.customerName || '-'
      const parsedName = coverageNameValidation.parsedCustomerName || '-'
      const proceed = window.confirm(`고객명이 불일치합니다.\n\n현재 고객: ${currentName}\n보장분석 파일: ${parsedName}\n\n그래도 계약목록을 저장할까요?`)
      if (!proceed) return
      setCoverageNameMismatchAcknowledged(true)
    }
    setCoverageSaving(true)
    setCoverageErr('')
    try {
      if (coverageEditingContractId) {
        await api.patch(
          `/customer-management/customers/${customerId}/coverage-contracts/${coverageEditingContractId}`,
          contracts[0],
          { __silent: true },
        )
      } else {
        await api.post(`/customer-management/customers/${customerId}/coverage-contracts`, {
          analysisJobId: coverageDraftJobId,
          contracts,
        }, { __silent: true })
      }
      syncCoverageDraftContracts([])
      setCoverageDraftJobId(null)
      setCoverageEditingContractId(null)
      setCoverageNameValidation(null)
      setCoverageExtractionQuality(null)
      setCoverageNameMismatchAcknowledged(false)
      await loadCoverageContracts()
      await loadDetail(customerId)
      try { window?.toast?.show?.(coverageEditingContractId ? '보험 계약을 수정했습니다.' : '보험 계약을 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '계약 저장에 실패했습니다.')
      setCoverageErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageSaving(false)
    }
  }

  const editSavedCoverageContract = (contract) => {
    if (!contract) return
    setCoverageDraftJobId(contract.analysisJobId || null)
    setCoverageEditingContractId(contract.id)
    syncCoverageDraftContracts([{ ...contract, coverages: Array.isArray(contract.coverages) ? contract.coverages : [] }], { stripParserNotes: true })
    setCoverageJsonOpen(false)
    setCoverageExtractionQuality(null)
    try { window?.toast?.show?.('저장된 계약을 편집 초안으로 불러왔습니다.', 'success') } catch { }
  }

  const openSavedCoverageTermsLookup = async (contract) => {
    if (!contract?.id || !Number.isFinite(customerId)) return
    const contractId = contract.id
    setCoverageTermsLookupBusyId(contractId)
    try {
      const res = await api.get(
        `/customer-management/customers/${customerId}/coverage-contracts/${contractId}/terms-matches`,
        { params: { limit: 5 }, __silent: true },
      )
      const payload = unwrap(res) || {}
      const candidates = Array.isArray(payload.candidates) ? payload.candidates : []
      const returnedCount = Number(payload.returnedCount ?? candidates.length ?? 0)
      if (candidates.length === 0 && (!Number.isFinite(returnedCount) || returnedCount <= 0)) {
        try { window?.toast?.show?.('조회 가능한 약관이 없습니다.', 'info', 4000) } catch { }
        return
      }
      navigate(`/work/policy-lookup?customerRefId=${encodeURIComponent(String(customerId))}&contractId=${encodeURIComponent(String(contractId))}`)
    } catch (e) {
      const status = e?.response?.status
      const noTerms = status === 404 || status === 500
      const msg = noTerms
        ? '조회 가능한 약관이 없습니다.'
        : getApiErrorMessage(e, '약관 조회에 실패했습니다.')
      try { window?.toast?.show?.(msg, noTerms ? 'info' : 'error', 5000) } catch { }
    } finally {
      setCoverageTermsLookupBusyId(null)
    }
  }

  const deleteSavedCoverageContract = async (contract) => {
    if (!contract?.id || !Number.isFinite(customerId)) return
    const label = contract.productName || contract.insurerName || `계약 #${contract.id}`
    if (!window.confirm(`저장된 보험 계약 "${label}"을 삭제할까요? 담보도 함께 삭제됩니다.`)) return
    setCoverageDeletingContractId(contract.id)
    setCoverageErr('')
    try {
      await api.delete(`/customer-management/customers/${customerId}/coverage-contracts/${contract.id}`, { __silent: true })
      if (coverageEditingContractId === contract.id) {
        syncCoverageDraftContracts([])
        setCoverageEditingContractId(null)
        setCoverageDraftJobId(null)
      }
      await loadCoverageContracts()
      await loadDetail(customerId)
      try { window?.toast?.show?.('보험 계약을 삭제했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '보험 계약 삭제에 실패했습니다.')
      setCoverageErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageDeletingContractId(null)
    }
  }

  const resetAllCoverageContracts = async () => {
    if (!Number.isFinite(customerId) || coverageResetting) return
    const savedCount = Array.isArray(coverageContracts) ? coverageContracts.length : 0
    const confirmed = window.confirm(
      `저장된 보험계약 ${savedCount}건과 보장요약표를 모두 삭제할까요?\n\n삭제 후에는 되돌릴 수 없으며, 엑셀 업로드 또는 보장분석 PDF 업로드로 다시 등록해야 합니다.`,
    )
    if (!confirmed) return
    setCoverageResetting(true)
    setCoverageErr('')
    try {
      const res = await api.post(
        `/customer-management/customers/${customerId}/coverage-contracts/reset`,
        { confirmReset: true },
        { __silent: true },
      )
      const data = unwrap(res) || {}
      syncCoverageDraftContracts([])
      setCoverageDraftJobId(null)
      setCoverageEditingContractId(null)
      setCoverageJsonOpen(false)
      setCoverageNameValidation(null)
      setCoverageExtractionQuality(null)
      setCoverageNameMismatchAcknowledged(false)
      setCoverageFileId('')
      setCoverageUploadedFileMeta(null)
      setCoverageSavedTab('all')
      setCoverageSummaryReport(null)
      setCoverageSummaryError('')
      await loadCoverageContracts()
      await loadDetail(customerId)
      try {
        window?.toast?.show?.(`저장된 보험계약과 보장요약표를 전체 초기화했습니다. 계약 삭제 ${data.deletedContracts ?? savedCount}건`, 'success')
      } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '저장된 보험계약 전체 초기화에 실패했습니다.')
      setCoverageErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageResetting(false)
    }
  }

  const resolveCoverageContractCandidate = async (candidate, action) => {
    if (!candidate?.id || !Number.isFinite(customerId)) return
    const isLink = action === 'link'
    const label = candidate.productName || candidate.insurerName || `후보 #${candidate.id}`
    if (!isLink && !window.confirm(`연말정산 보험계약 후보 "${label}"을 무시하고 목록에서 숨길까요?`)) return
    setCoverageCandidateBusyId(candidate.id)
    setCoverageErr('')
    try {
      await api.post(
        `/customer-management/customers/${customerId}/coverage-contract-candidates/${candidate.id}/${isLink ? 'link' : 'ignore'}`,
        {},
        { __silent: true },
      )
      await loadCoverageContracts()
      await loadDetail(customerId)
      try { window?.toast?.show?.(isLink ? '연말정산 보험계약 후보를 계약 목록에 연결했습니다.' : '연말정산 보험계약 후보를 무시 처리했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, isLink ? '계약 후보 연결에 실패했습니다.' : '계약 후보 무시 처리에 실패했습니다.')
      setCoverageErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCoverageCandidateBusyId(null)
    }
  }

  const openCardWizard = () => {
    setCardWizardOpen(true)
    setCardWizardStep('front')
    setCardDraftFrontId('')
    setCardDraftBackId('')
    setCardDiffs([])
    setCardExtracted(null)
    setCardExtracting(false)
    setErr('')
  }

  const runCardExtractPreview = async () => {
    if (!cardDraftFrontId) {
      try { window?.toast?.show?.('명함 앞면 이미지를 업로드해 주세요.', 'error') } catch { }
      return
    }
    setCardExtracting(true)
    setCardWizardBusy(true)
    setErr('')
    try {
      const res = await api.post('/customer-management/customers/extract-business-card-preview', {
        frontFileId: cardDraftFrontId,
        backFileId: cardDraftBackId || null,
      }, { __silent: true })
      const data = unwrap(res) || {}
      const cur = detail?.customer || {}
      const diffs = compareExtractedToCurrent(cur, data)
      setCardExtracted(data)
      setCardDiffs(diffs)
      setCardWizardStep('diff')
    } catch (e) {
      const msg = getApiErrorMessage(e, '명함 인식에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCardExtracting(false)
      setCardWizardBusy(false)
    }
  }

  const buildProfilePatchFromExtracted = (ext) => {
    const digits = (profilePhone || '').replace(/\D/g, '')
    const birthNorm = normalizeBirthDateForApi(profileBirth)
    const base = {
      customerName: (profileName || '').trim() || null,
      birthDate: birthNorm.ok ? birthNorm.value : null,
      customerPhone: digits.length === 10 || digits.length === 11 ? digits : null,
      region: (profileRegion || '').trim(),
      customerSegment: (profileCustomerSegment || '').trim(),
      affiliation: (profileAffiliation || '').trim(),
      occupation: (profileOccupation || '').trim(),
      honorific: (profileHonorific || '').trim(),
      homeAddress: (profileHomeAddress || '').trim(),
      companyAddress: (profileCompanyAddress || '').trim(),
      customerEmail: (profileCustomerEmail || '').trim(),
      customerSource: (profileCustomerSource || '').trim(),
    }
    if (!ext || typeof ext !== 'object') return base
    const pick = (v, fallback) => {
      const t = v != null ? String(v).trim() : ''
      return t || fallback
    }
    const exDigits = String(ext.customerPhone || '').replace(/\D/g, '')
    const exBirth = ext.birthDate ? normalizeBirthDateForApi(String(ext.birthDate)) : { ok: true, value: base.birthDate }
    return {
      customerName: pick(ext.customerName, base.customerName) || null,
      birthDate: exBirth.ok ? (String(ext.birthDate || '').trim() ? exBirth.value : base.birthDate) : base.birthDate,
      customerPhone: exDigits.length === 10 || exDigits.length === 11 ? exDigits : base.customerPhone,
      region: pick(ext.region, base.region),
      customerSegment: pick(ext.customerSegment, base.customerSegment),
      affiliation: pick(ext.affiliation, base.affiliation),
      occupation: pick(ext.occupation, base.occupation),
      honorific: pick(ext.honorific, base.honorific),
      homeAddress: pick(ext.homeAddress, base.homeAddress),
      companyAddress: pick(ext.companyAddress, base.companyAddress),
      customerEmail: pick(ext.customerEmail, base.customerEmail),
      customerSource: pick(ext.customerSource, base.customerSource),
    }
  }

  const saveBusinessCardSide = async (fileId, side) => {
    const fid = String(fileId || '').trim()
    const normalizedSide = String(side || '').trim().toUpperCase()
    if (!fid || !normalizedSide || !Number.isFinite(customerId)) {
      return { recovered: false }
    }
    try {
      await api.post(
        `/customer-management/customers/${customerId}/business-cards`,
        { fileId: fid, side: normalizedSide },
        { __silent: true },
      )
      return { recovered: false }
    } catch (e) {
      if (e?.response?.status !== 504) throw e
      try {
        const res = await api.get(`/customer-management/customers/${customerId}/business-cards`, { __silent: true })
        const rows = unwrap(res)
        const saved = Array.isArray(rows)
          && rows.some((row) => String(row?.fileId || '') === fid && String(row?.side || '').toUpperCase() === normalizedSide)
        if (saved) return { recovered: true }
      } catch {
        // 원래 504를 그대로 처리한다.
      }
      throw e
    }
  }

  const applyCardWizardToProfile = async () => {
    if (!cardExtracted || !Number.isFinite(customerId)) return
    setCardWizardBusy(true)
    try {
      let recoveredCardSave = false
      if (cardDraftFrontId) {
        const result = await saveBusinessCardSide(cardDraftFrontId, 'FRONT')
        recoveredCardSave = recoveredCardSave || result.recovered
      }
      if (cardDraftBackId) {
        const result = await saveBusinessCardSide(cardDraftBackId, 'BACK')
        recoveredCardSave = recoveredCardSave || result.recovered
      }
      const patch = buildProfilePatchFromExtracted(cardExtracted)
      const digits = (patch.customerPhone != null ? String(patch.customerPhone) : '').replace(/\D/g, '')
      const birthNorm2 = normalizeBirthDateForApi(
        patch.birthDate != null && String(patch.birthDate).trim() ? String(patch.birthDate) : (profileBirth || ''),
      )
      if (digits.length > 0 && digits.length !== 10 && digits.length !== 11) {
        try { window?.toast?.show?.('인식된 연락처가 올바르지 않습니다. 프로필에서 수정해 주세요.', 'error') } catch { }
        return
      }
      if (!birthNorm2.ok) {
        try { window?.toast?.show?.(birthNorm2.message, 'error') } catch { }
        return
      }
      await api.patch(
        `/customer-management/customers/${customerId}`,
        {
          customerName: patch.customerName,
          birthDate: birthNorm2.value,
          customerPhone: digits.length === 10 || digits.length === 11 ? digits : null,
          region: patch.region ?? '',
          customerSegment: patch.customerSegment ?? '',
          affiliation: patch.affiliation ?? '',
          occupation: patch.occupation ?? '',
          honorific: patch.honorific ?? '',
          homeAddress: patch.homeAddress ?? '',
          companyAddress: patch.companyAddress ?? '',
          customerEmail: patch.customerEmail ?? '',
          customerSource: patch.customerSource ?? '',
        },
        { __silent: true },
      )
      try {
        window?.toast?.show?.(
          recoveredCardSave ? '명함 저장을 확인했고 프로필에 반영했습니다.' : '명함을 반영했습니다.',
          'success',
        )
      } catch { }
      setCardWizardOpen(false)
      await loadDetail(customerId)
    } catch (e) {
      const msg = getApiErrorMessage(e, '저장에 실패했습니다.')
      setErr(msg)
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setCardWizardBusy(false)
    }
  }

  /** 프로필은 그대로 두고 업로드한 명함 이미지만 고객에 연결한다. */
  const saveBusinessCardImagesKeepProfile = async () => {
    if (!Number.isFinite(customerId)) return
    setCardWizardBusy(true)
    try {
      let recoveredCardSave = false
      if (cardDraftFrontId) {
        const result = await saveBusinessCardSide(cardDraftFrontId, 'FRONT')
        recoveredCardSave = recoveredCardSave || result.recovered
      }
      if (cardDraftBackId) {
        const result = await saveBusinessCardSide(cardDraftBackId, 'BACK')
        recoveredCardSave = recoveredCardSave || result.recovered
      }
      try {
        window?.toast?.show?.(
          recoveredCardSave
            ? '명함 이미지 저장을 확인했습니다. 프로필 필드는 변경하지 않았습니다.'
            : '명함 이미지를 저장했습니다. 프로필 필드는 변경하지 않았습니다.',
          'success',
        )
      } catch { }
      setCardWizardOpen(false)
      setCardExtracted(null)
      setCardDiffs([])
      await loadDetail(customerId)
    } catch (e) {
      const msg = getApiErrorMessage(e, '명함 저장에 실패했습니다.')
      setErr(msg)
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setCardWizardBusy(false)
    }
  }

  const deleteCurrentCard = async () => {
    if (!businessCardRows.length || !Number.isFinite(customerId)) return
    const i = Math.min(Math.max(0, cardCarouselIdx), businessCardRows.length - 1)
    const row = businessCardRows[i]
    const linkId = row?.id
    if (linkId == null) return
    const sideLabel = row?.side === 'BACK' ? '뒷면' : '앞면'
    if (
      !window.confirm(
        `${sideLabel} 저장된 명함 이미지를 삭제할까요?\n삭제 후에는 고객 상세에서 이 이미지를 다시 확인할 수 없습니다. 보관이 필요하면 먼저 다운로드해 주세요.`,
      )
    )
      return
    setCardDeleting(true)
    setErr('')
    try {
      await api.delete(`/customer-management/customers/${customerId}/business-cards/${encodeURIComponent(String(linkId))}`, {
        __silent: true,
      })
      try { window?.toast?.show?.('명함을 삭제했습니다.', 'success') } catch { }
      const nextLen = Math.max(0, businessCardRows.length - 1)
      setCardCarouselIdx((idx) => (nextLen <= 0 ? 0 : Math.min(idx, nextLen - 1)))
      await loadDetail(customerId)
    } catch (e) {
      const msg = getApiErrorMessage(e, '명함 삭제에 실패했습니다.')
      setErr(msg)
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setCardDeleting(false)
    }
  }

  const downloadCurrentCard = async () => {
    if (!businessCardRows.length) return
    const i = Math.min(Math.max(0, cardCarouselIdx), businessCardRows.length - 1)
    const fid = businessCardRows[i]?.fileId
    if (!fid) return
    try {
      const res = await api.get(`/files/${encodeURIComponent(fid)}/download`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `명함_${String(fid).replace(/[^a-zA-Z0-9._-]/g, '_').slice(-40)}`
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => {
        try { URL.revokeObjectURL(url) } catch { }
      }, 3000)
    } catch {
      try { window?.toast?.show?.('다운로드에 실패했습니다.', 'error') } catch { }
    }
  }

  if (!Number.isFinite(customerId) || customerId <= 0) {
    return (
      <div className="py-2">
        <p className="text-sm text-rose-700">잘못된 경로입니다.</p>
        <Link className="mt-4 inline-block text-sm font-medium text-brand-navy hover:underline" to="/support/customers">
          고객 목록으로
        </Link>
      </div>
    )
  }

  const c = detail?.customer
  const profileReadOnly = profileSaving
  const coverageRuleQuery = normalizeSearchText(coverageRuleFilter)
  const coverageRuleItems = asArray(coverageRuleSettings?.items)
  const filteredCoverageRuleItems = coverageRuleItems.filter((row) => {
    if (!coverageRuleQuery) return true
    return normalizeSearchText([
      row.category,
      row.label,
      row.importance,
      row.keywords,
    ].filter(Boolean).join(' ')).includes(coverageRuleQuery)
  })
  const diagnosisRuleQuery = normalizeSearchText(diagnosisRuleFilter)
  const filteredDiagnosisRuleRows = diagnosisRuleRows.filter((row) => {
    if (!diagnosisRuleQuery) return true
    return normalizeSearchText([
      row.title,
      row.diagnosisName,
      row.category,
      row.scenarioCode,
      row.coverageKeywords,
    ].filter(Boolean).join(' ')).includes(diagnosisRuleQuery)
  })
  const selectedDiagnosisRule = filteredDiagnosisRuleRows.find((row) => row.scenarioCode === diagnosisRuleSelectedCode)
    || filteredDiagnosisRuleRows[0]
    || diagnosisRuleRows.find((row) => row.scenarioCode === diagnosisRuleSelectedCode)
    || diagnosisRuleRows[0]
    || null
  const selectedDiagnosisNeedManWon = selectedDiagnosisRule
    ? Number(selectedDiagnosisRule.outOfPocketCostManWon || 0) + Number(selectedDiagnosisRule.recoveryLivingCostManWon || 0)
    : 0

  return (
    <div className="min-w-0 overflow-x-hidden pt-2 pb-8 sm:pb-10">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <Link className="text-sm font-medium text-brand-navy hover:underline" to="/support/customers">
            ← 고객 목록
          </Link>
          <h1 className="mt-2 break-words text-xl font-bold text-slate-900 sm:text-2xl">
            {c?.customerName ? `${c.customerName} · 고객 상세` : '고객 상세'}
          </h1>
        </div>
        <div className="flex shrink-0 gap-2 self-start">
          <Button
            type="button"
            variant="sgezOutline"
            size="sm"
            onClick={() => loadDetail(customerId)}
          >
            새로고침
          </Button>
        </div>
      </div>

      {err ? <div className="mb-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</div> : null}

      {detailLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">불러오는 중…</div>
      ) : !detail ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          상세를 표시할 수 없습니다.
          <div className="mt-3">
            <Link className="font-medium text-brand-navy hover:underline" to="/support/customers">목록으로</Link>
          </div>
        </div>
      ) : (
        <div className="min-w-0 space-y-4 sm:space-y-6">
          <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
            {CUSTOMER_PAGE_VIEWS.map((view) => (
              <button
                key={view.id}
                type="button"
                className={`shrink-0 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${pageView === view.id
                    ? 'border-brand-navy bg-sky-50 text-brand-navy'
                    : 'border-transparent text-slate-600 hover:bg-slate-50'
                  }`}
                onClick={() => selectCustomerPageView(view.id)}
              >
                {view.label}
              </button>
            ))}
          </div>

          {SHOW_CUSTOMER_SALES_PIPELINE ? (
          <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2">
              <div className="text-sm font-semibold text-slate-800">상담·영업 플로우</div>
            </div>
            <div className="grid gap-1.5 md:grid-cols-3 xl:grid-cols-6">
              {salesPipeline.map((step, idx) => {
                const done = step.status === 'done'
                const current = step.status === 'current'
                return (
                  <div
                    key={step.id}
                    className={`rounded-lg border px-2 py-2 ${done
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                        : current
                          ? 'border-sky-200 bg-sky-50 text-sky-950 ring-1 ring-sky-100'
                          : 'border-slate-200 bg-slate-50 text-slate-600'
                      }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-500">S{idx + 1}</span>
                        <span className="truncate text-xs font-black">{step.label}</span>
                      </div>
                      <div className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${done ? 'bg-emerald-100 text-emerald-700' : current ? 'bg-sky-100 text-sky-700' : 'bg-white text-slate-500'
                        }`}>
                        {done ? '완료' : current ? '현재' : '대기'}
                      </div>
                    </div>
                    {step.actionKind ? (
                      <button
                        type="button"
                        onClick={() => runPipelineAction(step)}
                        className={salesPipelineActionClass(current)}
                      >
                        {step.cta || '확인'} →
                      </button>
                    ) : step.ctaPath ? (
                      <Link
                        to={step.ctaPath}
                        className={salesPipelineActionClass(current)}
                      >
                        {step.cta || '확인'} →
                      </Link>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </section>
          ) : null}

          {pageView === 'info' ? (
          <div className="grid min-w-0 gap-3 sm:gap-4 lg:grid-cols-3 lg:items-stretch">
            <div className="flex min-w-0 flex-col gap-3 sm:gap-4">
              <div className="flex items-center gap-2 px-1">
                <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">기본 정보</div>
              </div>
              <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                    <div className="text-sm font-bold text-slate-900">기본 정보</div>
                  </div>
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    className="shrink-0"
                    disabled={profileSaving}
                    onClick={saveProfile}
                  >
                    {profileSaving ? '저장 중…' : '저장'}
                  </Button>
                </div>
                <div className="flex flex-1 flex-col space-y-2 p-4 sm:p-5">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">이름</label>
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-900"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        disabled={profileReadOnly}
                        placeholder="고객 이름"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">호칭</label>
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                        value={profileHonorific}
                        onChange={(e) => setProfileHonorific(e.target.value)}
                        disabled={profileReadOnly}
                        placeholder="님·선생님 등"
                        maxLength={80}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">전화번호</label>
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums"
                        value={profilePhone}
                        onChange={(e) => setProfilePhone(formatKoreanPhoneInput(e.target.value))}
                        disabled={profileReadOnly}
                        placeholder="010-0000-0000"
                        inputMode="numeric"
                        autoComplete="tel"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">이메일</label>
                      <input
                        type="email"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                        value={profileCustomerEmail}
                        onChange={(e) => setProfileCustomerEmail(e.target.value)}
                        disabled={profileReadOnly}
                        placeholder="선택"
                        maxLength={255}
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">생년월일</label>
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums"
                        value={profileBirth}
                        onChange={(e) => setProfileBirth(formatBirthYmdInput(e.target.value))}
                        disabled={profileReadOnly}
                        placeholder="YYYY-MM-DD"
                        inputMode="numeric"
                        autoComplete="bday"
                      />
                      {manAge != null ? <p className="mt-0.5 text-[11px] text-slate-500">만 {manAge}세</p> : null}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">성별</label>
                      <select
                        className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                        value={profileGender}
                        onChange={(e) => setProfileGender(e.target.value)}
                        disabled={profileReadOnly}
                      >
                        <option value="">미입력</option>
                        <option value="MALE">남성</option>
                        <option value="FEMALE">여성</option>
                        <option value="UNKNOWN">확인 필요</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">상령일</label>
                      <div className="w-full rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-sm tabular-nums text-slate-800">
                        {c?.insuranceAgeDate != null && String(c.insuranceAgeDate).trim() !== '' ? String(c.insuranceAgeDate) : '—'}
                      </div>
                      <p className="mt-0.5 text-[11px] text-slate-500">생일 + 6개월로 자동 계산합니다.</p>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">고객 구분</label>
                      <select
                        className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                        value={profileCustomerSegment}
                        onChange={(e) => setProfileCustomerSegment(e.target.value)}
                        disabled={profileReadOnly}
                      >
                        {segmentSelectOptions.map((o) => (
                          <option key={o.value === '' ? '_segment_empty' : o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">고객 출처</label>
                      <select
                        className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                        value={profileCustomerSource}
                        onChange={(e) => setProfileCustomerSource(e.target.value)}
                        disabled={profileReadOnly}
                      >
                        {sourceSelectOptions.map((o) => (
                          <option key={o.value === '' ? '_source_empty' : o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">소속</label>
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                        value={profileAffiliation}
                        onChange={(e) => setProfileAffiliation(e.target.value)}
                        disabled={profileReadOnly}
                        placeholder="직장·단체 등"
                        maxLength={255}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">하시는 일</label>
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                        value={profileOccupation}
                        onChange={(e) => setProfileOccupation(e.target.value)}
                        disabled={profileReadOnly}
                        placeholder="직무·업종 등"
                        maxLength={255}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">월 소득(만원)</label>
                      <div className="flex rounded border border-slate-300 bg-white focus-within:ring-1 focus-within:ring-brand-navy">
                        <input
                          type="text"
                          className="min-w-0 flex-1 rounded-l px-2.5 py-1.5 text-sm tabular-nums outline-none"
                          value={profileMonthlyIncome}
                          onChange={(e) => setProfileMonthlyIncome(e.target.value.replace(/\D/g, ''))}
                          disabled={profileReadOnly}
                          placeholder="예: 500"
                          inputMode="numeric"
                          maxLength={7}
                        />
                        <span className="shrink-0 border-l border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-500">만원</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">확인 기준</label>
                      <select
                        className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                        value={profileIncomeBasis}
                        onChange={(e) => setProfileIncomeBasis(e.target.value)}
                        disabled={profileReadOnly}
                      >
                        <option value="">미입력</option>
                        <option value="SELF">본인</option>
                        <option value="HOUSEHOLD">가구</option>
                        <option value="ESTIMATED">추정</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-0.5">맞벌이 여부</label>
                      <select
                        className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                        value={profileDualIncomeStatus}
                        onChange={(e) => setProfileDualIncomeStatus(e.target.value)}
                        disabled={profileReadOnly}
                      >
                        <option value="">미입력</option>
                        <option value="YES">맞벌이</option>
                        <option value="NO">외벌이</option>
                        <option value="UNKNOWN">미확인</option>
                      </select>
                    </div>
                  </div>
                  <div className="-mt-1 rounded border border-blue-100 bg-blue-50 px-2.5 py-1.5 text-[11px] leading-relaxed text-blue-900">
                    월소득은 리포트의 보험료 부담률 산출에 사용됩니다.
                  </div>
                  <div>
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <label className="block text-xs font-medium text-slate-600">집 주소</label>
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        disabled={profileReadOnly}
                        onClick={() => openProfileAddressSearch('home')}
                      >
                        주소 검색
                      </button>
                    </div>
                    <input
                      type="text"
                      className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={profileHomeAddress}
                      onChange={(e) => setProfileHomeAddress(e.target.value)}
                      disabled={profileReadOnly}
                      placeholder="선택"
                      maxLength={500}
                    />
                  </div>
                  <div>
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <label className="block text-xs font-medium text-slate-600">회사 주소</label>
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        disabled={profileReadOnly}
                        onClick={() => openProfileAddressSearch('company')}
                      >
                        주소 검색
                      </button>
                    </div>
                    <input
                      type="text"
                      className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={profileCompanyAddress}
                      onChange={(e) => setProfileCompanyAddress(e.target.value)}
                      disabled={profileReadOnly}
                      placeholder="선택"
                      maxLength={500}
                    />
                  </div>
                  {profilePostcodeError ? <p className="text-[11px] text-rose-600">{profilePostcodeError}</p> : null}
                  <div className="pt-1">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <label className="block text-xs font-medium text-slate-600">메모</label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={memoSaving}
                        onClick={saveMemo}
                      >
                        {memoSaving ? '저장 중…' : '메모 저장'}
                      </Button>
                    </div>
                    <div className="rounded-md border border-amber-200 bg-amber-50/90 px-2.5 py-1.5 text-[10px] leading-snug text-amber-950 sm:text-[11px]">
                      주민등록번호, 계좌번호, 카드번호, 건강·진단 상세 등 <strong>민감한 개인정보는 이 메모에 적지 마세요.</strong>
                    </div>
                    <textarea
                      className="mt-2 w-full min-h-[104px] rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={memoDraft}
                      onChange={(e) => setMemoDraft(e.target.value)}
                      maxLength={4000}
                      placeholder="상담 메모, 다음 연락 일정 등 (민감정보 제외)"
                      disabled={memoSaving}
                    />
                    <div className="mt-1 text-[11px] text-slate-500">{memoDraft.length} / 4000</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-3 sm:gap-4">
              <div className="flex items-center gap-2 px-1">
                <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">부가 정보</div>
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-3 sm:gap-4">
              <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:p-0">
                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-3 sm:px-4">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                    <div className="text-sm font-bold text-slate-900">기념일</div>
                  </div>
                </div>
                <div className="p-3 sm:p-5">
                <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_150px_auto]">
                  <input
                    type="text"
                    className="min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                    value={anniversaryTitle}
                    onChange={(e) => setAnniversaryTitle(e.target.value)}
                    maxLength={120}
                    placeholder="기념일명 예: 결혼기념일"
                    disabled={anniversarySaving}
                  />
                  <CustomerYmdInput
                    value={anniversaryDate}
                    onChange={setAnniversaryDate}
                    disabled={anniversarySaving}
                    placeholder="기념일 YYYY-MM-DD"
                    ariaLabel="기념일 날짜"
                  />
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    className="w-full shrink-0 sm:w-auto"
                    disabled={anniversarySaving}
                    onClick={addAnniversary}
                  >
                    {anniversarySaving ? '추가 중…' : '추가'}
                  </Button>
                </div>
                {anniversaryRows.length === 0 ? (
                  <p className="mt-3 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
                    등록된 기념일이 없습니다.
                  </p>
                ) : (
                  <div className="mt-3 space-y-1.5">
                    {anniversaryRows.map((row) => {
                      const days = daysUntilAnnualDate(row?.anniversaryDate)
                      const soon = days != null && days <= 14
                      return (
                        <div
                          key={row.id}
                          className={`flex w-full flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${soon ? 'border-amber-200 bg-amber-50 text-amber-950' : 'border-slate-200 bg-slate-50 text-slate-700'
                            }`}
                        >
                          <span className="truncate font-semibold">{row.title || '기념일'}</span>
                          <span className="shrink-0 font-bold">{anniversaryDDayLabel(row.anniversaryDate)}</span>
                          <span className="shrink-0 text-[11px] text-slate-500">{row.anniversaryDate || '—'}</span>
                          <button
                            type="button"
                            className="ml-auto shrink-0 rounded border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            disabled={anniversaryDeletingId === row.id}
                            onClick={() => deleteAnniversary(row)}
                            title="삭제"
                          >
                            {anniversaryDeletingId === row.id ? '삭제 중…' : '삭제'}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
                </div>
              </div>

              <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-3 sm:px-4">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                    <div className="text-sm font-bold text-slate-900">가족</div>
                  </div>
                </div>
                <div className="p-3 sm:p-5">
                  <div className="grid min-w-0 gap-2 sm:grid-cols-2">
                    <input
                      type="text"
                      className="min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                      maxLength={120}
                      placeholder="이름"
                      disabled={familySaving}
                    />
                    <input
                      type="text"
                      className="min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={familyRelationship}
                      onChange={(e) => setFamilyRelationship(e.target.value)}
                      maxLength={80}
                      placeholder="관계 예: 배우자, 자녀"
                      disabled={familySaving}
                    />
                    <CustomerYmdInput
                      value={familyBirthDate}
                      onChange={setFamilyBirthDate}
                      disabled={familySaving}
                      placeholder="생년월일 YYYY-MM-DD"
                      ariaLabel="가족 생년월일"
                    />
                    <select
                      className="min-w-0 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                      value={familyCohabiting}
                      onChange={(e) => setFamilyCohabiting(e.target.value)}
                      disabled={familySaving}
                    >
                      <option value="">동거여부 미확인</option>
                      <option value="YES">동거</option>
                      <option value="NO">비동거</option>
                    </select>
                  </div>
                  <div className="mt-2 grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                    <input
                      type="text"
                      className="min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={familyLinkedSearch}
                      onChange={(e) => setFamilyLinkedSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          searchFamilyLinkCandidates()
                        }
                      }}
                      placeholder="기존 고객 연결 검색"
                      disabled={familySaving}
                    />
                    <button
                      type="button"
                      className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={searchFamilyLinkCandidates}
                      disabled={familySaving}
                    >
                      검색
                    </button>
                  </div>
                  {familyLinkCandidates.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {familyLinkCandidates.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`max-w-full truncate rounded border px-2 py-1 text-[11px] font-semibold ${String(familyLinkedCustomerRefId) === String(item.id) ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                          onClick={() => {
                            setFamilyLinkedCustomerRefId(String(item.id))
                            setFamilyName(item.customerName || familyName)
                            setFamilyBirthDate(formatBirthYmdInput(item.birthDate || familyBirthDate))
                          }}
                        >
                          {customerNamePhoneLabel(item)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {selectedFamilyLink ? (
                    <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-2 rounded border border-brand-navy/20 bg-blue-50 px-2.5 py-1.5 text-[11px] text-blue-900">
                      <span className="min-w-0 flex-1 truncate">
                        기존 고객 연결됨: <strong>{customerNamePhoneLabel(selectedFamilyLink)}</strong>
                      </span>
                      <button
                        type="button"
                        className="shrink-0 rounded border border-blue-200 bg-white px-2 py-0.5 font-semibold text-blue-900 hover:bg-blue-50"
                        onClick={() => setFamilyLinkedCustomerRefId('')}
                        disabled={familySaving}
                      >
                        해제
                      </button>
                    </div>
                  ) : (
                    <p className="mt-2 text-[11px] text-slate-500">기존 고객과 연결하면 가족 정보에서 고객 상세로 이어지는 상담 맥락을 유지할 수 있습니다.</p>
                  )}
                  <textarea
                    className="mt-2 min-h-[64px] w-full min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                    value={familyMemo}
                    onChange={(e) => setFamilyMemo(e.target.value)}
                    maxLength={1000}
                    placeholder="메모"
                    disabled={familySaving}
                  />
                  <div className="mt-2 flex justify-end">
                    <Button type="button" variant="sgez" size="sm" className="w-full sm:w-auto" disabled={familySaving} onClick={addFamilyMember}>
                      {familySaving ? '추가 중…' : '가족 추가'}
                    </Button>
                  </div>
                  {familyRows.length === 0 ? (
                    <p className="mt-3 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
                      등록된 가족 정보가 없습니다.
                    </p>
                  ) : (
                    <div className="mt-3 space-y-1.5">
                      {familyRows.map((row) => (
                        <div key={row.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-slate-900">{row.name || '가족'}</span>
                            <span>{row.relationship || '관계 미입력'}</span>
                            <span className="text-slate-500">{row.birthDate || '생년월일 미입력'}</span>
                            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                              {row.cohabiting === true ? '동거' : row.cohabiting === false ? '비동거' : '동거 미확인'}
                            </span>
                            {row.linkedCustomerName ? (
                              <span className="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-800">
                                고객 연결
                              </span>
                            ) : null}
                            <button
                              type="button"
                              className="ml-auto shrink-0 rounded border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                              disabled={familyDeletingId === row.id}
                              onClick={() => deleteFamilyMember(row)}
                            >
                              {familyDeletingId === row.id ? '삭제 중…' : '삭제'}
                            </button>
                          </div>
                          {row.linkedCustomerName ? (
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-brand-navy">
                              <span>연결 고객: {row.linkedCustomerName}</span>
                              {row.linkedCustomerRefId ? (
                                <Link
                                  className="font-semibold hover:underline"
                                  to={`/support/customers/${row.linkedCustomerRefId}`}
                                >
                                  고객 정보로 바로가기
                                </Link>
                              ) : null}
                            </div>
                          ) : null}
                          {row.memo ? <div className="mt-1 text-[11px] text-slate-500">{row.memo}</div> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                    <div className="text-sm font-bold text-slate-900">명함</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50"
                      onClick={openCardWizard}
                    >
                      명함 업로드
                    </button>
                    {businessCardRows.length > 0 ? (
                      <>
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50"
                          onClick={downloadCurrentCard}
                        >
                          다운로드
                        </button>
                        <button
                          type="button"
                          className="rounded border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-900 hover:bg-rose-100 disabled:opacity-50"
                          disabled={cardDeleting}
                          onClick={deleteCurrentCard}
                        >
                          {cardDeleting ? '삭제 중…' : '삭제'}
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="p-4 sm:p-5">
                {businessCardRows.length === 0 ? (
                  <div className="space-y-2">
                    <div className="flex min-h-[200px] items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50">
                      <div className="w-full max-w-[320px] rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm">
                        <div className="h-3 w-24 rounded bg-slate-200" />
                        <div className="mt-5 h-5 w-32 rounded bg-slate-300" />
                        <div className="mt-2 h-3 w-44 rounded bg-slate-200" />
                        <div className="mt-5 space-y-1.5">
                          <div className="h-2.5 w-full rounded bg-slate-100" />
                          <div className="h-2.5 w-5/6 rounded bg-slate-100" />
                          <div className="h-2.5 w-2/3 rounded bg-slate-100" />
                        </div>
                        <button
                          type="button"
                          className="mt-5 w-full rounded bg-brand-navy px-3 py-2 text-xs font-bold text-white hover:opacity-95"
                          onClick={openCardWizard}
                        >
                          명함 이미지 업로드
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">명함 이미지를 업로드하면 이 영역에서 바로 확인하고, 필요하면 기본정보에 반영할 수 있습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative flex min-h-[200px] items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-50">
                      {cardPreviewUrl ? (
                        <img src={cardPreviewUrl} alt="명함" className="max-h-[280px] w-full object-contain" />
                      ) : (
                        <span className="text-xs text-slate-500">미리보기를 불러오는 중…</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        disabled={businessCardRows.length < 2}
                        onClick={() => setCardCarouselIdx((i) => (i - 1 + businessCardRows.length) % businessCardRows.length)}
                      >
                        이전
                      </button>
                      <span className="text-[11px] text-slate-500">
                        {businessCardRows[Math.min(cardCarouselIdx, businessCardRows.length - 1)]?.side === 'BACK' ? '뒷면' : '앞면'}
                        {' '}
                        ({cardCarouselIdx + 1}/{businessCardRows.length})
                      </span>
                      <button
                        type="button"
                        className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        disabled={businessCardRows.length < 2}
                        onClick={() => setCardCarouselIdx((i) => (i + 1) % businessCardRows.length)}
                      >
                        다음
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-3 sm:gap-4">
              <div className="flex items-center gap-2 px-1">
                <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">작업</div>
              </div>
              <div className="flex min-w-0 flex-col gap-3 sm:gap-4">
              <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-3 sm:px-4">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                    <div className="text-sm font-bold text-slate-900">다음 활동 추천</div>
                  </div>
                </div>
                <div className="flex min-w-0 flex-col p-3 sm:p-5">
                {nextActions.length === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-500">
                    현재 즉시 처리할 추천 액션은 없습니다.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {nextActions.slice(0, 3).map((action) => {
                      const className = `flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition hover:bg-slate-50 ${action.priority === 'HIGH' ? 'border-rose-200 bg-rose-50/70 text-rose-950' : 'border-slate-200 bg-slate-50 text-slate-800'
                        }`
                      const content = (
                        <>
                          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${action.priority === 'HIGH' ? 'bg-rose-100 text-rose-700' : 'bg-white text-slate-600'
                            }`}>
                            {action.priority === 'HIGH' ? '우선' : action.priority === 'LOW' ? '기본' : '추천'}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-semibold">{action.title}</span>
                          <span className="shrink-0 text-[11px] font-bold text-brand-navy">{action.ctaLabel} →</span>
                        </>
                      )
                      return action.actionKind ? (
                        <button key={action.code} type="button" className={className} onClick={() => runNextAction(action)}>
                          {content}
                        </button>
                      ) : (
                        <Link key={action.code} to={action.targetPath} className={className}>
                          {content}
                        </Link>
                      )
                    })}
                  </div>
                )}
                </div>
              </div>

              <div className="flex min-w-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-3 sm:px-4">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                    <div className="text-sm font-bold text-slate-900">AI 고객 메시지</div>
                  </div>
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={aiMessageGenerating}
                    onClick={generateAiMessageDraft}
                  >
                    {aiMessageGenerating ? '생성 중…' : '문구 생성'}
                  </Button>
                </div>
                <div className="flex min-w-0 flex-col p-3 pb-5 sm:p-5">
                <div className="grid min-w-0 gap-2 sm:grid-cols-2">
                  <label className="block">
                    <span className="block text-[11px] font-medium text-slate-600">생성 유형</span>
                    <select
                      className="mt-1 w-full min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={aiMessageType}
                      onChange={(e) => setAiMessageType(e.target.value)}
                      disabled={aiMessageGenerating}
                    >
                      {AI_MESSAGE_TYPES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[11px] font-medium text-slate-600">톤</span>
                    <select
                      className="mt-1 w-full min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={aiMessageTone}
                      onChange={(e) => setAiMessageTone(e.target.value)}
                      disabled={aiMessageGenerating}
                    >
                      {AI_MESSAGE_TONES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </label>
                </div>
                {aiMessageType === 'ANNIVERSARY' ? (
                  <label className="mt-2 block">
                    <span className="block text-[11px] font-medium text-slate-600">연결 기념일</span>
                    <select
                      className="mt-1 w-full min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                      value={aiMessageAnniversaryId}
                      onChange={(e) => setAiMessageAnniversaryId(e.target.value)}
                      disabled={aiMessageGenerating}
                    >
                      <option value="">기념일 선택 없이 생성</option>
                      {anniversaryRows.map((row) => (
                        <option key={row.id} value={row.id}>{row.title} · {row.anniversaryDate}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <textarea
                  className="mt-2 min-h-[72px] w-full min-w-0 rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                  value={aiMessageContext}
                  onChange={(e) => setAiMessageContext(e.target.value)}
                  maxLength={500}
                  placeholder="선택 입력: 고객에게 꼭 반영할 상담 맥락이나 요청사항"
                  disabled={aiMessageGenerating}
                />
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-950">
                  발송 전 고객 상황, 약관·심사 조건, 수신동의 여부를 확인해 주세요.
                </div>
                </div>
              </div>
              </div>
            </div>
          </div>
          ) : null}

          {pageView === 'work' ? (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="-mb-px flex gap-0 overflow-x-auto border-b border-slate-200 bg-slate-50/80 px-1 pt-1">
              {visibleSectionTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`shrink-0 whitespace-nowrap rounded-t-md border border-b-0 px-3 py-2.5 text-sm font-semibold transition-colors ${sectionTab === t.id
                      ? 'border-slate-200 bg-white text-brand-navy'
                      : 'border-transparent bg-transparent text-slate-600 hover:bg-white/60'
                    }`}
                  onClick={() => selectCustomerWorkTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="p-4 sm:p-5 text-sm space-y-6">
              {sectionTab === 'integrated-report' ? (
                <div className="space-y-5">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <div className="text-sm font-bold text-slate-900">통합 리포트 준비 상태</div>
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${integratedReportReadiness.canCreate ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                          필수 {integratedReportReadiness.readyRequired}/{integratedReportReadiness.requiredCount}
                        </span>
                        <span className="text-xs font-medium text-slate-500">{integratedReportReadiness.qualityLabel}</span>
                      </div>
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        onClick={openCoverageRuleSettings}
                      >
                        표준가입금액 설정
                      </button>
                    </div>

                    <div className="mt-2 grid gap-1.5 md:grid-cols-2">
                      {integratedReportReadiness.rows.filter((row) => row.group === '필수').map((row) => (
                        <div key={row.key} className={`rounded border px-2.5 py-1.5 ${readinessCardTone(row)}`}>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="min-w-0 text-xs font-bold text-slate-900">{row.title}</span>
                            <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${readinessBadgeTone(row)}`}>
                              {readinessStatusLabel(row.status)}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[11px] text-slate-600">{row.detail}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                  </div>

                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="text-sm font-bold text-slate-900">설계사 리포트 코멘트</div>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        고객에게 설명할 때 그대로 가져갈 수 있도록 근거, 전달 문장, 다음 액션을 분리해 기록합니다.
                      </p>
                      <div className="mt-4 grid gap-3">
                        <label className="block text-xs font-semibold text-slate-700">
                          내부 검토 코멘트
                          <textarea
                            className="mt-1 min-h-[96px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={integratedReportComment}
                            onChange={(e) => setIntegratedReportComment(e.target.value.slice(0, 1200))}
                            placeholder="예: 질병통계 대비 근골격계 외래 비중이 높고, 현재 계약의 입원/수술 담보와 연결성 확인 필요."
                          />
                        </label>
                        <label className="block text-xs font-semibold text-slate-700">
                          고객 전달용 요약
                          <textarea
                            className="mt-1 min-h-[96px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={integratedReportCustomerNote}
                            onChange={(e) => setIntegratedReportCustomerNote(e.target.value.slice(0, 1200))}
                            placeholder="예: 최근 진료 흐름과 기존 보장을 함께 보면, 먼저 확인해야 할 보장과 정리 가능한 항목을 나눠서 보실 수 있습니다."
                          />
                        </label>
                        <label className="block text-xs font-semibold text-slate-700">
                          다음 액션
                          <textarea
                            className="mt-1 min-h-[72px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={integratedReportNextStep}
                            onChange={(e) => setIntegratedReportNextStep(e.target.value.slice(0, 800))}
                            placeholder="예: 보장분석 검수 저장 후 질병통계 연계 리포트 생성, 만기계약 2건 갱신 전 상담 일정 등록."
                          />
                        </label>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm font-bold text-slate-900">리포트 미리보기 방향</div>
                      <ul className="mt-3 space-y-2 text-xs leading-relaxed text-slate-600">
                        <li>· 진료정보분석: 최근 고지/진료 흐름과 주요 질환군</li>
                        <li>· 숨은보험금: 미청구 가능성, 실손 수령 비교</li>
                        <li>· 보험계약: 질병통계 대비 보완/중복 점검 후보</li>
                        <li>· 만기/설문: 갱신 전 상담 포인트와 고객 니즈</li>
                      </ul>
                      <div className="mt-4 rounded border border-white bg-white px-3 py-2 text-xs text-slate-600">
                        코멘트 입력량: {integratedReportCommentCount.toLocaleString('ko-KR')}자
                      </div>
                      <div className="mt-3 rounded border border-white bg-white px-3 py-2 text-xs text-slate-600">
                        출력 항목은 생성하기를 누른 뒤 선택합니다. 최근 선택값은 {integratedReportSelectedSectionCount}/{INTEGRATED_REPORT_SECTION_OPTIONS.length}개입니다.
                      </div>
                      <button
                        type="button"
                        className="mt-3 w-full rounded bg-brand-navy px-3 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={integratedReportGenerating}
                        onClick={openIntegratedReportSectionModal}
                      >
                        {integratedReportGenerating ? '생성 중…' : '통합 리포트 생성'}
                      </button>
                      {integratedReportError ? (
                        <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                          {integratedReportError}
                        </div>
                      ) : null}
                      <div className="mt-4 border-t border-slate-200 pt-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-bold text-slate-900">최근 생성 이력</div>
                          <button
                            type="button"
                            className="text-[11px] font-semibold text-brand-navy hover:underline disabled:text-slate-400"
                            disabled={integratedReportRowsLoading}
                            onClick={loadIntegratedReports}
                          >
                            새로고침
                          </button>
                        </div>
                        {integratedReportRowsLoading ? (
                          <div className="mt-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">불러오는 중…</div>
                        ) : integratedReportRows.length === 0 ? (
                          <div className="mt-2 rounded border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">아직 생성된 통합 리포트가 없습니다.</div>
                        ) : (
                          <div className="mt-2 space-y-1.5">
                            {integratedReportRows.slice(0, 3).map((row) => {
                              const rowStatus = integratedReportStatusValue(row.status)
                              const running = rowStatus === 'PENDING' || rowStatus === 'RUNNING'
                              const failed = rowStatus === 'FAILED'
                              return (
                                <button
                                  key={row.id}
                                  type="button"
                                  className={`block w-full rounded border px-3 py-2 text-left text-xs hover:bg-slate-50 ${running ? 'border-blue-200 bg-blue-50/60' : failed ? 'border-rose-200 bg-rose-50/60' : 'border-slate-200 bg-white'}`}
                                  onClick={() => openIntegratedReport(row.id, { popup: true })}
                                >
                                  <span className="block truncate font-semibold text-slate-800">{row.title || `통합 리포트 #${row.id}`}</span>
                                  <span className="mt-0.5 block text-[11px] text-slate-500">
                                    {formatApiDateTimeDisplay(row.createdAtDisplay, row.createdAt)} · {row.qualityLabel || row.statusLabel || integratedReportStatusLabel(row.status)}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {integratedReportDraft ? (
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-slate-900">
                            {integratedReportDraft.summary?.customerFacingTitle || integratedReportDraft.summary?.headline || '통합분석 리포트'}
                          </div>
                          <div className="mt-1 text-xs text-slate-600">
                            {integratedReportDraft.reportDate || '-'} · 계약 {integratedReportDraft.summary?.coverageContractCount || 0}건 · 담보 {integratedReportDraft.summary?.coverageCount || 0}개
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <button
                            type="button"
                            className="rounded bg-brand-navy px-4 py-2 text-sm font-bold text-white hover:opacity-95"
                            onClick={previewIntegratedReport}
                          >
                            통합분석 리포트 보기
                          </button>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-brand-navy hover:bg-slate-50"
                            onClick={printIntegratedReport}
                          >
                            인쇄/PDF
                          </button>
                          {isOperator ? (
                            <>
                              {integratedReportTemplateOptions.length > 0 ? (
                                <select
                                  className="max-w-[190px] rounded border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                                  value={integratedReportTemplateId}
                                  onChange={(e) => setIntegratedReportTemplateId(e.target.value)}
                                  disabled={integratedReportTemplateBusy}
                                  title="운영자 전용 통합 리포트 템플릿"
                                >
                                  <option value="">운영 기본 템플릿</option>
                                  {integratedReportTemplateOptions.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {outputTemplateLabel(template)}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              <button
                                type="button"
                                className="rounded border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={integratedReportTemplateBusy || integratedReportTemplateOptions.length === 0}
                                onClick={downloadIntegratedReportTemplateImage}
                                title={integratedReportTemplateOptions.length > 0 ? '운영자 전용 출력물 템플릿 이미지' : '게시된 이미지 템플릿이 없습니다.'}
                              >
                                {integratedReportTemplateBusy ? '생성 중...' : '템플릿 이미지'}
                              </button>
                              {integratedReportHtmlTemplateOptions.length > 0 ? (
                                <select
                                  className="max-w-[190px] rounded border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                                  value={integratedReportHtmlTemplateId}
                                  onChange={(e) => setIntegratedReportHtmlTemplateId(e.target.value)}
                                  disabled={integratedReportHtmlBusy}
                                  title="운영자 전용 통합 리포트 HTML 템플릿"
                                >
                                  <option value="">운영 기본 HTML</option>
                                  {integratedReportHtmlTemplateOptions.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {outputTemplateLabel(template)}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              <button
                                type="button"
                                className="rounded border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={integratedReportHtmlBusy || integratedReportHtmlTemplateOptions.length === 0}
                                onClick={downloadIntegratedReportTemplateHtml}
                                title={integratedReportHtmlTemplateOptions.length > 0 ? '운영자 전용 출력물 HTML' : '게시된 HTML 템플릿이 없습니다.'}
                              >
                                {integratedReportHtmlBusy ? '생성 중...' : 'HTML'}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {sectionTab === 'contracts' ? (
                  <div className="space-y-5">
                    <details open={coverageContracts.length === 0} className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                      <summary className="cursor-pointer select-none text-sm font-bold text-indigo-950">
                        보장분석 PDF 계약 분석
                        {coverageContracts.length > 0 ? <span className="ml-2 text-xs font-semibold text-indigo-700">저장 계약이 있어 접힌 상태로 표시됩니다.</span> : null}
                      </summary>
                      <div className="mt-3 space-y-3">
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <div className="min-w-0 flex-1 rounded-md border border-indigo-100/90 bg-white/80 px-3 py-2 text-[11px] text-slate-700">
                            <span className="font-semibold text-slate-800">지원 보장분석 양식</span>
                            <span className="mx-2 text-slate-300">|</span>
                            <span className="text-slate-600">KB손해보험, DB손해보험, 메리츠화재, 삼성화재</span>
                          </div>
                          <button
                            type="button"
                            className="rounded border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-900 hover:bg-indigo-50 disabled:opacity-50"
                            disabled={coverageLoading}
                            onClick={loadCoverageContracts}
                          >
                            새로고침
                          </button>
                        </div>
                      {coverageErr ? (
                        <div className="rounded border border-rose-200 bg-white px-3 py-2 text-xs text-rose-700">{coverageErr}</div>
                      ) : null}
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)]">
                        <div className="space-y-2">
                        <label className="block text-xs font-semibold text-slate-700">파일 업로드</label>
                        {coverageContracts.length > 0 ? (
                          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            새 PDF 업로드 전 엑셀 다운로드로 기존 계약을 백업해 주세요.
                          </div>
                        ) : null}
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-xs"
                          onChange={handleCoverageFileChange}
                        />
                        </div>
                        <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                          <div className="mb-1 text-xs font-semibold text-slate-700">보장분석 파일</div>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div
                              className={`min-w-0 truncate ${displayCoverageFileId ? 'font-semibold text-slate-800' : 'text-slate-500'}`}
                              title={displayCoverageFileId ? displayCoverageFileName : undefined}
                            >
                              {displayCoverageFileId ? displayCoverageFileName : '업로드된 보장분석 파일 없음'}
                            </div>
                            {displayCoverageFileId ? (
                              <div className="flex shrink-0 gap-1.5">
                                <button
                                  type="button"
                                  className="rounded border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                                  onClick={() => openCoverageFilePreview(displayCoverageFileId)}
                                >
                                  미리보기
                                </button>
                                <button
                                  type="button"
                                  className="rounded border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                                  onClick={() => downloadCoverageFile(displayCoverageFileId)}
                                >
                                  다운로드
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                          disabled={coverageAnalyzing}
                          onClick={() => {
                            syncCoverageDraftContracts([])
                            setCoverageJsonOpen(false)
                            setCoverageDraftJobId(null)
                            setCoverageEditingContractId(null)
                            setCoverageNameValidation(null)
                            setCoverageExtractionQuality(null)
                            setCoverageNameMismatchAcknowledged(false)
                          }}
                        >
                          초기화
                        </button>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                          disabled={coverageAnalyzing}
                          onClick={() => runCoverageAnalysis()}
                        >
                          {coverageAnalyzing ? '분석/저장 중…' : '분석 후 바로 저장'}
                        </button>
                      </div>
                      </div>
                    </details>

                    {coverageEditingContractId && coverageDraftContracts.length > 0 ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-bold text-amber-950">저장된 계약/담보 수정</div>
                            <p className="mt-1 text-xs text-amber-900/80">
                              저장된 계약을 수정 중입니다. 저장하면 기존 계약/담보가 현재 표 내용으로 교체됩니다.
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-50"
                              onClick={() => {
                                syncCoverageDraftContracts([])
                                setCoverageDraftJobId(null)
                                setCoverageEditingContractId(null)
                                setCoverageJsonOpen(false)
                                setCoverageNameValidation(null)
                                setCoverageExtractionQuality(null)
                                setCoverageNameMismatchAcknowledged(false)
                              }}
                            >
                              수정 취소
                            </button>
                            {/* <button
                              type="button"
                              className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-50"
                              onClick={() => setCoverageJsonOpen(v => !v)}
                            >
                              {coverageJsonOpen ? 'JSON 닫기' : 'JSON 보기'}
                            </button> */}
                            <button
                              type="button"
                              className="rounded bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                              disabled={coverageSaving}
                              onClick={saveCoverageDraft}
                            >
                              {coverageSaving ? '저장 중…' : '수정 저장'}
                            </button>
                          </div>
                        </div>
                        {coverageNameValidation?.checked && coverageNameValidation?.matched === false ? (
                          <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                            <div className="font-bold">고객명 불일치 확인 필요</div>
                            <p className="mt-1">
                              현재 고객명은 <strong>{coverageNameValidation.currentCustomerName || '-'}</strong>,
                              보장분석 파일 고객명은 <strong>{coverageNameValidation.parsedCustomerName || '-'}</strong>입니다.
                              {coverageNameMismatchAcknowledged ? ' 운영자가 적용을 확인했습니다.' : ' 저장 전 적용 여부를 다시 확인합니다.'}
                            </p>
                          </div>
                        ) : coverageNameValidation?.checked ? (
                          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                            고객명 확인 완료: {coverageNameValidation.currentCustomerName || '-'}
                          </div>
                        ) : null}
                        {isOperator && coverageExtractionQuality ? (
                          <div className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="font-bold text-slate-900">추출 품질</div>
                            <div className="mt-1 text-slate-600">
                              계약 {coverageExtractionQuality.selectedContractInventoryStage || '-'}
                              {' · '}
                              담보 {coverageExtractionQuality.selectedCoverageDetailStage || '-'}
                              {' · '}
                              요약 {coverageExtractionQuality.selectedStandardCoverageStage || '-'}
                            </div>
                            <div className="mt-2 grid gap-2 md:grid-cols-3">
                              {asArray(coverageExtractionQuality.candidates).map((item) => (
                                <div key={item.stage || item.provider} className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5">
                                  <div className="font-semibold text-slate-800">{item.stage || '-'}</div>
                                  <div className="mt-0.5 text-[11px] text-slate-600">
                                    계약 {formatCount(item.standardContractCount)}건 · 담보 {formatCount(item.mappedCoverageCount)}개 · 표준 {formatCount(item.standardMappedCoverageCount)}개
                                  </div>
                                  <div className="mt-0.5 text-[11px] text-slate-500">
                                    점수 {formatCount((item.contractInventoryScore || 0) + (item.coverageDetailScore || 0) + (item.standardCoverageScore || 0))}
                                    {item.suspiciousAmountCount ? ` · 금액확인 ${formatCount(item.suspiciousAmountCount)}` : ''}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div className="mt-3 space-y-3">
                          {coverageDraftContracts.map((contract, contractIdx) => {
                            const coverages = Array.isArray(contract.coverages) ? contract.coverages : []
                            const matchStatus = contract.match?.status
                            const matchSelectValue = contract.matchResolution === 'NEW'
                              ? 'NEW'
                              : (contract.targetContractId != null && contract.targetContractId !== '' ? String(contract.targetContractId) : '')
                            return (
                              <div key={`draft-contract-${contractIdx}`} className="rounded border border-amber-200 bg-white p-3">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div className="text-xs font-bold text-slate-700">계약 #{contractIdx + 1}</div>
                                  <button
                                    type="button"
                                    className="text-xs font-semibold text-rose-600 hover:underline"
                                    onClick={() => removeCoverageDraftContract(contractIdx)}
                                  >
                                    계약 삭제
                                  </button>
                                </div>
                                {!coverageEditingContractId && matchStatus === 'AUTO' ? (
                                  <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
                                    <span className="font-semibold">기존 계약 자동 갱신</span>
                                    {' · '}
                                    {contract.match?.suggestedLabel || '-'}
                                    {contract.match?.matchScore != null ? ` (${contract.match.matchScore}점)` : ''}
                                  </div>
                                ) : null}
                                {!coverageEditingContractId && matchStatus === 'REVIEW' ? (
                                  <div className="mt-2 rounded border border-sky-200 bg-sky-50 px-2 py-2 text-xs text-sky-950">
                                    <div className="font-semibold">기존 계약 매칭 확인</div>
                                    <p className="mt-1 text-sky-900">{contract.match?.matchReason || '유사한 기존 계약이 있습니다.'}</p>
                                    <select
                                      className="mt-2 w-full rounded border border-sky-200 bg-white px-2 py-1.5 text-xs text-slate-800"
                                      value={matchSelectValue}
                                      onChange={(e) => updateCoverageDraftMatch(contractIdx, e.target.value)}
                                    >
                                      <option value="">매칭 계약 선택…</option>
                                      {(Array.isArray(contract.match?.options) ? contract.match.options : []).map((opt) => (
                                        <option key={`match-opt-${opt.contractId}`} value={String(opt.contractId)}>
                                          {(opt.label || `${opt.insurerName || '-'} / ${opt.productName || '-'}`)} ({opt.matchScore ?? '-'}점)
                                        </option>
                                      ))}
                                      <option value="NEW">신규 계약으로 저장</option>
                                    </select>
                                  </div>
                                ) : null}
                                {!coverageEditingContractId && matchStatus === 'NEW' ? (
                                  <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
                                    신규 계약으로 저장됩니다.
                                  </div>
                                ) : null}
                                <div className="mt-2 grid gap-3 md:grid-cols-6">
                                  <label className="flex min-w-0 flex-col gap-0.5">
                                    <span className="text-xs font-semibold text-slate-700">보험사</span>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                                      value={contract.insurerName || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'insurerName', e.target.value)}
                                      placeholder="예: OO생명"
                                    />
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5">
                                    <span className="text-xs font-semibold text-slate-700">구분</span>
                                    <select
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                                      value={normalizeCoverageInsuranceCategory(contract.insuranceCategory) || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'insuranceCategory', e.target.value)}
                                    >
                                      <option value="">선택</option>
                                      {COVERAGE_INSURANCE_CATEGORY_OPTIONS.map((cat) => (
                                        <option key={`cov-cat-${cat}`} value={cat}>{cat}</option>
                                      ))}
                                    </select>
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5 md:col-span-2">
                                    <span className="text-xs font-semibold text-slate-700">상품명</span>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                                      value={contract.productName || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'productName', e.target.value)}
                                      placeholder="상품명"
                                    />
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5">
                                    <span className="text-xs font-semibold text-slate-700">계약일</span>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs tabular-nums"
                                      value={contract.contractDate || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'contractDate', e.target.value)}
                                      placeholder="예: 2024-05-01"
                                    />
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5">
                                    <span className="text-xs font-semibold text-slate-700">월 보험료</span>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs tabular-nums"
                                      value={contract.monthlyPremium ?? ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'monthlyPremium', e.target.value)}
                                      placeholder="원"
                                    />
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5 md:col-span-2">
                                    <span className="text-xs font-semibold text-slate-700">납입기간</span>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                                      value={contract.paymentPeriod || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'paymentPeriod', e.target.value)}
                                      placeholder="예: 월납 / 20년 / 55세만기"
                                    />
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5 md:col-span-2">
                                    <span className="text-xs font-semibold text-slate-700">보장기간</span>
                                    <input
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                                      value={contract.coveragePeriod || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'coveragePeriod', e.target.value)}
                                      placeholder="예: 종신 또는 시작일 ~ 종료일"
                                    />
                                  </label>
                                  <label className="flex min-w-0 flex-col gap-0.5 md:col-span-2">
                                    <span className="text-xs font-semibold text-slate-700">갱신 여부</span>
                                    <select
                                      className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                                      value={contract.renewalType || ''}
                                      onChange={(e) => updateCoverageDraftContract(contractIdx, 'renewalType', e.target.value)}
                                    >
                                      <option value="">선택</option>
                                      <option value="갱신">갱신</option>
                                      <option value="비갱신">비갱신</option>
                                    </select>
                                  </label>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center justify-end gap-1.5 rounded border border-amber-100 bg-amber-50/50 px-2 py-2">
                                  <button
                                      type="button"
                                      className="rounded border border-amber-300 bg-white px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-50"
                                      onClick={() => applyCoverageRenewalToContract(contractIdx, '갱신')}
                                    >
                                      담보 전체 갱신
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded border border-amber-300 bg-white px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-50"
                                      onClick={() => applyCoverageRenewalToContract(contractIdx, '비갱신')}
                                    >
                                      담보 전체 비갱신
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                                      onClick={() => applyCoverageRenewalToContract(contractIdx, '')}
                                    >
                                      일괄값 지우기
                                    </button>
                                </div>
                                <div className="mt-2 overflow-x-auto">
                                  <table className="min-w-full text-left text-xs">
                                    <thead className="bg-amber-50 text-slate-600">
                                      <tr>
                                        <th className="px-2 py-1.5">원문 담보명</th>
                                        <th className="px-2 py-1.5">표준 담보명</th>
                                        <th className="px-2 py-1.5 w-[5rem] max-w-[5rem]">분류</th>
                                        <th className="px-2 py-1.5">가입금액</th>
                                        <th className="px-2 py-1.5">갱신</th>
                                        <th className="px-2 py-1.5">비고</th>
                                        <th className="px-2 py-1.5 text-right">작업</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {coverages.map((cv, cvIdx) => (
                                        <tr key={`draft-coverage-${contractIdx}-${cvIdx}`} className="border-t border-amber-100">
                                          <td className="px-2 py-1.5">
                                            <input
                                              className="w-56 min-w-full rounded border border-slate-300 px-2 py-1 text-xs"
                                              value={cv.coverageNameRaw || ''}
                                              onChange={(e) => updateCoverageDraftCoverage(contractIdx, cvIdx, 'coverageNameRaw', e.target.value)}
                                              onBlur={() => {
                                                const sug = suggestCoverageGroupFromName(cv.coverageNameRaw || '', cv.coverageGroup)
                                                if (!sug) return
                                                const cur = String(cv.coverageGroup || '').trim()
                                                if (!cur || cur === '실손') {
                                                  updateCoverageDraftCoverage(contractIdx, cvIdx, 'coverageGroup', sug)
                                                }
                                              }}
                                              placeholder="원문 담보명"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <input
                                              className="w-56 min-w-full rounded border border-slate-300 px-2 py-1 text-xs"
                                              value={cv.standardCoverageName || ''}
                                              onChange={(e) => updateCoverageDraftCoverage(contractIdx, cvIdx, 'standardCoverageName', e.target.value)}
                                              placeholder="표준 담보명"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <select
                                              className="w-full max-w-[5rem] rounded border border-slate-300 px-1 py-1 text-xs"
                                              value={cv.coverageGroup || ''}
                                              onChange={(e) => updateCoverageDraftCoverage(contractIdx, cvIdx, 'coverageGroup', e.target.value)}
                                            >
                                              {COVERAGE_GROUP_OPTIONS.map((g) => (
                                                <option key={g || 'empty'} value={g}>{g || '미분류'}</option>
                                              ))}
                                            </select>
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <input
                                              className="w-32 rounded border border-slate-300 px-2 py-1 text-right text-xs tabular-nums"
                                              value={cv.insuredAmount ?? ''}
                                              onChange={(e) => updateCoverageDraftCoverage(contractIdx, cvIdx, 'insuredAmount', e.target.value)}
                                              placeholder="원"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <select
                                              className="w-24 rounded border border-slate-300 px-2 py-1 text-xs"
                                              value={cv.renewalType || ''}
                                              onChange={(e) => updateCoverageDraftCoverage(contractIdx, cvIdx, 'renewalType', e.target.value)}
                                            >
                                              <option value="">-</option>
                                              <option value="갱신">갱신</option>
                                              <option value="비갱신">비갱신</option>
                                            </select>
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <input
                                              className="w-40 rounded border border-slate-300 px-2 py-1 text-xs"
                                              value={normalizeCoverageReviewNoteForUi(cv.note)}
                                              onFocus={() => {
                                                if (isCoverageParserProvenanceNote(cv.note)) {
                                                  updateCoverageDraftCoverage(contractIdx, cvIdx, 'note', '')
                                                }
                                              }}
                                              onChange={(e) => updateCoverageDraftCoverage(contractIdx, cvIdx, 'note', e.target.value)}
                                              placeholder="예: 원문 대조 필요"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5 text-right">
                                            <button
                                              type="button"
                                              className="text-xs font-semibold text-rose-600 hover:underline"
                                              onClick={() => removeCoverageDraftCoverage(contractIdx, cvIdx)}
                                            >
                                              삭제
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <button
                                  type="button"
                                  className="mt-2 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                  onClick={() => addCoverageDraftCoverage(contractIdx)}
                                >
                                  담보 추가
                                </button>
                              </div>
                            )
                          })}
                        </div>
                        {coverageJsonOpen ? (
                          <div className="mt-3">
                            <div className="mb-1 flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-600">고급 JSON 편집</span>
                              <button
                                type="button"
                                className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                onClick={applyCoverageDraftJson}
                              >
                                JSON을 표에 반영
                              </button>
                            </div>
                            <textarea
                              className="min-h-[220px] w-full rounded border border-amber-200 bg-white px-3 py-2 font-mono text-xs"
                              value={coverageDraftJson}
                              onChange={(e) => setCoverageDraftJson(e.target.value)}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {coverageSummaryInlineEditorVisible ? (
                    <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3">
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-bold text-sky-950">보장요약 리포트</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className="rounded border border-sky-300 bg-white px-2.5 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-50"
                            aria-expanded={coverageSummaryReportExpanded}
                            aria-controls="coverage-summary-report-panel"
                            onClick={() => setCoverageSummaryReportExpanded((prev) => !prev)}
                          >
                            {coverageSummaryReportExpanded ? '접기' : '펼치기'}
                          </button>
                          {coverageSummaryReportExpanded ? (
                            <>
                          <button
                            type="button"
                            className="rounded border border-sky-300 bg-white px-2.5 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-50 disabled:opacity-50"
                            disabled={coverageSummaryLoading || Boolean(coverageSummaryBusy)}
                            onClick={regenerateCoverageSummaryReport}
                          >
                            {coverageSummaryBusy === 'regenerate' ? '생성 중…' : '계약 기준 재생성'}
                          </button>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={!coverageSummaryReport || coverageSummarySaving}
                            onClick={saveCoverageSummaryReport}
                          >
                            {coverageSummarySaving ? '저장 중…' : '저장'}
                          </button>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={Boolean(coverageSummaryBusy)}
                            onClick={downloadCoverageSummaryExcel}
                          >
                            {coverageSummaryBusy === 'excel-download' ? '다운로드 중…' : '엑셀 다운로드'}
                          </button>
                          <label className={`rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${coverageSummaryBusy ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
                            {coverageSummaryBusy === 'excel-upload' ? '업로드 중…' : '엑셀 업로드'}
                            <input
                              type="file"
                              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                              className="hidden"
                              disabled={Boolean(coverageSummaryBusy)}
                              onChange={uploadCoverageSummaryExcel}
                            />
                          </label>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={Boolean(coverageSummaryBusy) || !coverageSummaryReport}
                            onClick={() => setCoverageSummaryTableOpen(true)}
                          >
                            요약표보기
                          </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {coverageSummaryReportExpanded ? (
                        <div id="coverage-summary-report-panel">
                      {coverageSummaryError ? (
                        <div className="mb-3 rounded border border-rose-200 bg-white px-3 py-2 text-xs text-rose-700">{coverageSummaryError}</div>
                      ) : null}
                      {coverageSummaryLoading ? (
                        <p className="rounded border border-sky-100 bg-white px-3 py-5 text-center text-xs text-slate-500">보장요약을 불러오는 중…</p>
                      ) : !coverageSummaryReport ? (
                        <EmptyState title="보장요약 리포트가 없습니다" className="rounded border border-dashed bg-white py-5" />
                      ) : (
                        <div className="space-y-3">
                          <details className="rounded border border-sky-100 bg-white">
                            <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-sky-900">리포트 수정</summary>
                            <div className="overflow-x-auto border-t border-sky-100">
                              <table className="min-w-[980px] w-full text-left text-xs">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <th className="px-2 py-2">표시</th>
                                <th className="px-2 py-2">분류</th>
                                <th className="px-2 py-2">보장</th>
                                <th className="px-2 py-2 text-right">총 보장금액</th>
                                <th className="px-2 py-2 text-right">권장금액</th>
                                <th className="px-2 py-2">상태</th>
                                <th className="px-2 py-2">보험사별 구성</th>
                                <th className="px-2 py-2">메모</th>
                              </tr>
                            </thead>
                            <tbody>
                              {asArray(coverageSummaryReport.rows).map((row, rowIdx) => {
                                const products = asArray(row.products)
                                return (
                                  <tr key={`${row.coverageCode || rowIdx}`} className="border-t border-sky-100 align-top">
                                    <td className="px-2 py-2">
                                      <input
                                        type="checkbox"
                                        checked={row.visible !== false}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'visible', e.target.checked)}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        className="w-20 rounded border border-slate-300 px-2 py-1"
                                        value={row.coverageGroup || ''}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'coverageGroup', e.target.value)}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        className="w-28 rounded border border-slate-300 px-2 py-1 font-semibold text-slate-800"
                                        value={row.coverageName || ''}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'coverageName', e.target.value)}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        type="number"
                                        className="w-28 rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                        value={row.totalAmount ?? ''}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'totalAmount', Number(e.target.value || 0))}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        type="number"
                                        className="w-24 rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                        value={row.recommendedAmount ?? ''}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'recommendedAmount', e.target.value === '' ? null : Number(e.target.value))}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        className="w-24 rounded border border-slate-300 px-2 py-1"
                                        value={row.statusLabel || ''}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'statusLabel', e.target.value)}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <details>
                                        <summary className="cursor-pointer text-xs font-semibold text-sky-800">
                                          {products.length}개 · {products.slice(0, 2).map((p) => `${p.insurerName || '-'} ${formatCoverageAmountKorean(p.amount)}`).join(' / ') || '-'}
                                        </summary>
                                        <div className="mt-2 space-y-1.5">
                                          {products.map((product, productIdx) => (
                                            <div key={`${row.coverageCode || rowIdx}-${productIdx}`} className="grid grid-cols-[minmax(12rem,1fr)_100px_44px] gap-1">
                                              <select
                                                className="min-w-0 rounded border border-slate-300 bg-white px-2 py-1"
                                                value={product.contractId ? String(product.contractId) : ''}
                                                onChange={(e) => updateCoverageSummaryProductContract(rowIdx, productIdx, e.target.value)}
                                              >
                                                <option value="">계약 선택</option>
                                                {coverageContracts.map((contract) => (
                                                  <option key={`coverage-summary-product-contract-${contract.id}`} value={contract.id}>
                                                    {[contract.insurerName, contract.productName, contract.contractDate].filter(Boolean).join(' · ') || '보유계약'}
                                                  </option>
                                                ))}
                                              </select>
                                              <input
                                                type="number"
                                                className="rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                                value={product.amount ?? ''}
                                                onChange={(e) => updateCoverageSummaryProduct(rowIdx, productIdx, 'amount', Number(e.target.value || 0))}
                                              />
                                              <button
                                                type="button"
                                                className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                                                onClick={() => removeCoverageSummaryProduct(rowIdx, productIdx)}
                                              >
                                                삭제
                                              </button>
                                            </div>
                                          ))}
                                          <button
                                            type="button"
                                            className="rounded border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-bold text-sky-800 hover:bg-sky-100"
                                            onClick={() => addCoverageSummaryProduct(rowIdx)}
                                          >
                                            계약 추가
                                          </button>
                                        </div>
                                      </details>
                                    </td>
                                    <td className="px-2 py-2">
                                      <textarea
                                        className="h-12 w-40 rounded border border-slate-300 px-2 py-1"
                                        value={row.note || ''}
                                        onChange={(e) => updateCoverageSummaryRow(rowIdx, 'note', e.target.value)}
                                      />
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                              </table>
                            </div>
                          </details>
                        </div>
                      )}
                        </div>
                      ) : null}
                    </div>
                    ) : null}

                    <div>
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">저장된 보험 계약</div>
                          {latestCoverageAnalysisDateText ? (
                            <div className="mt-0.5 text-[11px] text-slate-500">분석 시점 {latestCoverageAnalysisDateText}</div>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500">{coverageContracts.length}건</span>
                          <button
                            type="button"
                            className="rounded border border-sky-200 bg-white px-2.5 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-50 disabled:opacity-50"
                            disabled={coverageSummaryLoading || Boolean(coverageSummaryBusy)}
                            onClick={openCoverageSummaryReport}
                          >
                            {coverageSummaryLoading ? '불러오는 중…' : '보장요약리포트'}
                          </button>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={Boolean(coverageExcelBusy)}
                            onClick={() => downloadCustomerInsuranceExcel('coverage')}
                          >
                            {coverageExcelBusy === 'download' ? '다운로드 중…' : '엑셀 다운로드'}
                          </button>
                          <label className={`rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${coverageExcelBusy ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
                            {coverageExcelBusy === 'upload' ? '업로드 중…' : '엑셀 업로드'}
                            <input
                              type="file"
                              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                              className="hidden"
                              disabled={Boolean(coverageExcelBusy)}
                              onChange={(e) => uploadCustomerInsuranceExcel('coverage', e)}
                            />
                          </label>
                          <button
                            type="button"
                            className="rounded border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            disabled={coverageLoading || coverageAnalyzing || coverageSaving || coverageResetting || Boolean(coverageExcelBusy) || coverageContracts.length === 0}
                            onClick={resetAllCoverageContracts}
                          >
                            {coverageResetting ? '초기화 중…' : '전체 초기화'}
                          </button>
                          {coverageContracts.length > 0 ? (
                            <button
                              type="button"
                              className="rounded border border-emerald-200 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                              disabled={easyExplainGenerating === 'COVERAGE_ANALYSIS:'}
                              onClick={() => generateEasyExplanation('COVERAGE_ANALYSIS')}
                            >
                              {easyExplainGenerating === 'COVERAGE_ANALYSIS:' ? '생성 중…' : '고객용 설명'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                      {coverageLoading ? (
                        <p className="rounded border border-slate-100 bg-slate-50 px-3 py-6 text-center text-slate-500">불러오는 중…</p>
                      ) : coverageContracts.length === 0 && coverageContractCandidates.length === 0 ? (
                        <EmptyState
                          title="저장된 보험 계약이 없습니다"
                          className="rounded border border-dashed py-6"
                        />
                      ) : (
                        <div className="space-y-3">
                          {coverageContractCandidatePanelVisible && coverageContractCandidates.length > 0 ? (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <div className="text-sm font-bold text-emerald-950">연말정산 보험계약 연결 후보</div>
                                </div>
                                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-emerald-800">
                                  {coverageContractCandidates.length}건
                                </span>
                              </div>
                              <div className="mt-3 overflow-x-auto rounded border border-emerald-100 bg-white">
                                <table className="min-w-full text-left text-xs">
                                  <thead className="bg-emerald-50 text-emerald-900/80">
                                    <tr>
                                      <th className="px-2 py-1.5">보험사</th>
                                      <th className="px-2 py-1.5">보험종류/상품 후보</th>
                                      <th className="px-2 py-1.5">증권/계좌</th>
                                      <th className="px-2 py-1.5 text-right">연 납입보험료</th>
                                      <th className="px-2 py-1.5">연도</th>
                                      <th className="px-2 py-1.5">매칭</th>
                                      <th className="px-2 py-1.5 text-right">작업</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {coverageContractCandidates.map((candidate) => (
                                      <tr key={`coverage-candidate-${candidate.id}`} className="border-t border-emerald-100">
                                        <td className="px-2 py-1.5 font-medium text-slate-800">{candidate.insurerName || '-'}</td>
                                        <td className="px-2 py-1.5 text-slate-700">{candidate.productName || '연말정산 보험료 납입자료'}</td>
                                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-600">{candidate.policyNumberMasked || '-'}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums text-slate-800">{formatWon(candidate.annualPremium)}</td>
                                        <td className="px-2 py-1.5 text-slate-600">
                                          {Array.isArray(candidate.premiumYears) && candidate.premiumYears.length > 0 ? candidate.premiumYears.join(', ') : '-'}
                                        </td>
                                        <td className="px-2 py-1.5 text-slate-600">
                                          <div>{candidate.matchScore != null ? `${candidate.matchScore}점` : '-'}</div>
                                          <div className="mt-0.5 max-w-[220px] truncate text-[11px] text-slate-400" title={candidate.matchReason || ''}>
                                            {candidate.matchReason || '-'}
                                          </div>
                                        </td>
                                        <td className="px-2 py-1.5 text-right">
                                          <button
                                            type="button"
                                            className="mr-2 rounded bg-emerald-700 px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                                            disabled={coverageCandidateBusyId === candidate.id}
                                            onClick={() => resolveCoverageContractCandidate(candidate, 'link')}
                                          >
                                            {coverageCandidateBusyId === candidate.id ? '처리 중…' : '계약 연결'}
                                          </button>
                                          <button
                                            type="button"
                                            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                                            disabled={coverageCandidateBusyId === candidate.id}
                                            onClick={() => resolveCoverageContractCandidate(candidate, 'ignore')}
                                          >
                                            무시
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : null}
                          {coverageContracts.length === 0 ? (
                            <EmptyState
                              title="저장된 보험 계약이 없습니다"
                              className="rounded border border-dashed py-6"
                            />
                          ) : (
                            <>
                              <div className="flex gap-1 overflow-x-auto rounded-t border-b border-slate-200">
                                <button
                                  type="button"
                                  className={`shrink-0 rounded-t border px-3 py-2 text-xs font-semibold ${coverageSavedTab === 'all' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-transparent bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                                  onClick={() => setCoverageSavedTab('all')}
                                >
                                  전체계약목록
                                </button>
                                {coverageContracts.map((contract, idx) => (
                                  <button
                                    key={`coverage-tab-${contract.id}`}
                                    type="button"
                                    className={`shrink-0 rounded-t border px-3 py-2 text-xs font-semibold ${coverageSavedTab === String(contract.id) ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-transparent bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                                    onClick={() => setCoverageSavedTab(String(contract.id))}
                                  >
                                    {idx + 1}. {contract.productName || '계약별 보장목록'}
                                  </button>
                                ))}
                              </div>

                              {coverageSavedTab === 'all' ? (
                                <>
                                  <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/90 px-2 py-2">
                                    <div className="overflow-x-auto">
                                      <table className="w-full min-w-[36rem] border-collapse text-left text-xs">
                                        <thead>
                                          <tr className="border-b border-slate-200 text-slate-600">
                                            <th
                                              scope="col"
                                              className="whitespace-nowrap bg-white px-2 py-1.5 font-semibold align-bottom"
                                              title='보험사명에 "생명"이 포함되면 생보, "손해·화재·해상·손보" 등이 포함되면 손보로 분류합니다. 해당 없으면 기타입니다.'
                                            >
                                              보유 계약{' '}
                                              <span className="font-normal text-slate-500">(전체·생보·손보)</span>
                                            </th>
                                            <th scope="col" className="whitespace-nowrap bg-white px-2 py-1.5 text-right font-semibold align-bottom">
                                              월납 보험료 합계
                                            </th>
                                            <th scope="col" className="whitespace-nowrap bg-white px-2 py-1.5 text-right font-semibold align-bottom">
                                              <span className="block leading-tight">기납입 보험료 합계(추정)</span>
                                            </th>
                                            <th scope="col" className="whitespace-nowrap bg-white px-2 py-1.5 text-right font-semibold align-bottom">
                                              <span className="block leading-tight">향후 납입 보험료 합계(추정)</span>
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="text-slate-900">
                                          <tr>
                                            <td className="border-t border-slate-100 bg-white px-2 py-2 align-top">
                                              <span className="font-medium tabular-nums">전체 {coveragePortfolioSummary.totalCount}건</span>
                                              <span className="text-slate-600">
                                                {' '}· 생보 {coveragePortfolioSummary.lifeCount}건 · 손보 {coveragePortfolioSummary.damageCount}건
                                                {coveragePortfolioSummary.unknownCount > 0 ? (
                                                  <> · 기타 {coveragePortfolioSummary.unknownCount}건</>
                                                ) : null}
                                              </span>
                                            </td>
                                            <td className="border-t border-slate-100 bg-white px-2 py-2 text-right tabular-nums font-medium whitespace-nowrap align-top">
                                              {formatWonSummaryAmount(coveragePortfolioSummary.totalMonthlyPremium)}
                                            </td>
                                            <td className="border-t border-slate-100 bg-white px-2 py-2 text-right tabular-nums font-medium whitespace-nowrap align-top">
                                              {formatWonSummaryAmount(coveragePortfolioSummary.totalPaidEstimate)}
                                            </td>
                                            <td className="border-t border-slate-100 bg-white px-2 py-2 text-right tabular-nums font-medium whitespace-nowrap align-top">
                                              {formatWonSummaryAmount(coveragePortfolioSummary.totalFutureEstimate)}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                    <p className="mt-1.5 px-0.5 text-[11px] text-slate-500 leading-snug">
                                      추정 합계는 아래 목록 각 행과 같은 규칙으로 계산한 금액을 더한 값입니다. 월 보험료가 비었거나 납입기간을 알 수 없는 계약은 합계에서 제외될 수 있습니다.
                                    </p>
                                  </div>
                                  <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3">
                                    <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                                      <div>
                                        <div className="text-sm font-bold text-slate-900">보험료 현황 차트</div>
                                        <p className="mt-0.5 text-xs text-slate-500">
                                          월 보험료 구성, 총 납입 추정액, 만기 시점을 한 화면에서 확인합니다.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-3 gap-1 text-right text-[11px] text-slate-500">
                                        <div className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                                          <span className="block">총 납입</span>
                                          <b className="block text-xs text-slate-900 tabular-nums">
                                            {formatWonCompact(coveragePortfolioSummary.totalPaidEstimate + coveragePortfolioSummary.totalFutureEstimate)}
                                          </b>
                                        </div>
                                        <div className="rounded border border-emerald-100 bg-emerald-50 px-2 py-1">
                                          <span className="block">기납입</span>
                                          <b className="block text-xs text-emerald-800 tabular-nums">{formatWonCompact(coveragePortfolioSummary.totalPaidEstimate)}</b>
                                        </div>
                                        <div className="rounded border border-orange-100 bg-orange-50 px-2 py-1">
                                          <span className="block">남은 보험료</span>
                                          <b className="block text-xs text-orange-800 tabular-nums">{formatWonCompact(coveragePortfolioSummary.totalFutureEstimate)}</b>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="grid gap-3 xl:grid-cols-2">
                                      <div className="rounded border border-slate-100 bg-slate-50/70 p-3">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="text-xs font-bold text-slate-800">월 보험료 구성</div>
                                          <div className="text-[11px] text-slate-500">
                                            합계 {formatWonSummaryAmount(coveragePortfolioChartData.monthlyPremiumTotal)}
                                          </div>
                                        </div>
                                        {coveragePortfolioChartData.monthlyPremiumMix.length > 0 ? (
                                          <div className="mt-2 grid gap-2 md:grid-cols-[12rem_1fr]">
                                            <div className="h-44">
                                              <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                  <Pie
                                                    data={coveragePortfolioChartData.monthlyPremiumMix}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    innerRadius={44}
                                                    outerRadius={72}
                                                    paddingAngle={2}
                                                    stroke="#fff"
                                                    strokeWidth={2}
                                                  >
                                                    {coveragePortfolioChartData.monthlyPremiumMix.map((row, idx) => (
                                                      <Cell key={`monthly-premium-cell-${row.id}`} fill={COVERAGE_CHART_COLORS[idx % COVERAGE_CHART_COLORS.length]} />
                                                    ))}
                                                  </Pie>
                                                  <Tooltip
                                                    formatter={(value, name, props) => [
                                                      formatWonSummaryAmount(value),
                                                      props?.payload?.fullName || name,
                                                    ]}
                                                  />
                                                </PieChart>
                                              </ResponsiveContainer>
                                            </div>
                                            <div className="space-y-1.5">
                                              {coveragePortfolioChartData.monthlyPremiumMix.map((row, idx) => {
                                                const pct = coveragePortfolioChartData.monthlyPremiumTotal > 0
                                                  ? Math.round((row.value / coveragePortfolioChartData.monthlyPremiumTotal) * 100)
                                                  : 0
                                                return (
                                                  <div key={`monthly-premium-legend-${row.id}`} className="flex items-center gap-2 text-xs">
                                                    <span
                                                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                                                      style={{ backgroundColor: COVERAGE_CHART_COLORS[idx % COVERAGE_CHART_COLORS.length] }}
                                                    />
                                                    <span className="min-w-0 flex-1 truncate text-slate-700" title={row.fullName}>{row.name}</span>
                                                    <span className="shrink-0 tabular-nums text-slate-900">{formatWonCompact(row.value)}</span>
                                                    <span className="w-9 shrink-0 text-right tabular-nums text-slate-500">{pct}%</span>
                                                  </div>
                                                )
                                              })}
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="mt-2 rounded border border-dashed border-slate-200 bg-white px-3 py-6 text-center text-xs text-slate-500">
                                            월 보험료가 입력된 계약이 없습니다.
                                          </div>
                                        )}
                                      </div>
                                      <div className="rounded border border-slate-100 bg-slate-50/70 p-3">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="text-xs font-bold text-slate-800">기납입/남은 보험료</div>
                                          <div className="text-[11px] text-slate-500">계약별 추정 납입액 상위 8건</div>
                                        </div>
                                        {coveragePortfolioChartData.paymentRows.length > 0 ? (
                                          <div className="mt-2 h-56">
                                            <ResponsiveContainer width="100%" height="100%">
                                              <BarChart
                                                data={coveragePortfolioChartData.paymentRows}
                                                layout="vertical"
                                                margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
                                              >
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                                <XAxis type="number" tickFormatter={formatWonCompact} tick={{ fontSize: 11 }} />
                                                <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 11 }} />
                                                <Tooltip
                                                  formatter={(value, name) => [
                                                    formatWonSummaryAmount(value),
                                                    name === 'paidWon' ? '기납입 보험료' : '향후 납입 보험료',
                                                  ]}
                                                  labelFormatter={(label) => {
                                                    const row = coveragePortfolioChartData.paymentRows.find((item) => item.name === label)
                                                    return row?.fullName || label
                                                  }}
                                                />
                                                <Legend
                                                  verticalAlign="top"
                                                  height={24}
                                                  formatter={(value) => (value === 'paidWon' ? '기납입' : '남은 보험료')}
                                                />
                                                <Bar dataKey="paidWon" stackId="premium" fill="#0f766e" radius={[3, 0, 0, 3]} />
                                                <Bar dataKey="futureWon" stackId="premium" fill="#f97316" radius={[0, 3, 3, 0]} />
                                              </BarChart>
                                            </ResponsiveContainer>
                                          </div>
                                        ) : (
                                          <div className="mt-2 rounded border border-dashed border-slate-200 bg-white px-3 py-6 text-center text-xs text-slate-500">
                                            계약일과 납입기간이 입력되면 기납입/잔여 보험료를 추정할 수 있습니다.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="mt-3 rounded border border-slate-100 bg-slate-50/70 p-3">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                          <div className="text-xs font-bold text-slate-800">
                                            {coveragePortfolioChartData.maturityBasis === 'AGE' ? '나이대별 만기 분포' : '만기연도별 분포'}
                                          </div>
                                          <p className="mt-0.5 text-[11px] text-slate-500">
                                            {coveragePortfolioChartData.maturityBasis === 'AGE'
                                              ? '고객 생년월일과 계약별 만기일 기준으로 계산합니다.'
                                              : '생년월일이 입력되면 나이대별 만기 분포로 전환됩니다.'}
                                          </p>
                                        </div>
                                        <div className="text-[11px] text-slate-500">막대는 만기 계약 수 기준입니다.</div>
                                      </div>
                                      {coveragePortfolioChartData.maturityRows.length > 0 ? (
                                        <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(16rem,0.9fr)]">
                                          <div className="h-52">
                                            <ResponsiveContainer width="100%" height="100%">
                                              <BarChart data={coveragePortfolioChartData.maturityRows} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                                <Tooltip
                                                  formatter={(value) => [`${formatCount(value)}건`, '만기 계약']}
                                                />
                                                <Bar dataKey="count" name="만기 계약" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                                              </BarChart>
                                            </ResponsiveContainer>
                                          </div>
                                          <div className="space-y-2">
                                            {coveragePortfolioChartData.maturityRows.slice(0, 5).map((group) => (
                                              <div key={`maturity-group-${group.name}`} className="rounded border border-slate-200 bg-white px-2.5 py-2">
                                                <div className="flex items-center justify-between gap-2 text-xs">
                                                  <span className="font-bold text-slate-800">{group.name}</span>
                                                  <span className="tabular-nums text-slate-500">{group.count}건 · {formatWonCompact(group.monthlyPremium)}/월</span>
                                                </div>
                                                <div className="mt-1 space-y-0.5">
                                                  {group.contracts.slice(0, 3).map((row) => (
                                                    <div key={`maturity-contract-${group.name}-${row.fullName}-${row.maturityDate}`} className="flex items-center justify-between gap-2 text-[11px] text-slate-600">
                                                      <span className="min-w-0 truncate" title={row.fullName}>{row.name}</span>
                                                      <span className="shrink-0 tabular-nums">{row.maturityDate}</span>
                                                    </div>
                                                  ))}
                                                  {group.contracts.length > 3 ? (
                                                    <div className="text-[11px] text-slate-400">외 {group.contracts.length - 3}건</div>
                                                  ) : null}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="mt-2 rounded border border-dashed border-slate-200 bg-white px-3 py-6 text-center text-xs text-slate-500">
                                          보장기간 또는 계약일/납입기간이 입력되면 만기 분포를 볼 수 있습니다.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                                  <table className="min-w-full text-left text-xs">
                                    <thead className="bg-slate-50 text-slate-500">
                                      <tr>
                                        <th className="px-2 py-1.5">보험사</th>
                                        <th className="whitespace-nowrap px-2 py-1.5">구분</th>
                                        <th className="px-2 py-1.5">상품명</th>
                                        <th className="min-w-[7rem] whitespace-nowrap px-2 py-1.5">계약일</th>
                                        <th className="px-2 py-1.5 whitespace-nowrap">납입주기</th>
                                        <th className="min-w-[5rem] px-2 py-1.5">납입기간</th>
                                        <th className="min-w-[7rem] whitespace-nowrap px-2 py-1.5">만기</th>
                                        <th className="px-2 py-1.5 text-right whitespace-nowrap" title="저장값은 월 보험료입니다.">보험료</th>
                                        <th className="min-w-[7.5rem] px-2 py-1.5 text-right whitespace-nowrap">
                                          <span className="block leading-tight">기납입보험료(추정)</span>
                                        </th>
                                        <th className="min-w-[7.5rem] px-2 py-1.5 text-right whitespace-nowrap">
                                          <span className="block leading-tight">향후납입보험료(추정)</span>
                                        </th>
                                        {/* <th className="px-2 py-1.5 text-right">담보수</th> */}
                                        <th className="px-2 py-1.5 text-right">작업</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {coverageContracts.map((contract) => {
                                        const row = deriveCoverageContractRowPresentation(contract)
                                        return (
                                        <tr key={contract.id} className="border-t border-slate-100">
                                          <td className="px-2 py-1.5 text-slate-700">{contract.insurerName || '-'}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{contract.insuranceCategory || '—'}</td>
                                          <td className="px-2 py-1.5 font-medium text-slate-900">{contract.productName || '(상품명 미입력)'}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-slate-700 tabular-nums">{contract.contractDate || '—'}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{row.paymentCycleLabel}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-slate-700 tabular-nums">{row.paymentPeriodShort}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-slate-700 tabular-nums">{row.maturityDisplay}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-700">{row.premiumDisplay}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-700">{row.paidPremiumDisplay}</td>
                                          <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-700">{row.futurePremiumDisplay}</td>
                                          {/* <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{(contract.coverages || []).length}</td> */}
                                          <td className="px-2 py-1.5 text-right">
                                            <div className="flex justify-end gap-1">
                                              <button
                                                type="button"
                                                className="rounded border border-sky-200 bg-white px-2 py-1 text-xs font-semibold text-brand-navy hover:bg-sky-50 disabled:opacity-50"
                                                disabled={coverageTermsLookupBusyId === contract.id}
                                                onClick={() => openSavedCoverageTermsLookup(contract)}
                                              >
                                                {coverageTermsLookupBusyId === contract.id ? '조회 중…' : '약관'}
                                              </button>
                                              <button
                                                type="button"
                                                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                                onClick={() => setCoverageSavedTab(String(contract.id))}
                                              >
                                                상세
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                </>
                              ) : coverageContracts.filter((contract) => String(contract.id) === coverageSavedTab).map((contract) => (
                                <div key={contract.id} className="rounded-lg border border-slate-200 bg-white p-3">
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                      <div className="font-semibold text-slate-900">{contract.productName || '(상품명 미입력)'}</div>
                                      <div className="mt-0.5 text-xs text-slate-500">
                                        {contract.insuranceCategory ? <>구분 {contract.insuranceCategory} · </> : null}
                                        {contract.insurerName || '보험사 미입력'} · {contract.contractDate || '계약일 미입력'} · {contract.renewalType || '갱신 여부 미입력'} · 월 보험료 {formatWon(contract.monthlyPremium)}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        className="rounded border border-sky-200 bg-white px-2 py-1 text-xs font-semibold text-brand-navy hover:bg-sky-50 disabled:opacity-50"
                                        disabled={coverageTermsLookupBusyId === contract.id}
                                        onClick={() => openSavedCoverageTermsLookup(contract)}
                                      >
                                        {coverageTermsLookupBusyId === contract.id ? '조회 중…' : '약관조회'}
                                      </button>
                                      <button
                                        type="button"
                                        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                        onClick={() => editSavedCoverageContract(contract)}
                                      >
                                        수정
                                      </button>
                                      {afterContractAllowed === true && (
                                        <Link
                                          className="rounded border border-emerald-200 bg-white px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                                          to={`/work/tools?tool=after-contract-certificates&customerRefId=${encodeURIComponent(String(customerId))}&contractId=${encodeURIComponent(String(contract.id))}`}
                                        >
                                          3종확인서(인카) 작성
                                        </Link>
                                      )}
                                      <button
                                        type="button"
                                        className="rounded border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                                        disabled={coverageDeletingContractId === contract.id}
                                        onClick={() => deleteSavedCoverageContract(contract)}
                                      >
                                        {coverageDeletingContractId === contract.id ? '삭제 중…' : '삭제'}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="mt-3 overflow-x-auto">
                                    <table className="min-w-full text-left text-xs">
                                      <thead className="bg-slate-50 text-slate-500">
                                        <tr>
                                          <th className="w-12 px-2 py-1.5 text-right">No</th>
                                          <th className="px-2 py-1.5">담보</th>
                                          <th className="px-2 py-1.5 w-[5rem] max-w-[5rem]">분류</th>
                                          <th className="px-2 py-1.5 text-right">가입금액</th>
                                          <th className="px-2 py-1.5">갱신</th>
                                          <th className="px-2 py-1.5">비고</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(contract.coverages || []).map((cv, coverageIndex) => (
                                          <tr key={cv.id} className="border-t border-slate-100">
                                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{coverageIndex + 1}</td>
                                            <td className="px-2 py-1.5 text-slate-800">{cv.coverageNameRaw || cv.coverageNameNormalized || '-'}</td>
                                            <td className="px-2 py-1.5 text-slate-600 max-w-[5rem] truncate align-top" title={cv.coverageGroup || ''}>{cv.coverageGroup || '-'}</td>
                                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-800">{formatCoverageAmountKorean(cv.insuredAmount)}</td>
                                            <td className="px-2 py-1.5 text-slate-600">{cv.renewalType || '-'}</td>
                                            <td className="px-2 py-1.5 text-slate-600">{normalizeCoverageReviewNoteForUi(cv.note) || '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
              ) : null}

              {sectionTab === 'maturity' ? (
                <div className="space-y-4" id="maturity">
                  <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-bold text-slate-900">만기·갱신 추가</div>
                      </div>
                      <Link className="text-xs font-semibold text-brand-navy hover:underline" to="/support/maturity-ledger">
                        보험만기대장 열기
                      </Link>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          onClick={() => downloadCustomerInsuranceExcel('maturity')}
                          disabled={Boolean(maturityExcelBusy)}
                        >
                          {maturityExcelBusy === 'download' ? '다운로드 중…' : '엑셀 다운로드'}
                        </button>
                        <label className={`rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${maturityExcelBusy ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
                          {maturityExcelBusy === 'upload' ? '업로드 중…' : '엑셀 업로드'}
                          <input
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="hidden"
                            disabled={Boolean(maturityExcelBusy)}
                            onChange={(e) => uploadCustomerInsuranceExcel('maturity', e)}
                          />
                        </label>
                        {isOperator && SHOW_CUSTOMER_CARD_NEWS_BUTTONS ? (
                          <button
                            type="button"
                            className="rounded border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                            onClick={() => openCustomerCardNews('maturity')}
                          >
                            카드뉴스
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {maturityError ? (
                      <div className="mt-3 rounded border border-rose-200 bg-white px-3 py-2 text-xs text-rose-700">{maturityError}</div>
                    ) : null}
                    <div className="mt-4 grid gap-3 md:grid-cols-4">
                      <label className="text-xs font-semibold text-slate-700">
                        구분
                        <select
                          value={maturityForm.policyLine}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                          onChange={(e) => setMaturityFormField('policyLine', e.target.value)}
                        >
                          <option value="AUTO">자동차보험</option>
                          <option value="GENERAL">일반보험</option>
                        </select>
                      </label>
                      <label className="text-xs font-semibold text-slate-700">
                        다음 만기일
                        <input
                          type="date"
                          value={maturityForm.nextMaturityDate}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                          onChange={(e) => setMaturityFormField('nextMaturityDate', e.target.value)}
                        />
                      </label>
                      <label className="text-xs font-semibold text-slate-700">
                        가입 보험사
                        <input
                          value={maturityForm.insurerName}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                          onChange={(e) => setMaturityFormField('insurerName', e.target.value)}
                        />
                      </label>
                      <label className="text-xs font-semibold text-slate-700">
                        가입경로
                        <input
                          value={maturityForm.enrollmentChannel}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                          onChange={(e) => setMaturityFormField('enrollmentChannel', e.target.value)}
                        />
                      </label>
                      <label className="text-xs font-semibold text-slate-700">
                        차량번호
                        <input
                          value={maturityForm.vehicleNumber}
                          disabled={maturityForm.policyLine !== 'AUTO'}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                          onChange={(e) => setMaturityFormField('vehicleNumber', e.target.value)}
                        />
                      </label>
                      <label className="text-xs font-semibold text-slate-700">
                        운전자한정
                        <input
                          value={maturityForm.driverRestriction}
                          disabled={maturityForm.policyLine !== 'AUTO'}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                          onChange={(e) => setMaturityFormField('driverRestriction', e.target.value)}
                        />
                      </label>
                      <label className="md:col-span-2 text-xs font-semibold text-slate-700">
                        메모
                        <input
                          value={maturityForm.productLabel}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                          onChange={(e) => setMaturityFormField('productLabel', e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50"
                        disabled={maturitySaving}
                        onClick={submitMaturityPolicy}
                      >
                        {maturitySaving ? '등록 중…' : '새 계약 추가'}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-white">
                    <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                      <div className="text-sm font-bold text-slate-900">만기·갱신 대상 계약</div>
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        onClick={loadMaturityRows}
                        disabled={maturityLoading}
                      >
                        새로고침
                      </button>
                    </div>
                    {maturityLoading ? (
                      <div className="px-3 py-6 text-center text-sm text-slate-500">불러오는 중…</div>
                    ) : maturityRows.length === 0 ? (
                      <EmptyState
                        title="등록된 만기·갱신 행이 없습니다"
                        className="py-8"
                      />
                    ) : (
                      <div className="overflow-auto">
                        <table className="w-full min-w-[860px] border-collapse text-left text-xs">
                          <thead className="bg-slate-50 font-semibold text-slate-500">
                            <tr>
                              <th className="px-2 py-2">만기</th>
                              <th className="px-2 py-2">D-day</th>
                              <th className="px-2 py-2">구분</th>
                              <th className="px-2 py-2">보험사</th>
                              <th className="px-2 py-2">차량번호</th>
                              <th className="px-2 py-2">운전자한정</th>
                              <th className="px-2 py-2">메모</th>
                              <th className="px-2 py-2 text-center">작업</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {maturityRows.map((row) => {
                              const dday = daysUntilYmd(row.nextMaturityDate)
                              return (
                                <tr key={row.id}>
                                  <td className="px-2 py-2 tabular-nums text-slate-800">{row.nextMaturityDate || '—'}</td>
                                  <td className="px-2 py-2">
                                    <span className={`rounded-full px-2 py-0.5 font-bold ${dday == null
                                        ? 'bg-slate-100 text-slate-600'
                                        : dday <= 7
                                          ? 'bg-rose-50 text-rose-700'
                                          : 'bg-amber-50 text-amber-800'
                                      }`}>
                                      {dday == null ? '—' : dday === 0 ? 'D-day' : `D-${dday}`}
                                    </span>
                                  </td>
                                  <td className="px-2 py-2">{maturityLineLabel(row.policyLine)}</td>
                                  <td className="px-2 py-2">{row.insurerName || '—'}</td>
                                  <td className="px-2 py-2">{row.vehicleNumber || '—'}</td>
                                  <td className="px-2 py-2">{row.driverRestriction || '—'}</td>
                                  <td className="max-w-[240px] px-2 py-2">
                                    <div className="truncate" title={row.productLabel || row.memo || ''}>{row.productLabel || row.memo || '—'}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <button
                                      type="button"
                                      className="rounded border border-rose-200 bg-white px-2 py-1 font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                                      disabled={maturitySaving}
                                      onClick={() => closeMaturityPolicy(row)}
                                    >
                                      종료
                                    </button>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {sectionTab === 'diagnosis-simulation' ? (
                <div
                  className={SHOW_DIAGNOSIS_TERMS_SEARCH
                    ? 'grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)]'
                    : 'space-y-4'}
                  id="diagnosis-simulation"
                >
                  <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-bold text-slate-900">진단시뮬레이션</div>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">
                          암센터·심평원 등 공개 통계 기반 가정과 보유계약 담보를 연결해, 특정 진단 발생 시 예상 필요자금과 보장 대응력을 보여줍니다.
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {isOperator ? (
                          <button
                            type="button"
                            className="rounded border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100"
                            onClick={openDiagnosisRuleModal}
                          >
                            시나리오 룰 관리
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          onClick={loadDiagnosisOverview}
                          disabled={diagnosisLoading}
                        >
                          새로고침
                        </button>
                      </div>
                    </div>
                    {diagnosisError ? (
                      <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{diagnosisError}</div>
                    ) : null}
                    <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                      <div className="rounded border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="text-xs font-bold text-slate-900">생성 조건</div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-3">
                          <div className="rounded bg-white px-3 py-2 text-xs">
                            <span className="block text-slate-500">상태</span>
                            <strong className={diagnosisOverview?.ready ? 'text-emerald-700' : 'text-amber-700'}>
                              {diagnosisOverview?.readinessLabel || '확인 필요'}
                            </strong>
                          </div>
                          <div className="rounded bg-white px-3 py-2 text-xs">
                            <span className="block text-slate-500">계약</span>
                            <strong className="text-slate-900">{diagnosisOverview?.contractCount || coverageContracts.length || 0}건</strong>
                          </div>
                          <div className="rounded bg-white px-3 py-2 text-xs">
                            <span className="block text-slate-500">담보</span>
                            <strong className="text-slate-900">{diagnosisOverview?.coverageCount || 0}개</strong>
                          </div>
                        </div>
                        {!diagnosisOverview?.ready ? (
                          <p className="mt-3 text-xs text-slate-500">보험계약 탭에서 보장분석 PDF를 업로드하고 계약/담보를 저장하면 시뮬레이션을 생성할 수 있습니다.</p>
                        ) : null}
                        {SHOW_DIAGNOSIS_RECOMMENDATIONS && Array.isArray(diagnosisOverview?.recommendedScenarios) && diagnosisOverview.recommendedScenarios.length > 0 ? (
                          <div className="mt-3 border-t border-slate-200 pt-3">
                            <div className="text-xs font-bold text-slate-900">고객별 추천 TOP3</div>
                            <div className="mt-2 grid gap-2">
                              {diagnosisOverview.recommendedScenarios.slice(0, 3).map((scenario) => (
                                <button
                                  key={scenario.code}
                                  type="button"
                                  className={`rounded border px-3 py-2 text-left text-xs transition ${diagnosisSelectedCode === scenario.code
                                      ? 'border-brand-navy bg-white text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                                  onClick={() => setDiagnosisSelectedCode(scenario.code)}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-bold">{scenario.rank}. {scenario.title}</span>
                                    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                                      {scenario.score ?? 0}점
                                    </span>
                                  </div>
                                  <div className="mt-1 text-[11px] text-slate-500">
                                    부족 예상 {formatWon(scenario.estimatedShortfall)} · 진단비 예상 {formatWon(scenario.estimatedInsuranceBenefit)}
                                  </div>
                                  {Array.isArray(scenario.reasons) && scenario.reasons.length > 0 ? (
                                    <div className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-slate-600">
                                      {scenario.reasons[0]}
                                    </div>
                                  ) : null}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className="rounded border border-slate-200 bg-slate-50 px-3 py-3">
                        <label className="block text-xs font-bold text-slate-900">
                          진단 선택
                          <select
                            className="mt-2 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={diagnosisSelectedCode}
                            onChange={(e) => setDiagnosisSelectedCode(e.target.value)}
                          >
                            {(Array.isArray(diagnosisOverview?.scenarios) ? diagnosisOverview.scenarios : []).map((scenario) => (
                              <option key={scenario.code} value={scenario.code}>
                                {SHOW_DIAGNOSIS_RECOMMENDATIONS && Array.isArray(diagnosisOverview?.recommendedScenarios) && diagnosisOverview.recommendedScenarios.some((item) => item.code === scenario.code) ? '추천 · ' : ''}{scenario.title}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="mt-3 w-full rounded bg-brand-navy px-3 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={!diagnosisOverview?.ready || diagnosisGenerating || !diagnosisSelectedCode}
                          onClick={generateDiagnosisSimulation}
                        >
                          {diagnosisGenerating ? '생성 중…' : '시뮬레이션 생성/미리보기'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {diagnosisDraft ? (
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-bold text-slate-900">{diagnosisDraft.title || '진단시뮬레이션'}</div>
                          <div className="mt-1 text-xs text-slate-600">
                            부족 예상액 {formatWon(diagnosisDraft.estimatedShortfall)} · 진단비 예상액 {formatWon(diagnosisDraft.estimatedInsuranceBenefit)}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="rounded bg-brand-navy px-4 py-2 text-sm font-bold text-white hover:opacity-95"
                            onClick={() => openDiagnosisSimulationWindow(diagnosisDraft, false)}
                          >
                            미리보기
                          </button>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-brand-navy hover:bg-slate-50"
                            onClick={() => openDiagnosisSimulationWindow(diagnosisDraft, true)}
                          >
                            인쇄/PDF
                          </button>
                          {isOperator ? (
                            <>
                              {diagnosisTemplateOptions.length > 0 ? (
                                <select
                                  className="max-w-[190px] rounded border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                                  value={diagnosisTemplateId}
                                  onChange={(e) => setDiagnosisTemplateId(e.target.value)}
                                  disabled={diagnosisTemplateBusy}
                                  title="운영자 전용 진단시뮬레이션 템플릿"
                                >
                                  <option value="">운영 기본 템플릿</option>
                                  {diagnosisTemplateOptions.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {outputTemplateLabel(template)}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              <button
                                type="button"
                                className="rounded border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={diagnosisTemplateBusy || diagnosisTemplateOptions.length === 0}
                                onClick={downloadDiagnosisSimulationTemplateImage}
                                title={diagnosisTemplateOptions.length > 0 ? '운영자 전용 출력물 템플릿 이미지' : '게시된 이미지 템플릿이 없습니다.'}
                              >
                                {diagnosisTemplateBusy ? '생성 중...' : '템플릿 이미지'}
                              </button>
                              {diagnosisHtmlTemplateOptions.length > 0 ? (
                                <select
                                  className="max-w-[190px] rounded border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                                  value={diagnosisHtmlTemplateId}
                                  onChange={(e) => setDiagnosisHtmlTemplateId(e.target.value)}
                                  disabled={diagnosisHtmlBusy}
                                  title="운영자 전용 진단시뮬레이션 HTML 템플릿"
                                >
                                  <option value="">운영 기본 HTML</option>
                                  {diagnosisHtmlTemplateOptions.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {outputTemplateLabel(template)}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              <button
                                type="button"
                                className="rounded border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={diagnosisHtmlBusy || diagnosisHtmlTemplateOptions.length === 0}
                                onClick={downloadDiagnosisSimulationTemplateHtml}
                                title={diagnosisHtmlTemplateOptions.length > 0 ? '운영자 전용 출력물 HTML' : '게시된 HTML 템플릿이 없습니다.'}
                              >
                                {diagnosisHtmlBusy ? '생성 중...' : 'HTML'}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-lg border border-slate-200 bg-white">
                    <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                      <div className="text-sm font-bold text-slate-900">최근 생성 이력</div>
                    </div>
                    {diagnosisLoading ? (
                      <div className="px-3 py-6 text-center text-sm text-slate-500">불러오는 중…</div>
                    ) : !Array.isArray(diagnosisOverview?.history) || diagnosisOverview.history.length === 0 ? (
                      <EmptyState title="생성된 진단시뮬레이션이 없습니다" className="py-8" />
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {diagnosisOverview.history.slice(0, 5).map((row) => (
                          <button
                            key={row.id}
                            type="button"
                            className="block w-full px-3 py-2.5 text-left hover:bg-slate-50"
                            onClick={() => openDiagnosisSimulation(row.id, false)}
                          >
                            <span className="block text-sm font-semibold text-slate-900">{row.title || row.diagnosisName || '진단시뮬레이션'}</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {formatApiDateTimeDisplay(row.createdAtDisplay, row.createdAt)} · {row.statusLabel || row.status || 'DRAFT'} · 부족 {formatWon(row.estimatedShortfall)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  </div>

                  {SHOW_DIAGNOSIS_TERMS_SEARCH ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-bold text-slate-900">직접 질병검색</div>
                          <p className="mt-1 text-xs leading-relaxed text-slate-500">
                            질병명 또는 질병코드로 보유계약의 담보와 약관 근거를 함께 확인합니다.
                          </p>
                        </div>
                        {diagnosisTermsResult ? (
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600">
                            {formatCount(diagnosisTermsResult.matchedContractCount)} / {formatCount(diagnosisTermsResult.contractCount)}건
                          </span>
                        ) : null}
                      </div>

                      {diagnosisTermsError ? (
                        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{diagnosisTermsError}</div>
                      ) : null}

                      {diagnosisTermsResult?.summary ? (
                        <div className="mt-3 rounded border border-sky-100 bg-sky-50 px-3 py-2 text-xs leading-5 text-slate-700">
                          {diagnosisTermsResult.summary}
                        </div>
                      ) : null}

                      {diagnosisTermsResult?.resultCounts ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700">
                            가능성 높음 {formatCount(diagnosisTermsResult.resultCounts.HIGH_POSSIBILITY)}
                          </span>
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-bold text-sky-700">
                            검토 필요 {formatCount(diagnosisTermsResult.resultCounts.REVIEW_REQUIRED)}
                          </span>
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-bold text-amber-800">
                            약관 연결 필요 {formatCount(diagnosisTermsResult.resultCounts.TERMS_LINK_REQUIRED)}
                          </span>
                        </div>
                      ) : null}

                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <label className="block text-xs font-bold text-slate-900">
                          질병명
                          <input
                            className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-sm"
                            value={diagnosisTermsSearch.q}
                            onChange={(e) => setDiagnosisTermsSearch((prev) => ({ ...prev, q: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') searchDiagnosisTerms()
                            }}
                            placeholder="예: 암, 뇌졸중"
                          />
                        </label>
                        <label className="block text-xs font-bold text-slate-900">
                          질병코드
                          <input
                            className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-sm"
                            value={diagnosisTermsSearch.diseaseCode}
                            onChange={(e) => setDiagnosisTermsSearch((prev) => ({ ...prev, diseaseCode: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') searchDiagnosisTerms()
                            }}
                            placeholder="예: C50.9"
                          />
                        </label>
                      </div>

                      <div className="mt-3 grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
                        <label className="block text-xs font-bold text-slate-900">
                          범위
                          <select
                            className="mt-1 h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm"
                            value={diagnosisTermsSearch.scope}
                            onChange={(e) => setDiagnosisTermsSearch((prev) => ({ ...prev, scope: e.target.value }))}
                          >
                            <option value="selected">선택 계약</option>
                            <option value="all">전체 계약</option>
                          </select>
                        </label>
                        <label className="block text-xs font-bold text-slate-900">
                          선택 계약
                          <select
                            className="mt-1 h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-100"
                            value={diagnosisTermsSelectedContractId}
                            disabled={diagnosisTermsSearch.scope !== 'selected'}
                            onChange={(e) => setDiagnosisTermsSearch((prev) => ({ ...prev, contractId: e.target.value }))}
                          >
                            {coverageContracts.length === 0 ? (
                              <option value="">검색 시 계약 목록을 불러옵니다</option>
                            ) : coverageContracts.map((contract) => (
                              <option key={contract.id} value={contract.id}>
                                {[contract.insurerName, contract.productName, contract.contractDate].filter(Boolean).join(' · ')}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <button
                        type="button"
                        className="mt-3 w-full rounded bg-brand-navy px-3 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={diagnosisTermsLoading || coverageLoading}
                        onClick={searchDiagnosisTerms}
                      >
                        {diagnosisTermsLoading ? '검색 중…' : '보장 후보 검색'}
                      </button>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white">
                      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                        <div className="text-sm font-bold text-slate-900">검색 결과</div>
                        <span className="text-xs text-slate-500">{formatCount(diagnosisTermsRows.length)}건</span>
                      </div>
                      {diagnosisTermsLoading ? (
                        <div className="px-3 py-6 text-center text-sm text-slate-500">검색 중…</div>
                      ) : !diagnosisTermsResult ? (
                        <EmptyState title="질병명 또는 질병코드를 검색하세요" className="py-8" />
                      ) : diagnosisTermsRows.length === 0 ? (
                        <EmptyState title="관련 담보 또는 약관 근거를 찾지 못했습니다" className="py-8" />
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {diagnosisTermsRows.map((row) => {
                            const contract = row.contract || {}
                            return (
                              <div key={contract.id || `${contract.insurerName}-${contract.productName}`} className="px-3 py-3">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="line-clamp-1 text-sm font-bold text-slate-900">
                                      {[contract.insurerName, contract.productName].filter(Boolean).join(' · ') || '보유계약'}
                                    </div>
                                    <div className="mt-0.5 text-xs text-slate-500">
                                      {contract.contractDate || '계약일 미입력'} · 월 보험료 {formatWon(contract.monthlyPremium)}
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      <span className={`inline-flex min-h-[1.35rem] items-center rounded-full border px-2 text-[11px] font-bold ${diseaseJudgementClass(row.judgementStatus)}`}>
                                        {diseaseJudgementLabel(row.judgementStatus, row.judgementLabel)}
                                      </span>
                                      <span className={`inline-flex min-h-[1.35rem] items-center rounded-full border px-2 text-[11px] font-bold ${row.termsLinked ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                                        {row.termsLinked ? '약관 연결됨' : '약관 연결 필요'}
                                      </span>
                                    </div>
                                    {row.termsSearchMessage ? (
                                      <div className="mt-1 rounded border border-amber-100 bg-amber-50 px-2 py-1 text-[11px] leading-4 text-amber-800">
                                        {row.termsSearchMessage}
                                      </div>
                                    ) : null}
                                  </div>
                                  {contract.id ? (
                                    <button
                                      type="button"
                                      className="rounded border border-sky-200 bg-white px-2 py-1 text-xs font-semibold text-brand-navy hover:bg-sky-50 disabled:opacity-50"
                                      disabled={coverageTermsLookupBusyId === contract.id}
                                      onClick={() => openSavedCoverageTermsLookup(contract)}
                                    >
                                      {coverageTermsLookupBusyId === contract.id ? '조회 중…' : '약관'}
                                    </button>
                                  ) : null}
                                </div>

                                {row.matchedCoverages.length ? (
                                  <div className="mt-3">
                                    <div className="mb-1 text-xs font-bold text-slate-700">상세담보</div>
                                    <div className="space-y-1.5">
                                      {row.matchedCoverages.map((coverage) => (
                                        <div key={coverage.id || `${coverage.coverageNameRaw}-${coverage.insuredAmount}`} className="rounded border border-emerald-100 bg-emerald-50/70 px-2 py-1.5 text-xs">
                                          <div className="flex items-start justify-between gap-2">
                                            <span className="min-w-0 font-semibold text-emerald-950">{coverage.coverageNameRaw || coverage.coverageNameNormalized || '담보명 미입력'}</span>
                                            <span className="shrink-0 font-bold tabular-nums text-emerald-800">{formatCoverageAmountKorean(coverage.insuredAmount)}</span>
                                          </div>
                                          {coverage.coverageGroup ? <div className="mt-0.5 text-[11px] text-emerald-700">{coverage.coverageGroup}</div> : null}
                                          {asArray(coverage.matchedTerms).length ? (
                                            <div className="mt-1 text-[11px] text-emerald-700">
                                              일치 키워드 {asArray(coverage.matchedTerms).slice(0, 4).join(', ')}
                                            </div>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}

                                {row.documents.length ? (
                                  <div className="mt-3">
                                    <div className="mb-1 text-xs font-bold text-slate-700">약관 근거</div>
                                    <div className="space-y-2">
                                      {row.documents.slice(0, 3).map((documentResult) => {
                                        const doc = documentResult.document || {}
                                        const chunks = asArray(documentResult.matchedChunks)
                                        return (
                                          <div key={doc.id || doc.pdfUrl} className="rounded border border-slate-200 bg-slate-50 px-2 py-2">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                              <div className="line-clamp-1 text-xs font-bold text-slate-900">{termsDocumentTitle(doc)}</div>
                                              <div className="flex flex-wrap gap-1">
                                                <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                                                  본문 {formatCount(chunks.length)}건
                                                </span>
                                                {doc.pdfUrl ? (
                                                  <a
                                                    className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-brand-navy hover:bg-sky-50"
                                                    href={doc.pdfUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                  >
                                                    PDF
                                                  </a>
                                                ) : null}
                                              </div>
                                            </div>
                                            {chunks.slice(0, 2).map((chunk) => (
                                              <div key={chunk.chunkId || `${chunk.documentId}-${chunk.chunkIndex}`} className="mt-1.5 rounded bg-white px-2 py-1.5 text-xs leading-5 text-slate-700">
                                                <span className="mb-0.5 block text-[11px] font-bold text-slate-500">본문 {Number(chunk.chunkIndex ?? 0) + 1}</span>
                                                <span className="line-clamp-3">{chunk.text || '-'}</span>
                                              </div>
                                            ))}
                                            {documentResult.searchMessage ? (
                                              <div className="mt-1.5 rounded border border-amber-100 bg-white px-2 py-1.5 text-[11px] leading-4 text-amber-800">
                                                {documentResult.searchMessage}
                                              </div>
                                            ) : null}
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                ) : null}
                                {!row.matchedCoverages.length && !row.documents.length ? (
                                  <div className="mt-3 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">
                                    관련 담보와 연결 약관 본문 근거가 아직 확인되지 않았습니다.
                                  </div>
                                ) : null}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  ) : null}
                </div>
              ) : null}

              {sectionTab === 'claims' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">보험금 청구 이력</div>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                        to={`/support/claims?${crmQuery(customerId)}`}
                      >
                        새 청구
                      </Link>
                      <Link
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                        to="/support/claims/applications"
                      >
                        전체 청구 이력
                      </Link>
                    </div>
                  </div>
                  {recentClaimApplications.length === 0 ? (
                    <EmptyState
                      title="연결된 보험금 청구 이력이 없습니다"
                      description="이 고객 연락처(또는 이름)로 작성한 청구 건이 있으면 여기에서 확인할 수 있습니다."
                      className="rounded border border-dashed py-6"
                    />
                  ) : (
                    <ul className="space-y-2">
                      {recentClaimApplications.map((row) => (
                        <li
                          key={`claim-${row.id}`}
                          className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-slate-900">{claimApplicationTitle(row)}</span>
                              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${claimApplicationStatusClass(row.status)}`}>
                                {claimApplicationStatusLabel(row.status)}
                              </span>
                            </div>
                            <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
                              {row.insurerOrgName ? <span>{row.insurerOrgName}</span> : null}
                              {row.updatedAt ? <span>{formatApiDateTimeDisplay(row.updatedAtDisplay, row.updatedAt)}</span> : null}
                              {row.claimDate ? <span>청구일 {String(row.claimDate).slice(0, 10)}</span> : null}
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-2">
                            {row.status === 'DRAFT' ? (
                              <Link
                                className="inline-flex h-8 items-center rounded border border-slate-300 bg-white px-2.5 text-xs font-semibold text-brand-navy hover:bg-slate-50"
                                to={`/support/claims?resumeApplicationId=${row.id}`}
                              >
                                이어쓰기
                              </Link>
                            ) : (
                              <Link
                                className="inline-flex h-8 items-center rounded border border-slate-300 bg-white px-2.5 text-xs font-semibold text-brand-navy hover:bg-slate-50"
                                to={`/support/claims/${row.id}`}
                              >
                                상세
                              </Link>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {latestClaimApplication && recentClaimApplications.length > 0 ? (
                    <p className="text-xs text-slate-500">
                      최근 청구: {claimApplicationTitle(latestClaimApplication)} · {claimApplicationStatusLabel(latestClaimApplication.status)}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {sectionTab === 'health' ? (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">진료정보분석 (최근)</div>
                    <Link
                      className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                      to={`/health-analysis?${crmQuery(customerId)}`}
                    >
                      새 진료정보분석
                    </Link>
                  </div>
                  {!latestHealth ? (
                    <EmptyState
                      title="연결된 진료분석 리포트가 없습니다"
                      description="이 고객으로 진료정보분석을 진행하면 최근 리포트와 분석 결과를 여기에서 바로 확인할 수 있습니다."
                      className="mt-2 rounded border border-dashed py-6"
                    />
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded border border-slate-100 bg-slate-50/80 px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800">{latestHealth.customerName || '리포트'}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{formatApiDateTimeDisplay(latestHealth.createdAtDisplay, latestHealth.createdAt)}</div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex h-8 items-center rounded border border-emerald-200 bg-white px-2.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                          disabled={easyExplainGenerating === `HEALTH_REPORT:${latestHealth.id || ''}`}
                          onClick={() => generateEasyExplanation('HEALTH_REPORT', latestHealth.id)}
                        >
                          {easyExplainGenerating === `HEALTH_REPORT:${latestHealth.id || ''}` ? '생성 중…' : '고객용 설명'}
                        </button>
                        {isOperator && SHOW_CUSTOMER_CARD_NEWS_BUTTONS ? (
                          <button
                            type="button"
                            className="inline-flex h-8 items-center rounded border border-teal-200 bg-teal-50 px-2.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                            onClick={() => openCustomerCardNews('health')}
                          >
                            카드뉴스
                          </button>
                        ) : null}
                        <Link className="inline-flex h-8 items-center rounded border border-slate-300 bg-white px-2.5 text-xs font-semibold text-brand-navy hover:bg-slate-50" to={`/health-analysis?${crmQuery(customerId)}`}>
                          진료정보분석 열기
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {sectionTab === 'hidden-insurance' ? (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">숨은보험금찾기 (최근)</div>
                    <Link
                      className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                      to={customerPath('/hidden-insurance-money', customerId)}
                    >
                      새 조회
                    </Link>
                  </div>
                  {!latestHiddenInsurance ? (
                    <EmptyState
                      title="연결된 조회 이력이 없습니다"
                      description="이 고객으로 숨은보험금찾기를 진행하면 최근 조회 결과를 여기에서 바로 확인할 수 있습니다."
                      className="mt-2 rounded border border-dashed py-6"
                    />
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded border border-slate-100 bg-slate-50/80 px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800">
                          {latestHiddenInsurance.customerName || c?.customerName || '조회'}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500">
                          {formatApiDateTimeDisplay(latestHiddenInsurance.createdAtDisplay, latestHiddenInsurance.createdAt)}
                          {' · '}
                          {hiddenLookupStatusLabel(latestHiddenInsurance.status)}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {isOperator && SHOW_CUSTOMER_CARD_NEWS_BUTTONS ? (
                          <button
                            type="button"
                            className="inline-flex h-8 items-center rounded border border-teal-200 bg-teal-50 px-2.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                            onClick={() => openCustomerCardNews('hidden')}
                          >
                            카드뉴스
                          </button>
                        ) : null}
                        {latestHiddenInsurance.status === 'COMPLETED' ? (
                          <Link
                            className="inline-flex h-8 items-center rounded border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                            to={`/hidden-insurance-money?${crmQuery(customerId)}&openLookupId=${latestHiddenInsurance.id}`}
                          >
                            결과 보기
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {sectionTab === 'fire' ? (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">화재보험설계 (최근 조회·설계 이력)</div>
                    <Link
                      className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                      to={`/fire/building-registry?${crmQuery(customerId)}`}
                    >
                      화재보험설계 시작
                    </Link>
                  </div>
                  {!highlights.latestFireHistory ? (
                    <EmptyState
                      title="화재보험 설계 이력이 없습니다"
                      description="이 고객명으로 건축물대장을 조회하거나 설계의뢰를 만들면 최근 이력이 연결됩니다."
                      className="mt-2 rounded border border-dashed py-6"
                    />
                  ) : (
                    <div className="mt-2 space-y-1 rounded border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs">
                      <div className="font-medium text-slate-800">이력 #{highlights.latestFireHistory.id}</div>
                      <div className="text-slate-600">{highlights.latestFireHistory.roadAddress || highlights.latestFireHistory.jibunAddress || highlights.latestFireHistory.bldNm || '—'}</div>
                      <div className="text-slate-500">{highlights.latestFireHistory.createdAt || ''}</div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Link className="font-medium text-brand-navy hover:underline" to={`/fire/building-registry?${crmQuery(customerId)}`}>화재보험설계</Link>
                        {highlights.latestFireHistory.requestId ? (
                          <Link className="font-medium text-brand-navy hover:underline" to={`/requests/${highlights.latestFireHistory.requestId}`}>연결된 설계의뢰</Link>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {highlights.unlinkedFireHistoryCandidates?.length ? (
                    <div className="mt-3 rounded border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs">
                      <div className="font-semibold text-amber-900">연결 후보</div>
                      <div className="mt-1 space-y-1">
                        {highlights.unlinkedFireHistoryCandidates.map((item) => (
                          <div key={`fire-candidate-${item.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white/70 px-2 py-1.5">
                            <span className="text-slate-700">#{item.id} · {item.roadAddress || item.jibunAddress || item.bldNm || '-'} · {item.createdAt || ''}</span>
                            <button
                              type="button"
                              className="font-semibold text-brand-navy hover:underline"
                              onClick={() => linkFireHistoryToCustomer(item.id)}
                            >
                              이 고객과 연결
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {sectionTab === 'request' ? (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">설계의뢰 (최근)</div>
                    <Link
                      className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                      to={`/requests/new?${crmQuery(customerId)}`}
                    >
                      새 설계의뢰
                    </Link>
                  </div>
                  {!highlights.latestDesignRequest ? (
                    <EmptyState
                      title="연결된 설계 의뢰가 없습니다"
                      description="이 고객으로 진행한 설계 의뢰가 생기면 상태와 담당자를 이곳에서 함께 확인할 수 있습니다."
                      className="mt-2 rounded border border-dashed py-6"
                    />
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50/80 px-3 py-2">
                      <span className="font-medium text-slate-800">{highlights.latestDesignRequest.title || `의뢰 #${highlights.latestDesignRequest.id}`}</span>
                      <span className="text-xs text-slate-500">{formatApiDateTimeDisplay(highlights.latestDesignRequest.createdAtDisplay, highlights.latestDesignRequest.createdAt)}</span>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded border border-emerald-200 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                          disabled={easyExplainGenerating === `DESIGN_REQUEST:${highlights.latestDesignRequest.id || ''}`}
                          onClick={() => generateEasyExplanation('DESIGN_REQUEST', highlights.latestDesignRequest.id)}
                        >
                          {easyExplainGenerating === `DESIGN_REQUEST:${highlights.latestDesignRequest.id || ''}` ? '생성 중…' : '고객용 설명'}
                        </button>
                        <Link className="text-xs font-medium text-brand-navy hover:underline" to={`/requests/${highlights.latestDesignRequest.id}`}>
                          의뢰 열기
                        </Link>
                      </div>
                    </div>
                  )}
                  {highlights.unlinkedDesignRequestCandidates?.length ? (
                    <div className="mt-3 rounded border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs">
                      <div className="font-semibold text-amber-900">연결 후보</div>
                      <div className="mt-1 space-y-1">
                        {highlights.unlinkedDesignRequestCandidates.map((item) => (
                          <div key={`request-candidate-${item.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white/70 px-2 py-1.5">
                            <span className="text-slate-700">#{item.id} · {item.title || '-'} · {formatApiDateTimeDisplay(item.createdAtDisplay, item.createdAt)}</span>
                            <button
                              type="button"
                              className="font-semibold text-brand-navy hover:underline"
                              onClick={() => linkDesignRequestToCustomer(item.id)}
                            >
                              이 고객과 연결
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {sectionTab === 'consultation' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 border-b border-slate-200">
                    {EVENT_CONSULTATION_TABS.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`border-b-2 px-1 pb-2 text-xs font-semibold transition-colors ${eventConsultationTab === t.id
                            ? 'border-brand-navy text-brand-navy'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                          }`}
                        onClick={() => setEventConsultationTab(t.id)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {eventConsultationTab === 'assist' ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">상담 전후 보조 도구</div>
                          <p className="mt-1 text-xs leading-5 text-slate-600">
                            이 화면에서 고지사항 체크, 상담 첫 질문, 상담 후 요약을 처리하고 결과를 상담 이력으로 남깁니다.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                            onClick={() => openScheduleModal('consultation')}
                          >
                            상담 일정 추가
                          </button>
                          <Link
                            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50"
                            to={`/support/surveys?tab=list&${crmQuery(customerId)}`}
                          >
                            설문발송
                          </Link>
                        </div>
                      </div>

                      <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-bold text-amber-950">고지사항 체크</div>
                            <p className="mt-1 text-xs leading-5 text-amber-900">
                              기간별 고지 질문을 체크합니다. 진료정보분석 결과가 있으면 우선 확인할 항목을 자동 체크할 수 있습니다.
                            </p>
                          </div>
                          <button
                            type="button"
                            className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                            disabled={disclosureAutoLoading || !latestHealth?.id}
                            onClick={applyDisclosureFromLatestHealth}
                          >
                            {disclosureAutoLoading ? '반영 중…' : '진료분석으로 자동 체크'}
                          </button>
                        </div>
                        {disclosureAutoNote ? (
                          <div className="mt-2 rounded border border-amber-200 bg-white/80 px-3 py-2 text-xs leading-5 text-amber-900">
                            {disclosureAutoNote}
                          </div>
                        ) : null}
                        <div className="mt-3 grid gap-3 lg:grid-cols-3">
                          {DISCLOSURE_GROUPS.map((group) => (
                            <div key={group.title} className="rounded-lg border border-amber-100 bg-white p-3">
                              <div className="text-xs font-bold text-slate-900">{group.title}</div>
                              <div className="mt-2 space-y-2">
                                {group.items.map((item) => {
                                  const key = `${group.title}:${item}`
                                  return (
                                    <label key={key} className="flex gap-2 text-xs leading-5 text-slate-700">
                                      <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={!!disclosureChecked[key]}
                                        onChange={(e) => {
                                          setDisclosureAutoRows([])
                                          setDisclosureAutoQuestions([])
                                          setDisclosureChecked((prev) => ({ ...prev, [key]: e.target.checked }))
                                        }}
                                      />
                                      <span>{item}</span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-700">
                          {disclosureMemoLines.map((line) => <div key={line}>{line}</div>)}
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50" onClick={() => copyAiMessage(disclosureMemoLines.join('\n'))}>복사</button>
                          <button type="button" className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50" disabled={consultSaving} onClick={() => saveConsultationHistory('고지사항 체크', disclosureMemoLines)}>
                            상담 이력에 남기기
                          </button>
                        </div>
                      </section>

                      <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
                        <div className="text-sm font-bold text-emerald-950">상담 첫 질문</div>
                        <p className="mt-1 text-xs leading-5 text-emerald-900">상담 목적에 맞춰 첫 질문을 준비하고 상담 이력에 남깁니다.</p>
                        <select className="mt-3 w-full rounded border border-emerald-200 bg-white px-3 py-2 text-sm" value={questionPurpose} onChange={(e) => setQuestionPurpose(e.target.value)}>
                          {QUESTION_PURPOSES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                        </select>
                        <div className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-700">
                          {consultationQuestionLines.map((q, idx) => <div key={q}>{idx + 1}. {q}</div>)}
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50" onClick={() => copyAiMessage(consultationQuestionLines.map((q, idx) => `${idx + 1}. ${q}`).join('\n'))}>복사</button>
                          <button
                            type="button"
                            className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                            disabled={consultSaving}
                            onClick={() => saveConsultationHistory(`상담 질문 · ${selectedQuestionPurpose.label}`, consultationQuestionLines.map((q, idx) => `${idx + 1}. ${q}`))}
                          >
                            상담 이력에 남기기
                          </button>
                        </div>
                      </section>

                      <section className="rounded-xl border border-sky-200 bg-sky-50/50 p-3">
                        <div className="text-sm font-bold text-sky-950">상담 후 요약</div>
                        <p className="mt-1 text-xs leading-5 text-sky-900">상담 내용을 붙여넣으면 요약, 니즈, 확인 필요, 다음 액션으로 정리합니다.</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" className="rounded border border-sky-200 bg-white px-3 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-50" onClick={fillPostConsultMemoFromCustomerMemo}>
                            고객 메모 불러오기
                          </button>
                          <button type="button" className="rounded border border-sky-200 bg-white px-3 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-50" onClick={fillPostConsultMemoFromRecentHistory}>
                            최근 상담/연락 이력 불러오기
                          </button>
                        </div>
                        <textarea
                          className="mt-3 min-h-[120px] w-full rounded border border-sky-200 bg-white px-3 py-2 text-sm"
                          value={postConsultMemo}
                          onChange={(e) => setPostConsultMemo(e.target.value)}
                          placeholder="상담 메모를 입력하세요. 예: 암 진단비 보완 희망, 보험료 15만원 이내, 다음 주 기존 증권 전달 예정"
                        />
                        <div className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-700">
                          {(postConsultSummaryLines || ['상담 내용을 입력하면 요약 결과가 표시됩니다.']).map((line) => <div key={line}>{line}</div>)}
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50" disabled={!postConsultSummaryLines} onClick={() => copyAiMessage(postConsultSummaryLines.join('\n'))}>복사</button>
                          <button type="button" className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50" disabled={consultSaving || !postConsultSummaryLines} onClick={() => saveConsultationHistory('상담 후 요약', postConsultSummaryLines)}>
                            상담 이력에 남기기
                          </button>
                        </div>
                      </section>

                    </div>
                  ) : null}

                  {eventConsultationTab === 'consult-history' ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">상담 이력</div>
                          <p className="mt-1 text-xs text-slate-500">상담 일정과 상담 도구에서 남긴 기록입니다.</p>
                        </div>
                        <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50" disabled={scheduleLoading} onClick={() => loadScheduleRows()}>
                          새로고침
                        </button>
                      </div>
                      {scheduleLoading ? (
                        <p className="text-xs text-slate-500">상담 이력 불러오는 중…</p>
                      ) : consultationRows.length === 0 ? (
                        <EmptyState title="상담 이력이 없습니다" className="rounded border border-dashed py-6" />
                      ) : (
                        <ul className="space-y-2">
                          {consultationRows.map((row) => (
                            <li key={row.occurrenceKey || row.id} className="rounded border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs">
                              <div className="flex flex-wrap justify-between gap-2">
                                <span className="font-medium text-slate-800">{row.title || '[상담]'}</span>
                                <span className="text-slate-500">{formatRangeLabel(row.startAt, row.endAt, row.allDay)}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}

                  {eventConsultationTab === 'survey' ? (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">상담 설문 (최근)</div>
                      {!latestSurvey ? (
                        <EmptyState
                          title="연결된 설문 세션이 없습니다"
                          className="mt-2 rounded border border-dashed py-6"
                        />
                      ) : (
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50/80 px-3 py-2">
                          <span className="font-medium text-slate-800">세션 #{latestSurvey.id}</span>
                          <span className="text-xs text-slate-500">{latestSurvey.status || '-'}</span>
                          <Link className="text-xs font-medium text-brand-navy hover:underline" to={`/support/surveys/sessions/${latestSurvey.id}/fill`}>
                            설문 열기
                          </Link>
                        </div>
                      )}
                    </div>
                  ) : null}

                  {eventConsultationTab === 'lead' ? (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">리드 (개인·카테고리 랜딩, 최근)</div>
                      {(!highlights.recentPublicLeads?.length && !highlights.recentLandingLeads?.length) ? (
                        <EmptyState
                          title="연결된 리드가 없습니다"
                          className="mt-2 rounded border border-dashed py-6"
                        />
                      ) : (
                        <ul className="mt-2 space-y-2">
                          {(highlights.recentPublicLeads || []).map((l) => (
                            <li key={`p-${l.id}`} className="rounded border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs">
                              <span className="font-medium text-slate-800">개인랜딩</span> · {l.customerName || '-'} · {l.status || '-'} · {l.createdAt || ''}
                            </li>
                          ))}
                          {(highlights.recentLandingLeads || []).map((l) => (
                            <li key={`c-${l.id}`} className="rounded border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs">
                              <span className="font-medium text-slate-800">카테고리랜딩</span> · {l.category || '-'} · {l.customerName || '-'} · {l.status || '-'} · {l.createdAt || ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {sectionTab === 'inquiries' ? (
                <div className="space-y-3">
                  <p className="text-slate-600">고객 문의 목록·상세에서 접수·답변을 관리합니다.</p>
                  <Button
                    asChild
                    variant="sgez"
                    size="sm"
                  >
                    <Link
                      to="/support/inquiries"
                    >
                      고객 문의 열기
                    </Link>
                  </Button>
                </div>
              ) : null}

              {sectionTab === 'sales' ? (
                <div className="rounded border border-slate-100 bg-slate-50/80 px-3 py-6 text-center text-slate-600">
                  상품 판매 이력은 추후 연동 예정입니다.
                </div>
              ) : null}

              {sectionTab === 'schedule' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* <p className="text-xs text-slate-600">
                      이 고객과 연결된 일정(개인 일정 위주)입니다. 미팅 준비·통화 내용은 일정 <strong className="font-medium text-slate-800">설명</strong>에 남기면 캘린더와 동기화됩니다.
                      {' '}
                      기본 정보에 생년월일을 넣어 두면 상령일(생일+6개월)과 생일이 각각 반영되어, 해당 월·일에 작성자 캘린더에「상령일 도래 고객」「생일 도래 고객」요약 일정이 자동으로 생깁니다. 업무 캘린더에서 표시 여부를 끌 수 있습니다.
                    </p> */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                        disabled={scheduleLoading}
                        onClick={() => loadScheduleRows()}
                      >
                        새로고침
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                        onClick={openScheduleModal}
                      >
                        일정 추가
                      </button>
                      <Link className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-slate-50" to="/work/calendar">
                        업무 캘린더
                      </Link>
                    </div>
                  </div>
                  {scheduleError ? (
                    <ErrorState
                      description={scheduleError}
                      onAction={() => loadScheduleRows()}
                      className="rounded-xl border border-rose-100 bg-white py-6"
                    />
                  ) : null}
                  {scheduleLoading ? (
                    <p className="text-slate-500">일정 불러오는 중…</p>
                  ) : scheduleRows.length === 0 ? (
                    <EmptyState
                      title="연결된 일정이 없습니다"
                      description="일정등록으로 미팅이나 통화 일정을 추가하면 캘린더와 함께 이 고객 상세에서도 확인할 수 있습니다."
                      className="rounded border border-dashed py-6"
                    />
                  ) : (
                    <ul className="space-y-2">
                      {scheduleRows.map((row) => {
                        const occKey = row.occurrenceKey || `${row.id}`
                        const open = expandedOccKey === occKey
                        const kindLabel = row.eventKind === 'ORG' ? '조직' : '개인'
                        return (
                          <li key={occKey} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden">
                            <div className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-slate-900 truncate">{row.title || '(제목 없음)'}</span>
                                  <span className="shrink-0 rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">{kindLabel}</span>
                                </div>
                                <p className="mt-0.5 text-xs text-slate-600">{formatRangeLabel(row.startAt, row.endAt, row.allDay)}</p>
                              </div>
                              <button
                                type="button"
                                className="shrink-0 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 hover:bg-slate-100"
                                onClick={() => toggleScheduleMemoPanel(row)}
                              >
                                {open ? '접기' : '미팅 메모'}
                              </button>
                            </div>
                            {open ? (
                              <div className="border-t border-slate-200 bg-white px-3 py-3 space-y-2">
                                {expDetailLoading ? (
                                  <p className="text-xs text-slate-500">불러오는 중…</p>
                                ) : expDetail?.eventKind !== 'PERSONAL' ? (
                                  <p className="text-xs text-slate-600">
                                    조직 일정은 권한·대상 조직 설정이 필요해 여기서 바로 수정할 수 없습니다.{' '}
                                    <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">업무 캘린더</Link>에서 확인해 주세요.
                                  </p>
                                ) : (
                                  <>
                                    <label className="block text-[11px] font-medium text-slate-600">일정 설명 · 미팅 메모</label>
                                    <textarea
                                      className="w-full min-h-[88px] rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                                      value={expMemoDraft}
                                      onChange={(e) => setExpMemoDraft(e.target.value)}
                                      disabled={expMemoSaving}
                                      placeholder="통화 목적, 안건, 후속 조치 등"
                                    />
                                    <div className="flex justify-end gap-2">
                                      <button
                                        type="button"
                                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                        disabled={expMemoSaving}
                                        onClick={() => toggleScheduleMemoPanel(row)}
                                      >
                                        취소
                                      </button>
                                      <button
                                        type="button"
                                        className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                                        disabled={expMemoSaving}
                                        onClick={saveScheduleOccMemo}
                                      >
                                        {expMemoSaving ? '저장 중…' : '저장'}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ) : null}

              {sectionTab === 'contact' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* <p className="text-xs text-slate-600">
                      고객과 연락한 일시와 내용을 남깁니다. 등록된 연락은 고객 연결 개인 일정으로 저장되어 일정 메뉴에도 자동으로 표시됩니다.
                    </p> */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                        disabled={scheduleLoading}
                        onClick={() => loadScheduleRows()}
                      >
                        새로고침
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                        onClick={openContactModal}
                      >
                        연락 추가
                      </button>
                    </div>
                  </div>
                  {scheduleError ? (
                    <ErrorState
                      description={scheduleError}
                      onAction={() => loadScheduleRows()}
                      className="rounded-xl border border-rose-100 bg-white py-6"
                    />
                  ) : null}
                  {scheduleLoading ? (
                    <p className="text-slate-500">연락 이력 불러오는 중…</p>
                  ) : contactRows.length === 0 ? (
                    <EmptyState
                      title="등록된 연락 이력이 없습니다"
                      className="rounded border border-dashed py-6"
                    />
                  ) : (
                    <ul className="space-y-2">
                      {contactRows.map((row) => {
                        const occKey = row.occurrenceKey || `${row.id}`
                        const open = expandedOccKey === occKey
                        return (
                          <li key={occKey} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden">
                            <div className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900 truncate">{row.title || '[연락]'}</div>
                                <p className="mt-0.5 text-xs text-slate-600">{formatRangeLabel(row.startAt, row.endAt, row.allDay)}</p>
                              </div>
                              <div className="flex shrink-0 flex-wrap gap-1.5">
                                <button
                                  type="button"
                                  className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 hover:bg-slate-100"
                                  onClick={() => toggleScheduleMemoPanel(row)}
                                >
                                  {open ? '접기' : '내용 보기'}
                                </button>
                                <Link
                                  className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-brand-navy hover:bg-slate-100"
                                  to="/work/calendar"
                                >
                                  일정 보기
                                </Link>
                              </div>
                            </div>
                            {open ? (
                              <div className="border-t border-slate-200 bg-white px-3 py-3 space-y-2">
                                {expDetailLoading ? (
                                  <p className="text-xs text-slate-500">불러오는 중…</p>
                                ) : expDetail?.eventKind !== 'PERSONAL' ? (
                                  <p className="text-xs text-slate-600">
                                    조직 일정은 권한·대상 조직 설정이 필요해 여기서 바로 수정할 수 없습니다.{' '}
                                    <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">업무 캘린더</Link>에서 확인해 주세요.
                                  </p>
                                ) : (
                                  <>
                                    <label className="block text-[11px] font-medium text-slate-600">연락 내용</label>
                                    <textarea
                                      className="w-full min-h-[88px] rounded border border-slate-300 px-2.5 py-1.5 text-sm"
                                      value={expMemoDraft}
                                      onChange={(e) => setExpMemoDraft(e.target.value)}
                                      disabled={expMemoSaving}
                                      placeholder="연락 내용과 후속 조치 등"
                                    />
                                    <div className="flex justify-end gap-2">
                                      <button
                                        type="button"
                                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                        disabled={expMemoSaving}
                                        onClick={() => toggleScheduleMemoPanel(row)}
                                      >
                                        취소
                                      </button>
                                      <button
                                        type="button"
                                        className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                                        disabled={expMemoSaving}
                                        onClick={saveScheduleOccMemo}
                                      >
                                        {expMemoSaving ? '저장 중…' : '저장'}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ) : null}

              {sectionTab === 'history' ? (
                <>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">이력</div>
                    <p className="mt-1 text-xs text-slate-500">
                      기본 정보 저장, 메모 저장, 명함 등록·삭제, 설문 연결, 진료분석, 리드 등 이 고객에 기록된 활동입니다.
                    </p>
                    {activityLoading ? (
                      <p className="mt-3 text-xs text-slate-500">이력 불러오는 중…</p>
                    ) : activityItems.length === 0 ? (
                      <EmptyState
                        title="기록된 활동 이력이 없습니다"
                        description="고객 정보 저장, 메모 작성, 설문 연결, 진료분석 등 업무가 진행되면 활동 이력이 쌓입니다."
                        className="mt-3 rounded border border-dashed py-6"
                      />
                    ) : (
                      <>
                        <ul className="mt-3 space-y-2">
                          {activityItems.map((a) => {
                            const sum = activityPayloadSummary(a.payloadJson)
                            return (
                              <li key={a.id} className="rounded border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs text-slate-700">
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                  <span className="font-medium text-slate-800">{activityTypeLabel(a.activityType)}</span>
                                  <span className="shrink-0 text-[11px] text-slate-500 tabular-nums">{formatActivityWhen(a.createdAt)}</span>
                                </div>
                                {sum ? <p className="mt-1 text-[11px] text-slate-600">{sum}</p> : null}
                                {a.payloadJson && !sum ? (
                                  <pre className="mt-1 max-h-24 overflow-auto rounded bg-white/80 p-2 text-[10px] text-slate-600 whitespace-pre-wrap">{a.payloadJson}</pre>
                                ) : null}
                              </li>
                            )
                          })}
                        </ul>
                        {activityTotalPages > 1 ? (
                          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-600">
                            <button
                              type="button"
                              className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
                              disabled={activityPage <= 0 || activityLoading}
                              onClick={() => loadActivityPage(activityPage - 1)}
                            >
                              이전
                            </button>
                            <span className="tabular-nums">
                              {activityPage + 1} / {activityTotalPages}
                              {activityTotalElements > 0 ? (
                                <span className="ml-1 text-slate-400">({activityTotalElements}건)</span>
                              ) : null}
                            </span>
                            <button
                              type="button"
                              className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
                              disabled={activityLoading || activityPage + 1 >= activityTotalPages}
                              onClick={() => loadActivityPage(activityPage + 1)}
                            >
                              다음
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
          ) : null}
        </div>
      )}

      {integratedReportSectionModalOpen ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border bg-white shadow-xl" role="dialog" aria-modal="true">
            <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
              <div>
                <div className="text-base font-bold text-slate-900">통합 리포트 출력 항목 선택</div>
                <div className="mt-1 text-xs text-slate-500">
                  선택한 항목으로 리포트를 생성하고, 이후 미리보기와 인쇄에도 같은 구성이 적용됩니다.
                </div>
              </div>
              <button
                type="button"
                className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                onClick={() => setIntegratedReportSectionModalOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="max-h-[min(72vh,620px)] overflow-y-auto px-5 py-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-semibold text-slate-600">
                  선택 {integratedReportDraftSectionCount}/{INTEGRATED_REPORT_SECTION_OPTIONS.length}
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => setIntegratedReportSectionDraft(DEFAULT_INTEGRATED_REPORT_SECTION_IDS)}
                  >
                    전체 선택
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => setIntegratedReportSectionDraft(COUNSELING_INTEGRATED_REPORT_SECTION_IDS)}
                  >
                    상담용 선택
                  </button>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {INTEGRATED_REPORT_SECTION_OPTIONS.map((section) => (
                  <label key={section.id} className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-xs hover:bg-white">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={normalizeIntegratedReportSections(integratedReportSectionDraft).includes(section.id)}
                      onChange={() => toggleIntegratedReportSectionDraft(section.id)}
                    />
                    <span className="min-w-0">
                      <span className="block font-bold text-slate-800">{section.label}</span>
                      <span className="block truncate text-[11px] text-slate-500">{section.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-slate-50 px-5 py-3">
              <button
                type="button"
                className="rounded border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                onClick={() => setIntegratedReportSectionModalOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="rounded bg-brand-navy px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={integratedReportGenerating}
                onClick={confirmIntegratedReportGenerate}
              >
                {integratedReportGenerating ? '생성 중…' : '선택 항목으로 생성'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {schedModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={schedBusy}
            onClick={() => { if (!schedBusy) setSchedModal(null) }}
            aria-label="일정 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div className="font-bold text-sm sm:text-base">일정 등록 · {schedModal.name}</div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50" disabled={schedBusy} onClick={() => setSchedModal(null)}>닫기</button>
              </div>
              <div className="p-4 sm:p-5 space-y-3 text-sm">
                <p className="text-xs text-slate-500">
                  내 개인 일정으로 등록되며, 이 고객과 연결됩니다.{' '}
                  <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">업무 캘린더</Link>에서 확인할 수 있습니다.
                </p>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">제목</label>
                  <input className="w-full rounded border px-3 py-2 text-sm" value={schedTitle} onChange={(e) => setSchedTitle(e.target.value)} disabled={schedBusy} />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">설명 (선택)</label>
                  <textarea className="w-full min-h-[72px] rounded border px-3 py-2 text-sm" value={schedDesc} onChange={(e) => setSchedDesc(e.target.value)} disabled={schedBusy} placeholder="통화 목적, 준비물 등" />
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-0.5">시작</label>
                    <input type="datetime-local" className="w-full rounded border px-2 py-2 text-sm" value={schedStart} onChange={(e) => setSchedStart(e.target.value)} disabled={schedBusy} />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-0.5">종료</label>
                    <input type="datetime-local" className="w-full rounded border px-2 py-2 text-sm" value={schedEnd} onChange={(e) => setSchedEnd(e.target.value)} disabled={schedBusy} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="sgezOutline" size="sm" disabled={schedBusy} onClick={() => setSchedModal(null)}>취소</Button>
                  <Button type="button" variant="sgez" size="sm" disabled={schedBusy} onClick={submitSchedule}>{schedBusy ? '등록 중…' : '일정 등록'}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {contactModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={contactBusy}
            onClick={() => { if (!contactBusy) setContactModal(null) }}
            aria-label="연락 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div className="font-bold text-sm sm:text-base">연락 등록 · {contactModal.name}</div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50" disabled={contactBusy} onClick={() => setContactModal(null)}>닫기</button>
              </div>
              <div className="p-4 sm:p-5 space-y-3 text-sm">
                <p className="text-xs text-slate-500">
                  연락 이력은 고객과 연결된 개인 일정으로 함께 저장되어 <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">일정 메뉴</Link>에서도 확인할 수 있습니다.
                </p>
                {contactDraftInfo ? (
                  <div className={`rounded-lg border px-3 py-2 text-xs ${
                    contactDraftInfo.error ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-sky-200 bg-sky-50 text-sky-900'
                  }`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-bold">
                        {contactDraftInfo.loading ? '연락 문구 초안 생성 중...' : contactDraftInfo.intentLabel || '연락 초안'}
                      </div>
                      {contactModal?.intent ? (
                        <button
                          type="button"
                          className="rounded border border-sky-200 bg-white px-2 py-0.5 text-[11px] font-bold text-brand-navy hover:bg-sky-50 disabled:opacity-50"
                          disabled={contactDraftGenerating || contactBusy}
                          onClick={() => generateContactDraft({
                            intent: contactModal.intent,
                            relatedId: contactModal.relatedId,
                            openModal: false,
                          })}
                        >
                          {contactDraftGenerating ? '생성 중...' : '다시 생성'}
                        </button>
                      ) : null}
                    </div>
                    <div className="mt-1 leading-snug">
                      {contactDraftInfo.error || contactDraftInfo.riskNotice || '자동 발송되지 않습니다. 내용을 확인·수정한 뒤 연락 기록으로 저장하세요.'}
                    </div>
                  </div>
                ) : null}
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">연락 일시</label>
                  <input type="datetime-local" className="w-full rounded border px-2 py-2 text-sm" value={contactAt} onChange={(e) => setContactAt(e.target.value)} disabled={contactBusy} />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">연락 내용</label>
                  <textarea
                    className="w-full min-h-[120px] rounded border px-3 py-2 text-sm"
                    value={contactContent}
                    onChange={(e) => setContactContent(e.target.value)}
                    disabled={contactBusy}
                    maxLength={2000}
                    placeholder="통화/문자/방문 등 연락 내용과 후속 조치를 간략히 적어 주세요."
                  />
                  <div className="mt-1 text-right text-[11px] text-slate-400">{contactContent.length}/2000</div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="sgezOutline" size="sm" disabled={contactBusy} onClick={() => setContactModal(null)}>취소</Button>
                  <Button type="button" variant="sgez" size="sm" disabled={contactBusy} onClick={submitContact}>{contactBusy ? '등록 중…' : '연락 등록'}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {aiMessageModalOpen && aiMessageDraft ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setAiMessageModalOpen(false)}
            aria-label="AI 문구 닫기"
          />
          <div className="relative flex min-h-full items-center justify-center px-3 py-10 sm:px-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-xl rounded-2xl border bg-white shadow-xl overflow-hidden" role="dialog" aria-modal="true">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div>
                  <div className="font-bold text-sm sm:text-base">{aiMessageDraft.title || 'AI 메시지 초안'}</div>
                  {aiMessageDraft.generationMode === 'RULE' ? (
                    <div className="mt-0.5 text-[11px] text-slate-600">룰 기반 템플릿 초안입니다. 필요 시 문구를 수정해 주세요.</div>
                  ) : null}
                  {aiMessageDraft.aiAvailable === false && aiMessageDraft.generationMode !== 'RULE' ? (
                    <div className="mt-0.5 text-[11px] text-amber-700">AI 설정을 사용할 수 없어 기본 초안으로 표시했습니다.</div>
                  ) : null}
                </div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setAiMessageModalOpen(false)}>닫기</button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-5 space-y-3">
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-1 text-[11px] font-semibold text-slate-500">고객 안내문</div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800">
                    {aiMessageDraft.detailedMessage || aiMessageDraft.shortMessage || '—'}
                  </p>
                </div>
                {Array.isArray(aiMessageDraft.riskWarnings) && aiMessageDraft.riskWarnings.length > 0 ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
                    {aiMessageDraft.riskWarnings.map((w) => <div key={String(w)}>· {String(w)}</div>)}
                  </div>
                ) : null}
                <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                  생성 시 확정·과장 표현을 회피하도록 요청했지만, 발송 전 수신동의·광고성 표현·약관/심사 조건을 다시 확인해 주세요.
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="sgezOutline" size="sm" onClick={() => setAiMessageModalOpen(false)}>닫기</Button>
                  <Button
                    type="button"
                    variant="sgezOutline"
                    size="sm"
                    disabled={riskChecking}
                    onClick={() => checkComplianceRisk(aiMessageDraft.detailedMessage || aiMessageDraft.shortMessage)}
                  >
                    리스크 검사
                  </Button>
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    onClick={() => copyAiMessage(aiMessageDraft.detailedMessage || aiMessageDraft.shortMessage)}
                  >
                    문구 복사
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {easyExplainModalOpen && easyExplainDraft ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setEasyExplainModalOpen(false)}
            aria-label="고객용 설명 닫기"
          />
          <div className="relative flex min-h-full items-center justify-center px-3 py-10 sm:px-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-xl rounded-2xl border bg-white shadow-xl overflow-hidden" role="dialog" aria-modal="true">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div>
                  <div className="font-bold text-sm sm:text-base">{easyExplainDraft.title || '고객용 쉬운 설명'}</div>
                  {easyExplainDraft.aiAvailable === false ? (
                    <div className="mt-0.5 text-[11px] text-amber-700">AI 설정을 사용할 수 없어 기본 설명으로 표시했습니다.</div>
                  ) : null}
                </div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setEasyExplainModalOpen(false)}>닫기</button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-5 space-y-3">
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-1 text-[11px] font-semibold text-slate-500">고객 설명문</div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800">
                    {easyExplainDraft.customerExplanation || '—'}
                  </p>
                </div>
                {Array.isArray(easyExplainDraft.keyPoints) && easyExplainDraft.keyPoints.length > 0 ? (
                  <div className="rounded border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-950">
                    <div className="mb-1 font-semibold">핵심 포인트</div>
                    {easyExplainDraft.keyPoints.map((p) => <div key={String(p)}>· {String(p)}</div>)}
                  </div>
                ) : null}
                {Array.isArray(easyExplainDraft.riskWarnings) && easyExplainDraft.riskWarnings.length > 0 ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
                    {easyExplainDraft.riskWarnings.map((w) => <div key={String(w)}>· {String(w)}</div>)}
                  </div>
                ) : null}
                {easyExplainDraft.nextAction ? (
                  <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                    다음 액션: {easyExplainDraft.nextAction}
                  </div>
                ) : null}
                <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                  고객용 설명은 자동 발송되지 않습니다. 발송 전 약관, 심사 조건, 수신동의, 광고성 표현 여부를 확인해 주세요.
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="sgezOutline" size="sm" onClick={() => setEasyExplainModalOpen(false)}>닫기</Button>
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    onClick={() => copyAiMessage(easyExplainDraft.customerExplanation)}
                  >
                    설명 복사
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {riskCheckModalOpen && riskCheckResult ? (
        <div className="fixed inset-0 z-[61]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setRiskCheckModalOpen(false)}
            aria-label="표현 리스크 검사 닫기"
          />
          <div className="relative flex min-h-full items-center justify-center px-3 py-10 sm:px-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-xl rounded-2xl border bg-white shadow-xl overflow-hidden" role="dialog" aria-modal="true">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div>
                  <div className="font-bold text-sm sm:text-base">금소법·광고 표현 리스크 검사</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    위험도 {riskCheckResult.riskLevel || 'LOW'} · 점수 {riskCheckResult.score ?? 0}
                  </div>
                </div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setRiskCheckModalOpen(false)}>닫기</button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-5 space-y-3">
                {Array.isArray(riskCheckResult.findings) && riskCheckResult.findings.length > 0 ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
                    <div className="mb-1 font-semibold">감지된 리스크</div>
                    {riskCheckResult.findings.map((f, idx) => (
                      <div key={`risk-finding-${idx}`} className="border-t border-amber-200/70 py-1 first:border-t-0">
                        <strong>{f.keyword}</strong> · {f.category}: {f.reason}
                        {f.suggestion ? <div className="text-amber-800">제안: {f.suggestion}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    대표 위험 표현은 감지되지 않았습니다.
                  </div>
                )}
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-slate-500">수정 제안 문구</span>
                    <button
                      type="button"
                      className="text-[11px] font-semibold text-brand-navy hover:underline"
                      onClick={() => copyAiMessage(riskCheckResult.safeRewrite)}
                    >
                      복사
                    </button>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800">{riskCheckResult.safeRewrite || '—'}</p>
                </div>
                {Array.isArray(riskCheckResult.checklist) && riskCheckResult.checklist.length > 0 ? (
                  <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                    {riskCheckResult.checklist.map((item) => <div key={String(item)}>· {String(item)}</div>)}
                  </div>
                ) : null}
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="sgezOutline" size="sm" onClick={() => setRiskCheckModalOpen(false)}>닫기</Button>
                  <Button type="button" variant="sgez" size="sm" onClick={() => copyAiMessage(riskCheckResult.safeRewrite)}>수정 문구 복사</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {cardWizardOpen ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={cardWizardBusy}
            onClick={() => { if (!cardWizardBusy) setCardWizardOpen(false) }}
            aria-label="명함 업로드 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div className="pointer-events-auto relative rounded-2xl bg-white shadow-xl border overflow-hidden max-h-[min(90vh,640px)] flex flex-col" role="dialog" aria-modal="true">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5 shrink-0">
                <div className="font-bold text-sm sm:text-base">
                  {cardWizardStep === 'front' ? '명함 앞면 업로드' : cardWizardStep === 'back' ? '명함 뒷면 업로드' : '인식 결과 확인'}
                </div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50" disabled={cardWizardBusy} onClick={() => setCardWizardOpen(false)}>닫기</button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 text-sm">
                {cardWizardStep === 'front' ? (
                  <>
                    <p className="text-xs text-slate-500">명함 앞면 이미지를 선택한 뒤「다음」으로 뒷면을 올리거나, 뒷면 없이 바로 인식할 수 있습니다.</p>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={cardWizardBusy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        setCardWizardBusy(true)
                        try {
                          const fid = await uploadCardFile(f)
                          setCardDraftFrontId(fid)
                          try { window?.toast?.show?.('앞면이 업로드되었습니다.', 'success') } catch { }
                        } catch (upErr) {
                          const um = getApiErrorMessage(upErr, '업로드에 실패했습니다.')
                          setErr(um)
                          try { window?.toast?.show?.(um, 'error', 4000) } catch { }
                        } finally {
                          setCardWizardBusy(false)
                        }
                      }}
                      className="block w-full text-xs"
                    />
                    {cardDraftFrontId ? <p className="text-[11px] text-emerald-700">앞면 파일이 준비되었습니다.</p> : null}
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" className="rounded border px-4 py-2 text-sm hover:bg-slate-50" disabled={cardWizardBusy} onClick={() => setCardWizardOpen(false)}>취소</button>
                      <button type="button" className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50" disabled={cardWizardBusy || !cardDraftFrontId} onClick={() => setCardWizardStep('back')}>다음</button>
                    </div>
                  </>
                ) : null}
                {cardWizardStep === 'back' ? (
                  <>
                    <p className="text-xs text-slate-500">뒷면이 없으면 건너뛰고 인식만 진행할 수 있습니다.</p>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={cardWizardBusy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        setCardWizardBusy(true)
                        try {
                          const fid = await uploadCardFile(f)
                          setCardDraftBackId(fid)
                          try { window?.toast?.show?.('뒷면이 업로드되었습니다.', 'success') } catch { }
                        } catch (upErr) {
                          const um = getApiErrorMessage(upErr, '업로드에 실패했습니다.')
                          setErr(um)
                          try { window?.toast?.show?.(um, 'error', 4000) } catch { }
                        } finally {
                          setCardWizardBusy(false)
                        }
                      }}
                      className="block w-full text-xs"
                    />
                    {cardDraftBackId ? <p className="text-[11px] text-emerald-700">뒷면 파일이 준비되었습니다.</p> : null}
                    <div className="flex justify-between gap-2 pt-2">
                      <button type="button" className="rounded border px-4 py-2 text-sm hover:bg-slate-50" disabled={cardWizardBusy} onClick={() => setCardWizardStep('front')}>이전</button>
                      <div className="flex gap-2">
                        <button type="button" className="rounded border px-4 py-2 text-sm hover:bg-slate-50" disabled={cardWizardBusy} onClick={runCardExtractPreview}>뒷면 없이 업로드</button>
                        <button type="button" className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50" disabled={cardWizardBusy} onClick={runCardExtractPreview}>업로드하기</button>
                      </div>
                    </div>
                  </>
                ) : null}
                {cardWizardStep === 'diff' ? (
                  <>
                    <p className="text-xs text-slate-600">기존 입력과 다른 항목입니다. 반영하면 명함 이미지가 저장되고 프로필이 갱신됩니다.</p>
                    {cardDiffs.length === 0 ? (
                      <p className="text-xs text-slate-500">변경이 감지되지 않았습니다. 그래도 명함만 저장할 수 있습니다.</p>
                    ) : (
                      <ul className="space-y-2 rounded border border-slate-100 bg-slate-50/80 p-3 text-xs">
                        {cardDiffs.map((d) => (
                          <li key={d.key} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                            <div className="font-semibold text-slate-800">{d.label}</div>
                            <div className="text-rose-700">기존: {d.before || '—'}</div>
                            <div className="text-emerald-800">인식: {d.after || '—'}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex flex-wrap justify-end gap-2 pt-2">
                      <button type="button" className="rounded border px-4 py-2 text-sm hover:bg-slate-50" disabled={cardWizardBusy} onClick={saveBusinessCardImagesKeepProfile}>기존 입력 유지</button>
                      <button type="button" className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50" disabled={cardWizardBusy} onClick={applyCardWizardToProfile}>반영</button>
                    </div>
                  </>
                ) : null}
              </div>
              {(cardWizardBusy || cardExtracting) ? (
                <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center bg-white/80 px-4 backdrop-blur-[1px]">
                  <span
                    className="inline-block h-9 w-9 animate-spin rounded-full border-2 border-slate-200 border-t-brand-navy"
                    aria-hidden
                  />
                  <p className="mt-3 max-w-[17rem] text-center text-sm font-medium text-slate-800">
                    {cardExtracting ? '명함에서 정보를 읽는 중입니다. 잠시만 기다려 주세요.' : '처리 중입니다…'}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {coverageRuleSettingsOpen ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl" role="dialog" aria-modal="true">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <div className="text-base font-bold text-slate-900">내 표준가입금액 설정</div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                  <span>{coverageRuleSettings?.sourceLabel || '운영자 템플릿 기준'}</span>
                  <span>·</span>
                  <span>{coverageRuleSettings?.hasPersonalRules ? '내 설정 적용 중' : '아직 개인 설정을 저장하지 않음'}</span>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  disabled={coverageRuleSettingsLoading}
                  onClick={loadCoverageRuleSettings}
                >
                  새로고침
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setCoverageRuleSettingsOpen(false)}
                >
                  닫기
                </button>
              </div>
            </div>
            {coverageRuleError ? (
              <div className="border-b border-rose-100 bg-rose-50 px-5 py-2 text-xs text-rose-700">
                {coverageRuleError}
              </div>
            ) : null}
            <div className="overflow-auto p-5">
              {coverageRuleSettingsLoading ? (
                <div className="rounded border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  표준가입금액 설정을 불러오는 중입니다.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded border border-slate-200 bg-slate-50/80 p-3">
                    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                      <label className="block text-xs font-bold text-slate-700">
                        운영자 템플릿 불러오기
                        <select
                          className="mt-1 h-9 w-full rounded border border-slate-300 bg-white px-3 text-sm"
                          value={coverageRuleTemplateId}
                          onChange={(e) => setCoverageRuleTemplateId(e.target.value)}
                        >
                          {asArray(coverageRuleSettings?.templateOptions).map((template) => (
                            <option key={template.id} value={template.id}>
                              {coverageRuleTemplateLabel(template)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                          disabled={coverageRuleApplying || !coverageRuleTemplateId}
                          onClick={applyCoverageRuleTemplate}
                        >
                          {coverageRuleApplying ? '불러오는 중' : '내 설정으로 불러오기'}
                        </button>
                        <button
                          type="button"
                          className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                          disabled={coverageRuleApplying || !coverageRuleSettings?.hasPersonalRules}
                          onClick={resetCoverageRuleSettings}
                        >
                          개인 설정 초기화
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <input
                      className="h-9 w-full rounded border border-slate-300 px-3 text-sm sm:w-80"
                      value={coverageRuleFilter}
                      onChange={(e) => setCoverageRuleFilter(e.target.value)}
                      placeholder="담보명, 분류, 키워드 검색"
                    />
                    <div className="text-xs font-medium text-slate-500">
                      전체 {coverageRuleItems.length.toLocaleString('ko-KR')}개 · 표시 {filteredCoverageRuleItems.length.toLocaleString('ko-KR')}개
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded border border-slate-200">
                    <table className="min-w-full text-left text-xs">
                      <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700">
                        <tr>
                          <th className="px-2 py-2">분류</th>
                          <th className="px-2 py-2">담보</th>
                          <th className="px-2 py-2">중요도</th>
                          <th className="px-2 py-2 text-right">최소</th>
                          <th className="px-2 py-2 text-right">권장</th>
                          <th className="px-2 py-2 text-right">상한</th>
                          <th className="px-2 py-2">키워드</th>
                          <th className="px-2 py-2 text-center">사용</th>
                          <th className="px-2 py-2 text-right">작업</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {filteredCoverageRuleItems.map((row) => (
                          <tr key={row.ruleCode || row.label}>
                            <td className="px-2 py-2 align-top">
                              <input
                                className="h-8 w-28 rounded border border-slate-300 px-2 text-xs"
                                value={row.category || ''}
                                onChange={(e) => updateCoverageRuleSettingDraft(row.ruleCode, 'category', e.target.value)}
                              />
                            </td>
                            <td className="px-2 py-2 align-top">
                              <input
                                className="h-8 w-40 rounded border border-slate-300 px-2 text-xs font-semibold text-slate-900"
                                value={row.label || ''}
                                onChange={(e) => updateCoverageRuleSettingDraft(row.ruleCode, 'label', e.target.value)}
                              />
                            </td>
                            <td className="px-2 py-2 align-top">
                              <select
                                className="h-8 w-20 rounded border border-slate-300 bg-white px-2 text-xs"
                                value={row.importance || '권장'}
                                onChange={(e) => updateCoverageRuleSettingDraft(row.ruleCode, 'importance', e.target.value)}
                              >
                                <option value="필수">필수</option>
                                <option value="권장">권장</option>
                                <option value="선택">선택</option>
                              </select>
                            </td>
                            {['minAmountManWon', 'targetAmountManWon', 'maxAmountManWon'].map((field) => (
                              <td key={`${row.ruleCode}-${field}`} className="px-2 py-2 align-top">
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    className="h-8 w-24 rounded border border-slate-300 px-2 text-right text-xs tabular-nums"
                                    value={row[field] ?? ''}
                                    onChange={(e) => updateCoverageRuleSettingDraft(row.ruleCode, field, e.target.value)}
                                  />
                                  <span className="text-[11px] text-slate-500">만원</span>
                                </div>
                              </td>
                            ))}
                            <td className="px-2 py-2 align-top">
                              <textarea
                                className="min-h-[2rem] w-48 rounded border border-slate-300 px-2 py-1 text-xs"
                                value={row.keywords || ''}
                                onChange={(e) => updateCoverageRuleSettingDraft(row.ruleCode, 'keywords', e.target.value)}
                              />
                            </td>
                            <td className="px-2 py-2 text-center align-top">
                              <input
                                type="checkbox"
                                checked={row.active !== false}
                                onChange={(e) => updateCoverageRuleSettingDraft(row.ruleCode, 'active', e.target.checked)}
                              />
                            </td>
                            <td className="px-2 py-2 text-right align-top">
                              <button
                                type="button"
                                className="rounded bg-brand-navy px-2.5 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                                disabled={coverageRuleSavingCode === row.ruleCode}
                                onClick={() => saveCoverageRuleSettingItem(row)}
                              >
                                {coverageRuleSavingCode === row.ruleCode ? '저장 중' : '저장'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredCoverageRuleItems.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-500">
                              표시할 표준가입금액 항목이 없습니다.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {diagnosisRuleModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <div className="text-base font-bold text-slate-900">진단시뮬레이션 룰 관리</div>
                <p className="mt-1 text-xs text-slate-500">
                  시나리오별 금액 가정, 추천 조건, 담보 키워드를 편집합니다. 금액은 만원 단위입니다.
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  disabled={diagnosisRuleLoading}
                  onClick={loadDiagnosisScenarioRules}
                >
                  새로고침
                </button>
                <button
                  type="button"
                  className="rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                  disabled={diagnosisRuleLoading}
                  onClick={resetDiagnosisScenarioRules}
                >
                  기본값 복원
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setDiagnosisRuleModalOpen(false)}
                >
                  닫기
                </button>
              </div>
            </div>
            {diagnosisRuleError ? (
              <div className="border-b border-rose-100 bg-rose-50 px-5 py-2 text-xs text-rose-700">
                {diagnosisRuleError}
              </div>
            ) : null}
            <div className="overflow-auto p-5">
              {diagnosisRuleLoading ? (
                <div className="rounded border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  룰을 불러오는 중입니다.
                </div>
              ) : (
                <div className="grid min-h-[620px] gap-4 lg:grid-cols-[19rem_minmax(0,1fr)]">
                  <div className="flex min-h-0 flex-col rounded border border-slate-200 bg-slate-50">
                    <div className="border-b border-slate-200 p-3">
                      <input
                        className="h-9 w-full rounded border border-slate-300 bg-white px-3 text-sm"
                        value={diagnosisRuleFilter}
                        onChange={(e) => setDiagnosisRuleFilter(e.target.value)}
                        placeholder="시나리오 검색"
                      />
                      <div className="mt-2 text-[11px] font-medium text-slate-500">
                        전체 {diagnosisRuleRows.length.toLocaleString('ko-KR')}개 · 표시 {filteredDiagnosisRuleRows.length.toLocaleString('ko-KR')}개
                      </div>
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto p-2">
                      {filteredDiagnosisRuleRows.length ? filteredDiagnosisRuleRows.map((row) => {
                        const active = row.scenarioCode === selectedDiagnosisRule?.scenarioCode
                        const need = Number(row.outOfPocketCostManWon || 0) + Number(row.recoveryLivingCostManWon || 0)
                        return (
                          <button
                            key={row.scenarioCode}
                            type="button"
                            className={`mb-2 w-full rounded border px-3 py-2 text-left transition ${active ? 'border-brand-navy bg-white shadow-sm' : 'border-transparent bg-white hover:border-slate-200'}`}
                            onClick={() => setDiagnosisRuleSelectedCode(row.scenarioCode)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="line-clamp-1 text-sm font-bold text-slate-900">{row.title || row.diagnosisName || '진단 시나리오'}</div>
                                <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">{[row.diagnosisName, row.category].filter(Boolean).join(' · ') || '분류 미입력'}</div>
                              </div>
                              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${row.active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
                                {row.active === false ? '미사용' : '사용'}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                              <span>필요 자금</span>
                              <b className="text-slate-800">{Number.isFinite(need) ? `${need.toLocaleString('ko-KR')}만원` : '-'}</b>
                            </div>
                          </button>
                        )
                      }) : (
                        <div className="rounded border border-dashed border-slate-300 bg-white px-3 py-8 text-center text-sm text-slate-500">
                          검색 결과가 없습니다.
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedDiagnosisRule ? (
                    <div className="min-h-0 overflow-auto rounded border border-slate-200 bg-white">
                      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">{selectedDiagnosisRule.title || '진단 시나리오'}</span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                              필요 자금 {selectedDiagnosisNeedManWon.toLocaleString('ko-KR')}만원
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            본인부담과 회복·생활비를 합산해 고객용 이미지의 총 필요 자금을 계산합니다.
                          </div>
                        </div>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                          disabled={diagnosisRuleSavingCode === selectedDiagnosisRule.scenarioCode}
                          onClick={() => saveDiagnosisScenarioRule(selectedDiagnosisRule)}
                        >
                          {diagnosisRuleSavingCode === selectedDiagnosisRule.scenarioCode ? '저장 중' : '저장'}
                        </button>
                      </div>

                      <div className="grid gap-4 p-4">
                        <section className="rounded border border-slate-200 p-4">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 className="text-sm font-bold text-slate-900">기본 정보</h3>
                            <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700">
                              <input
                                type="checkbox"
                                checked={selectedDiagnosisRule.active !== false}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'active', e.target.checked)}
                              />
                              사용
                            </label>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <label className="block text-xs font-bold text-slate-700">
                              시나리오 제목
                              <input
                                className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-sm"
                                value={selectedDiagnosisRule.title || ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'title', e.target.value)}
                                placeholder="예: 대장암·위암 수술치료 가상 시나리오"
                              />
                            </label>
                            <label className="block text-xs font-bold text-slate-700">
                              진단명
                              <input
                                className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-sm"
                                value={selectedDiagnosisRule.diagnosisName || ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'diagnosisName', e.target.value)}
                                placeholder="예: 대장암/위암"
                              />
                            </label>
                            <label className="block text-xs font-bold text-slate-700">
                              분류
                              <input
                                className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-sm"
                                value={selectedDiagnosisRule.category || ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'category', e.target.value)}
                                placeholder="예: 암/소화기"
                              />
                            </label>
                            <label className="block text-xs font-bold text-slate-700">
                              표시 순서
                              <input
                                type="number"
                                className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-right text-sm"
                                value={selectedDiagnosisRule.displayOrder ?? 0}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'displayOrder', e.target.value)}
                              />
                            </label>
                          </div>
                        </section>

                        <section className="rounded border border-slate-200 p-4">
                          <h3 className="text-sm font-bold text-slate-900">금액 가정</h3>
                          <div className="mt-3 grid gap-3 sm:grid-cols-4">
                            {[
                              ['expectedMedicalCostManWon', '총 진료비', 'slate'],
                              ['outOfPocketCostManWon', '본인부담·비급여', 'rose'],
                              ['recoveryLivingCostManWon', '회복·생활비', 'amber'],
                            ].map(([field, label]) => (
                              <label key={field} className="block rounded border border-slate-200 bg-slate-50 p-3 text-xs font-bold text-slate-700">
                                {label}
                                <div className="mt-2 flex items-center overflow-hidden rounded border border-slate-300 bg-white">
                                  <input
                                    type="number"
                                    min="0"
                                    className="h-9 min-w-0 flex-1 px-3 text-right text-sm font-bold text-slate-900 outline-none"
                                    value={selectedDiagnosisRule[field] ?? ''}
                                    onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, field, e.target.value)}
                                  />
                                  <span className="border-l border-slate-200 bg-white px-2 text-[11px] text-slate-500">만원</span>
                                </div>
                              </label>
                            ))}
                            <div className="rounded border border-violet-200 bg-violet-50 p-3">
                              <div className="text-xs font-bold text-violet-900">총 필요 자금</div>
                              <div className="mt-3 text-right text-lg font-black text-violet-700">
                                {selectedDiagnosisNeedManWon.toLocaleString('ko-KR')}만원
                              </div>
                              <div className="mt-1 text-right text-[11px] text-violet-700">본인부담 + 회복·생활비</div>
                            </div>
                          </div>
                        </section>

                        <section className="rounded border border-slate-200 p-4">
                          <h3 className="text-sm font-bold text-slate-900">추천 조건</h3>
                          <div className="mt-3 grid gap-3 sm:grid-cols-4">
                            <label className="block text-xs font-bold text-slate-700">
                              성별
                              <select
                                className="mt-1 h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm"
                                value={selectedDiagnosisRule.gender || ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'gender', e.target.value)}
                              >
                                <option value="">전체</option>
                                <option value="MALE">남성</option>
                                <option value="FEMALE">여성</option>
                              </select>
                            </label>
                            <label className="block text-xs font-bold text-slate-700">
                              최소 나이
                              <input
                                type="number"
                                min="0"
                                className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-right text-sm"
                                value={selectedDiagnosisRule.minAge ?? ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'minAge', e.target.value)}
                                placeholder="전체"
                              />
                            </label>
                            <label className="block text-xs font-bold text-slate-700">
                              최대 나이
                              <input
                                type="number"
                                min="0"
                                className="mt-1 h-9 w-full rounded border border-slate-300 px-3 text-right text-sm"
                                value={selectedDiagnosisRule.maxAge ?? ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'maxAge', e.target.value)}
                                placeholder="전체"
                              />
                            </label>
                          </div>
                        </section>

                        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                          <div className="rounded border border-slate-200 p-4">
                            <h3 className="text-sm font-bold text-slate-900">고객 설명</h3>
                            <textarea
                              className="mt-3 min-h-[96px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                              value={selectedDiagnosisRule.summary || ''}
                              onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'summary', e.target.value)}
                              placeholder="고객에게 보여줄 시나리오 요약"
                            />
                          </div>
                          <div className="rounded border border-slate-200 p-4">
                            <h3 className="text-sm font-bold text-slate-900">통계·출처</h3>
                            <textarea
                              className="mt-3 min-h-[58px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                              value={selectedDiagnosisRule.statBasis || ''}
                              onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'statBasis', e.target.value)}
                              placeholder="통계/가정 근거"
                            />
                            <div className="mt-2 grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)]">
                              <input
                                className="h-9 rounded border border-slate-300 px-3 text-sm"
                                value={selectedDiagnosisRule.sourceName || ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'sourceName', e.target.value)}
                                placeholder="출처명"
                              />
                              <input
                                className="h-9 rounded border border-slate-300 px-3 text-sm"
                                value={selectedDiagnosisRule.sourceUrl || ''}
                                onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'sourceUrl', e.target.value)}
                                placeholder="출처 URL"
                              />
                            </div>
                          </div>
                        </section>

                        <section className="rounded border border-slate-200 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="text-sm font-bold text-slate-900">담보 매칭 키워드</h3>
                              <p className="mt-1 text-xs text-slate-500">쉼표 또는 줄바꿈으로 구분합니다. 고객 보유 담보명과 비교할 때 사용됩니다.</p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                              {(selectedDiagnosisRule.coverageKeywords || '').split(/[\n,]/).map((v) => v.trim()).filter(Boolean).length}개
                            </span>
                          </div>
                          <textarea
                            className="mt-3 min-h-[110px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={selectedDiagnosisRule.coverageKeywords || ''}
                            onChange={(e) => updateDiagnosisRuleDraft(selectedDiagnosisRule.scenarioCode, 'coverageKeywords', e.target.value)}
                            placeholder="예: 대장암, 위암, 일반암, 암진단"
                          />
                        </section>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
                      편집할 시나리오가 없습니다.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {coverageSummaryTableOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="coverage-summary-table-title"
          onClick={() => setCoverageSummaryTableOpen(false)}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div>
                <h2 id="coverage-summary-table-title" className="text-sm font-bold text-slate-900">보장요약표</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-bold text-sky-900 hover:bg-sky-100 disabled:opacity-50"
                  disabled={coverageSummaryLoading || Boolean(coverageSummaryBusy)}
                  onClick={regenerateCoverageSummaryReport}
                >
                  {coverageSummaryBusy === 'regenerate' ? '생성 중…' : '재생성'}
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={Boolean(coverageSummaryBusy)}
                  onClick={downloadCoverageSummaryExcel}
                >
                  {coverageSummaryBusy === 'excel-download' ? '다운로드 중…' : '엑셀 다운로드'}
                </button>
                <label className={`rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${coverageSummaryBusy ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
                  {coverageSummaryBusy === 'excel-upload' ? '업로드 중…' : '엑셀 업로드'}
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    disabled={Boolean(coverageSummaryBusy)}
                    onChange={uploadCoverageSummaryExcel}
                  />
                </label>
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={Boolean(coverageSummaryBusy) || !coverageSummaryReport}
                  onClick={downloadCoverageSummaryPdf}
                >
                  {coverageSummaryBusy === 'pdf' ? '다운로드 중…' : 'PDF 다운로드'}
                </button>
                {isOperator ? (
                  <>
                    {coverageSummaryTemplateOptions.length > 0 ? (
                      <select
                        className="max-w-[190px] rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
                        value={coverageSummaryTemplateId}
                        onChange={(e) => setCoverageSummaryTemplateId(e.target.value)}
                        disabled={Boolean(coverageSummaryBusy)}
                        title="운영자 전용 보장요약 템플릿"
                      >
                        <option value="">운영 기본 템플릿</option>
                        {coverageSummaryTemplateOptions.map((template) => (
                          <option key={template.id} value={template.id}>
                            {outputTemplateLabel(template)}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <button
                      type="button"
                      className="rounded border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={Boolean(coverageSummaryBusy) || !coverageSummaryReport || coverageSummaryTemplateOptions.length === 0}
                      onClick={downloadCoverageSummaryTemplateImage}
                      title={coverageSummaryTemplateOptions.length > 0 ? '운영자 전용 출력물 템플릿 이미지' : '게시된 이미지 템플릿이 없습니다.'}
                    >
                      {coverageSummaryBusy === 'template-image' ? '생성 중...' : '템플릿 이미지'}
                    </button>
                    {coverageSummaryHtmlTemplateOptions.length > 0 ? (
                      <select
                        className="max-w-[190px] rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
                        value={coverageSummaryHtmlTemplateId}
                        onChange={(e) => setCoverageSummaryHtmlTemplateId(e.target.value)}
                        disabled={Boolean(coverageSummaryBusy)}
                        title="운영자 전용 보장요약 HTML 템플릿"
                      >
                        <option value="">운영 기본 HTML</option>
                        {coverageSummaryHtmlTemplateOptions.map((template) => (
                          <option key={template.id} value={template.id}>
                            {outputTemplateLabel(template)}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <button
                      type="button"
                      className="rounded border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={Boolean(coverageSummaryBusy) || !coverageSummaryReport || coverageSummaryHtmlTemplateOptions.length === 0}
                      onClick={downloadCoverageSummaryTemplateHtml}
                      title={coverageSummaryHtmlTemplateOptions.length > 0 ? '운영자 전용 출력물 HTML' : '게시된 HTML 템플릿이 없습니다.'}
                    >
                      {coverageSummaryBusy === 'template-html' ? '생성 중...' : 'HTML'}
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setCoverageSummaryTableOpen(false)}
                >
                  닫기
                </button>
              </div>
            </div>

            {coverageSummaryPdfProductOptionsVisible ? (
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-700">
                    <span>
                      표시 보험사
                      <span className="ml-2 font-normal text-slate-500">
                        {coverageSummaryPdfAllSelected
                          ? '전체 표시'
                          : `선택 ${coverageSummaryTableProducts.length}개 · 나머지는 외 ${coverageSummaryTableOtherCount}건`}
                      </span>
                    </span>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 font-medium text-slate-600">
                      <input
                        type="checkbox"
                        checked={coverageSummaryIncludeNoDetailContracts}
                        onChange={(e) => setCoverageSummaryIncludeNoDetailContracts(e.target.checked)}
                      />
                      <span>세부담보 없는 계약 포함</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => setCoverageSummaryPdfAllProducts(true)}
                    >
                      전체 선택
                    </button>
                    <button
                      type="button"
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => setCoverageSummaryPdfAllProducts(false)}
                    >
                      전체 표시
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {coverageSummaryPdfProducts.map((product) => {
                    const checked = coverageSummaryPdfEffectiveSelectedKeys.includes(product.key)
                    return (
                      <label
                        key={product.key}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold ${checked ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-slate-200 bg-white text-slate-600'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setCoverageSummaryPdfProductSelected(product.key, e.target.checked)}
                        />
                        <span>
                          {[product.insurerName, product.productName].filter(Boolean).join(' · ') || '계약'}
                          {product.noDetailContract ? <span className="ml-1 font-medium text-amber-700">담보없음</span> : null}
                        </span>
                      </label>
                    )
                  })}
                </div>
                {!coverageSummaryIncludeNoDetailContracts && coverageSummaryOmittedContracts.length > 0 ? (
                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800">
                    {coverageSummaryOmittedContracts.slice(0, 4).map((contract) => contract.insurerName || contract.productName || '보유계약').join(', ')}
                    {coverageSummaryOmittedContracts.length > 4 ? ` 외 ${coverageSummaryOmittedContracts.length - 4}건` : ''}은 세부담보 내역이 없어 요약표 목록에 포함되지 않습니다.
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto">
              {coverageSummaryError ? (
                <div className="m-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{coverageSummaryError}</div>
              ) : null}
              {coverageSummaryLoading ? (
                <p className="m-4 rounded border border-slate-200 bg-slate-50 px-3 py-10 text-center text-xs text-slate-500">보장요약을 불러오는 중…</p>
              ) : !coverageSummaryReport ? (
                <EmptyState title="보장요약 리포트가 없습니다" className="m-4 rounded border border-dashed py-8" />
              ) : (
              <table className="table-fixed border-collapse text-left text-xs" style={{ width: coverageSummaryTableWidth }}>
                <colgroup>
                  <col style={{ width: 72 }} />
                  <col style={{ width: 240 }} />
                  <col style={{ width: 148 }} />
                  {coverageSummaryTableProducts.map((product) => (
                    <col key={`coverage-summary-table-col-${product.key}`} style={{ width: 152 }} />
                  ))}
                  {coverageSummaryTableOtherCount > 0 ? (
                    <col style={{ width: 152 }} />
                  ) : null}
                </colgroup>
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700">
                  <tr>
                    <th className="border border-slate-200 px-2 py-2">분류</th>
                    <th className="border border-slate-200 px-2 py-2">담보</th>
                    <th className="border border-slate-200 px-2 py-2 text-right">합계</th>
                    {coverageSummaryTableProducts.map((product) => (
                      <th key={`coverage-summary-table-head-${product.key}`} className="border border-slate-200 px-2 py-2 text-left">
                        <span className="block max-w-full truncate font-bold" title={[product.insurerName, product.productName].filter(Boolean).join(' · ')}>
                          {product.insurerName || product.productName || '계약'}
                        </span>
                        {product.productName ? (
                          <span className="block max-w-full truncate text-[10px] font-medium text-slate-500" title={product.productName}>
                            {product.productName}
                          </span>
                        ) : null}
                      </th>
                    ))}
                    {coverageSummaryTableOtherCount > 0 ? (
                      <th className="border border-slate-200 px-2 py-2 text-right">외 {coverageSummaryTableOtherCount}건</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {coverageSummaryTableRows.map((row, rowIdx) => {
                    const products = asArray(row.products)
                    const otherRow = isCoverageSummaryOtherRow(row)
                    const selectedAmount = otherRow ? 0 : coverageSummaryTableProducts.reduce((sum, product) => {
                      const productAmount = products
                        .filter((item) => coverageSummaryProductKey(item) === product.key)
                        .reduce((inner, item) => inner + Number(item?.amount || 0), 0)
                      return sum + productAmount
                    }, 0)
                    const totalAmount = Number(row.totalAmount || 0)
                    const otherAmount = otherRow ? 0 : Math.max(0, totalAmount - selectedAmount)
                    const groupRowSpan = coverageSummaryTableGroupSpans.get(rowIdx) || 0
                    const selectedKeys = new Set(coverageSummaryTableProducts.map((product) => product.key))
                    const collapsedItems = otherRow
                      ? products.filter((item) => !selectedKeys.has(coverageSummaryProductKey(item)))
                      : []
                    const collapsedOtherText = otherRow ? coverageSummaryOtherProductText(row, collapsedItems) : ''
                    return (
                      <tr key={`coverage-summary-table-row-${row.coverageCode || rowIdx}`} className="odd:bg-white even:bg-slate-50/70">
                        {groupRowSpan > 0 ? (
                          <td rowSpan={groupRowSpan} className="border border-slate-200 bg-white px-2 py-1.5 text-center align-middle font-semibold text-slate-700">
                            {row.coverageGroup || '-'}
                          </td>
                        ) : null}
                        <td className="break-keep border border-slate-200 px-2 py-1.5 font-bold text-slate-900">{row.coverageName || '-'}</td>
                        <td className={`whitespace-nowrap border border-slate-200 px-2 py-1.5 text-right tabular-nums ${otherRow ? 'bg-slate-100 text-slate-400' : 'text-slate-900'}`}>
                          {otherRow ? '' : formatCoverageAmountKorean(totalAmount)}
                        </td>
                        {coverageSummaryTableProducts.map((product) => {
                          const matchingItems = products.filter((item) => coverageSummaryProductKey(item) === product.key)
                          const amount = matchingItems.reduce((sum, item) => sum + Number(item?.amount || 0), 0)
                          const otherText = otherRow ? coverageSummaryOtherProductText(row, matchingItems) : ''
                          return (
                            <td
                              key={`coverage-summary-table-cell-${row.coverageCode || rowIdx}-${product.key}`}
                              className={`border border-slate-200 px-2 py-1.5 text-slate-800 ${otherRow ? 'whitespace-pre-wrap break-words text-left text-[11px] leading-4' : 'whitespace-nowrap text-right tabular-nums'}`}
                              title={otherRow ? otherText : undefined}
                            >
                              {otherRow ? (otherText || '-') : (amount > 0 ? formatCoverageAmountKorean(amount) : '-')}
                            </td>
                          )
                        })}
                        {coverageSummaryTableOtherCount > 0 ? (
                          <td
                            className={`border border-slate-200 px-2 py-1.5 text-slate-800 ${otherRow ? 'whitespace-pre-wrap break-words text-left text-[11px] leading-4' : 'whitespace-nowrap text-right tabular-nums'}`}
                            title={otherRow ? collapsedOtherText : undefined}
                          >
                            {otherRow ? (collapsedOtherText || '-') : (otherAmount > 0 ? formatCoverageAmountKorean(otherAmount) : '-')}
                          </td>
                        ) : null}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
